import {
  useRef,
  useState,
  useEffect,
  useCallback,
  useMemo,
  forwardRef,
  Fragment,
  type KeyboardEvent,
  type DragEvent,
} from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { DndContext, PointerSensor, useSensor, useSensors, closestCenter, type DragEndEvent } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Box,
  Text,
  Group,
  Stack,
  ActionIcon,
  Loader,
  Modal,
  Drawer,
  UnstyledButton,
  Textarea,
  Tooltip,
  Center,
  ScrollArea,
  Menu,
  TextInput,
  Checkbox,
  Button,
  Select,
  MultiSelect,
  Divider,
  Alert,
  SegmentedControl,
} from '@mantine/core';
import {
  IconInfoCircle,
  IconMessageCircle,
  IconPlus,
  IconSend,
  IconShield,
  IconShieldCheckFilled,
  IconX,
  IconTrashX,
  IconFileText,
  IconPhoto,
  IconPlayerPlay,
  IconMicrophone,
  IconArrowLeft,
  IconLayoutGrid,
  IconCar,
  IconChevronRight,
  IconChevronLeft,
  IconSearch,
  IconDots,
  IconArchive,
  IconArchiveOff,
  IconPin,
  IconPinFilled,
  IconTrash,
  IconAlertCircle,
  IconChevronDown,
  IconClipboardList,
  IconReportMedical,
  IconCircleCheck,
  IconReceipt,
  IconTool,
  IconCopy,
  IconMail,
  IconCheck,
  IconPrinter,
  IconMessage,
  IconFilter,
  IconStar,
  IconStarFilled,
  IconFileImport,
  IconArrowsMaximize,
  IconArrowBack,
  IconEye,
  IconEyeOff,
  IconSparkles,
  IconCamera,
  IconLink,
  IconPencil,
  IconGripVertical,
} from '@tabler/icons-react';
import {
  useConversation,
  useMessages,
  useConversations,
} from '../hooks/useConversations';
import { conversationsApi, vehicleApi, shopApi, quoteApi, smsApi, storageApi } from '../api/client';
import type { RoSuggestion } from '../api/client';
import { thumb, publicUrl, getBackendMode } from '../../utils/backendConfig';
import { useInspection } from '../hooks/useInspection';
import { latestReplyKey } from '../lib/latestReply';
import { maybeFollowTekTab, type TekMediaState } from '../embed/embedBridge';
import { useDeviceOverrides, effectiveFlags } from '../hooks/featureFlags';
import { AuthorizedDot } from '../components/AuthorizedDot';
import { CaptureDocTypePicker } from '../../components/CaptureDocTypePicker';
import { InspectionVideoSegments } from '../components/InspectionVideoSegments';
import { InspectionSuggestionCard } from '../components/InspectionSuggestionCard';
import { QuoteSuggestionCard } from '../components/QuoteSuggestionCard';
import { PaperSignatureModal } from '../components/PaperSignatureModal';
import { DigitalSignatureModal } from '../components/DigitalSignatureModal';
import { PhotoAnnotator } from '../components/PhotoAnnotator';
import { processingState } from '../state/processingState';
import { ReportPreviewPanel, type ReportActions } from '../components/ReportPreviewPanel';
import { AudioRecordingOverlay } from '../components/AudioRecordingOverlay';
import { FuseBoxDiagram } from '../components/FuseBoxDiagram';
import { PriceLookupModal } from '../components/PriceLookupModal';
import { MessageQueueBadge } from '../components/MessageQueueBadge';
import { messageQueue, useMessageQueue, selectCounts, type QueueEntry } from '../state/messageQueue';

const MAX_IN_FLIGHT = 5;

// ── annotated-photo helper ───────────────────────────────────────────────────
// A photo that has been annotated keeps the edited blob as its canonical `url`
// and preserves the pristine original under `original_url`. The galleries always
// show the edited (canonical) version; the original is kept in data only.
type PhotoLike = { url?: string; public_url?: string; gcs_url?: string; original_url?: string } & Record<string, unknown>;
const photoDisplayUrl = (p: PhotoLike | undefined | null): string => {
  if (!p) return '';
  return (p.public_url as string) || publicUrl(p.url) || publicUrl(p.gcs_url) || '';
};

function FuseBoxButton({ boxes }: { boxes: any[] }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <UnstyledButton
        onClick={() => setOpen(true)}
        style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '8px 14px', marginTop: 8,
          background: '#18181B', border: `1px solid ${BORDER}`, borderRadius: 8,
          width: '100%',
        }}
      >
        <Box style={{ width: 24, height: 24, borderRadius: 4, background: '#3B82F6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Text size="10px" fw={700} c="#fff">F</Text>
        </Box>
        <Box style={{ flex: 1 }}>
          <Text size="sm" fw={600} c={TEXT}>Fuse Box Diagram</Text>
          <Text size="xs" c={TEXT_MUTED}>{boxes.reduce((sum, b) => sum + (b.fuses?.length || 0), 0)} fuses — tap to view</Text>
        </Box>
        <IconChevronRight size={16} color={TEXT_MUTED} />
      </UnstyledButton>
      {open && (
        <Modal
          opened onClose={() => setOpen(false)}
          size="xl" padding="md" centered
          title={<Text fw={700} size="md">Fuse Box Diagram</Text>}
          styles={{
            content: { background: '#0C0C0E', border: `1px solid ${BORDER}`, borderRadius: 12 },
            header: { background: '#0C0C0E', borderBottom: `1px solid ${BORDER}`, padding: '12px 16px' },
            body: { padding: 16, maxHeight: '70vh', overflowY: 'auto' },
            overlay: { backdropFilter: 'blur(4px)' },
          }}
        >
          <FuseBoxDiagram boxes={boxes} />
        </Modal>
      )}
    </>
  );
}
import { useInspectionVoice } from '../hooks/useInspectionVoice';
import { useQuoteReadOnly } from '../hooks/useQuoteReadOnly';
import { useAuth } from '../context/AuthContext';
import type {
  MessageResponse,
  ConversationResponse,
  InspectionItemResponse,
  InspectionCategory,
  ItemPatchRequest,
} from '../types';

// ─── Design tokens ────────────────────────────────────────────────────────────
const BG = '#0C0C0E';
const PANEL_BG = '#111113';
const CARD_BG = '#18181B';
const BORDER = '#27272A';
const TEXT = '#F4F4F5';
const TEXT_SECONDARY = '#A1A1AA';
const TEXT_MUTED = '#71717A';
const BLUE = '#2563EB';

// ─── Workflow stages ─────────────────────────────────────────────────────────
// Backend stage values are unchanged (registration/inspection/quote/completed);
// the UI relabels them to Intake / Inspect / Job / Complete.
const WORKFLOW_STAGES = ['registration', 'inspection', 'quote', 'completed'] as const;
const STAGE_LABELS: Record<string, string> = {
  registration: 'INTAKE',
  inspection: 'INSPECT',
  quote: 'JOB',
  completed: 'COMPLETE',
};
const NEXT_LABELS: Record<string, string> = {
  registration: 'Start Inspect →',
  inspection: 'Send Job →',
  quote: 'Mark Complete →',
};

// An inspection item is "rated" once the writer marks it good/soon/replace.
// Every other state (pending, uploaded, analyzing, partial, completed, skipped,
// failed, or null) is still awaiting a rating, so it counts/filters as Pending —
// NOT just literal state === 'pending'.
const isRatedInspState = (s?: string | null): boolean => s === 'good' || s === 'soon' || s === 'replace';

// ─── Stage cards (card-stack UI) ─────────────────────────────────────────────
// The card stack replaces the old tab system. The active stage's card expands
// (own scroll); other stage cards collapse to headers (single-expand accordion).
// Three separate collapsible cards: Intake (date/mileage/concerns), Job (the
// quote/jobs table — its OWN sole card, NOT embedded in Intake), and Inspect.
// Order matches the stack: Intake → Job → Inspect.
const STAGE_CARD_ORDER = ['intake', 'job', 'inspect'] as const;
type StageCardId = (typeof STAGE_CARD_ORDER)[number];

// Map backend workflow_stage → the stage card that auto-expands at that stage.
const STAGE_CARD_OF_STAGE: Record<string, StageCardId> = {
  registration: 'intake',
  inspection: 'inspect',
  quote: 'job',
  completed: 'job', // Complete: all cards visible/readonly, default-expand Job
};

// Card → earliest backend stage at which it appears. Intake AND Job both
// surface from registration (Job is a separate, always-available card you can
// build into early); Inspect appears once the inspection stage is reached.
const STAGE_CARD_STAGE: Record<StageCardId, (typeof WORKFLOW_STAGES)[number]> = {
  intake: 'registration',
  job: 'registration',
  inspect: 'inspection',
};

const STAGE_CARD_LABEL: Record<StageCardId, string> = {
  intake: 'Intake',
  job: 'Job',
  inspect: 'Inspect',
};

// Always-present info cards (header-only collapsed by default, free toggle).
const INFO_CARD_ORDER = ['vehicleInfo', 'vehicleHistory', 'customerInfo'] as const;
type InfoCardId = (typeof INFO_CARD_ORDER)[number];

// Legacy tab map — retained until the tab machinery is fully removed (kept so
// transition auto-switch + stageTab fallback keep compiling during migration).
const STAGE_TAB_MAP: Record<string, string> = {
  registration: 'repair',
  inspection: 'inspect',
  quote: 'quote',
  completed: 'sms',
};

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA',
  'HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
  'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
  'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC',
].map((s) => ({ value: s, label: s }));

// ─── Helpers ──────────────────────────────────────────────────────────────────

function isMediaFile(f: File) {
  if (f.type.startsWith('image/') || f.type.startsWith('video/')) return true;
  const ext = f.name.split('.').pop()?.toLowerCase() || '';
  return ['jpg','jpeg','png','gif','webp','heic','heif','mp4','mov','webm','avi','mkv','m4v'].includes(ext);
}

function isVideoFile(f: File) {
  if (f.type.startsWith('video/')) return true;
  const ext = f.name.split('.').pop()?.toLowerCase() || '';
  return ['mp4','mov','webm','avi','mkv','m4v'].includes(ext);
}

const MAX_VIDEO_SECONDS = 180; // 3 minutes
function checkVideoDuration(file: File): Promise<boolean> {
  return new Promise((resolve) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.onloadedmetadata = () => {
      URL.revokeObjectURL(video.src);
      if (video.duration > MAX_VIDEO_SECONDS) {
        alert(`Video is ${Math.ceil(video.duration / 60)} minutes. Max is 3 minutes. Please trim and try again.`);
        resolve(false);
      } else {
        resolve(true);
      }
    };
    video.onerror = () => { URL.revokeObjectURL(video.src); resolve(true); }; // allow if can't check
    video.src = URL.createObjectURL(file);
  });
}

const VIDEO_EXTS = ['mp4','mov','webm','avi','mkv','m4v','wav'];
function isVideoUrl(url: string) {
  if (!url) return false;
  const ext = url.split('?')[0].split('.').pop()?.toLowerCase() || '';
  return VIDEO_EXTS.includes(ext);
}

function vehicleLabel(conv: ConversationResponse | undefined) {
  if (!conv?.vehicle) return 'No vehicle';
  const { plate, year, make, model } = conv.vehicle;
  const name = [year, make, model].filter(Boolean).join(' ');
  return plate ? `${name} · ${plate}` : name || 'Unknown vehicle';
}

function vehicleShort(conv: ConversationResponse) {
  const v = conv.vehicle;
  if (!v) return conv.title ?? 'New conversation';
  const name = [v.year, v.make, v.model].filter(Boolean).join(' ');
  return name || conv.title || 'Unknown vehicle';
}

function formatTime(iso: string) {
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function formatRelative(iso: string) {
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m`;
  const diffHrs = Math.floor(diffMins / 60);
  if (diffHrs < 24) return `${diffHrs}h`;
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

// ─── Intake mileage quick-edit ────────────────────────────────────────────────
// Inline "add contact" — appends a sub_phone on the Customer Info card itself
// (no drawer/modal). Saves via vehicleApi.patch and refreshes.
const CONTACT_INPUT_STYLE = { background: '#111113', border: '1px solid #27272A', borderRadius: 6, color: '#F4F4F5', fontSize: 13, padding: '6px 8px', outline: 'none', width: '100%' } as const;

function AddContactInline({ conversationId, existingSubs, onSaved }: { conversationId?: number; existingSubs: any[]; onSaved?: () => void }) {
  const [open, setOpen] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [saving, setSaving] = useState(false);
  if (!open) {
    return (
      <UnstyledButton onClick={() => setOpen(true)} style={{ alignSelf: 'flex-start', padding: '2px 2px' }}>
        <Text size="xs" c="#2563EB" fw={600}>+ Add contact</Text>
      </UnstyledButton>
    );
  }
  const inp = CONTACT_INPUT_STYLE;
  const save = async () => {
    if (!conversationId || (!phone.trim() && !email.trim() && !firstName.trim() && !lastName.trim())) { setOpen(false); return; }
    setSaving(true);
    try {
      const name = [firstName.trim(), lastName.trim()].filter(Boolean).join(' ');
      const sub: any = {};
      if (name) { sub.name = name; sub.first_name = firstName.trim(); sub.last_name = lastName.trim(); }
      if (phone.trim()) sub.phone = phone.trim();
      if (email.trim()) sub.email = email.trim();
      await vehicleApi.patch(conversationId, { sub_phones: [...existingSubs, sub] } as any);
      await onSaved?.();
      setFirstName(''); setLastName(''); setPhone(''); setEmail(''); setOpen(false);
    } catch (e) { console.error('[ADD_CONTACT]', e); } finally { setSaving(false); }
  };
  return (
    <Box style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 4 }}>
      <Group gap={6} grow>
        <input placeholder="First name" value={firstName} onChange={(e) => setFirstName(e.target.value)} style={inp} />
        <input placeholder="Last name" value={lastName} onChange={(e) => setLastName(e.target.value)} style={inp} />
      </Group>
      <input placeholder="Phone" value={phone} onChange={(e) => setPhone(e.target.value)} style={inp} />
      <input placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} style={inp} />
      <Group gap={6}>
        <UnstyledButton onClick={save} disabled={saving} style={{ fontSize: 12, fontWeight: 600, color: '#fff', background: '#2563EB', borderRadius: 6, padding: '5px 14px', opacity: saving ? 0.6 : 1 }}>Add</UnstyledButton>
        <UnstyledButton onClick={() => setOpen(false)} style={{ fontSize: 12, fontWeight: 600, color: '#71717A', padding: '5px 10px' }}>Cancel</UnstyledButton>
      </Group>
    </Box>
  );
}

// Inline "find existing customer / vehicle" — searches prior ROs by plate, VIN,
// name, or phone (mirrors NewRoPage's finder) and copies the matched customer's
// contact fields onto the current vehicle record. Collapsed to a single button
// until tapped, matching the AddContactInline pattern.
function CustomerLookupInline({ conversationId, onSaved }: { conversationId?: number; onSaved?: () => void }) {
  const BORDER = '#27272A', BLUE = '#2563EB', TEXT = '#F4F4F5', TEXT_MUTED = '#71717A', PANEL_BG = '#111113';
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<ConversationResponse[]>([]);
  const [searching, setSearching] = useState(false);
  const [applyingId, setApplyingId] = useState<number | null>(null);
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const runSearch = (q: string) => {
    setQuery(q);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    const trimmed = q.trim();
    if (trimmed.length < 2) { setResults([]); setSearching(false); return; }
    setSearching(true);
    searchTimer.current = setTimeout(async () => {
      try {
        const res = await conversationsApi.list({ search: trimmed, limit: 8 });
        // Keep only rows that carry an identifiable vehicle or named customer —
        // those are the only matches worth copying contact details from.
        setResults((res.conversations || []).filter((c) => c.vehicle && (c.vehicle.plate || c.vehicle.vin || c.vehicle.make || c.vehicle.first_name || c.vehicle.phone)));
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
  };

  const applyMatch = async (conv: ConversationResponse) => {
    if (!conversationId || !conv.vehicle) return;
    const v = conv.vehicle;
    setApplyingId(conv.id);
    try {
      await vehicleApi.patch(conversationId, {
        first_name: v.first_name || null,
        last_name: v.last_name || null,
        phone: v.phone || null,
        email: v.email || null,
      });
      await onSaved?.();
      setQuery(''); setResults([]); setOpen(false);
    } catch (e) { console.error('[CUSTOMER_LOOKUP]', e); } finally { setApplyingId(null); }
  };

  if (!open) {
    return (
      <UnstyledButton
        onClick={() => setOpen(true)}
        style={{ width: '100%', border: `1px solid ${BORDER}`, borderRadius: 8, background: 'rgba(37,99,235,0.06)', padding: '12px 14px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
      >
        <IconSearch size={15} color={BLUE} />
        <Text size="sm" fw={600} c={BLUE}>Find existing customer / vehicle</Text>
      </UnstyledButton>
    );
  }
  return (
    <Box style={{ border: `1px solid ${BORDER}`, borderRadius: 8, background: PANEL_BG, padding: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
      <TextInput
        placeholder="Find by plate, VIN, name, or phone…"
        value={query}
        onChange={(e) => runSearch(e.currentTarget.value)}
        leftSection={searching ? <Loader size={14} /> : <IconSearch size={15} color={TEXT_MUTED} />}
        autoComplete="off"
        autoFocus
        styles={{ input: { background: '#0C0C0E', borderColor: BORDER, color: TEXT, fontSize: 13 } }}
      />
      {results.length > 0 && (
        <Stack gap={2}>
          {results.map((conv) => {
            const v = conv.vehicle!;
            const veh = [v.year, v.make, v.model].filter(Boolean).join(' ') || 'Unknown vehicle';
            const who = [v.first_name, v.last_name].filter(Boolean).join(' ');
            const meta = [v.plate, who, v.phone].filter(Boolean).join(' · ');
            return (
              <UnstyledButton
                key={conv.id}
                onClick={() => applyMatch(conv)}
                disabled={applyingId != null}
                style={{ padding: '7px 9px', borderRadius: 6, display: 'block', opacity: applyingId != null && applyingId !== conv.id ? 0.5 : 1 }}
                onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.04)')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
              >
                <Group justify="space-between" wrap="nowrap" gap={8}>
                  <Box style={{ minWidth: 0 }}>
                    <Text size="sm" fw={600} c={TEXT} truncate>{veh}</Text>
                    {meta && <Text size="xs" c={TEXT_MUTED} truncate>{meta}</Text>}
                  </Box>
                  {applyingId === conv.id && <Loader size={12} />}
                </Group>
              </UnstyledButton>
            );
          })}
        </Stack>
      )}
      {query.trim().length >= 2 && !searching && results.length === 0 && (
        <Text size="xs" c={TEXT_MUTED} ta="center" py={4}>No matches.</Text>
      )}
      <UnstyledButton onClick={() => { setOpen(false); setQuery(''); setResults([]); }} style={{ alignSelf: 'flex-end', fontSize: 12, fontWeight: 600, color: TEXT_MUTED, padding: '4px 6px' }}>Cancel</UnstyledButton>
    </Box>
  );
}

// Editable customer contacts (Primary + sub-contacts). Each card has an Edit
// button revealing First/Last/Phone/Email; Primary saves to the vehicle's native
// fields, sub-contacts to the sub_phones array.
function CustomerContacts({ conversationId, vehicle, onSaved }: { conversationId?: number; vehicle: any; onSaved?: () => void }) {
  const BORDER = '#27272A', BLUE = '#2563EB', TEXT = '#F4F4F5', TEXT_MUTED = '#71717A';
  const subs = ((vehicle?.sub_phones ?? []) as any[]).map((sp) => (typeof sp === 'string' ? { phone: sp } : sp));
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState({ first_name: '', last_name: '', phone: '', email: '' });
  const [saving, setSaving] = useState(false);
  const inp = CONTACT_INPUT_STYLE;

  const splitName = (name?: string) => {
    const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
    return { first: parts[0] || '', last: parts.slice(1).join(' ') };
  };
  const contacts = [
    { key: 'primary', label: '★ Primary', primary: true, first_name: vehicle?.first_name || '', last_name: vehicle?.last_name || '', phone: vehicle?.phone || '', email: vehicle?.email || '' },
    ...subs.map((sp: any, i: number) => {
      const sn = splitName(sp?.name);
      return { key: `sub-${i}`, label: `Contact ${i + 2}`, primary: false, first_name: sp?.first_name || sn.first, last_name: sp?.last_name || sn.last, phone: sp?.phone || '', email: sp?.email || '' };
    }),
  ];

  const startEdit = (c: (typeof contacts)[number]) => {
    setDraft({ first_name: c.first_name, last_name: c.last_name, phone: c.phone, email: c.email });
    setEditing(c.key);
  };
  const save = async () => {
    if (!conversationId) { setEditing(null); return; }
    setSaving(true);
    try {
      if (editing === 'primary') {
        await vehicleApi.patch(conversationId, {
          first_name: draft.first_name.trim() || null,
          last_name: draft.last_name.trim() || null,
          phone: draft.phone.trim() || null,
          email: draft.email.trim() || null,
        });
      } else if (editing?.startsWith('sub-')) {
        const idx = Number(editing.slice(4));
        const name = [draft.first_name.trim(), draft.last_name.trim()].filter(Boolean).join(' ');
        const next = subs.map((s: any, i: number) => (i === idx ? { ...s, name, first_name: draft.first_name.trim(), last_name: draft.last_name.trim(), phone: draft.phone.trim(), email: draft.email.trim() } : s));
        await vehicleApi.patch(conversationId, { sub_phones: next } as any);
      }
      await onSaved?.();
      setEditing(null);
    } catch (e) { console.error('[EDIT_CONTACT]', e); } finally { setSaving(false); }
  };
  const removeSub = async (idx: number) => {
    if (!conversationId) return;
    const next = subs.filter((_: any, i: number) => i !== idx);
    try { await vehicleApi.patch(conversationId, { sub_phones: next } as any); await onSaved?.(); setEditing(null); } catch (e) { console.error('[REMOVE_CONTACT]', e); }
  };

  return (
    <Stack gap={8}>
      <CustomerLookupInline conversationId={conversationId} onSaved={onSaved} />
      {contacts.map((c) => {
        const isEditing = editing === c.key;
        const fullName = [c.first_name, c.last_name].filter(Boolean).join(' ');
        const rows: [string, string][] = ([['Name', fullName], ['Phone', c.phone], ['Email', c.email]] as [string, string][]).filter(([, v]) => v);
        return (
          <Box key={c.key} data-no-collapse style={{ border: `1px solid ${BORDER}`, borderRadius: 8, padding: '9px 12px', background: '#0C0C0E' }}>
            <Group justify="space-between" mb={6} wrap="nowrap">
              <Text size="10px" fw={700} c={c.primary ? BLUE : TEXT_MUTED} tt="uppercase" style={{ letterSpacing: 0.5 }}>{c.label}</Text>
              {!isEditing && (
                <UnstyledButton onClick={() => startEdit(c)} style={{ fontSize: 12, fontWeight: 600, color: BLUE }}>Edit</UnstyledButton>
              )}
            </Group>
            {isEditing ? (
              <Stack gap={6}>
                <Group gap={6} grow>
                  <input placeholder="First name" value={draft.first_name} onChange={(e) => setDraft((d) => ({ ...d, first_name: e.target.value }))} style={inp} />
                  <input placeholder="Last name" value={draft.last_name} onChange={(e) => setDraft((d) => ({ ...d, last_name: e.target.value }))} style={inp} />
                </Group>
                <input placeholder="Phone" value={draft.phone} onChange={(e) => setDraft((d) => ({ ...d, phone: e.target.value }))} style={inp} />
                <input placeholder="Email" value={draft.email} onChange={(e) => setDraft((d) => ({ ...d, email: e.target.value }))} style={inp} />
                <Group gap={6} justify="space-between" wrap="nowrap">
                  <Group gap={6}>
                    <UnstyledButton onClick={save} disabled={saving} style={{ fontSize: 12, fontWeight: 600, color: '#fff', background: BLUE, borderRadius: 6, padding: '5px 14px', opacity: saving ? 0.6 : 1 }}>Save</UnstyledButton>
                    <UnstyledButton onClick={() => setEditing(null)} style={{ fontSize: 12, fontWeight: 600, color: TEXT_MUTED, padding: '5px 10px' }}>Cancel</UnstyledButton>
                  </Group>
                  {!c.primary && (
                    <UnstyledButton onClick={() => removeSub(Number(c.key.slice(4)))} style={{ fontSize: 12, fontWeight: 600, color: '#ef4444', padding: '5px 10px' }}>Remove</UnstyledButton>
                  )}
                </Group>
              </Stack>
            ) : rows.length ? (
              rows.map(([label, val]) => (
                <Group key={label} justify="space-between" gap={8} wrap="nowrap">
                  <Text size="xs" c={TEXT_MUTED}>{label}</Text>
                  <Text size="sm" c={TEXT} fw={600} style={{ textAlign: 'right', wordBreak: 'break-all' }}>{val}</Text>
                </Group>
              ))
            ) : (
              <Text size="xs" c={TEXT_MUTED}>Empty — tap Edit to add details.</Text>
            )}
          </Box>
        );
      })}
      <AddContactInline conversationId={conversationId} existingSubs={subs} onSaved={onSaved} />
    </Stack>
  );
}

function MileageQuickEdit({ conversationId, mileage, onSaved }: { conversationId?: number; mileage: number | null; onSaved?: () => void }) {
  const [draft, setDraft] = useState(mileage != null ? String(mileage) : '');
  const [saving, setSaving] = useState(false);
  useEffect(() => { setDraft(mileage != null ? String(mileage) : ''); }, [mileage]);
  const save = async () => {
    if (!conversationId) return;
    const digits = draft.replace(/[^0-9]/g, '');
    if (!digits) return;
    const n = parseInt(digits, 10);
    if (Number.isNaN(n) || n === mileage) return;
    setSaving(true);
    try {
      await vehicleApi.patch(conversationId, { mileage: n } as any);
      onSaved?.();
    } catch { /* non-fatal */ } finally {
      setSaving(false);
    }
  };
  return (
    <TextInput
      value={draft}
      onChange={(e) => setDraft(e.currentTarget.value)}
      onBlur={save}
      onKeyDown={(e) => { if (e.key === 'Enter') (e.currentTarget as HTMLInputElement).blur(); }}
      placeholder="Odometer (mi)"
      rightSection={saving ? <Loader size={12} /> : null}
      size="sm"
      styles={{ input: { background: '#111113', border: '1px solid #27272A', color: '#F4F4F5' } }}
    />
  );
}

// Unified chronological feed for the Summary card: editable memos (source='haist';
// Tek RO notes arrive read-only as source='tekmetric') interleaved with read-only
// customer/report activity events. Newest first; the add-memo composer is pinned
// at the top.
function ConversationTimeline({ conversationId, events, hideComposer = false }: { conversationId?: number; events: any[]; hideComposer?: boolean }) {
  const BORDER = '#27272A', BLUE = '#2563EB', TEXT = '#F4F4F5', TEXT_MUTED = '#71717A';
  const { data: memos, mutate } = useSWR(
    conversationId ? `memos-${conversationId}` : null,
    () => conversationsApi.memos.list(conversationId!).catch(() => [] as Awaited<ReturnType<typeof conversationsApi.memos.list>>),
  );
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editDraft, setEditDraft] = useState('');
  const inp = CONTACT_INPUT_STYLE;

  const add = async () => {
    if (!conversationId || !draft.trim() || saving) return;
    setSaving(true);
    try { await conversationsApi.memos.create(conversationId, { content: draft.trim() }); setDraft(''); await mutate(); } catch (e) { console.error('[MEMO_ADD]', e); } finally { setSaving(false); }
  };
  const saveEdit = async (id: number) => {
    if (!conversationId || !editDraft.trim()) { setEditingId(null); return; }
    try { await conversationsApi.memos.update(conversationId, id, { content: editDraft.trim() }); setEditingId(null); await mutate(); } catch (e) { console.error('[MEMO_EDIT]', e); }
  };
  const remove = async (id: number) => {
    if (!conversationId) return;
    try { await conversationsApi.memos.remove(conversationId, id); await mutate(); } catch (e) { console.error('[MEMO_DEL]', e); }
  };
  const fmtWhen = (raw?: string | null) => {
    if (!raw) return '';
    try { return new Date(raw).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); } catch { return ''; }
  };

  // Merge memos + activity events into one newest-first stream.
  const items = [
    ...((memos || []).map((m) => ({ kind: 'memo' as const, key: `m-${m.id}`, ts: m.created_at || '', memo: m }))),
    ...((events || []).map((ev: any, i: number) => ({ kind: 'event' as const, key: `e-${ev.id ?? i}`, ts: ev.created_at || '', ev }))),
  ].sort((a, b) => String(b.ts).localeCompare(String(a.ts)));

  return (
    <Stack gap={10}>
      {/* Add-memo composer — pinned at the top (new memos land newest-first).
          Hidden in the compact card preview (hideComposer); the feed stays read-only there. */}
      {!hideComposer && (
        <Group gap={6} wrap="nowrap">
          <input placeholder="Add a memo…" value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') add(); }} style={{ ...inp, flex: 1 }} />
          <UnstyledButton onClick={add} disabled={saving || !draft.trim()} style={{ fontSize: 12, fontWeight: 600, color: '#fff', background: BLUE, borderRadius: 6, padding: '6px 14px', opacity: (saving || !draft.trim()) ? 0.5 : 1, flexShrink: 0 }}>Add</UnstyledButton>
        </Group>
      )}

      {items.length === 0 ? (
        <Text size="xs" c={TEXT_MUTED}>No activity yet.</Text>
      ) : (
        <Stack gap={10}>
          {items.map((it) => {
            const when = fmtWhen(it.ts);
            if (it.kind === 'event') {
              const ev = it.ev;
              return (
                <Box key={it.key} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                  <Box style={{ width: 6, height: 6, borderRadius: '50%', background: BLUE, marginTop: 7, flexShrink: 0 }} />
                  <Box style={{ flex: 1, minWidth: 0 }}>
                    <Text size="sm" c={TEXT} style={{ lineHeight: 1.4 }}>{String(ev.content || '').replace(/\*\*/g, '')}</Text>
                    {when && <Text size="xs" c={TEXT_MUTED}>{when}</Text>}
                  </Box>
                </Box>
              );
            }
            // memo — distinct ✎ marker, editable inline (except Tek-sourced).
            const m = it.memo;
            const isEditing = editingId === m.id;
            const isTek = m.source === 'tekmetric';
            return (
              <Box key={it.key} data-no-collapse style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <Text span style={{ fontSize: 11, lineHeight: 1, marginTop: 3, flexShrink: 0, color: TEXT_MUTED }}>✎</Text>
                <Box style={{ flex: 1, minWidth: 0 }}>
                  {isEditing ? (
                    <Stack gap={6}>
                      <textarea value={editDraft} onChange={(e) => setEditDraft(e.target.value)} rows={2} style={{ ...inp, resize: 'vertical' }} />
                      <Group gap={6}>
                        <UnstyledButton onClick={() => saveEdit(m.id)} style={{ fontSize: 12, fontWeight: 600, color: '#fff', background: BLUE, borderRadius: 6, padding: '4px 12px' }}>Save</UnstyledButton>
                        <UnstyledButton onClick={() => setEditingId(null)} style={{ fontSize: 12, color: TEXT_MUTED, padding: '4px 8px' }}>Cancel</UnstyledButton>
                      </Group>
                    </Stack>
                  ) : (
                    <Group justify="space-between" align="flex-start" gap={8} wrap="nowrap">
                      <Box style={{ flex: 1, minWidth: 0 }}>
                        <Text size="sm" c={TEXT} style={{ whiteSpace: 'pre-wrap', lineHeight: 1.4, wordBreak: 'break-word' }}>{isTek ? m.content.replace(/<[^>]*>/g, '') : m.content}</Text>
                        <Text size="xs" c={TEXT_MUTED}>{[m.author_name, when, isTek ? 'Tek' : null].filter(Boolean).join(' · ')}</Text>
                      </Box>
                      {!isTek && (
                        <Group gap={4} style={{ flexShrink: 0 }}>
                          <UnstyledButton onClick={() => { setEditDraft(m.content); setEditingId(m.id); }} style={{ fontSize: 12, color: BLUE, fontWeight: 600 }}>Edit</UnstyledButton>
                          <UnstyledButton onClick={() => remove(m.id)} style={{ fontSize: 14, color: '#ef4444', fontWeight: 600 }}>×</UnstyledButton>
                        </Group>
                      )}
                    </Group>
                  )}
                </Box>
              </Box>
            );
          })}
        </Stack>
      )}
    </Stack>
  );
}

// ─── Skeleton ─────────────────────────────────────────────────────────────────

function SkeletonLine({ width = '100%', height = 14 }: { width?: string | number; height?: number }) {
  return (
    <Box
      style={{
        width,
        height,
        borderRadius: 4,
        background: '#27272A',
        animation: 'pulse 1.5s ease-in-out infinite',
      }}
    />
  );
}

function MessageSkeleton() {
  return (
    <Stack gap={20} px={24} pt={16}>
      {/* User skeleton */}
      <Box style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Box style={{ width: '55%' }}>
          <SkeletonLine width="100%" height={36} />
        </Box>
      </Box>
      {/* AI skeleton */}
      <Box style={{ display: 'flex', gap: 10 }}>
        <Box style={{ width: 28, height: 28, borderRadius: '50%', background: '#27272A', flexShrink: 0 }} />
        <Stack gap={6} style={{ flex: 1 }}>
          <SkeletonLine width="85%" />
          <SkeletonLine width="70%" />
          <SkeletonLine width="50%" />
        </Stack>
      </Box>
      {/* User skeleton */}
      <Box style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Box style={{ width: '45%' }}>
          <SkeletonLine width="100%" height={28} />
        </Box>
      </Box>
      {/* AI skeleton */}
      <Box style={{ display: 'flex', gap: 10 }}>
        <Box style={{ width: 28, height: 28, borderRadius: '50%', background: '#27272A', flexShrink: 0 }} />
        <Stack gap={6} style={{ flex: 1 }}>
          <SkeletonLine width="90%" />
          <SkeletonLine width="60%" />
        </Stack>
      </Box>
    </Stack>
  );
}

// ─── Vehicle Info Modal ───────────────────────────────────────────────────────

function InfoRow({ label, value }: { label: string; value?: string | null }) {
  if (!value) return null;
  return (
    <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '10px 0', borderBottom: `1px solid ${BORDER}` }}>
      <Text size="xs" c={TEXT_MUTED} fw={500} style={{ minWidth: 110 }}>{label}</Text>
      <Text size="sm" fw={500} c={TEXT} style={{ textAlign: 'right', wordBreak: 'break-all' }}>{value}</Text>
    </Box>
  );
}

type RecordType = 'all' | 'service' | 'recall';

const SOURCE_LABEL: Record<string, string> = {
  carfax: 'Carfax',
  nhtsa: 'NHTSA',
  haist: 'HAIST',
  tekmetric: 'Tekmetric',
  autofluent: 'Autofluent',
  winworks: 'Winworks',
  vin_report: 'VIN Report',
};

function recordSourceKeys(rec: { source?: string; origins?: string[] }): string[] {
  // Recall records originate from carfax, nhtsa, or both — pivot off `origins`.
  // Plain service records use the single `source` key.
  if (rec.source === 'recall' && rec.origins && rec.origins.length) return rec.origins;
  return rec.source ? [rec.source] : [];
}

// Shared SWR key so the parent's eager preview fetch and this body dedupe into
// ONE request (no lazy/double fetch when the card is collapsed vs expanded).
const vehicleHistoryKey = (conversationId: number) => `vehicle-history-${conversationId}`;
const fetchVehicleHistory = (conversationId: number) => vehicleApi.getHistory(conversationId).catch(() => null);

function VehicleHistory({ conversationId }: { conversationId: number }) {
  const { data } = useSWR(vehicleHistoryKey(conversationId), () => fetchVehicleHistory(conversationId));
  const [typeFilter, setTypeFilter] = useState<RecordType>('all');
  const [activeSources, setActiveSources] = useState<Set<string> | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  if (!data || !data.sources.length) {
    return (
      <Box p="md" style={{ textAlign: 'center' }}>
        <Text size="xs" c={TEXT_MUTED}>No vehicle history available</Text>
      </Box>
    );
  }

  type Flat = (typeof data.sources)[number]['records'][number] & {
    _type: RecordType;
    _sourceKeys: string[];
  };
  const flat: Flat[] = [];
  for (const src of data.sources) {
    for (const rec of src.records || []) {
      const keys = recordSourceKeys(rec);
      const isRecall = rec.source === 'recall';
      flat.push({ ...rec, _type: isRecall ? 'recall' : 'service', _sourceKeys: keys });
    }
  }
  flat.sort((a, b) => (b.date || '').localeCompare(a.date || ''));

  const availableSources = Array.from(new Set(flat.flatMap((r) => r._sourceKeys))).filter(Boolean);
  // Lazy init so a later getHistory refresh doesn't snap-back to defaults
  // if the user has already toggled chips.
  const effectiveSources = activeSources ?? new Set(availableSources);

  const toggleSource = (key: string) => {
    setActiveSources((prev) => {
      const next = new Set(prev ?? new Set(availableSources));
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
    setExpanded(new Set());
  };

  const visible = flat.filter((r) => {
    if (typeFilter !== 'all' && r._type !== typeFilter) return false;
    if (!r._sourceKeys.length) return true; // keep unknown-source records visible
    return r._sourceKeys.some((k) => effectiveSources.has(k));
  });

  const typeCounts = {
    all: flat.length,
    service: flat.filter((r) => r._type === 'service').length,
    recall: flat.filter((r) => r._type === 'recall').length,
  };

  const recKey = (rec: Flat, i: number) => `${rec.source || '?'}:${rec.ro_number || rec.date || i}`;

  return (
    <Box style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <Box px="md" pt={10} pb={6} style={{ borderBottom: `1px solid ${BORDER}`, flexShrink: 0 }}>
        <Group gap={4} mb={6}>
          {(['all', 'service', 'recall'] as RecordType[]).map((t) => {
            const active = typeFilter === t;
            const label = t === 'all' ? 'All' : t === 'service' ? 'Service' : 'Recall';
            return (
              <UnstyledButton key={t} onClick={() => { setTypeFilter(t); setExpanded(new Set()); }}
                style={{
                  padding: '4px 10px', borderRadius: 4, fontSize: 11, fontWeight: 600,
                  background: active ? '#2563EB' : '#27272A',
                  color: active ? '#fff' : '#A1A1AA',
                }}>
                {label} {typeCounts[t] > 0 && <Text span size="10px" c={active ? '#fff' : TEXT_MUTED}>({typeCounts[t]})</Text>}
              </UnstyledButton>
            );
          })}
        </Group>
        {availableSources.length > 1 && (
          <Group gap={4}>
            {availableSources.map((key) => {
              const on = effectiveSources.has(key);
              return (
                <UnstyledButton key={key} onClick={() => toggleSource(key)}
                  style={{
                    padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 600,
                    background: on ? 'rgba(37,99,235,0.20)' : '#1e1e22',
                    color: on ? '#93c5fd' : '#71717A',
                    border: `1px solid ${on ? 'rgba(37,99,235,0.4)' : '#27272A'}`,
                  }}>
                  {SOURCE_LABEL[key] || key}
                </UnstyledButton>
              );
            })}
          </Group>
        )}
      </Box>

      <ScrollArea style={{ flex: 1 }}>
        <Box px="md" py={8}>
          {visible.map((rec, i) => {
            const id = recKey(rec, i);
            const isOpen = expanded.has(id);
            const chipLabel = SOURCE_LABEL[rec.source || ''] || rec.source || '';
            return (
              <Box key={id}
                data-no-collapse
                onClick={() => rec.lines?.length ? setExpanded(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; }) : undefined}
                mb={6}
                style={{
                  background: rec.is_current ? 'rgba(37,99,235,0.06)' : '#141416',
                  border: `1px solid ${rec.is_current ? 'rgba(37,99,235,0.3)' : '#1e1e22'}`,
                  borderRadius: 8,
                  cursor: rec.lines?.length ? 'pointer' : 'default',
                  overflow: 'hidden',
                }}>
                <Box px={14} py={10} style={{ borderBottom: isOpen ? `1px solid #1e1e22` : 'none' }}>
                  <Group justify="space-between" align="center" wrap="nowrap">
                    <Group gap={8} align="center">
                      {rec.ro_number && <Text size="14px" fw={700} c={TEXT}>RO #{rec.ro_number}</Text>}
                      {rec._type === 'recall' ? (
                        <Text size="11px" fw={700} c="#F59E0B" style={{ background: 'rgba(245,158,11,0.12)', padding: '2px 6px', borderRadius: 4 }}>RECALL</Text>
                      ) : chipLabel && (
                        <Text size="11px" fw={600} c={TEXT_MUTED} style={{ background: '#27272A', padding: '2px 6px', borderRadius: 4 }}>{chipLabel}</Text>
                      )}
                      {rec.is_current && (
                        <Text size="10px" fw={700} c="#fff" style={{ background: '#2563EB', padding: '2px 8px', borderRadius: 4 }}>NOW</Text>
                      )}
                    </Group>
                    {rec.total != null && rec.total > 0 && <Text size="15px" fw={700} c={TEXT} ff="monospace">${Math.round(rec.total).toLocaleString()}</Text>}
                  </Group>
                  <Group gap={10} mt={4}>
                    <Text size="13px" c={TEXT_MUTED}>{rec.date}</Text>
                    {rec.mileage && <Text size="13px" c={TEXT_MUTED}>@ {rec.mileage} mi</Text>}
                    {rec.lines?.length && !isOpen && <Text size="13px" c={TEXT_MUTED}>· {rec.lines.length} items</Text>}
                  </Group>
                </Box>
                {isOpen && rec.lines?.filter((l) => l.type !== 'Summary').map((line, j, arr) => {
                  const isInsp = line.type === 'Inspection';
                  const isRecall = rec._type === 'recall';
                  const statusColor = line.type === 'Approved' ? '#22c55e' : line.type === 'Declined' ? '#ef4444' : (line.type === 'Draft' || line.type === 'Pending') ? '#f59e0b' : isInsp ? '#60a5fa' : TEXT_MUTED;
                  return (
                    <Box key={j} px={14} py={6} style={{ borderBottom: j < arr.length - 1 ? `1px solid #1a1a1c` : 'none' }}>
                      <Group justify="space-between" wrap="nowrap" gap={8}>
                        <Group gap={6} style={{ flex: 1, minWidth: 0 }}>
                          {isInsp && <Box style={{ width: 7, height: 7, borderRadius: '50%', background: '#3b82f6', flexShrink: 0, marginTop: 5 }} />}
                          <Text size="13px" c={isInsp ? '#93c5fd' : TEXT_SECONDARY} lineClamp={isRecall ? undefined : 2}>{line.description}</Text>
                        </Group>
                        <Group gap={6} style={{ flexShrink: 0 }}>
                          {line.amount != null && line.amount > 0 && <Text size="13px" fw={500} c={TEXT} ff="monospace">${Math.round(line.amount)}</Text>}
                          {!isInsp && line.type && (
                            <Text size="11px" fw={700} c={statusColor} style={{ padding: '2px 6px', border: `1px solid ${statusColor}33`, borderRadius: 4 }}>
                              {line.type}
                            </Text>
                          )}
                        </Group>
                      </Group>
                    </Box>
                  );
                })}
              </Box>
            );
          })}
          {!visible.length && (
            <Box py={20} style={{ textAlign: 'center' }}><Text size="xs" c={TEXT_MUTED}>No records match these filters</Text></Box>
          )}
        </Box>
      </ScrollArea>
    </Box>
  );
}

function VehicleInfoModal({
  opened,
  onClose,
  conv,
  onSaved,
  categories,
  inspectionId,
  inspectionMode,
  inspectionTemplateId,
  templates,
  members,
  onAssign,
}: {
  opened: boolean;
  onClose: () => void;
  conv: ConversationResponse | undefined;
  onSaved?: () => void;
  categories?: InspectionCategory[];
  inspectionId?: number;
  inspectionMode?: string;
  inspectionTemplateId?: number | null;
  templates?: { id: number; name: string; is_active?: boolean; items_count?: number }[];
  members?: { id: number; name: string }[];
  onAssign?: (userIds: number[]) => void;
}) {
  const v = conv?.vehicle;
  const [saving, setSaving] = useState(false);
  const [trimOptions, setTrimOptions] = useState<string[]>([]);
  const [selectedTrim, setSelectedTrim] = useState<string | null>(null);
  const [trimLoading, setTrimLoading] = useState(false);
  const [form, setForm] = useState({
    mileage: '',
    first_name: '',
    last_name: '',
    phone: '',
    sub_phones: [] as { phone?: string; email?: string }[],
    email: '',
  });
  const formRef = useRef(form);
  formRef.current = form;

  // Reset form when modal opens
  useEffect(() => {
    if (opened && v) {
      setForm({
        mileage: v.mileage != null ? String(v.mileage) : '',
        first_name: v.first_name ?? '',
        last_name: v.last_name ?? '',
        phone: v.phone ?? '',
        sub_phones: (v.sub_phones ?? []).map((sp: any) => typeof sp === 'string' ? { phone: sp } : { phone: sp?.phone, email: sp?.email }),
        email: v.email ?? '',
      });
    }
  }, [opened, v]);

  // Fetch trim options for YMM-only vehicles (no VIN)
  useEffect(() => {
    if (opened && v && v.make && v.model && v.year && !v.vin && conv?.id) {
      setTrimLoading(true);
      conversationsApi.getTrimOptions(conv.id)
        .then((trims) => setTrimOptions(trims || []))
        .catch(() => setTrimOptions([]))
        .finally(() => setTrimLoading(false));
    }
  }, [opened, v?.make, v?.model, v?.year, v?.vin, conv?.id]);

  if (!v || !conv) return null;

  const vehicleName = [v.year, v.make, v.model].filter(Boolean).join(' ');

  const doSave = async (f: typeof form) => {
    if (!conv.id) return;
    setSaving(true);
    try {
      const mileageNum = f.mileage.trim() ? parseInt(f.mileage.replace(/[^0-9]/g, ''), 10) : null;
      const cleanSubs = f.sub_phones.filter((c) => c.phone?.trim() || c.email?.trim());
      const payload: Record<string, unknown> = {
        mileage: isNaN(mileageNum as number) ? null : mileageNum,
        first_name: f.first_name.trim() || null,
        last_name: f.last_name.trim() || null,
        phone: f.phone.trim() || null,
        sub_phones: cleanSubs.length > 0 ? cleanSubs : null,
        email: f.email.trim() || null,
      };
      await vehicleApi.patch(conv.id, payload as Parameters<typeof vehicleApi.patch>[1]);
      await onSaved?.();
    } catch {
      // silently ignore
    } finally {
      setSaving(false);
    }
  };


  const updateForm = (updates: Partial<typeof form>) => {
    setForm((f) => ({ ...f, ...updates }));
  };

  const handleClose = () => {
    onClose();
    doSave(formRef.current).catch(() => {});
  };

  return (
    <Drawer
      opened={opened}
      onClose={handleClose}
      position="right"
      size="xl"
      title={
        <Group gap={8}>
          <Box style={{ width: 28, height: 28, borderRadius: 6, background: 'rgba(37,99,235,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <IconCar size={15} color={BLUE} />
          </Box>
          <Text fw={700} c={TEXT} size="sm">Information</Text>
        </Group>
      }
      styles={{
        content: { background: CARD_BG, borderLeft: `1px solid ${BORDER}`, display: 'flex', flexDirection: 'column' },
        header: { background: CARD_BG, borderBottom: `1px solid ${BORDER}` },
        close: { color: TEXT_MUTED },
        body: { background: CARD_BG, padding: 0, flex: 1, display: 'flex', flexDirection: 'row' },
      }}
    >
      {/* Left column: vehicle info */}
      <ScrollArea style={{ flex: 1, borderRight: `1px solid ${BORDER}` }}>
      <Box p="md">
      {/* Inspection Template section */}
      {inspectionId && templates && (
        <>
          <Text size="10px" fw={700} c={TEXT_MUTED} style={{ textTransform: 'uppercase', letterSpacing: 0.8 }} mb={4}>Inspection</Text>
          <Box mb={16}>
            <Stack gap={6}>
              <UnstyledButton
                onClick={async () => {
                  if (inspectionMode === 'ai') return;
                  try {
                    const { inspectionApi: api } = await import('../api/client');
                    await api.switchMode(inspectionId, 'ai');
                    const { mutate: swrMutate } = await import('swr');
                    await swrMutate(`vehicle/inspection/${inspectionId}`);
                    await onSaved?.();
                  } catch {}
                }}
                style={{
                  padding: '10px 14px', borderRadius: 8,
                  background: inspectionMode === 'ai' ? 'rgba(37,99,235,0.12)' : '#111113',
                  border: `1px solid ${inspectionMode === 'ai' ? 'rgba(37,99,235,0.5)' : BORDER}`,
                }}
              >
                <Group justify="space-between">
                  <Text size="xs" fw={500} c={inspectionMode === 'ai' ? '#fff' : TEXT}>AI Inspection</Text>
                  {inspectionMode === 'ai' && <Text size="xs" c={BLUE} fw={600}>Active</Text>}
                </Group>
              </UnstyledButton>
              {/* The built-in "AI Inspection" button above already represents
                  AI mode, so don't also list a DB template named "AI Inspection"
                  (e.g. the empty shop placeholder used to suppress the global
                  template fallback) — it would render as a duplicate row. */}
              {templates
                .filter((t) => t.is_active !== false && t.name?.trim().toLowerCase() !== 'ai inspection')
                .map((t) => (
                <UnstyledButton
                  key={t.id}
                  onClick={async () => {
                    if (inspectionTemplateId === t.id && inspectionMode !== 'ai') return;
                    try {
                      const { inspectionApi: api } = await import('../api/client');
                      await api.switchTemplate(inspectionId, t.id);
                      const { mutate: swrMutate } = await import('swr');
                      await swrMutate(`vehicle/inspection/${inspectionId}`);
                      await onSaved?.();
                    } catch (e) {
                      console.error('Switch template failed:', e);
                    }
                  }}
                  style={{
                    padding: '10px 14px', borderRadius: 8, cursor: 'pointer',
                    background: inspectionTemplateId === t.id && inspectionMode !== 'ai' ? 'rgba(37,99,235,0.12)' : '#111113',
                    border: `1px solid ${inspectionTemplateId === t.id && inspectionMode !== 'ai' ? 'rgba(37,99,235,0.5)' : BORDER}`,
                  }}
                >
                  <Group justify="space-between">
                    <Text size="xs" fw={500} c={inspectionTemplateId === t.id && inspectionMode !== 'ai' ? '#fff' : TEXT}>{t.name}</Text>
                    {t.items_count != null && <Text size="xs" c={TEXT_MUTED}>{t.items_count} items</Text>}
                  </Group>
                </UnstyledButton>
              ))}
            </Stack>
          </Box>
        </>
      )}

      {/* Vehicle name header */}
      {vehicleName && (
        <Box style={{ background: '#0C0C0E', borderRadius: 8, padding: '14px 16px', marginBottom: 16 }}>
          <Text size="lg" fw={700} c={TEXT}>{vehicleName}</Text>
          {v.plate && (
            <Text size="xs" c={TEXT_MUTED} mt={2} style={{ fontFamily: 'monospace', letterSpacing: 1 }}>
              {v.plate}{v.state ? ` · ${v.state}` : ''}
            </Text>
          )}
        </Box>
      )}

      {/* RO dates — canonical job dates (mig 102). Editable; intake_date
          drives the default conv-list ordering + owner dashboard buckets. */}
      <Text size="10px" fw={700} c={TEXT_MUTED} style={{ textTransform: 'uppercase', letterSpacing: 0.8 }} mb={4}>RO Dates</Text>
      <Group gap={8} mb={16} wrap="nowrap">
        <Box style={{ flex: 1 }}>
          <Text size="11px" c={TEXT_SECONDARY} mb={4}>Intake</Text>
          <input
            type="date"
            // intake_date is a tz-aware timestamp (mig 112); show its CA-local
            // calendar day. Picking a day sends yyyy-mm-dd → backend stamps it at
            // CA midnight. (en-CA renders YYYY-MM-DD.)
            value={conv.intake_date ? new Date(conv.intake_date).toLocaleDateString('en-CA') : ''}
            onChange={async (e) => {
              const v = e.currentTarget.value || '';
              try {
                await conversationsApi.patch(conv.id, { intake_date: v } as any);
                await onSaved?.();
              } catch (err) { console.error('[INTAKE_DATE] patch failed', err); }
            }}
            style={{
              width: '100%', background: '#111113', border: `1px solid ${BORDER}`,
              borderRadius: 6, color: TEXT, fontSize: 13, padding: '7px 10px',
              colorScheme: 'dark',
            }}
          />
        </Box>
        <Box style={{ flex: 1 }}>
          <Text size="11px" c={TEXT_SECONDARY} mb={4}>Complete</Text>
          <input
            type="date"
            value={conv.complete_date ? new Date(conv.complete_date).toLocaleDateString('en-CA') : ''}
            onChange={async (e) => {
              const v = e.currentTarget.value || '';
              try {
                await conversationsApi.patch(conv.id, { complete_date: v } as any);
                await onSaved?.();
              } catch (err) { console.error('[COMPLETE_DATE] patch failed', err); }
            }}
            style={{
              width: '100%', background: '#111113', border: `1px solid ${BORDER}`,
              borderRadius: 6, color: TEXT, fontSize: 13, padding: '7px 10px',
              colorScheme: 'dark',
            }}
          />
        </Box>
      </Group>

      {/* Vehicle section */}
      <Text size="10px" fw={700} c={TEXT_MUTED} style={{ textTransform: 'uppercase', letterSpacing: 0.8 }} mb={4}>Vehicle</Text>
      <Box mb={16}>
        {!vehicleName && <InfoRow label="Plate" value={v.plate ? `${v.plate}${v.state ? ` (${v.state})` : ''}` : null} />}
        <InfoRow label="VIN" value={v.vin} />
        <InfoRow label="Make" value={v.make} />
        <InfoRow label="Model" value={v.model} />
        <InfoRow label="Year" value={v.year?.toString()} />
        {!v.vin && trimOptions.length > 0 && (
          <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: `1px solid ${BORDER}` }}>
            <Text size="xs" c={TEXT_MUTED} fw={500} style={{ minWidth: 110 }}>Trim</Text>
            <select
              value={selectedTrim || ''}
              onChange={async (e) => {
                const trim = e.target.value;
                if (!trim || !conv.id) return;
                setSelectedTrim(trim);
                try {
                  await vehicleApi.selectTrim(conv.id, trim);
                } catch (err) {
                  console.error('[TRIM]', err);
                }
              }}
              style={{
                background: 'transparent', border: 'none', borderBottom: `1px solid ${BORDER}`,
                color: selectedTrim ? TEXT : TEXT_MUTED, fontSize: 13, textAlign: 'right',
                width: 180, padding: '4px 0', outline: 'none', cursor: 'pointer',
              }}
            >
              <option value="" style={{ background: '#111' }}>Select trim...</option>
              {trimOptions.map((t) => (
                <option key={t} value={t} style={{ background: '#111' }}>{t}</option>
              ))}
            </select>
          </Box>
        )}
        {!v.plate && <InfoRow label="State" value={v.state} />}
        {/* Mileage — always editable */}
        <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: `1px solid ${BORDER}` }}>
          <Text size="xs" c={TEXT_MUTED} fw={500} style={{ minWidth: 110 }}>Mileage</Text>
          <TextInput
            value={form.mileage}
            onChange={(e) => updateForm({ mileage: e.currentTarget.value })}
            placeholder={v.estimated_mileage ? `~${v.estimated_mileage.toLocaleString()} (estimated)` : 'e.g. 85,000'}
            size="xs"
            rightSection={<Text size="xs" c={TEXT_MUTED}>mi</Text>}
            styles={{
              input: { background: 'transparent', border: 'none', borderBottom: `1px solid ${BORDER}`, borderRadius: 0, color: TEXT, textAlign: 'right', paddingRight: 28 },
              wrapper: { width: 160 },
            }}
          />
        </Box>
      </Box>

      {/* Contacts section — card view */}
      <Group justify="space-between" mb={4}>
        <Text size="10px" fw={700} c={TEXT_MUTED} style={{ textTransform: 'uppercase', letterSpacing: 0.8 }}>Contacts</Text>
      </Group>
      <Box mb={16}>
        {/* Primary contact card */}
        <Box style={{ border: `1px solid ${BORDER}`, borderRadius: 10, padding: '10px 12px', marginBottom: 8, background: '#141416' }}>
          <Group gap={6} mb={6}>
            <IconStarFilled size={10} color={BLUE} />
            <Text size="10px" fw={700} c={BLUE} style={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>Primary</Text>
          </Group>
          <Group gap={8} mb={4}>
            <TextInput
              value={form.first_name}
              onChange={(e) => updateForm({ first_name: e.currentTarget.value })}
              placeholder="First"
              size="xs"
              styles={{ input: { background: 'transparent', border: 'none', borderBottom: `1px solid ${BORDER}`, borderRadius: 0, color: TEXT, padding: '2px 4px' }, root: { flex: 1 } }}
            />
            <TextInput
              value={form.last_name}
              onChange={(e) => updateForm({ last_name: e.currentTarget.value })}
              placeholder="Last"
              size="xs"
              styles={{ input: { background: 'transparent', border: 'none', borderBottom: `1px solid ${BORDER}`, borderRadius: 0, color: TEXT, padding: '2px 4px' }, root: { flex: 1 } }}
            />
          </Group>
          <TextInput
            value={form.phone}
            onChange={(e) => updateForm({ phone: e.currentTarget.value })}
            placeholder="+1 (555) 000-0000"
            size="xs"
            leftSection={<Text size="10px" c={TEXT_MUTED}>Phone</Text>}
            leftSectionWidth={50}
            styles={{ input: { background: 'transparent', border: 'none', borderBottom: `1px solid ${BORDER}`, borderRadius: 0, color: TEXT }, root: { marginBottom: 4 } }}
          />
          <TextInput
            value={form.email}
            onChange={(e) => updateForm({ email: e.currentTarget.value })}
            placeholder="email@example.com"
            size="xs"
            leftSection={<Text size="10px" c={TEXT_MUTED}>Email</Text>}
            leftSectionWidth={50}
            styles={{ input: { background: 'transparent', border: 'none', borderBottom: `1px solid ${BORDER}`, borderRadius: 0, color: TEXT } }}
          />
        </Box>

        {/* Additional contact cards */}
        {form.sub_phones.map((contact, idx) => (
          <Box key={idx} style={{ border: `1px solid ${BORDER}`, borderRadius: 10, padding: '10px 12px', marginBottom: 8, background: '#141416' }}>
            <Group justify="space-between" mb={4}>
              <Group gap={6}>
                <Tooltip label="Set as primary">
                  <ActionIcon variant="subtle" size="xs" color="gray" onClick={() => {
                    // Additional contacts have no name; promoting swaps phone/email only.
                    // The old primary is demoted to a nameless contact; the writer
                    // re-enters the new primary's name.
                    const oldPrimary = { phone: form.phone, email: form.email };
                    const newSubs = [...form.sub_phones];
                    newSubs[idx] = oldPrimary;
                    updateForm({ first_name: '', last_name: '', phone: contact.phone || '', email: contact.email || '', sub_phones: newSubs });
                  }}>
                    <IconStar size={10} />
                  </ActionIcon>
                </Tooltip>
                <Text size="10px" fw={700} c={TEXT_MUTED} style={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>Contact {idx + 2}</Text>
              </Group>
              <ActionIcon variant="subtle" size="xs" color="red" onClick={() => {
                updateForm({ sub_phones: form.sub_phones.filter((_, i) => i !== idx) });
              }}>
                <IconX size={12} />
              </ActionIcon>
            </Group>
            <TextInput
              value={contact.phone || ''}
              onChange={(e) => {
                const newSubs = [...form.sub_phones];
                newSubs[idx] = { ...contact, phone: e.currentTarget.value };
                updateForm({ sub_phones: newSubs });
              }}
              placeholder="+1 (555) 000-0000"
              size="xs"
              leftSection={<Text size="10px" c={TEXT_MUTED}>Phone</Text>}
              leftSectionWidth={50}
              styles={{ input: { background: 'transparent', border: 'none', borderBottom: `1px solid ${BORDER}`, borderRadius: 0, color: TEXT }, root: { marginBottom: 4 } }}
            />
            <TextInput
              value={contact.email || ''}
              onChange={(e) => {
                const newSubs = [...form.sub_phones];
                newSubs[idx] = { ...contact, email: e.currentTarget.value };
                updateForm({ sub_phones: newSubs });
              }}
              placeholder="email@example.com"
              size="xs"
              leftSection={<Text size="10px" c={TEXT_MUTED}>Email</Text>}
              leftSectionWidth={50}
              styles={{ input: { background: 'transparent', border: 'none', borderBottom: `1px solid ${BORDER}`, borderRadius: 0, color: TEXT } }}
            />
          </Box>
        ))}

        {/* Add contact button */}
        <UnstyledButton
          onClick={() => updateForm({ sub_phones: [...form.sub_phones, { phone: '' }] })}
          style={{ padding: '8px 0', display: 'flex', alignItems: 'center', gap: 6 }}
        >
          <IconPlus size={12} color={BLUE} />
          <Text size="xs" c={BLUE} fw={500}>Add contact</Text>
        </UnstyledButton>
      </Box>

      {/* Account (assigned worker) section */}
      {members && members.length > 0 && (
        <>
          <Text size="10px" fw={700} c={TEXT_MUTED} style={{ textTransform: 'uppercase', letterSpacing: 0.8 }} mb={4}>Account</Text>
          <Box mb={16}>
            <MultiSelect
              value={(conv?.charger_user_ids ?? []).map((id) => String(id))}
              onChange={(vals) => { if (onAssign) onAssign(vals.map(Number)); }}
              data={members.map((m) => ({ value: String(m.id), label: m.name }))}
              placeholder="Assign to..."
              size="xs"
              searchable
              clearable
              styles={{
                input: { background: '#111113', border: `1px solid ${BORDER}`, color: TEXT, fontSize: 12 },
              }}
              comboboxProps={{
                withinPortal: true,
                styles: {
                  dropdown: { background: '#1C1C1F', border: `1px solid ${BORDER}` },
                  option: { color: TEXT, fontSize: 12 },
                },
              }}
            />
          </Box>
        </>
      )}

      {/* Service History from Tekmetric */}
      {/* Combined History — jobs + inspections grouped by RO# */}
      {(() => {
        const meta = v?.vehicle_metadata as Record<string, any> | null | undefined;
        if (!meta) return null;
        const allJobs = [...(meta.tekmetric_jobs || []), ...(meta.tekmetric_current_jobs || [])] as any[];
        const allInsp = (meta.tekmetric_inspections || []) as any[];
        if (!allJobs.length && !allInsp.length) return null;

        // Group everything by RO number
        const roMap = new Map<number, { jobs: any[]; inspections: any[]; date: string; miles: string }>();
        for (const j of allJobs) {
          const roNum = j.repairOrderNumber;
          if (!roMap.has(roNum)) roMap.set(roNum, { jobs: [], inspections: [], date: (j.repairOrderPostedDate || j.createdDate || '').slice(0, 10), miles: j.repairOrderMilesIn ? `${Math.round(j.repairOrderMilesIn).toLocaleString()} mi` : '' });
          roMap.get(roNum)!.jobs.push(j);
        }
        for (const insp of allInsp) {
          const roNum = insp.repairOrderNumber;
          if (!roMap.has(roNum)) roMap.set(roNum, { jobs: [], inspections: [], date: (insp.inspectionCreatedDate || '').slice(0, 10), miles: '' });
          roMap.get(roNum)!.inspections.push(insp);
        }

        return (
          <>
            <Text size="10px" fw={700} c={TEXT_MUTED} style={{ textTransform: 'uppercase', letterSpacing: 0.8 }} mb={4} mt={16}>
              History ({allJobs.length} jobs, {allInsp.length} inspections)
            </Text>
            <Box mb={16}>
              {Array.from(roMap.entries()).map(([roNum, data]) => (
                <Box key={roNum} mb={10} style={{ borderBottom: `1px solid ${BORDER}`, paddingBottom: 8 }}>
                  <Text size="xs" fw={700} c={TEXT}>
                    RO #{roNum}{data.date ? ` (${data.date})` : ''}{data.miles ? ` @ ${data.miles}` : ''}
                  </Text>
                  {data.jobs.map((j: any, i: number) => {
                    const cost = j.subtotal ? `$${(j.subtotal / 100).toFixed(0)}` : '';
                    const status = j.jobStatus || (j.authorized ? 'Approved' : 'Draft');
                    const statusColor = status === 'Approved' ? '#22c55e' : status === 'Declined' ? '#ef4444' : TEXT_MUTED;
                    return (
                      <Box key={`j${i}`} style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
                        <Text size="xs" c={TEXT_MUTED} style={{ flex: 1 }}>{j.name}</Text>
                        <Text size="xs" c={statusColor} fw={500} ml={8} style={{ whiteSpace: 'nowrap' }}>
                          {cost}{cost && ' '}{status}
                        </Text>
                      </Box>
                    );
                  })}
                  {data.inspections.map((insp: any, i: number) => {
                    const ok = insp.ratingCheckedOk || 0;
                    const warn = insp.ratingMayRequireAttentionSoon || 0;
                    const urgent = insp.ratingRequiresImmediateAction || 0;
                    const imgs = insp.attachedImageCount || 0;
                    return (
                      <Box key={`i${i}`} style={{ padding: '2px 0' }}>
                        <Text size="xs" c="#60a5fa">{insp.name}</Text>
                        <Text size="10px" c={TEXT_MUTED}>
                          <span style={{ color: '#22c55e' }}>OK {ok}</span>
                          {warn > 0 && <span style={{ color: '#f59e0b' }}> · Attention {warn}</span>}
                          {urgent > 0 && <span style={{ color: '#ef4444' }}> · Urgent {urgent}</span>}
                          {imgs > 0 && <span> · {imgs} photos</span>}
                        </Text>
                      </Box>
                    );
                  })}
                </Box>
              ))}
            </Box>
          </>
        );
      })()}

      </Box>
      </ScrollArea>

      {/* Right column: vehicle history */}
      <Box style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {conv?.id ? <VehicleHistory conversationId={conv.id} /> : (
          <Box p="md"><Text size="xs" c={TEXT_MUTED}>No vehicle history available</Text></Box>
        )}
      </Box>

      {saving && (
        <Box style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '8px 20px', borderTop: `1px solid ${BORDER}`, background: CARD_BG }}>
          <Text size="xs" c={TEXT_MUTED}>Saving...</Text>
        </Box>
      )}
    </Drawer>
  );
}

// ─── References ───────────────────────────────────────────────────────────────

function extractYtId(url: string) {
  const m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  return m ? m[1] : null;
}

function isMediaRef(ref: { type?: string | null; url?: string | null }) {
  const t = (ref.type ?? '').toLowerCase();
  const url = (ref.url ?? '').toLowerCase();
  // YouTube videos → Sources (visual card with thumbnail)
  if (t === 'youtube' || url.includes('youtube.com') || url.includes('youtu.be')) return true;
  // OEM diagram images (type="diagram" with direct image URL) → Sources
  if (t === 'diagram' && /\.(jpg|jpeg|png|gif|webp|svg)(\?|$)/.test(url)) return true;
  // Everything else (citations, guides, docs, OEM parts pages) → References (text links)
  return false;
}

function TextReferences({ refs }: { refs: NonNullable<MessageResponse['references']> }) {
  if (!refs.length) return null;
  return (
    <Box mt={8}>
      <Text size="xs" c={TEXT_MUTED} fw={600} mb={4} style={{ letterSpacing: 0.5, textTransform: 'uppercase' }}>
        References
      </Text>
      <Box style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {refs.map((ref, i) => (
          <Box key={i} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <IconFileText size={12} color={TEXT_MUTED} style={{ flexShrink: 0 }} />
            {ref.url ? (
              <a href={ref.url} target="_blank" rel="noreferrer" style={{ color: BLUE, fontSize: 12, textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {ref.title || ref.url}
              </a>
            ) : (
              <Text size="xs" c={TEXT_SECONDARY} lineClamp={1}>{ref.title}</Text>
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function ReferencesSection({ refs }: { refs: NonNullable<MessageResponse['references']> }) {
  // Extract fusebox structured data reference
  const fuseboxRef = refs.find(r => r.type === 'fusebox' && r.detail);
  const fuseboxData = useMemo(() => {
    if (!fuseboxRef?.detail) return null;
    try { return JSON.parse(fuseboxRef.detail); } catch { return null; }
  }, [fuseboxRef?.detail]);

  const hasFuseboxDiagram = !!(fuseboxData && Array.isArray(fuseboxData) && fuseboxData.length > 0);
  const mediaRefs = refs.filter(r => {
    if (!isMediaRef(r) || r.type === 'fusebox') return false;
    // Hide fuse diagram thumbnails when interactive diagram is shown
    if (hasFuseboxDiagram && (r.title || '').toLowerCase().includes('fuse')) return false;
    return true;
  });
  const textRefs = refs.filter(r => !isMediaRef(r) && r.type !== 'fusebox');
  const [ytModal, setYtModal] = useState<{ videoId: string; title: string } | null>(null);
  const [galleryIdx, setGalleryIdx] = useState<number | null>(null);

  // Collect internal image URLs for gallery slideshow
  const galleryImages = useMemo(() => mediaRefs
    .map((ref, i) => {
      const url = (ref.url ?? '').toLowerCase();
      if (url.includes('youtube.com') || url.includes('youtu.be')) return null;
      if (!/\.(jpg|jpeg|png|gif|webp|svg)(\?|$)/i.test(url)) return null;
      return { src: ref.url ?? '', title: ref.title ?? '', mediaIdx: i };
    })
    .filter(Boolean) as { src: string; title: string; mediaIdx: number }[],
  [mediaRefs]);

  return (
    <Box mt={10}>
      {/* Text references (citations, guides, docs) — clickable links */}
      <TextReferences refs={textRefs} />

      {/* YouTube embed modal */}
      {ytModal && (
        <Modal
          opened
          onClose={() => setYtModal(null)}
          size="lg"
          padding={0}
          withCloseButton={false}
          centered
          styles={{
            content: { background: '#000', border: 'none', borderRadius: 12, overflow: 'hidden' },
            overlay: { backdropFilter: 'blur(6px)' },
            body: { padding: 0 },
          }}
        >
          <Box style={{ position: 'relative' }}>
            <Box style={{ position: 'relative', paddingBottom: '56.25%', height: 0 }}>
              <iframe
                src={`https://www.youtube.com/embed/${ytModal.videoId}?autoplay=1&rel=0`}
                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }}
                allow="autoplay; encrypted-media"
                allowFullScreen
              />
            </Box>
            {ytModal.title && (
              <Box px={14} py={10}>
                <Text size="sm" fw={600} c="#fff" lineClamp={2}>{ytModal.title}</Text>
              </Box>
            )}
            <ActionIcon
              variant="filled"
              color="dark"
              radius="xl"
              size={28}
              style={{ position: 'absolute', top: 8, right: 8 }}
              onClick={() => setYtModal(null)}
            >
              <IconX size={14} />
            </ActionIcon>
          </Box>
        </Modal>
      )}

      {/* Image gallery slideshow for internal diagrams */}
      {galleryIdx !== null && galleryImages.length > 0 && (
        <ImageSlideshow
          images={galleryImages.map(g => g.src)}
          titles={galleryImages.map(g => g.title)}
          startIndex={galleryIdx}
          onClose={() => setGalleryIdx(null)}
        />
      )}

      {/* Fuse box diagram button → opens modal */}
      {fuseboxData && Array.isArray(fuseboxData) && fuseboxData.length > 0 && (
        <FuseBoxButton boxes={fuseboxData} />
      )}

      {/* Sources — videos + diagrams */}
      {mediaRefs.length > 0 && (
        <Box mt={textRefs.length ? 8 : 0}>
          <Text size="xs" c={TEXT_MUTED} fw={600} mb={6} style={{ letterSpacing: 0.5, textTransform: 'uppercase' }}>
            Sources
          </Text>
          <Box style={{ display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 4 }}>
            {mediaRefs.map((ref, i) => {
              const t = (ref.type ?? '').toLowerCase();
              const rawUrl = ref.url ?? '';
              const url = rawUrl.toLowerCase();
              const isYoutube = url.includes('youtube.com') || url.includes('youtu.be');
              const isImage = t.includes('image') || t.includes('diagram') || url.match(/\.(jpg|jpeg|png|gif|webp|svg)$/);

              const ytMatch = rawUrl.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
              const youtubeThumbnail = ytMatch ? `https://img.youtube.com/vi/${ytMatch[1]}/hqdefault.jpg` : null;

              return (
                <UnstyledButton
                  key={i}
                  onClick={() => {
                    if (!ref.url) return;
                    const ytId = extractYtId(ref.url);
                    if (ytId) {
                      setYtModal({ videoId: ytId, title: ref.title ?? '' });
                    } else if (isImage) {
                      // Internal diagram/image → open in gallery slideshow
                      const imgIdx = galleryImages.findIndex(g => g.mediaIdx === i);
                      if (imgIdx >= 0) setGalleryIdx(imgIdx);
                      else window.open(ref.url, '_blank');
                    } else {
                      window.open(ref.url, '_blank');
                    }
                  }}
                  style={{ flexShrink: 0, width: 320 }}
                >
                  <Box
                    style={{
                      borderRadius: 8,
                      overflow: 'hidden',
                      background: CARD_BG,
                      border: `1px solid ${BORDER}`,
                      height: 240,
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <Box
                      style={{
                        height: 200,
                        flexShrink: 0,
                        background: '#0C0C0E',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative',
                      }}
                    >
                      {youtubeThumbnail ? (
                        <>
                          <img src={youtubeThumbnail} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          <Box style={{ position: 'absolute', width: 28, height: 28, borderRadius: '50%', background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <IconPlayerPlay size={12} color="#fff" />
                          </Box>
                        </>
                      ) : isImage && rawUrl ? (
                        <img src={rawUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (
                        <Box
                          style={{
                            width: 36,
                            height: 36,
                            borderRadius: 8,
                            background: 'rgba(37,99,235,0.15)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <IconPhoto size={20} color={BLUE} />
                        </Box>
                      )}
                    </Box>
                    <Box p={8} style={{ flex: 1, overflow: 'hidden' }}>
                      <Text size="xs" c={TEXT_SECONDARY} lineClamp={2}>
                        {ref.title ?? ref.url}
                      </Text>
                    </Box>
                  </Box>
                </UnstyledButton>
              );
            })}
          </Box>
        </Box>
      )}
    </Box>
  );
}

// ─── Trim Selector ───────────────────────────────────────────────────────────

function TrimSelector({ trims, conversationId }: { trims: string[]; conversationId: number }) {
  const [selected, setSelected] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  if (selected) {
    return (
      <Box mt={10}>
        <Group gap={6} align="center">
          <IconCar size={14} color="#22C55E" />
          <Text size="xs" c="#22C55E" fw={600}>Trim: {selected}</Text>
        </Group>
      </Box>
    );
  }

  return (
    <Box mt={10}>
      <Text size="xs" c={TEXT_MUTED} fw={600} mb={4}>Which trim?</Text>
      <Group gap={6} wrap="wrap">
        {trims.map((trim, i) => (
          <UnstyledButton
            key={i}
            disabled={loading}
            onClick={async () => {
              setLoading(true);
              try {
                await vehicleApi.selectTrim(conversationId, trim);
                setSelected(trim);
              } catch (e) {
                console.error('[TRIM_SELECT]', e);
              } finally {
                setLoading(false);
              }
            }}
            style={{
              padding: '5px 12px',
              borderRadius: 6,
              border: `1px solid ${BORDER}`,
              color: TEXT_SECONDARY,
              fontSize: 12,
              background: CARD_BG,
              opacity: loading ? 0.5 : 1,
              transition: 'all 0.12s',
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLElement).style.borderColor = BLUE;
              (e.currentTarget as HTMLElement).style.color = TEXT;
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLElement).style.borderColor = BORDER;
              (e.currentTarget as HTMLElement).style.color = TEXT_SECONDARY;
            }}
          >
            {trim}
          </UnstyledButton>
        ))}
      </Group>
    </Box>
  );
}

// ─── Artifact Card ────────────────────────────────────────────────────────────

function ArtifactCard({ msg, shopName, customerName, customerEmail, customerPhone, conversationId }: { msg: MessageResponse; shopName?: string | null; customerName?: string | null; customerEmail?: string | null; customerPhone?: string | null; conversationId?: number }) {
  const { conversation } = useConversation(conversationId);
  const [drawerUrl, setDrawerUrl] = useState<string | null>(null);
  const [drawerTitle, setDrawerTitle] = useState('');
  const [drawerArtifactType, setDrawerArtifactType] = useState<string | null>(null);
  const previewUrl = (url: string | null | undefined) => url ? (url.includes('?') ? `${url}&preview` : `${url}?preview`) : null;
  const [copiedUrl, setCopiedUrl] = useState<string | null>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [smsModalOpen, setSmsModalOpen] = useState(false);
  const [smsPhone, setSmsPhone] = useState('');
  const [smsBody, setSmsBody] = useState('');
  const [smsSending, setSmsSending] = useState(false);
  const [smsSent, setSmsSent] = useState(false);

  const openSmsModal = (url: string, title: string, artifactType?: string | null) => {
    const shop = shopName || 'Our shop';
    const name = customerName || 'Valued Customer';
    const isFinal = artifactType === 'final_report';
    const reportLabel = isFinal ? 'service report' : 'estimate';
    const body = isFinal
      ? `Hi ${name}, your ${reportLabel} from ${shop} is ready. View it here: ${url}\n\nLoved your experience? We'd really appreciate a quick Google or Yelp review!`
      : `Hi ${name}, your ${reportLabel} from ${shop} is ready. View it here: ${url}`;
    setSmsPhone(customerPhone || '');
    setSmsBody(body);
    setSmsSent(false);
    setSmsModalOpen(true);
  };

  const handleSmsSend = async () => {
    if (!smsPhone.trim() || !smsBody.trim()) return;
    setSmsSending(true);
    try {
      const { smsApi } = await import('../api/client');
      await smsApi.send({ to: smsPhone.trim(), body: smsBody.trim(), conversation_id: conversationId });
      setSmsSent(true);
    } catch (e) {
      console.error('[SMS_REPORT] send failed:', e);
    } finally {
      setSmsSending(false);
    }
  };

  const printReport = async (url?: string | null) => {
    if (!url) return;
    try {
      const res = await fetch(url);
      const html = await res.text();
      const win = window.open('', '_blank');
      if (!win) return;
      win.document.open();
      win.document.write(html);
      win.document.close();
      let printed = false;
      const doPrint = () => {
        if (printed) return;
        printed = true;
        win.print();
        win.close();
      };
      win.addEventListener('load', doPrint);
      setTimeout(doPrint, 800);
    } catch {
      window.open(url, '_blank');
    }
  };

const copyLink = (url: string) => {
    navigator.clipboard.writeText(url).then(() => {
      setCopiedUrl(url);
      setTimeout(() => setCopiedUrl(null), 2000);
    });
  };

  const openEmail = (url: string, title: string, artifactType?: string | null) => {
    const shop = shopName || 'Us';
    const greeting = customerName ? `Dear ${customerName},` : 'Dear Valued Customer,';
    const isFinal = artifactType === 'final_report';
    const isEstimate = artifactType === 'estimate_report';
    const reportLabel = isFinal ? 'Service Completion Report' : isEstimate ? 'Estimate Report' : (title || 'Vehicle Report');
    const subject = encodeURIComponent(shopName ? `[${shopName}] ${reportLabel}` : reportLabel);
    const intro = isFinal
      ? `Your vehicle service has been completed. Please find your service completion report below:`
      : isEstimate
      ? `We have prepared an estimate report for your vehicle. Please find the details below:`
      : `Please find your vehicle report below:`;
    const body = encodeURIComponent(
      `${greeting}\n\nThank you for choosing ${shop}. ${intro}\n\n${url}\n\nIf you have any questions, please don't hesitate to contact us.\n\nBest regards,\n${shop}`
    );
    const to = customerEmail ? encodeURIComponent(customerEmail) : '';
    window.open(`mailto:${to}?subject=${subject}&body=${body}`);
  };

  if (!msg.artifacts?.length) return null;
  return (
    <>
      {msg.artifacts.map((art) => (
        <Box mt={8} key={art.id}>
          <Box style={{ display: 'flex', alignItems: 'center', gap: 10, background: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: 8, padding: '12px 14px' }}>
            <UnstyledButton
              style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}
              onClick={() => { setDrawerUrl(previewUrl(art.public_url) ?? null); setDrawerTitle(art.title ?? 'Report'); setDrawerArtifactType(art.artifact_type ?? null); }}
            >
              <Box style={{ width: 32, height: 32, background: 'rgba(37,99,235,0.1)', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <IconFileText size={16} color={BLUE} />
              </Box>
              <Box style={{ flex: 1, minWidth: 0 }}>
                <Text size="sm" fw={600} c={TEXT}>Report</Text>
                <Text size="xs" c={TEXT_MUTED} truncate>{art.title ?? art.artifact_type}</Text>
              </Box>
            </UnstyledButton>
            <Group gap={4} style={{ flexShrink: 0 }}>
              <Tooltip label={copiedUrl === art.public_url ? 'Copied!' : 'Copy link'} withArrow>
                <ActionIcon
                  variant="subtle"
                  size="sm"
                  color={copiedUrl === art.public_url ? 'teal' : 'gray'}
                  onClick={() => art.public_url && copyLink(art.public_url)}
                >
                  {copiedUrl === art.public_url ? <IconCheck size={14} /> : <IconCopy size={14} />}
                </ActionIcon>
              </Tooltip>
              <Tooltip label="Print / Save as PDF" withArrow>
                <ActionIcon
                  variant="subtle"
                  size="sm"
                  color="gray"
                  onClick={() => printReport(previewUrl(art.public_url))}
                >
                  <IconPrinter size={14} />
                </ActionIcon>
              </Tooltip>
              <Tooltip label="SMS report" withArrow>
                <ActionIcon
                  variant="subtle"
                  size="sm"
                  color="teal"
                  onClick={() => art.public_url && openSmsModal(art.public_url, art.title ?? 'Report', art.artifact_type)}
                >
                  <IconMessage size={14} />
                </ActionIcon>
              </Tooltip>
              <Tooltip label="Email report" withArrow>
                <ActionIcon
                  variant="subtle"
                  size="sm"
                  color="gray"
                  onClick={() => art.public_url && openEmail(art.public_url, art.title ?? 'Report', art.artifact_type)}
                >
                  <IconMail size={14} />
                </ActionIcon>
              </Tooltip>
              <ActionIcon
                variant="subtle"
                size="sm"
                color="gray"
                onClick={() => { setDrawerUrl(previewUrl(art.public_url) ?? null); setDrawerTitle(art.title ?? 'Report'); setDrawerArtifactType(art.artifact_type ?? null); }}
              >
                <IconChevronRight size={16} />
              </ActionIcon>
            </Group>
          </Box>
        </Box>
      ))}

      <Drawer
        opened={!!drawerUrl}
        onClose={() => setDrawerUrl(null)}
        position="right"
        size="xl"
        title={<Text fw={600} size="sm" c={TEXT}>{drawerTitle}</Text>}
        styles={{
          content: { background: '#fff', display: 'flex', flexDirection: 'column' },
          header: { background: '#18181B', borderBottom: '1px solid #27272A', flexShrink: 0 },
          body: { padding: 0, flex: 1, display: 'flex', flexDirection: 'column' },
          close: { color: '#A1A1AA' },
        }}
      >
        <Group px={16} py={10} style={{ borderBottom: '1px solid #e5e7eb', background: '#f9fafb', flexShrink: 0 }}>
          <UnstyledButton
            onClick={() => drawerUrl && window.open(drawerUrl, '_blank')}
            style={{ fontSize: 12, color: BLUE, fontWeight: 600, padding: '4px 10px', borderRadius: 6, background: 'rgba(37,99,235,0.08)' }}
          >
            Open in new tab ↗
          </UnstyledButton>
          <UnstyledButton
            onClick={() => drawerUrl && copyLink(drawerUrl)}
            style={{ fontSize: 12, color: copiedUrl === drawerUrl ? '#059669' : '#52525B', fontWeight: 600, padding: '4px 10px', borderRadius: 6, background: copiedUrl === drawerUrl ? 'rgba(5,150,105,0.08)' : 'rgba(82,82,91,0.08)' }}
          >
            {copiedUrl === drawerUrl ? '✓ Copied!' : 'Copy link'}
          </UnstyledButton>
          <UnstyledButton
            onClick={() => printReport(drawerUrl)}
            style={{ fontSize: 12, color: '#52525B', fontWeight: 600, padding: '4px 10px', borderRadius: 6, background: 'rgba(82,82,91,0.08)', display: 'flex', alignItems: 'center', gap: 4 }}
          >
            <IconPrinter size={13} />Print / PDF
          </UnstyledButton>
          <UnstyledButton
            onClick={() => drawerUrl && openSmsModal(drawerUrl, drawerTitle, drawerArtifactType)}
            style={{ fontSize: 12, color: '#22C55E', fontWeight: 600, padding: '4px 10px', borderRadius: 6, background: 'rgba(34,197,94,0.08)', display: 'flex', alignItems: 'center', gap: 4 }}
          >
            <IconMessage size={13} />SMS
          </UnstyledButton>
          <UnstyledButton
            onClick={() => drawerUrl && openEmail(drawerUrl, drawerTitle, drawerArtifactType)}
            style={{ fontSize: 12, color: '#52525B', fontWeight: 600, padding: '4px 10px', borderRadius: 6, background: 'rgba(82,82,91,0.08)' }}
          >
            Email ✉
          </UnstyledButton>
        </Group>
        {drawerUrl && (
          <iframe
            ref={iframeRef}
            src={drawerUrl}
            style={{ flex: 1, width: '100%', border: 'none' }}
            title={drawerTitle}
          />
        )}
      </Drawer>

      {/* SMS Report Modal */}
      <Modal
        opened={smsModalOpen}
        onClose={() => setSmsModalOpen(false)}
        title={<Group gap={8}><IconMessage size={16} color="#22C55E" /><Text fw={600} size="sm" c={TEXT}>Send Report via SMS</Text></Group>}
        centered
        styles={{
          content: { background: '#1C1C1F', border: `1px solid ${BORDER}` },
          header: { background: '#1C1C1F', borderBottom: `1px solid ${BORDER}` },
          close: { color: TEXT_MUTED },
        }}
      >
        <Stack gap={12}>
          {/* Contact cards */}
          {(() => {
            const v = (conversation as any)?.vehicle;
            const contacts: { label: string; phone: string }[] = [];
            if (v?.phone) contacts.push({ label: [v.first_name, v.last_name].filter(Boolean).join(' ') || 'Primary', phone: v.phone });
            for (const sp of ((v?.sub_phones || []) as any[])) {
              const phone = typeof sp === 'string' ? sp : sp?.phone;
              const label = typeof sp === 'object' ? (sp?.name || '') : '';
              if (phone && !contacts.some((c) => c.phone === phone)) contacts.push({ label, phone });
            }
            if (contacts.length <= 1) return null;
            return (
              <Box>
                <Text size="xs" fw={500} c={TEXT_MUTED} mb={4}>Select contact</Text>
                <Group gap={6}>
                  {contacts.map((c) => (
                    <UnstyledButton
                      key={c.phone}
                      onClick={() => setSmsPhone(c.phone)}
                      style={{
                        padding: '6px 10px', borderRadius: 6, fontSize: 12,
                        border: `1px solid ${smsPhone === c.phone ? '#2563eb' : BORDER}`,
                        background: smsPhone === c.phone ? 'rgba(37,99,235,0.1)' : '#111113',
                        color: smsPhone === c.phone ? '#60a5fa' : TEXT_SECONDARY,
                      }}
                    >
                      {c.label ? <Text span size="xs" fw={600}>{c.label} </Text> : null}
                      <Text span size="xs">{c.phone}</Text>
                    </UnstyledButton>
                  ))}
                </Group>
              </Box>
            );
          })()}
          <Box>
            <Text size="xs" fw={500} c={TEXT_MUTED} mb={4}>Phone number</Text>
            <TextInput
              value={smsPhone}
              onChange={(e) => setSmsPhone(e.currentTarget.value)}
              placeholder="+1 (555) 000-0000"
              styles={{
                input: { background: '#111113', border: `1px solid ${BORDER}`, color: TEXT, fontSize: 14 },
              }}
            />
          </Box>
          <Box>
            <Text size="xs" fw={500} c={TEXT_MUTED} mb={4}>Message</Text>
            <Textarea
              value={smsBody}
              onChange={(e) => setSmsBody(e.currentTarget.value)}
              autosize
              minRows={3}
              maxRows={6}
              styles={{
                input: { background: '#111113', border: `1px solid ${BORDER}`, color: TEXT, fontSize: 13, lineHeight: 1.6 },
              }}
            />
            <Text size="xs" c={TEXT_MUTED} mt={4} style={{ opacity: 0.6 }}>{smsBody.length} / 1,600 characters</Text>
          </Box>
          {smsSent ? (
            <Box style={{ padding: '10px 14px', borderRadius: 8, background: 'rgba(34,197,94,0.12)', border: '1px solid rgba(34,197,94,0.3)', textAlign: 'center' }}>
              <Text size="sm" c="#22C55E" fw={600}>SMS sent successfully</Text>
            </Box>
          ) : (
            <Button
              fullWidth
              onClick={handleSmsSend}
              loading={smsSending}
              disabled={!smsPhone.trim() || !smsBody.trim()}
              leftSection={<IconMessage size={16} />}
              style={{ background: '#22C55E', color: '#fff', fontWeight: 600 }}
              styles={{ root: { '&:hover': { background: '#16A34A' } } }}
            >
              Send SMS
            </Button>
          )}
        </Stack>
      </Modal>
    </>
  );
}

// ─── Chat Message (Gemini-style) ──────────────────────────────────────────────

function renderWithCitations(children: React.ReactNode): React.ReactNode {
  if (typeof children === 'string') {
    const parts = children.split(/(\[\d{1,2}\])/g);
    if (parts.length <= 1) return children;
    return parts.map((part, i) => {
      const m = part.match(/^\[(\d{1,2})\]$/);
      if (m) return <sup key={i} style={{ color: '#3B82F6', fontWeight: 600, fontSize: 10, marginLeft: 1 }}>{part}</sup>;
      return part;
    });
  }
  if (Array.isArray(children)) return children.map((c, i) => <span key={i}>{renderWithCitations(c)}</span>);
  return children;
}

// Strip image markdown from content — images are rendered by MessageImageCarousel instead
function stripMarkdownImages(content: string): string {
  // Keep chart images inline, strip everything else
  return content.replace(/!\[([^\]]*)\]\(([^)]+)\)\n*/g, (match, alt, src) => {
    if ((alt || '').toLowerCase().includes('chart') && src.includes('quickchart')) return match;
    return '';
  }).replace(/\n{3,}/g, '\n\n');
}

const markdownComponents: React.ComponentProps<typeof ReactMarkdown>['components'] = {
  img: ({ src, alt }) => {
    if (!src) return null;
    // Only charts render inline — everything else was already stripped
    return (
      <Box style={{ margin: '8px 0' }}>
        <img src={src} alt={alt ?? ''} style={{ maxWidth: '100%', borderRadius: 8 }} />
      </Box>
    );
  },
  p: ({ children }) => <p style={{ margin: '0 0 8px', whiteSpace: 'pre-wrap', lineHeight: 1.65 }}>{renderWithCitations(children)}</p>,
  strong: ({ children }) => <strong style={{ fontWeight: 600, color: TEXT }}>{children}</strong>,
  em: ({ children }) => <em style={{ color: TEXT_SECONDARY }}>{children}</em>,
  h1: ({ children }) => <h1 style={{ fontSize: 17, fontWeight: 700, margin: '12px 0 6px', color: TEXT }}>{children}</h1>,
  h2: ({ children }) => <h2 style={{ fontSize: 15, fontWeight: 700, margin: '10px 0 4px', color: TEXT }}>{children}</h2>,
  h3: ({ children }) => <h3 style={{ fontSize: 14, fontWeight: 600, margin: '8px 0 4px', color: TEXT }}>{children}</h3>,
  ul: ({ children }) => <ul style={{ margin: '4px 0 8px', paddingLeft: 20 }}>{children}</ul>,
  ol: ({ children }) => <ol style={{ margin: '4px 0 8px', paddingLeft: 20 }}>{children}</ol>,
  li: ({ children }) => <li style={{ marginBottom: 4, color: TEXT_SECONDARY }}>{renderWithCitations(children)}</li>,
  code: ({ children, className }) => {
    const isBlock = className?.includes('language-');
    return isBlock ? (
      <code style={{ display: 'block', background: '#0C0C0E', border: `1px solid ${BORDER}`, borderRadius: 6, padding: '10px 14px', fontSize: 12, fontFamily: 'monospace', whiteSpace: 'pre', overflowX: 'auto', margin: '6px 0', color: '#93C5FD' }}>
        {children}
      </code>
    ) : (
      <code style={{ background: 'rgba(37,99,235,0.12)', borderRadius: 4, padding: '1px 5px', fontSize: 12, fontFamily: 'monospace', color: '#93C5FD' }}>
        {children}
      </code>
    );
  },
  pre: ({ children }) => <pre style={{ margin: '6px 0', background: 'transparent' }}>{children}</pre>,
  blockquote: ({ children }) => (
    <blockquote style={{ borderLeft: `3px solid ${BLUE}`, margin: '6px 0', paddingLeft: 12, color: TEXT_SECONDARY }}>
      {children}
    </blockquote>
  ),
  a: ({ href, children }) => (
    <a href={href} target="_blank" rel="noreferrer" style={{ color: BLUE, textDecoration: 'underline' }}>
      {children}
    </a>
  ),
  hr: () => <hr style={{ border: 'none', borderTop: `1px solid ${BORDER}`, margin: '10px 0' }} />,
  table: ({ children }) => (
    <div style={{ overflowX: 'auto', margin: '8px 0' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>{children}</table>
    </div>
  ),
  th: ({ children }) => <th style={{ padding: '6px 10px', borderBottom: `1px solid ${BORDER}`, textAlign: 'left', color: TEXT_SECONDARY, fontWeight: 600 }}>{children}</th>,
  td: ({ children }) => <td style={{ padding: '6px 10px', borderBottom: `1px solid #1C1C1F`, color: TEXT_SECONDARY }}>{renderWithCitations(children)}</td>,
  tbody: ({ children }) => <tbody>{children}</tbody>,
  thead: ({ children }) => <thead>{children}</thead>,
  tr: ({ children }) => <tr>{children}</tr>,
};

// Collapsible markdown: any ## or ### heading becomes collapsible when 3+ sections exist
const HEADING_RE = /^#{2,3}\s+(.+)$/;
// Min number of heading sections to activate collapsible mode
const COLLAPSIBLE_MIN_SECTIONS = 2;
// Strip References/Sources sections from markdown — these are rendered as structured UI
const REFS_SECTION_RE = /\n*#{1,4}\s*(?:References?|Sources?)\s*\n[\s\S]*/i;
const isRefHeading = (h: string) => /^(?:References?|Sources?)$/i.test(h.trim());

function CollapsibleMarkdown({ content }: { content: string }) {
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});

  const sections = useMemo(() => {
    const lines = content.split('\n');
    const result: { heading?: string; body: string; idx: number }[] = [];
    let current: { heading?: string; bodyLines: string[]; idx: number } = { bodyLines: [], idx: 0 };

    for (const line of lines) {
      if (HEADING_RE.test(line)) {
        if (current.heading || current.bodyLines.length) {
          result.push({ heading: current.heading, body: current.bodyLines.join('\n'), idx: current.idx });
        }
        current = { heading: line.replace(/^#{2,3}\s+/, ''), bodyLines: [], idx: result.length };
      } else {
        current.bodyLines.push(line);
      }
    }
    if (current.heading || current.bodyLines.length) {
      result.push({ heading: current.heading, body: current.bodyLines.join('\n'), idx: current.idx });
    }
    return result;
  }, [content]);

  // Filter out References/Sources sections — rendered as structured UI below
  const filtered = sections.filter(s => !s.heading || !isRefHeading(s.heading));
  const headingSections = filtered.filter(s => s.heading);

  // Check if any section should always be collapsed (e.g. Fuse Box Layout)
  const ALWAYS_COLLAPSE_RE = /fuse\s*box|full\s*fuse|all\s*fuses/i;
  const hasAlwaysCollapse = headingSections.some(s => ALWAYS_COLLAPSE_RE.test(s.heading || ''));

  // Not enough sections — render plain markdown (unless a section must be collapsed)
  if (headingSections.length < COLLAPSIBLE_MIN_SECTIONS && !hasAlwaysCollapse) {
    return (
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
        {stripMarkdownImages(content.replace(REFS_SECTION_RE, '').trim())}
      </ReactMarkdown>
    );
  }

  // First heading section defaults open, rest default collapsed
  const firstHeadingIdx = filtered.findIndex(s => s.heading);

  return (
    <>
      {filtered.map((section) => {
        if (!section.heading) {
          return (
            <ReactMarkdown key={section.idx} remarkPlugins={[remarkGfm]} components={markdownComponents}>
              {stripMarkdownImages(section.body)}
            </ReactMarkdown>
          );
        }
        // First heading is the title — render open, UNLESS it's a fuse box table (always collapsed)
        if (section.idx === firstHeadingIdx && !ALWAYS_COLLAPSE_RE.test(section.heading || '')) {
          return (
            <Fragment key={section.idx}>
              <h2 style={{ fontSize: 15, fontWeight: 700, margin: '10px 0 4px', color: TEXT }}>{section.heading}</h2>
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                {section.body}
              </ReactMarkdown>
            </Fragment>
          );
        }
        const isOpen = expanded[section.idx] ?? false;
        const bodyLineCount = section.body.split('\n').filter(l => l.trim()).length;
        return (
          <Box key={section.idx} style={{ margin: '4px 0', borderBottom: `1px solid ${BORDER}` }}>
            <UnstyledButton
              onClick={() => setExpanded(prev => ({ ...prev, [section.idx]: !isOpen }))}
              style={{
                display: 'flex', alignItems: 'center', gap: 6, width: '100%',
                padding: '6px 0', color: TEXT,
              }}
            >
              {isOpen
                ? <IconChevronDown size={14} color={TEXT_MUTED} style={{ flexShrink: 0 }} />
                : <IconChevronRight size={14} color={TEXT_MUTED} style={{ flexShrink: 0 }} />
              }
              <Text size="sm" fw={700} c={TEXT} style={{ flex: 1 }}>{section.heading}</Text>
              {!isOpen && bodyLineCount > 0 && (
                <Text size="xs" c={TEXT_MUTED} style={{ flexShrink: 0 }}>{bodyLineCount} lines</Text>
              )}
            </UnstyledButton>
            {isOpen && (
              <Box style={{ paddingLeft: 20, paddingBottom: 8 }}>
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                  {section.body}
                </ReactMarkdown>
              </Box>
            )}
          </Box>
        );
      })}
    </>
  );
}

type VideoHighlight = { t: number; url?: string; blob?: string };
type TranscriptSeg = { text: string; start_ms: number; end_ms: number };

// Group word-level STT segments into readable caption lines (a new line on a long
// pause, a long span, or after ~9 words) so the overlay shows a phrase, not a word.
function buildCaptionCues(segments?: TranscriptSeg[] | null): { start: number; end: number; text: string }[] {
  if (!segments || !segments.length) return [];
  const cues: { start: number; end: number; text: string }[] = [];
  let cur: TranscriptSeg[] = [];
  const flush = () => {
    if (!cur.length) return;
    cues.push({ start: cur[0].start_ms, end: cur[cur.length - 1].end_ms, text: cur.map((w) => w.text).join(' ') });
    cur = [];
  };
  for (const w of segments) {
    if (cur.length) {
      const span = w.end_ms - cur[0].start_ms;
      const gap = w.start_ms - cur[cur.length - 1].end_ms;
      if (cur.length >= 9 || span > 3500 || gap > 700) flush();
    }
    cur.push(w);
  }
  flush();
  return cues;
}

function VideoPlayerWithHighlights({ url, highlights, blob, mediaKind, itemId }: { url: string; highlights?: VideoHighlight[]; blob?: string | null; mediaKind?: 'audio' | 'video'; itemId?: number }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [captionsOn, setCaptionsOn] = useState(true);
  const [segments, setSegments] = useState<TranscriptSeg[] | null>(null);
  const [transcribing, setTranscribing] = useState(false);

  // Auto-transcribe the clip on open via the long-running RPC, then poll until
  // ready. Cached server-side per blob, so a re-open returns instantly.
  useEffect(() => {
    if (!blob) return;
    let cancelled = false;
    setTranscribing(true);
    (async () => {
      try {
        const { inspectionApi } = await import('../api/client');
        const tr = await inspectionApi.transcribeAndPoll({ blob_name: blob, item_id: itemId, media_kind: mediaKind ?? 'video' });
        if (!cancelled) setSegments((tr.segments as TranscriptSeg[]) || []);
      } catch {
        if (!cancelled) setSegments([]);
      } finally {
        if (!cancelled) setTranscribing(false);
      }
    })();
    return () => { cancelled = true; };
  }, [blob, itemId, mediaKind]);

  const cues = useMemo(() => buildCaptionCues(segments), [segments]);
  const tMs = currentTime * 1000;
  const activeCue = captionsOn ? cues.find((c) => tMs >= c.start && tMs <= c.end + 400) : undefined;

  const seekTo = (t: number) => {
    if (videoRef.current) {
      videoRef.current.currentTime = t;
      videoRef.current.play();
      setPlaying(true);
    }
  };

  const togglePlay = () => {
    if (!videoRef.current) return;
    if (videoRef.current.paused) { videoRef.current.play(); setPlaying(true); }
    else { videoRef.current.pause(); setPlaying(false); }
  };

  const fmt = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  return (
    <Box style={{ width: '100%', maxWidth: '100%' }} onClick={(e) => e.stopPropagation()}>
      <Box style={{ position: 'relative', width: 'fit-content', maxWidth: '100%', margin: '0 auto' }}>
        <video
          ref={videoRef}
          src={url}
          autoPlay
          playsInline
          controls
          style={{ maxWidth: '100%', maxHeight: highlights?.length ? '60vh' : '70vh', borderRadius: 8, display: 'block' }}
          onTimeUpdate={() => setCurrentTime(videoRef.current?.currentTime || 0)}
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
        />
        {/* CC toggle — once a transcript exists */}
        {cues.length > 0 && (
          <UnstyledButton
            onClick={() => setCaptionsOn((v) => !v)}
            title={captionsOn ? 'Hide captions' : 'Show captions'}
            style={{
              position: 'absolute',
              top: 8,
              right: 8,
              background: captionsOn ? 'rgba(255,107,53,0.92)' : 'rgba(0,0,0,0.6)',
              color: '#fff',
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: 0.5,
              borderRadius: 5,
              padding: '3px 7px',
              border: '1px solid rgba(255,255,255,0.25)',
              lineHeight: 1,
            }}
          >
            CC
          </UnstyledButton>
        )}
        {/* Transcribing… indicator while the STT job runs (no cues yet) */}
        {transcribing && cues.length === 0 && (
          <Box
            style={{
              position: 'absolute',
              top: 8,
              right: 8,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              background: 'rgba(0,0,0,0.6)',
              color: '#fff',
              borderRadius: 5,
              padding: '3px 8px',
              border: '1px solid rgba(255,255,255,0.25)',
            }}
          >
            <Loader size={11} color="#fff" />
            <Text size="xs" c="white" fw={600}>Transcribing…</Text>
          </Box>
        )}
        {/* Synced caption overlay (sits above the native controls) */}
        {activeCue && (
          <Box
            style={{
              position: 'absolute',
              bottom: 52,
              left: '50%',
              transform: 'translateX(-50%)',
              maxWidth: '92%',
              background: 'rgba(0,0,0,0.78)',
              borderRadius: 8,
              padding: '6px 14px',
              pointerEvents: 'none',
              textAlign: 'center',
            }}
          >
            <Text size="md" c="white" fw={500} style={{ lineHeight: 1.35, textShadow: '0 1px 3px rgba(0,0,0,0.9)' }}>
              {activeCue.text}
            </Text>
          </Box>
        )}
      </Box>
      {/* Findings thumbnail strip */}
      {highlights && highlights.length > 0 && (
        <Box style={{ padding: '4px 12px 8px' }}>
          <Text size="xs" c="orange" fw={500} mb={6}>{highlights.length} finding{highlights.length > 1 ? 's' : ''} detected</Text>
          <Group gap={8} wrap="wrap">
            {highlights.map((h, i) => (
              <Box
                key={i}
                onClick={() => seekTo(h.t)}
                style={{
                  cursor: 'pointer',
                  borderRadius: 6,
                  overflow: 'hidden',
                  border: `2px solid ${Math.abs(currentTime - h.t) < 1 ? '#FF6B35' : 'rgba(255,255,255,0.15)'}`,
                  transition: 'border-color 0.2s',
                  position: 'relative',
                }}
              >
                {h.url ? (
                  <img src={h.url} alt="" style={{ width: 80, height: 56, objectFit: 'cover', display: 'block' }} />
                ) : (
                  <Box style={{ width: 80, height: 56, background: '#222', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <IconPlayerPlay size={16} color="#fff" style={{ opacity: 0.5 }} />
                  </Box>
                )}
                <Box style={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  background: 'rgba(0,0,0,0.7)',
                  padding: '1px 4px',
                  textAlign: 'center',
                }}>
                  <Text size="10px" c="white" fw={500}>{fmt(h.t)}</Text>
                </Box>
              </Box>
            ))}
          </Group>
        </Box>
      )}
    </Box>
  );
}

function ImageCarousel({ images, onOpen }: { images: { src: string; alt: string }[]; onOpen: (index: number) => void }) {
  if (!images.length) return null;
  return (
    <Box style={{ display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 4, marginBottom: 8 }}>
      {images.map((img, i) => (
        <UnstyledButton key={i} onClick={() => onOpen(i)} style={{ flexShrink: 0, width: 220 }}>
          <Box
            style={{
              borderRadius: 8,
              overflow: 'hidden',
              background: CARD_BG,
              border: `1px solid ${BORDER}`,
              height: 180,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Box
              style={{
                height: 140,
                flexShrink: 0,
                background: '#0C0C0E',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <img src={img.src} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
            </Box>
            <Box px={10} py={6} style={{ flex: 1, overflow: 'hidden' }}>
              <Text size="xs" c={TEXT_SECONDARY} lineClamp={1}>
                {img.alt || 'Image'}
              </Text>
            </Box>
          </Box>
        </UnstyledButton>
      ))}
    </Box>
  );
}

function ImageSlideshow({ images, titles, startIndex, onClose }: { images: string[]; titles?: string[]; startIndex: number; onClose: () => void }) {
  const [idx, setIdx] = useState(startIndex);

  // Keyboard navigation
  useEffect(() => {
    const handler = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'ArrowLeft') setIdx(prev => (prev - 1 + images.length) % images.length);
      else if (e.key === 'ArrowRight') setIdx(prev => (prev + 1) % images.length);
      else if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [images.length, onClose]);

  return (
    <Modal
      opened
      onClose={onClose}
      size="90vw"
      padding={0}
      centered
      withCloseButton={false}
      styles={{
        content: { background: 'rgba(0,0,0,0.95)', border: 'none', borderRadius: 12, maxWidth: '95vw' },
        overlay: { backdropFilter: 'blur(6px)' },
        body: { padding: 0 },
      }}
    >
      <Box style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        {/* Main image */}
        <Box style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300, width: '100%' }}>
          <img src={images[idx]} alt="" style={{ maxWidth: '100%', maxHeight: '85vh', objectFit: 'contain', borderRadius: 8, display: 'block' }} />
          {images.length > 1 && (
            <>
              <ActionIcon variant="filled" color="dark" radius="xl" size={36} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)' }} onClick={() => setIdx((idx - 1 + images.length) % images.length)}>
                <IconChevronLeft size={18} />
              </ActionIcon>
              <ActionIcon variant="filled" color="dark" radius="xl" size={36} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)' }} onClick={() => setIdx((idx + 1) % images.length)}>
                <IconChevronRight size={18} />
              </ActionIcon>
            </>
          )}
          {/* Open full size in new tab */}
          <ActionIcon variant="filled" color="dark" radius="xl" size={30} style={{ position: 'absolute', top: 10, left: 10 }} onClick={() => window.open(images[idx], '_blank')}>
            <IconArrowsMaximize size={14} />
          </ActionIcon>
          <ActionIcon variant="filled" color="dark" radius="xl" size={30} style={{ position: 'absolute', top: 10, right: 10 }} onClick={onClose}>
            <IconX size={14} />
          </ActionIcon>
        </Box>
        {/* Image title */}
        {titles?.[idx] && (
          <Text size="sm" c={TEXT_SECONDARY} ta="center" px={16} py={6}>{titles[idx]}</Text>
        )}
        {/* Thumbnail strip */}
        {images.length > 1 && (
          <Box style={{ display: 'flex', gap: 6, padding: '10px 16px', overflowX: 'auto', width: '100%', justifyContent: 'center' }}>
            {images.map((src, i) => (
              <img
                key={i}
                src={src}
                alt=""
                onClick={() => setIdx(i)}
                style={{
                  width: 48, height: 48, objectFit: 'cover', borderRadius: 6, cursor: 'pointer', flexShrink: 0,
                  border: i === idx ? '2px solid #3B82F6' : '2px solid transparent',
                  opacity: i === idx ? 1 : 0.5,
                  transition: 'all 0.15s',
                }}
              />
            ))}
          </Box>
        )}
      </Box>
    </Modal>
  );
}

// Voice-note player: <audio> + a click-to-seek transcript (auto-transcribed on
// open via the long-running RPC, cached per blob). The transcript has no video to
// overlay on, so it renders as a synced, clickable list instead of subtitles.
function AudioNotePlayer({ url, blob, itemId }: { url: string; blob?: string | null; itemId?: number }) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [segments, setSegments] = useState<TranscriptSeg[] | null>(null);
  const [transcribing, setTranscribing] = useState(false);

  useEffect(() => {
    if (!blob) return;
    let cancelled = false;
    setTranscribing(true);
    (async () => {
      try {
        const { inspectionApi } = await import('../api/client');
        const tr = await inspectionApi.transcribeAndPoll({ blob_name: blob, item_id: itemId, media_kind: 'audio' });
        if (!cancelled) setSegments((tr.segments as TranscriptSeg[]) || []);
      } catch {
        if (!cancelled) setSegments([]);
      } finally {
        if (!cancelled) setTranscribing(false);
      }
    })();
    return () => { cancelled = true; };
  }, [blob, itemId]);

  const cues = useMemo(() => buildCaptionCues(segments), [segments]);
  const tMs = currentTime * 1000;
  const activeIdx = cues.findIndex((c) => tMs >= c.start && tMs <= c.end + 400);
  const fmt = (ms: number) => {
    const s = Math.floor(ms / 1000);
    return `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;
  };
  const seekTo = (ms: number) => {
    if (audioRef.current) { audioRef.current.currentTime = ms / 1000; audioRef.current.play(); }
  };

  return (
    <Box style={{ width: '100%', maxWidth: 640, padding: 20 }} onClick={(e) => e.stopPropagation()}>
      <Group gap={8} justify="center" mb={14}>
        <IconMicrophone size={18} color="#FF6B35" />
        <Text size="sm" c="dimmed" fw={600}>Voice note</Text>
      </Group>
      <audio
        ref={audioRef}
        src={url}
        autoPlay
        controls
        style={{ width: '100%' }}
        onTimeUpdate={() => setCurrentTime(audioRef.current?.currentTime || 0)}
      />
      <Box style={{ marginTop: 16, maxHeight: '46vh', overflowY: 'auto' }}>
        {transcribing && cues.length === 0 ? (
          <Group gap={8} justify="center" py={20}><Loader size={14} color="#fff" /><Text size="sm" c="dimmed">Transcribing…</Text></Group>
        ) : cues.length === 0 ? (
          <Text size="sm" c="dimmed" ta="center" py={20}>No speech detected.</Text>
        ) : (
          <Stack gap={2}>
            {cues.map((c, i) => (
              <UnstyledButton
                key={i}
                onClick={() => seekTo(c.start)}
                style={{
                  display: 'flex', gap: 10, alignItems: 'baseline', padding: '6px 10px', borderRadius: 6,
                  background: i === activeIdx ? 'rgba(255,107,53,0.18)' : 'transparent',
                }}
              >
                <Text size="sm" c="dimmed" style={{ fontVariantNumeric: 'tabular-nums', flexShrink: 0, minWidth: 42 }}>{fmt(c.start)}</Text>
                <Text size="sm" c={i === activeIdx ? 'white' : 'gray.4'} fw={i === activeIdx ? 600 : 400} style={{ textAlign: 'left' }}>{c.text}</Text>
              </UnstyledButton>
            ))}
          </Stack>
        )}
      </Box>
    </Box>
  );
}

function PhotoLightbox({ url, onClose, highlights, blob, mediaKind, itemId }: { url: string; onClose: () => void; highlights?: VideoHighlight[]; blob?: string | null; mediaKind?: 'audio' | 'video'; itemId?: number }) {
  const isVideo = isVideoUrl(url);
  return (
    <Modal
      opened
      onClose={onClose}
      size="xl"
      padding={0}
      withCloseButton={false}
      styles={{
        content: { background: 'rgba(0,0,0,0.92)', border: 'none', borderRadius: 12 },
        overlay: { backdropFilter: 'blur(6px)' },
        body: { padding: 0 },
      }}
    >
      <Box style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 200 }}>
        {mediaKind === 'audio' ? (
          <AudioNotePlayer url={url} blob={blob} itemId={itemId} />
        ) : isVideo ? (
          <VideoPlayerWithHighlights url={url} highlights={highlights} blob={blob} mediaKind={mediaKind} itemId={itemId} />
        ) : (
          <img
            src={url}
            alt=""
            style={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain', borderRadius: 8, display: 'block' }}
          />
        )}
        <ActionIcon
          variant="filled"
          color="dark"
          radius="xl"
          size={30}
          style={{ position: 'absolute', top: 10, right: 10 }}
          onClick={onClose}
        >
          <IconX size={14} />
        </ActionIcon>
      </Box>
    </Modal>
  );
}

function MessageImageCarousel({ content }: { content?: string | null }) {
  const imgs = useMemo(() => {
    if (!content) return [];
    // OEM parts catalog images (exploded parts views) are not useful as inline images
    const OEM_CATALOG_DOMAINS = ['cdn-illustrations', 'revolutionparts', 'genuineoem'];
    return (content.match(/!\[[^\]]*\]\(([^)]+)\)/g) || [])
      .map(m => {
        const alt = m.match(/!\[([^\]]*)\]/)?.[1] || '';
        const src = m.match(/\(([^)]+)\)/)?.[1];
        if (!src) return null;
        // Skip OEM parts catalog images
        const srcLower = src.toLowerCase();
        if (OEM_CATALOG_DOMAINS.some(d => srcLower.includes(d))) return null;
        return { src, alt };
      })
      .filter(Boolean) as { src: string; alt: string }[];
  }, [content]);
  const [slideIdx, setSlideIdx] = useState<number | null>(null);
  if (!imgs.length) return null;
  return (
    <>
      <ImageCarousel images={imgs} onOpen={(i) => setSlideIdx(i)} />
      {slideIdx !== null && <ImageSlideshow images={imgs.map(i => i.src)} titles={imgs.map(i => i.alt)} startIndex={slideIdx} onClose={() => setSlideIdx(null)} />}
    </>
  );
}

// Renders a persisted 'ro_suggestion' assistant message: the recommended jobs
// (canned/estimated) with their parts + labor and a one-click Add to Job that
// reuses the standard onAddToQuote path. All incoming amounts are in CENTS.
function RoSuggestionCard({
  suggestions,
  onAddToQuote,
  quotedLabels,
  notes,
  outcome,
}: {
  suggestions: RoSuggestion[];
  onAddToQuote?: (item: QuoteItem) => void;
  quotedLabels?: Set<string>;
  // Terminal-marker fields (when suggestions is empty): outcome 'empty' | 'error'
  // + the model's notes, so an empty/failed rec isn't a silent blank.
  notes?: string;
  outcome?: 'empty' | 'error';
}) {
  const usd = (cents?: number) => `$${Math.round((cents ?? 0) / 100).toLocaleString()}`;
  // Per-line writer overrides, keyed `${sIdx}:${liIdx}`.
  //  picked → chosen part_option INDEX (index, not inventory_id: free-form
  //  estimate options share inventory_id=undefined, so index is the stable key).
  //  qty → edited quantity (lets them use exact capacity e.g. 4.2 vs the ceiling).
  const [picked, setPicked] = useState<Record<string, number>>({});
  const [qty, setQty] = useState<Record<string, number>>({});

  const chosenOptIdx = useCallback((sIdx: number, liIdx: number, s: RoSuggestion): number => {
    const key = `${sIdx}:${liIdx}`;
    if (picked[key] != null) return picked[key];
    const opts = s.line_items[liIdx]?.part_options || [];
    const rec = opts.findIndex((o) => o.recommended);
    return rec >= 0 ? rec : 0;
  }, [picked]);

  const chosenQty = useCallback((sIdx: number, liIdx: number, s: RoSuggestion): number => {
    const key = `${sIdx}:${liIdx}`;
    return qty[key] != null ? qty[key] : (s.line_items[liIdx]?.quantity || 1);
  }, [qty]);

  const chosenOpt = useCallback((sIdx: number, liIdx: number, s: RoSuggestion) =>
    s.line_items[liIdx]?.part_options?.[chosenOptIdx(sIdx, liIdx, s)],
  [chosenOptIdx]);

  // Live total = canned labor + Σ(chosen retail × chosen qty). Recomputed from
  // the writer's picks, not the static estimated_total_cents.
  const estTotal = useCallback((sIdx: number, s: RoSuggestion): number => {
    let total = s.canned_job?.labor_total_cents || 0;
    (s.line_items || []).forEach((_li, liIdx) => {
      const opt = chosenOpt(sIdx, liIdx, s);
      if (opt) total += (opt.retail_cents || 0) * chosenQty(sIdx, liIdx, s);
    });
    return total;
  }, [chosenOpt, chosenQty]);

  if (!suggestions.length) {
    // Terminal outcome of a rec run that produced no cards — shown so the writer
    // knows it ran (vs. a silent blank). 'error' = generation failed; otherwise
    // there simply was nothing to recommend.
    const isError = outcome === 'error';
    return (
      <Box style={{
        display: 'inline-flex', alignItems: 'center', gap: 8,
        padding: '8px 12px', borderRadius: 10,
        border: `1px solid ${isError ? 'rgba(239,68,68,0.35)' : BORDER}`,
        background: isError ? 'rgba(239,68,68,0.08)' : '#0C0C0E',
      }}>
        <Text span style={{ fontSize: 14, lineHeight: 1 }}>{isError ? '⚠️' : '🔧'}</Text>
        <Text size="sm" c={isError ? '#F87171' : TEXT_MUTED}>
          {notes?.trim() || (isError ? "Couldn't generate recommendations." : 'No additional work recommended.')}
        </Text>
      </Box>
    );
  }

  return (
    <Box style={{ border: `1px solid ${BORDER}`, borderRadius: 12, overflow: 'hidden', background: '#0C0C0E' }}>
      <Box style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderBottom: `1px solid ${BORDER}` }}>
        <Text span style={{ fontSize: 16, lineHeight: 1 }}>🔧</Text>
        <Text size="sm" fw={700} c={TEXT}>Recommended jobs ({suggestions.length})</Text>
      </Box>
      {suggestions.map((s, i) => {
        const isAdded = quotedLabels?.has((s.title || '').trim().toLowerCase()) ?? false;
        const laborCents = s.canned_job?.labor_total_cents ?? 0;
        const total = estTotal(i, s);
        const partsCents = total - laborCents;
        const laborLabel = s.estimated_labor_hours
          ? `${s.estimated_labor_hours}h · ${usd(laborCents)}`
          : usd(laborCents);

        const handleAdd = () => {
          if (isAdded || !onAddToQuote) return;
          // Build a real QuoteItem with itemized labor + part lines so it opens
          // in the SAME card editor as any job — recommended prices pre-filled
          // (the writer edits freely from there). Amounts in DOLLARS.
          const partLines: any[] = [];
          (s.line_items || []).forEach((li, liIdx) => {
            const opt = chosenOpt(i, liIdx, s);
            if (!opt) return;
            const q = chosenQty(i, liIdx, s);
            partLines.push({
              name: opt.name,
              partNumber: opt.part_number || '',
              qty: q,
              cost: opt.cost_cents != null ? String(Math.round(opt.cost_cents / 100)) : '',
              retail: String(Math.round((opt.retail_cents || 0) / 100)),
            });
          });
          const labors: any[] = [];
          if (laborCents) {
            labors.push({
              description: s.canned_job?.name || s.title,
              hours: s.estimated_labor_hours ? String(s.estimated_labor_hours) : '',
              rate: '',
              total: String(Math.round(laborCents / 100)),
            });
          }
          onAddToQuote({
            label: s.title,
            description: s.rationale || '',
            parts: '', qty: '1', labor: '', sublet: '', fee: '', discount: '', laborHours: '',
            source_id: makeLocalSourceId(),
            labors,
            partLines,
          } as any);
        };

        return (
          <Box key={i} style={{ padding: '11px 14px', borderTop: i ? `1px solid ${BORDER}` : undefined }}>
            <Group justify="space-between" align="flex-start" gap={10} wrap="nowrap">
              <Box style={{ flex: 1, minWidth: 0 }}>
                <Text size="sm" fw={600} c={TEXT} style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.title}</Text>
                <Text size="sm" c={TEXT_MUTED} mt={3}>
                  {[laborCents || s.estimated_labor_hours ? `Labor ${laborLabel}` : null, partsCents ? `Parts ${usd(partsCents)}` : null, `Total ${usd(total)}`].filter(Boolean).join('   ·   ')}
                </Text>
              </Box>
              {onAddToQuote && (
                <UnstyledButton
                  onClick={handleAdd}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4, padding: '5px 11px', borderRadius: 6, flexShrink: 0,
                    border: `1px solid ${isAdded ? 'rgba(34,197,94,0.4)' : BLUE}`,
                    color: isAdded ? '#22C55E' : BLUE, fontSize: 13, fontWeight: 600,
                    background: isAdded ? 'rgba(34,197,94,0.08)' : 'rgba(37,99,235,0.08)',
                    cursor: isAdded ? 'default' : 'pointer',
                  }}
                >
                  {isAdded ? '✓ Added' : <><IconPlus size={13} />Add to Job</>}
                </UnstyledButton>
              )}
            </Group>

            {/* Per-line: role × [qty] · spec_basis, then a dropdown of EVERY
                part option (price · stock · est · ★). Show real parts options
                outside, so the writer can swap the part before adding. */}
            {(s.line_items || []).map((li, liIdx) => {
              const key = `${i}:${liIdx}`;
              const opts = li.part_options || [];
              return (
                <Box key={liIdx} mt={8}>
                  <Group gap={6} align="center" wrap="wrap">
                    <Text size="sm" c={TEXT_SECONDARY}>{li.role}</Text>
                    <Text size="sm" c={TEXT_MUTED}>×</Text>
                    <input
                      type="number"
                      min={0}
                      step={0.1}
                      value={chosenQty(i, liIdx, s)}
                      onChange={(e) => setQty((q) => ({ ...q, [key]: Number(e.target.value) }))}
                      title="Quantity — edit to exact capacity (e.g. 4.2) or keep the suggested amount"
                      style={{
                        width: 56, fontSize: 13, padding: '3px 6px', borderRadius: 6,
                        border: `1px solid ${BORDER}`, background: '#0C0C0E', color: TEXT,
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    />
                    {li.spec_basis ? <Text size="sm" c={TEXT_MUTED}>· {li.spec_basis}</Text> : null}
                  </Group>
                  {opts.length === 0 ? (
                    <Text size="sm" c={TEXT_MUTED} fs="italic" mt={4}>no in-stock part — source externally</Text>
                  ) : (
                    <select
                      value={chosenOptIdx(i, liIdx, s)}
                      onChange={(e) => setPicked((p) => ({ ...p, [key]: Number(e.target.value) }))}
                      style={{
                        marginTop: 5, width: '100%', fontSize: 13, padding: '6px 8px', borderRadius: 6,
                        border: `1px solid ${BORDER}`, background: '#0C0C0E', color: TEXT,
                      }}
                    >
                      {opts.map((o, oIdx) => (
                        <option key={oIdx} value={oIdx}>
                          {o.name}{o.part_number ? ` (${o.part_number})` : ''} — {usd(o.retail_cents)}{(o.available ?? 0) > 0 ? ` · ${o.available} in stock` : ''}{o.estimated ? ' · est' : ''}{o.recommended ? ' ★' : ''}
                        </option>
                      ))}
                    </select>
                  )}
                </Box>
              );
            })}

            {s.rationale ? <Text size="sm" c={TEXT_MUTED} mt={8} style={{ lineHeight: 1.45 }}>{s.rationale}</Text> : null}
          </Box>
        );
      })}
    </Box>
  );
}

function ChatMessage({
  msg,
  showHeader = true,
  onSuggestReply,
  onAddToQuote,
  quotedLabels,
  shopLaborRate,
  shopName,
  customerName,
  customerEmail,
  customerPhone,
  conversationId,
}: {
  msg: MessageResponse;
  showHeader?: boolean;
  onSuggestReply: (text: string) => void;
  onAddToQuote?: (item: QuoteItem) => void;
  quotedLabels?: Set<string>;
  shopLaborRate?: number | null;
  shopName?: string | null;
  customerName?: string | null;
  customerEmail?: string | null;
  customerPhone?: string | null;
  conversationId?: number;
}) {
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const isUser = msg.role === 'user';
  const isAssistant = msg.role === 'assistant';

  if (isUser) {
    return (
      <Box px={24} py={6} style={{ display: 'flex', justifyContent: 'flex-end' }}>
        {lightboxUrl && <PhotoLightbox url={lightboxUrl} onClose={() => setLightboxUrl(null)} />}
        <Box style={{ maxWidth: '68%' }}>
          {/* Image attachments */}
          {msg.attachments && msg.attachments.length > 0 && (
            <Box style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end', marginBottom: 6 }}>
              {msg.attachments
                .filter((a) => a.file_type?.startsWith('image') || a.file_type?.startsWith('video') || a.public_url)
                .map((att) => {
                  const attIsVideo = att.file_type === 'video' || isVideoUrl(att.public_url || '');
                  return attIsVideo ? (
                    <Box
                      key={att.id}
                      style={{ width: 120, height: 90, borderRadius: 8, cursor: 'pointer', border: `1px solid ${BORDER}`, overflow: 'hidden', position: 'relative', background: '#000' }}
                      onClick={() => setLightboxUrl(att.public_url ?? null)}
                    >
                      <video src={att.public_url ?? undefined} muted style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      <Box style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <IconPlayerPlay size={24} color="#fff" style={{ opacity: 0.8 }} />
                      </Box>
                    </Box>
                  ) : (
                    <img
                      key={att.id}
                      src={thumb(att.public_url)}
                      alt=""
                      style={{ width: 120, height: 90, objectFit: 'cover', borderRadius: 8, cursor: 'pointer', border: `1px solid ${BORDER}` }}
                      onClick={() => setLightboxUrl(att.public_url ?? null)}
                    />
                  );
                })}
            </Box>
          )}
          {msg.content && (() => {
            const isReportRequest = msg.used_tools?.some((t) => t === 'final_report' || t === 'estimate_report');
            const displayContent = isReportRequest ? msg.content.split('\n')[0] : msg.content;
            return (
              <Box
                style={{
                  background: 'rgba(37,99,235,0.22)',
                  border: `1px solid rgba(37,99,235,0.45)`,
                  borderRadius: '14px 14px 4px 14px',
                  padding: '10px 14px',
                  minWidth: 56,
                  display: 'inline-block',
                }}
              >
                <Text size="sm" c={TEXT} style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                  {displayContent}
                </Text>
              </Box>
            );
          })()}
          <Group gap={6} justify="flex-end" mt={4}>
            {msg.user_name && (
              <Text size="xs" c={TEXT_MUTED}>{msg.user_name}</Text>
            )}
            <Text size="xs" c={TEXT_MUTED}>
              {formatTime(msg.created_at)}
              {msg.is_pending && ' · Sending…'}
            </Text>
          </Group>
        </Box>
      </Box>
    );
  }

  if (isAssistant) {
    const roSuggestions = (msg.message_metadata as any)?.type === 'ro_suggestion'
      ? ((msg.message_metadata as any)?.suggestions as RoSuggestion[] | undefined)
      : undefined;
    // Server-side on-create rec in progress — a PERSISTED marker (survives reload,
    // visible without the extension); the backend swaps it for the outcome card.
    const isRoPending = (msg.message_metadata as any)?.type === 'ro_suggestion_pending';
    return (
      <Box px={24} py={8}>
        <Group gap={10} align="flex-start" wrap="nowrap">
          {/* AI avatar */}
          <Box style={{ width: 28, height: 28, borderRadius: '50%', flexShrink: 0, marginTop: 2, overflow: 'hidden', visibility: showHeader ? 'visible' : 'hidden' }}>
            <img src="/images/haist/haist-icon.jpg" alt="HAIST" style={{ width: 28, height: 28, objectFit: 'cover', display: 'block' }} />
          </Box>

          <Box style={{ flex: 1, minWidth: 0 }}>
            {showHeader && <Text size="xs" c={TEXT_MUTED} fw={500} mb={4}>HAIST</Text>}
            {/* On-creation RO job recommendations — render the card, skip prose. */}
            {isRoPending ? (
              <Group gap={8} align="center" style={{ paddingTop: 4 }}>
                <Text size="sm" c={TEXT_MUTED}>Pulling recommended work…</Text>
                <Box style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  {[0, 1, 2].map((n) => (
                    <Box key={n} style={{ width: 6, height: 6, borderRadius: '50%', background: TEXT_MUTED, animation: `thinking-dot 1.2s ease-in-out ${n * 0.2}s infinite` }} />
                  ))}
                </Box>
              </Group>
            ) : roSuggestions ? (
              <RoSuggestionCard
                suggestions={roSuggestions}
                onAddToQuote={onAddToQuote}
                quotedLabels={quotedLabels}
                notes={(msg.message_metadata as any)?.notes}
                outcome={(msg.message_metadata as any)?.outcome}
              />
            ) : (
            <>
            {/* eslint-disable-next-line react/jsx-no-useless-fragment */}
            {/* Image carousel — extracted from markdown */}
            {/* Images handled by Sources section — no inline carousel */}
            {/* Prose content — no bubble */}
            {msg.content && (
              <Box
                style={{ fontSize: 14, lineHeight: 1.7, color: msg.is_pending ? TEXT_MUTED : TEXT_SECONDARY }}
                className="md-content"
              >
                <CollapsibleMarkdown content={msg.content} />
              </Box>
            )}

            {/* Artifacts */}
            {msg.artifacts && msg.artifacts.length > 0 && <ArtifactCard msg={msg} shopName={shopName} customerName={customerName} customerEmail={customerEmail} customerPhone={customerPhone} conversationId={conversationId} />}

            {/* References (diagrams, videos, guides) */}
            {msg.references && msg.references.length > 0 && <ReferencesSection refs={msg.references} />}

            {/* Suggested replies */}
            {msg.suggested_replies && msg.suggested_replies.length > 0 && (
              <Box mt={10}>
                <Group gap={6} wrap="wrap">
                  {msg.suggested_replies.filter((r) => !/add.*(to\s*)?quote/i.test(r)).map((reply, i) => (
                    <UnstyledButton
                      key={i}
                      onClick={() => onSuggestReply(reply)}
                      style={{
                        padding: '5px 12px',
                        borderRadius: 6,
                        border: `1px solid ${BORDER}`,
                        color: TEXT_SECONDARY,
                        fontSize: 12,
                        background: CARD_BG,
                        transition: 'all 0.12s',
                      }}
                      onMouseEnter={(e) => {
                        (e.currentTarget as HTMLElement).style.borderColor = BLUE;
                        (e.currentTarget as HTMLElement).style.color = TEXT;
                      }}
                      onMouseLeave={(e) => {
                        (e.currentTarget as HTMLElement).style.borderColor = BORDER;
                        (e.currentTarget as HTMLElement).style.color = TEXT_SECONDARY;
                      }}
                    >
                      {reply}
                    </UnstyledButton>
                  ))}
                </Group>
              </Box>
            )}

            <Text size="xs" c={TEXT_MUTED} mt={6}>
              {formatTime(msg.created_at)}
              {msg.is_pending && ' · Sending…'}
              {!msg.is_pending && msg.status === 'processing' && ' · Thinking…'}
            </Text>
            </>
            )}
          </Box>
        </Group>
      </Box>
    );
  }

  return null;
}

// ─── Inspection (unchanged logic, restyled) ────────────────────────────────────

type ItemState = 'replace' | 'soon' | 'good';

function StatusButton({
  label,
  state,
  current,
  onSelect,
  onClear,
}: {
  label: string;
  state: ItemState;
  current: string;
  onSelect: (s: ItemState) => void;
  onClear: () => void;
}) {
  const isActive = current === state;
  const colorMap: Record<ItemState, string> = { replace: '#EF4444', soon: '#F59E0B', good: '#22C55E' };
  const color = colorMap[state];
  return (
    <UnstyledButton
      onClick={() => isActive ? onClear() : onSelect(state)}
      style={{
        padding: '3px 10px',
        borderRadius: 6,
        fontSize: 11,
        fontWeight: 600,
        border: `1px solid ${color}`,
        background: isActive ? color : 'transparent',
        color: isActive ? '#fff' : color,
        transition: 'all 0.12s',
        whiteSpace: 'nowrap',
        flexShrink: 0,
      }}
    >
      {label}
    </UnstyledButton>
  );
}

function ItemCard({
  item,
  onPatch,
  onUploadPhoto,
  onSaveAnnotation,
  onRevertAnnotation,
  readOnly,
  uploading,
  onAddToQuote,
  inspectionId,
  tekMediaByUrl,
}: {
  item: InspectionItemResponse;
  onPatch: (id: number, p: ItemPatchRequest) => void;
  onUploadPhoto: (id: number, files: File[]) => void;
  onSaveAnnotation?: (id: number, photoIndex: number, file: File) => Promise<void> | void;
  onRevertAnnotation?: (id: number, photoIndex: number) => Promise<void> | void;
  readOnly?: boolean;
  uploading?: boolean;
  onAddToQuote?: (item: QuoteItem) => void;
  // Needed so the "click suggestion → fetch one more rec" path can re-fire
  // aiInspectChat scoped to this inspection. Optional: the chained-rec call
  // is skipped when missing (fail-soft).
  inspectionId?: number;
  // Background HAIST→Tek upload state per photo URL (extension embed only).
  tekMediaByUrl?: Map<string, TekMediaState>;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  // description is the single canonical field.
  const effectiveMechanicText = (it: any): string => (it?.description || '') as string;
  const [notes, setNotes] = useState(effectiveMechanicText(item));
  const isFocusedRef = useRef(false);
  const [fetchingPrice, setFetchingPrice] = useState(false);
  // A freshly-created item (no title AND no description) is "being created":
  // it auto-opens in edit mode and shows no Edit button — the user finishes via
  // the long Save / Cancel.
  const isNewItem = (it: any): boolean =>
    !((it?.display_name || (it?.part_name && !/^item_\d+$/.test(it.part_name))) || (it?.description || '').trim());
  // Explicit edit mode: the title + description are read-only until the user
  // clicks "Edit". No autosave — edits are committed only on "Save" (or
  // discarded on "Cancel"). `editing` gates the inputs + swaps the button row.
  // New (blank) items start in edit mode.
  const [editing, setEditing] = useState(() => isNewItem(item));
  // Title (display name) edit buffer. Was previously a save-on-blur input, so
  // tabbing from the title into the description silently saved the title — now
  // it only persists on Save.
  const titleOf = (it: any): string =>
    /^item_\d+$/.test(it?.part_name || '') ? '' : (it?.display_name || (it?.part_name || '').replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase()));
  const [titleDraft, setTitleDraft] = useState(titleOf(item));

  // Sync from server when not actively editing (don't clobber an in-progress
  // edit). `editing` is the source of truth while the user has the card open.
  useEffect(() => {
    if (!editing) {
      setNotes(effectiveMechanicText(item));
      setTitleDraft(titleOf(item));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.description, item.display_name, item.part_name, editing]);

  // Warn on tab close/refresh while an unsaved edit is open.
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (editing) e.preventDefault();
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [editing]);

  const isAnalyzing = item.state === 'analyzing' || uploading;
  const dotColor =
    item.state === 'replace' ? '#EF4444' :
    item.state === 'soon' ? '#F59E0B' :
    item.state === 'good' ? '#22C55E' :
    isAnalyzing ? '#3B82F6' : '#3F3F46';

  // Buffer-only while editing — nothing persists until Save.
  const handleNotesChange = (val: string) => setNotes(val);

  const saveNotes = () => {
    // Commit title + description together. description is the single canonical
    // field that flows to report / portal / Tek sync; title maps to part_name.
    const patch: any = { description: notes };
    const t = titleDraft.trim();
    if (t && t !== (item.display_name || item.part_name)) {
      patch.part_name = t.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
    }
    onPatch(item.id, patch);
    setEditing(false);
  };
  const cancelNotes = () => {
    // Discard buffers back to the persisted values.
    setNotes(effectiveMechanicText(item));
    setTitleDraft(titleOf(item));
    setEditing(false);
  };

  const photos = item.photos ?? [];
  const [annotate, setAnnotate] = useState<{ url: string; index: number } | null>(null);
  const [lightbox, setLightbox] = useState<{ url: string; highlights?: VideoHighlight[]; blob?: string | null; mediaKind?: 'audio' | 'video'; itemId?: number } | null>(null);
  const [hoveredPhotoIdx, setHoveredPhotoIdx] = useState<number | null>(null);
  const [confirmDeleteIdx, setConfirmDeleteIdx] = useState<number | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const dragCounterRef = useRef(0);

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    dragCounterRef.current++;
    if (e.dataTransfer.types.includes('Files') || e.dataTransfer.types.includes('application/x-inspection-photo')) setDragOver(true);
  };
  const handleDragLeave = () => {
    dragCounterRef.current--;
    if (dragCounterRef.current === 0) setDragOver(false);
  };
  const handleDragOver = (e: React.DragEvent) => e.preventDefault();
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    dragCounterRef.current = 0;
    setDragOver(false);
    if (readOnly || uploading || isAnalyzing) return;
    // Handle photo move between items
    const photoData = e.dataTransfer.getData('application/x-inspection-photo');
    if (photoData) {
      try {
        const parsed = JSON.parse(photoData);
        const { sourceItemId, photo, sourcePhotos, photoIndex } = parsed;
        if (sourceItemId === item.id) return; // same item, ignore
        // Remove from source
        onPatch(sourceItemId, { photos: sourcePhotos.filter((_: any, idx: number) => idx !== photoIndex) });
        // Add to target
        onPatch(item.id, { photos: [...photos, photo] });
      } catch { /* ignore bad data */ }
      return;
    }
    const files = Array.from(e.dataTransfer.files).filter((f) => f.type.startsWith('image/') || f.type.startsWith('video/'));
    if (files.length) onUploadPhoto(item.id, files);
  };

  return (
    <Box
      data-item-id={item.id}
      data-batch-id={item.batch_id ?? undefined}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      style={{
        margin: '5px 0', padding: '12px 14px', borderRadius: 10,
        border: `1px solid ${dragOver ? '#3B82F6' : BORDER}`,
        background: dragOver ? 'rgba(59,130,246,0.06)' : '#111114',
        outline: dragOver ? `2px dashed #3B82F6` : '2px solid transparent',
        transition: 'outline 0.1s, background 0.1s, border-color 0.1s',
      }}
    >
      <Box style={{ display: 'flex', alignItems: 'center', gap: 6, opacity: item.is_hidden ? 0.4 : readOnly ? 0.5 : 1, pointerEvents: readOnly ? 'none' : undefined }}>
        <UnstyledButton
          onClick={() => onPatch(item.id, { is_hidden: !item.is_hidden })}
          title={item.is_hidden ? 'Show item' : 'Hide item'}
          style={{
            flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
            width: 22, height: 22, borderRadius: 4,
            background: item.is_hidden ? 'rgba(239,68,68,0.12)' : 'transparent',
            border: item.is_hidden ? '1px solid rgba(239,68,68,0.3)' : '1px solid transparent',
            transition: 'all 0.15s',
          }}
        >
          {item.is_hidden ? <IconEyeOff size={13} color="#EF4444" /> : <IconEye size={13} color={TEXT_MUTED} />}
        </UnstyledButton>
        {isAnalyzing && <Loader size={12} color={BLUE} style={{ flexShrink: 0 }} />}
        {readOnly || isAnalyzing || !editing ? (
          <Text size="sm" fw={600} c={isAnalyzing ? BLUE : TEXT} style={{ flex: 1, minWidth: 0 }} truncate>
            {/^item_\d+$/.test(item.part_name) ? '' : (item.display_name || item.part_name)}
            {isAnalyzing && <Text component="span" size="xs" c={BLUE} fw={400} ml={6}>analyzing…</Text>}
          </Text>
        ) : (
          // Editable only in edit mode; the value lives in titleDraft and is
          // persisted by Save (no save-on-blur).
          <input
            value={titleDraft}
            onChange={(e) => setTitleDraft(e.currentTarget.value)}
            placeholder="Item name"
            style={{ flex: 1, minWidth: 0, background: '#111113', border: `1px solid #3F3F46`, borderRadius: 6, color: TEXT, fontSize: 14, fontWeight: 600, outline: 'none', padding: '4px 8px' }}
          />
        )}
        {!isAnalyzing && <>
          <StatusButton label="Good" state="good" current={item.state} onSelect={(s) => onPatch(item.id, { state: s })} onClear={() => onPatch(item.id, { state: 'pending' })} />
          <StatusButton label="Soon" state="soon" current={item.state} onSelect={(s) => onPatch(item.id, { state: s })} onClear={() => onPatch(item.id, { state: 'pending' })} />
          <StatusButton label="Replace" state="replace" current={item.state} onSelect={(s) => onPatch(item.id, { state: s })} onClear={() => onPatch(item.id, { state: 'pending' })} />
        </>}
      </Box>

      {lightbox && <PhotoLightbox url={lightbox.url} highlights={lightbox.highlights} blob={lightbox.blob} mediaKind={lightbox.mediaKind} itemId={lightbox.itemId} onClose={() => setLightbox(null)} />}
      {annotate && onSaveAnnotation && (
        <PhotoAnnotator
          opened
          photoUrl={annotate.url}
          onClose={() => setAnnotate(null)}
          onSave={(file) => onSaveAnnotation(item.id, annotate.index, file)}
          canRevert={!!photos[annotate.index]?.original_url}
          onRevert={onRevertAnnotation ? () => onRevertAnnotation(item.id, annotate.index) : undefined}
        />
      )}

      <Modal
        opened={confirmDeleteIdx !== null}
        onClose={() => setConfirmDeleteIdx(null)}
        title="Remove photo"
        centered
        size="xs"
      >
        <Text size="sm" c={TEXT_SECONDARY} mb={20}>Are you sure you want to remove this photo?</Text>
        <Group justify="flex-end" gap={8}>
          <Button variant="default" size="sm" onClick={() => setConfirmDeleteIdx(null)}>Cancel</Button>
          <Button
            color="red"
            size="sm"
            onClick={() => {
              onPatch(item.id, { photos: photos.filter((_, idx) => idx !== confirmDeleteIdx) });
              setConfirmDeleteIdx(null);
            }}
          >Remove</Button>
        </Group>
      </Modal>

      <Group gap={8} mt={10} ml={16} wrap="wrap">
        {(item as any).video_url && (
          <Box style={{ position: 'relative', cursor: 'pointer' }} onClick={() => setLightbox({ url: (item as any).video_url, highlights: (item as any).video_highlights, blob: (item as any).video_blob_name, mediaKind: 'video', itemId: item.id })}>
            <Box style={{ width: 68, height: 56, borderRadius: 6, border: `1px solid ${BORDER}`, overflow: 'hidden', position: 'relative', background: '#000' }}>
              <video src={(item as any).video_url} muted preload="metadata" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              <Box style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <IconPlayerPlay size={18} color="#fff" style={{ opacity: 0.8 }} />
              </Box>
            </Box>
          </Box>
        )}
        {(item as any).audio_blob_name && (
          <Box
            title="Voice note"
            onClick={() => setLightbox({ url: publicUrl((item as any).audio_blob_name), blob: (item as any).audio_blob_name, mediaKind: 'audio', itemId: item.id })}
            style={{
              width: 68, height: 56, borderRadius: 6, border: `1px solid ${BORDER}`, background: CARD_BG,
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 3, cursor: 'pointer',
            }}
          >
            <IconMicrophone size={18} color="#FF6B35" />
            <Text size="sm" c="dimmed" fw={600}>Voice</Text>
          </Box>
        )}
        {photos.map((photo, i) => {
          // photoUrl = canonical (edited) version — used for display, drag,
          // annotate & video detection. The pristine original is preserved in
          // the photo's `original_url` (set by editPhoto) but never shown here.
          const photoUrl = photoDisplayUrl(photo);
          const isVid = isVideoUrl(photoUrl);
          // Still on its way to Tek (background sync). The pulled stamp wins, so a
          // missed final status can't leave a stale badge once the refetch lands.
          const tekState = photo.pulled_to_tekmetric ? undefined : tekMediaByUrl?.get(photoUrl);
          const handlePhotoDragStart = (e: React.DragEvent) => {
            if (readOnly || isVid) return;
            e.dataTransfer.setData('application/x-inspection-photo', JSON.stringify({
              sourceItemId: item.id,
              photoIndex: i,
              photo,
              sourcePhotos: photos,
            }));
            e.dataTransfer.effectAllowed = 'move';
            // Large drag preview
            const preview = document.createElement('img');
            preview.src = photoUrl;
            preview.style.width = '120px';
            preview.style.height = '100px';
            preview.style.objectFit = 'cover';
            preview.style.borderRadius = '8px';
            preview.style.border = '2px solid #3B82F6';
            document.body.appendChild(preview);
            e.dataTransfer.setDragImage(preview, 60, 50);
            requestAnimationFrame(() => document.body.removeChild(preview));
          };
          return (
          <Box
            key={i}
            style={{ position: 'relative', cursor: readOnly || isVid ? 'pointer' : 'grab' }}
            draggable={!readOnly && !isVid}
            onDragStart={handlePhotoDragStart}
            onMouseEnter={() => setHoveredPhotoIdx(i)}
            onMouseLeave={() => setHoveredPhotoIdx(null)}
          >
            {isVid ? (
              <Box
                style={{ width: 68, height: 56, borderRadius: 6, cursor: 'pointer', border: `1px solid ${BORDER}`, overflow: 'hidden', position: 'relative', background: '#000' }}
                onClick={() => setLightbox({ url: photoUrl })}
              >
                <video src={photoUrl} muted preload="metadata" style={{ width: '100%', height: '100%', objectFit: 'cover', pointerEvents: 'none' }} />
                <Box style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                  <IconPlayerPlay size={18} color="#fff" style={{ opacity: 0.8 }} />
                </Box>
              </Box>
            ) : (
              <img
                src={thumb(photoUrl) || photoUrl}
                alt=""
                style={{ width: 68, height: 56, objectFit: 'cover', borderRadius: 6, cursor: 'pointer', border: `1px solid ${BORDER}`, display: 'block' }}
                onClick={() => {
                  // pop-up = edit mode: editable inspection photos open straight
                  // into the annotator (always on the canonical/edited image so
                  // re-edits accumulate); otherwise just view in the lightbox.
                  if (!readOnly && onSaveAnnotation) setAnnotate({ url: photoUrl, index: i });
                  else setLightbox({ url: photoUrl });
                }}
              />
            )}
            {!readOnly && hoveredPhotoIdx === i && (
              <ActionIcon
                size={16}
                radius="xl"
                variant="filled"
                color="red"
                style={{ position: 'absolute', top: -5, right: -5, zIndex: 10 }}
                onClick={(e) => {
                  e.stopPropagation();
                  setConfirmDeleteIdx(i);
                }}
              >
                <IconX size={10} />
              </ActionIcon>
            )}
            {(tekState === 'pending' || tekState === 'uploading' || tekState === 'failed' || tekState === 'given-up') && (
              <Box
                title={
                  tekState === 'given-up' ? "Couldn't send to Tek"
                    : tekState === 'failed' ? 'Not sent to Tek yet — will retry'
                      : 'Sending to Tek…'
                }
                style={{
                  position: 'absolute', bottom: 3, right: 3, width: 16, height: 16, borderRadius: '50%',
                  background: 'rgba(12,12,14,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                {tekState === 'given-up' ? <IconAlertCircle size={12} color="#EF4444" />
                  : tekState === 'failed' ? <IconAlertCircle size={12} color="#F59E0B" />
                    : <Loader size={10} color="blue" />}
              </Box>
            )}
          </Box>
          );
        })}
        {uploading && (
          <Box style={{
            width: 68, height: 56, borderRadius: 6,
            border: `1.5px dashed ${BORDER}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Loader size={16} color="blue" />
          </Box>
        )}
        {!readOnly && (
          <ActionIcon
            variant="subtle"
            size={56}
            radius="sm"
            disabled={uploading}
            style={{ border: `1.5px dashed ${BORDER}`, color: TEXT_MUTED, height: 56, width: 68 }}
            onClick={() => fileRef.current?.click()}
          >
            <IconPhoto size={18} />
          </ActionIcon>
        )}
      </Group>

      <input
        ref={fileRef}
        type="file"
        accept="image/*,video/*"
        multiple
        style={{ display: 'none' }}
        onChange={(e) => {
          const files = Array.from(e.target.files ?? []);
          if (files.length) onUploadPhoto(item.id, files);
          e.target.value = '';
        }}
      />

      {isAnalyzing ? (
        <Box mt={10} ml={16} style={{ background: '#0C0C0E', borderRadius: 6, border: `1px solid #1D4ED8`, padding: '10px 12px' }}>
          <Group gap={8} align="center" justify="space-between">
            <Group gap={8} align="center">
              <Loader size={12} color="blue" />
              <Text size="xs" c="#60A5FA">Analyzing…</Text>
            </Group>
            {!readOnly && (
              <UnstyledButton
                onClick={() => onPatch(item.id, { state: 'pending' })}
                style={{ fontSize: 11, color: TEXT_MUTED, textDecoration: 'underline', cursor: 'pointer' }}
              >
                Reset
              </UnstyledButton>
            )}
          </Group>
        </Box>
      ) : (notes || item.description || !readOnly) && (
        <Box mt={10} ml={16}>
          {/* Mechanic-authored description box. The AI-rewritten text
              (`description`) surfaces above as a dismissible suggestion
              card — never auto-applied. California BAR compliance
              (Title 16 CCR § 3353) requires the mechanic to author the
              text the customer signs against. */}
          {(() => {
            // Mechanic text is the canonical record. When the AI ran on
            // photos alone (no audio, no typing), description_mechanic
            // stays empty and only `description` carries the AI enhance
            // — that surfaces in the suggestion card, NOT here. Don't
            // fall back to `description` or we'd duplicate the AI text
            // into the textarea, defeating the BAR-compliance design.
            const mechanicText = effectiveMechanicText(item);
            const suggestion = (item.description_autofill || '') as string;
            // Display value: user's in-progress edit (notes) when it
            // diverges from the persisted mechanic text, otherwise the
            // persisted mechanic text itself.
            // Use `notes` (the controlled edit buffer) as-is in the else branch
            // — NOT `notes || mechanicText`, which snapped a CLEARED field back
            // to the original text (empty `notes` is falsy), making the
            // description impossible to delete. `notes` is seeded from + synced
            // to item.description, so it's the source of truth while editing.
            const displayValue = (notes || '').trim() === mechanicText.trim() ? mechanicText : notes;
            // Paste = APPEND the AI suggestion to the mechanic's
            // existing text. The two pieces are joined with
            // sentence-boundary hygiene so the resulting prose reads
            // cleanly even when one side skipped terminal punctuation
            // or started lowercase. Mechanic's authorship is preserved
            // (BAR), AI is supplementary context. Empty mechanic →
            // append == replace.
            // Append the suggestion to the mechanic text, but DROP any
            // sentence the suggestion echoes from what's already there. The AI
            // autofill frequently restates the existing description; without
            // this the same sentence would double on every accept (the
            // headlamps "shown twice" bug).
            const joinSuggestion = (left: string, right: string): string => {
              const splitSents = (s: string) =>
                (s || '').split(/(?<=[.!?])\s+/).map((x) => x.trim()).filter(Boolean);
              const norm = (s: string) => s.replace(/\s+/g, ' ').replace(/[.!?]+$/, '').trim().toLowerCase();
              const l = (left || '').trim().replace(/\s+/g, ' ');
              const r = (right || '').trim().replace(/\s+/g, ' ');
              if (!l) return r;
              if (!r) return l;
              const seen = new Set(splitSents(l).map(norm));
              const fresh = splitSents(r).filter((s) => !seen.has(norm(s)));
              if (!fresh.length) return l; // suggestion added nothing new
              let addition = fresh.join(' ');
              const lEnd = /[.!?]$/.test(l) ? l : `${l}.`;
              if (/^[a-z]/.test(addition)) addition = addition.charAt(0).toUpperCase() + addition.slice(1);
              return `${lEnd} ${addition}`;
            };
            const onPasteAppend = () => {
              const next = joinSuggestion(mechanicText, suggestion);
              // Append (joinSuggestion drops echoed sentences) + clear autofill.
              onPatch(item.id, ({ description: next, description_autofill: '', suggestion_accepted: true } as any));
              // Fire a follow-up enhance so the mechanic can keep adding ("add
              // add add"). The prompt forbids restating; if it only echoes, the
              // suggestion card filters it to empty and hides → loop ends.
              if (inspectionId) {
                void (async () => {
                  try {
                    const { inspectionApi: api } = await import('../api/client');
                    await api.aiInspectChat(inspectionId, {
                      prompt: `For item "${item.part_name}" ONLY: the write-up already says "${next}". Add exactly ONE brief, genuinely NEW observation (a different angle, measurement, or condition not already stated). Do NOT repeat, rephrase, or restate anything already written. If there is nothing new, change nothing. Do NOT touch other items.`,
                      // Internal re-enhance — NOT a visible chat message. persist_to_chat
                      // defaults off (opt-in), so simply not opting in keeps it silent.
                    });
                  } catch { /* fire-and-forget */ }
                })();
              }
            };
            return (
              <>
                <Box style={{ background: '#0C0C0E', borderRadius: 6, border: `1px solid ${editing ? '#3F3F46' : BORDER}`, overflow: 'hidden', position: 'relative' }}>
                  <Textarea
                    value={displayValue}
                    onChange={(e) => handleNotesChange(e.currentTarget.value)}
                    onFocus={() => { isFocusedRef.current = true; }}
                    onBlur={() => { isFocusedRef.current = false; }}
                    readOnly={readOnly || !editing}
                    placeholder={editing ? 'Type the finding…' : 'No analysis yet…'}
                    minRows={2}
                    autosize
                    maxRows={8}
                    size="xs"
                    styles={{
                      input: {
                        background: '#0C0C0E',
                        border: 'none',
                        borderRadius: 6,
                        color: TEXT_SECONDARY,
                        fontSize: 13,
                        padding: '8px 12px',
                        cursor: readOnly || !editing ? 'default' : 'text',
                      },
                    }}
                  />
                </Box>
                {/* AI autofill suggestion renders BELOW the description. */}
                <InspectionSuggestionCard
                  itemId={item.id}
                  // Pass the effective displayed text (notes when the
                  // user is editing, else mechanic) so the card hides
                  // automatically when the textarea already matches the
                  // suggestion — no point offering "paste" of identical
                  // text.
                  currentText={displayValue}
                  suggestion={suggestion}
                  onPaste={onPasteAppend}
                  readOnly={readOnly}
                />
                {/* Explicit edit controls — no autosave. Edit is a full-width
                    line button; it splits into Cancel | Save while editing. */}
                {!readOnly && (
                  editing ? (
                    <Group gap={8} mt={8} wrap="nowrap" align="stretch">
                      <UnstyledButton
                        onClick={cancelNotes}
                        style={{ flexShrink: 0, fontSize: 12, fontWeight: 600, color: TEXT_MUTED, textAlign: 'center', padding: '8px 14px', borderRadius: 6, border: `1px solid ${BORDER}`, background: 'transparent' }}
                      >Cancel</UnstyledButton>
                      <UnstyledButton
                        onClick={saveNotes}
                        style={{ flex: 1, fontSize: 12, fontWeight: 600, color: '#fff', textAlign: 'center', padding: '8px 14px', borderRadius: 6, border: `1px solid ${BLUE}`, background: BLUE }}
                      >Save</UnstyledButton>
                    </Group>
                  ) : (
                    <UnstyledButton
                      onClick={() => setEditing(true)}
                      style={{ display: 'block', width: '100%', marginTop: 8, fontSize: 12, fontWeight: 600, color: TEXT_SECONDARY, textAlign: 'center', padding: '8px 14px', borderRadius: 6, border: `1px solid ${BORDER}`, background: '#27272A' }}
                    >Edit</UnstyledButton>
                  )
                )}
              </>
            );
          })()}
        </Box>
      )}

      {/* Add to Job button removed — items flow through "From Inspection" in Job tab */}
    </Box>
  );
}

const CategorySection = forwardRef<HTMLDivElement, {
  category: InspectionCategory;
  onPatch: (id: number, p: ItemPatchRequest) => void;
  onUploadPhoto: (id: number, files: File[]) => void;
  onSaveAnnotation?: (id: number, photoIndex: number, file: File) => Promise<void> | void;
  onRevertAnnotation?: (id: number, photoIndex: number) => Promise<void> | void;
  readOnly?: boolean;
  isActive?: boolean;
  uploadingItemIds?: Set<number>;
  onAddToQuote?: (item: QuoteItem) => void;
  inspectionId?: number;
  stickyTop?: number;
  tekMediaByUrl?: Map<string, TekMediaState>;
}>(function CategorySection({ category, onPatch, onUploadPhoto, onSaveAnnotation, onRevertAnnotation, readOnly, isActive, uploadingItemIds, onAddToQuote, inspectionId, stickyTop = 0, tekMediaByUrl }, ref) {
  const [collapsed, setCollapsed] = useState(false);
  const completed = category.items.filter((i) => i.state === 'good' || i.state === 'replace' || i.state === 'soon').length;
  const total = category.items.length;

  return (
    <Box ref={ref} data-category={category.name} style={{ marginBottom: 8 }}>
      {/* Sticky header */}
      <Box style={{
        position: 'sticky',
        top: stickyTop,
        zIndex: 2,
        background: isActive ? '#1a2a4a' : '#111114',
        borderLeft: isActive ? `3px solid ${BLUE}` : '3px solid transparent',
        borderBottom: `1px solid ${isActive ? BLUE : BORDER}`,
        borderTop: `1px solid ${BORDER}`,
        borderRight: `1px solid ${BORDER}`,
        borderRadius: 0,
        transition: 'background 0.15s, border-color 0.15s',
      }}>
        <UnstyledButton onClick={() => setCollapsed(!collapsed)} style={{ width: '100%' }}>
          <Group justify="space-between" align="center" px="sm" py={8}>
            <Group gap={8} wrap="nowrap">
              <Text size="xs" c={isActive ? BLUE : TEXT_MUTED} style={{ fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>{completed}/{total}</Text>
              <Text size="sm" fw={700} c={isActive ? BLUE : TEXT} style={{ textTransform: 'capitalize' }}>
                {category.display_name}
              </Text>
            </Group>
            <Box style={{ transform: collapsed ? 'rotate(-90deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
              <IconChevronRight size={14} color={isActive ? BLUE : TEXT_MUTED} />
            </Box>
          </Group>
        </UnstyledButton>
      </Box>
      {!collapsed && (
        <Box px={8} pt={10} pb={10}>
          {category.items.map((item) => (
            <ItemCard key={item.id} item={item} onPatch={onPatch} onUploadPhoto={onUploadPhoto} onSaveAnnotation={onSaveAnnotation} onRevertAnnotation={onRevertAnnotation} readOnly={readOnly} uploading={uploadingItemIds?.has(item.id)} onAddToQuote={onAddToQuote} inspectionId={inspectionId} tekMediaByUrl={tekMediaByUrl} />
          ))}
        </Box>
      )}
    </Box>
  );
});

import useSWR, { mutate as swrMutate } from 'swr';

function catDotColor(cat: InspectionCategory): string {
  if (cat.items.some((i) => i.state === 'replace')) return '#EF4444';
  if (cat.items.some((i) => i.state === 'soon')) return '#F59E0B';
  if (cat.items.some((i) => i.state === 'good')) return '#22C55E';
  return '#3F3F46';
}

function InspectionTab({ conversationId, readOnly, onAddToQuote, onInspectionIdChange, onItemChange, toggleOverlayNode, isSwitchingTemplate, onSendMessage, onScrollToQueueEntry, hideComposer, onCountsChange }: { conversationId: number; readOnly?: boolean; onAddToQuote?: (item: QuoteItem) => void; onInspectionIdChange?: (id: number | undefined) => void; onItemChange?: () => void; toggleOverlayNode?: React.ReactNode; isSwitchingTemplate?: boolean; onSendMessage?: (text: string) => Promise<void>; onScrollToQueueEntry?: (entry: QueueEntry) => boolean; hideComposer?: boolean; onCountsChange?: (counts: { total: number; good: number; soon: number; replace: number; pending: number }) => void }) {
  const { conversation } = useConversation(conversationId);
  const vehicleId = conversation?.vehicle?.id;
  const [aiChatLoading, setAiChatLoading] = useState(false);
  const [showHidden, setShowHidden] = useState(false);
  const [vehicleToast, setVehicleToast] = useState<string | null>(null);
  const [inspPendingFiles, setInspPendingFiles] = useState<File[]>([]);
  const [inspPendingPreviews, setInspPendingPreviews] = useState<string[]>([]);
  const [inspDragging, setInspDragging] = useState(false);
  const inspDragCounter = useRef(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  const catRefs = useRef<(HTMLDivElement | null)[]>([]);
  const spacerRef = useRef<HTMLDivElement>(null);
  const inspectionFileInputRef = useRef<HTMLInputElement>(null);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [inspSevFilter, setInspSevFilter] = useState<string | null>(null);
  // Single source of truth for which items are visible given the current
  // hidden/severity filters. Used by both the category nav and the list so a
  // category with no visible items disappears from BOTH (after filter same).
  const itemPassesFilter = (item: InspectionCategory['items'][number]): boolean => {
    if (!showHidden && item.is_hidden) return false;
    if (inspSevFilter) {
      if (inspSevFilter === 'pending') return !isRatedInspState(item.state);
      return item.state === inspSevFilter;
    }
    return true;
  };
  const [voiceSending, setVoiceSending] = useState(false);
  const [draftItems, setDraftItems] = useState<{ id: string; name: string; desc: string; saving?: boolean }[]>([]);
  const [addingItem, setAddingItem] = useState(false);
  const voice = useInspectionVoice();

  // Inspection chat file helpers
  const inspAddFiles = async (files: FileList | File[] | null) => {
    if (!files) return;
    const arr = Array.from(files).filter(isMediaFile);
    if (!arr.length) return;
    // Only allow 1 video at a time
    const hasExistingVideo = inspPendingFiles.some(isVideoFile);
    const newVideos = arr.filter(isVideoFile);
    if (newVideos.length > 1 || (hasExistingVideo && newVideos.length > 0)) {
      alert('Only one video per message is supported');
      const filtered = arr.filter((f) => !isVideoFile(f));
      if (!filtered.length) return;
      setInspPendingFiles((prev) => [...prev, ...filtered]);
      setInspPendingPreviews((prev) => [...prev, ...filtered.map((f) => URL.createObjectURL(f))]);
      return;
    }
    // Check video duration (max 3 min)
    for (const v of newVideos) {
      const ok = await checkVideoDuration(v);
      if (!ok) return;
    }
    setInspPendingFiles((prev) => [...prev, ...arr]);
    setInspPendingPreviews((prev) => [...prev, ...arr.map((f) => URL.createObjectURL(f))]);
  };
  const inspRemoveFile = (idx: number) => {
    URL.revokeObjectURL(inspPendingPreviews[idx]);
    setInspPendingFiles((prev) => prev.filter((_, i) => i !== idx));
    setInspPendingPreviews((prev) => prev.filter((_, i) => i !== idx));
  };
  useEffect(() => {
    return () => inspPendingPreviews.forEach(URL.revokeObjectURL);
  }, [inspPendingPreviews]);
  const inspHandleDragEnter = (e: React.DragEvent) => { e.preventDefault(); inspDragCounter.current++; if (e.dataTransfer.items?.length) setInspDragging(true); };
  const inspHandleDragLeave = (e: React.DragEvent) => { e.preventDefault(); inspDragCounter.current--; if (inspDragCounter.current === 0) setInspDragging(false); };
  const inspHandleDragOver = (e: React.DragEvent) => { e.preventDefault(); };
  const inspHandleDrop = (e: React.DragEvent) => { e.preventDefault(); inspDragCounter.current = 0; setInspDragging(false); inspAddFiles(e.dataTransfer.files); };

  // Fetch inspection list by conversation (each conversation = one job)
  const listKey = `vehicle/inspection/list?conversation=${conversationId}`;
  const { data: listData, mutate: mutateList } = useSWR(
    listKey,
    async () => {
      const { inspectionApi: api } = await import('../api/client');
      return api.list({ conversation_id: conversationId });
    },
  );

  const { data: templates } = useSWR(
    'vehicle/inspection/templates',
    async () => {
      const { inspectionApi: api } = await import('../api/client');
      return api.getTemplates();
    },
  );

  // Pick the best inspection: prefer one with items, then in_progress, then latest
  const inspectionId = (() => {
    const list = listData?.inspections;
    if (!list?.length) return undefined;
    // Prefer inspection with items (item_count > 0)
    const withItems = list.find((i) => i.item_count > 0);
    if (withItems) return withItems.id;
    // Then prefer in_progress
    const inProgress = list.find((i) => i.status === 'in_progress');
    if (inProgress) return inProgress.id;
    // Fallback to latest
    return list[0]?.id;
  })();
  const { inspection, categories, isLoading, patchItem: rawPatchItem, addPhotos, editPhoto, revertPhoto, uploadingItemIds, tekMedia } = useInspection(inspectionId, { mediaSyncConversationId: conversationId });

  // Report the visible item count up so the Inspect CARD title shows it (single
  // "Inspect" header — no duplicate label inside the body). Keyed on the count
  // primitive so it only fires when the number actually changes.
  const inspectVisible = (inspection?.items || []).filter((i: any) => !i.is_hidden && !i.is_extra);
  const inspectCounts = {
    total: inspectVisible.length,
    good: inspectVisible.filter((i: any) => i.state === 'good').length,
    soon: inspectVisible.filter((i: any) => i.state === 'soon').length,
    replace: inspectVisible.filter((i: any) => i.state === 'replace').length,
    pending: inspectVisible.filter((i: any) => !isRatedInspState(i.state)).length,
  };
  const inspectCountsKey = `${inspectCounts.total}|${inspectCounts.good}|${inspectCounts.soon}|${inspectCounts.replace}|${inspectCounts.pending}`;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { onCountsChange?.(inspectCounts); }, [inspectCountsKey]);

  // Notify parent of inspectionId changes
  useEffect(() => { onInspectionIdChange?.(inspectionId); }, [inspectionId]);

  // Refresh conversation vehicle data when inspection items finish analyzing
  // (bulk AI inspection updates vehicle in background — frontend needs to pick it up)
  const prevBusyRef = useRef(false);
  useEffect(() => {
    const isBusy = inspection?.items.some(i => !i.is_hidden && (i.state === 'analyzing' || i.state === 'uploaded')) ?? false;
    if (prevBusyRef.current && !isBusy && conversationId) {
      swrMutate(`conversations/${conversationId}`);
    }
    prevBusyRef.current = isBusy;
  }, [inspection, conversationId]);

  const handleVoiceSend = useCallback(async () => {
    if (!inspectionId) return;
    setVoiceSending(true);
    // Queue the voice-driven inspect-chat so the history badge shows it
    // alongside other AI runs in this conversation.
    const queueId = conversationId ? `vq_${Date.now()}_${Math.random().toString(36).slice(2, 8)}` : null;
    if (conversationId && queueId) {
      messageQueue.enqueue({
        id: queueId,
        conversationId,
        content: '[voice]',
        status: 'in-flight',
        optimisticMessageId: -Date.now(),
        createdAt: Date.now(),
      });
    }
    try {
      const { blob, transcript } = await voice.stopVoice();
      if (!blob) { setVoiceSending(false); if (conversationId && queueId) messageQueue.fail(conversationId, queueId); return; }
      const { inspectionApi: api } = await import('../api/client');
      if (transcript) {
        const chatResult = await api.aiInspectChat(inspectionId, { prompt: transcript, persist_to_chat: true });
        if (chatResult.vehicle_detection) {
          const vd = chatResult.vehicle_detection;
          const parts: string[] = [];
          if (vd.make || vd.model || vd.year) parts.push(`${vd.year || ''} ${vd.make || ''} ${vd.model || ''}`.trim());
          if (vd.plate_number) parts.push(`Plate: ${vd.plate_number}`);
          if (vd.mileage) parts.push(`Mileage: ${vd.mileage.toLocaleString()} mi`);
          if (parts.length) { setVehicleToast(`Vehicle detected — ${parts.join(' · ')}`); setTimeout(() => setVehicleToast(null), 5000); }
          swrMutate(`conversations/${conversationId}`);
        }
        const firstItemId = (chatResult.actions || []).find((a: any) => typeof a?.item_id === 'number')?.item_id as number | undefined;
        if (firstItemId && conversationId && queueId) {
          messageQueue.setTargetItem(conversationId, queueId, firstItemId);
        }
      } else if (voice.durationMs < 1500) {
        setVoiceSending(false);
        if (conversationId && queueId) messageQueue.fail(conversationId, queueId);
        return;
      } else {
        const blobName = await voice.uploadAndGetBlobName(blob);
        // Internal instruction (the mechanic's input is the audio itself, shown as the
        // '[voice]' queue entry) — must NOT surface as a visible chat message.
        await api.aiInspectChat(inspectionId, { prompt: 'Analyze this audio recording — only create items if you hear clear diagnostic sounds.', audio_blob_names: [blobName] }); // internal — persist_to_chat default off
      }
      await mutateList();
      await swrMutate(`vehicle/inspection/${inspectionId}`);
      if (conversationId && queueId) messageQueue.markOldestDone(conversationId, Date.now());
    } catch (err) {
      console.error('[VOICE_SEND]', err);
      if (conversationId && queueId) messageQueue.fail(conversationId, queueId);
    } finally {
      setVoiceSending(false);
    }
  }, [inspectionId, voice, mutateList]);

  // Detect vehicle auto-linked via batch AI inspection (SWR poll sees vehicle_id appear)
  // Skip if chat toast was already shown (chatToastRef prevents double toast)
  // Also skip if conversation already has a vehicle on mount (e.g. pushed from extension)
  const prevVehicleIdRef = useRef<number | null | undefined>(undefined);
  const chatToastShownRef = useRef(false);
  const mountedWithVehicleRef = useRef(!!conversation?.vehicle);
  useEffect(() => {
    const vid = inspection?.vehicle_id;
    if (prevVehicleIdRef.current === undefined) { prevVehicleIdRef.current = vid ?? null; return; }
    if (vid && !prevVehicleIdRef.current && !chatToastShownRef.current && !mountedWithVehicleRef.current) {
      // Toast removed — vehicle identification is silent
    }
    chatToastShownRef.current = false;
    mountedWithVehicleRef.current = false;
    prevVehicleIdRef.current = vid ?? null;
  }, [inspection?.vehicle_id]);

  // Wrap patchItem to notify parent of changes
  const patchItem = useCallback(async (...args: Parameters<typeof rawPatchItem>) => {
    const result = await rawPatchItem(...args);
    onItemChange?.();
    return result;
  }, [rawPatchItem, onItemChange]);

  // Track active category via IntersectionObserver
  useEffect(() => {
    const container = scrollRef.current;
    if (!container || categories.length === 0) return;
    const intersecting = new Set<string>();
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const name = entry.target.getAttribute('data-category');
          if (!name) continue;
          if (entry.isIntersecting) intersecting.add(name);
          else intersecting.delete(name);
        }
        const first = categories.find((c) => intersecting.has(c.name));
        if (first) setActiveCategory(first.name);
      },
      { root: container, threshold: 0, rootMargin: '0px 0px -70% 0px' },
    );
    catRefs.current.forEach((el) => { if (el) observer.observe(el); });
    return () => observer.disconnect();
  }, [categories]);

  const scrollToCategory = (index: number) => {
    const el = catRefs.current[index];
    const container = scrollRef.current;
    if (!el || !container) return;
    const offset = el.offsetTop - container.offsetTop;
    container.scrollTo({ top: offset, behavior: 'smooth' });
  };

  // Scroll to top on template switch
  const prevTemplateIdRef = useRef(inspection?.template_id);
  useEffect(() => {
    if (!inspection) return;
    if (prevTemplateIdRef.current !== undefined && prevTemplateIdRef.current !== inspection.template_id) {
      scrollRef.current?.scrollTo({ top: 0 });
    }
    prevTemplateIdRef.current = inspection.template_id;
  }, [inspection?.template_id]);

  // Dynamic bottom spacer: allows last category to scroll near the top
  useEffect(() => {
    const container = scrollRef.current;
    const spacer = spacerRef.current;
    if (!container || !spacer) return;
    const lastCat = catRefs.current[categories.length - 1];
    if (!lastCat || categories.length <= 1) {
      spacer.style.height = '16px';
      return;
    }
    const containerH = container.clientHeight;
    // Only pad when the content actually OVERFLOWS the viewport. Otherwise the
    // "scroll last category to the top" spacer adds a near-full-height empty gap
    // below short content (the reported too-long inspect scroll). Exclude the
    // current spacer height when measuring real content.
    const contentH = container.scrollHeight - spacer.offsetHeight;
    if (contentH <= containerH) {
      spacer.style.height = '16px';
      return;
    }
    // Spacer = container height - last category height - some breathing room
    const lastH = lastCat.getBoundingClientRect().height;
    spacer.style.height = `${Math.max(16, containerH - lastH - 40)}px`;
  }, [categories]);

  const hasAnalyzingItems = !!inspection?.items.some(
    i => !i.is_hidden && (i.state === 'analyzing' || i.state === 'uploaded')
  );
  const isAnalyzingAny = aiChatLoading || hasAnalyzingItems;

  // When AI batch analysis finishes (analyzing → done), notify parent so report preview refreshes
  const wasAnalyzingRef = useRef(false);
  useEffect(() => {
    if (hasAnalyzingItems) {
      wasAnalyzingRef.current = true;
    } else if (wasAnalyzingRef.current) {
      wasAnalyzingRef.current = false;
      onItemChange?.();
    }
  }, [hasAnalyzingItems]);


  // Auto-start AI inspection when no inspection exists
  const autoStartedRef = useRef(false);
  useEffect(() => {
    // CRITICAL: wait for the inspection list to actually load before concluding
    // there is none. On refresh listData is briefly undefined → inspectionId
    // reads as none → this fired POST /vehicle/inspection/start on every render
    // until the list arrived (the burst of 201s). The backend now creates the
    // inspection on conversation-create, so this is only a legacy safety net for
    // old conversations — and must only run once the list has loaded empty.
    if (listData === undefined) return;
    if (!inspectionId && !inspection && !isLoading && !autoStartedRef.current) {
      autoStartedRef.current = true;
      (async () => {
        try {
          const { inspectionApi: api } = await import('../api/client');
          await api.start({ conversation_id: conversationId, mode: 'ai' });
          await mutateList();
        } catch (e: any) {
          console.error('[AUTO_START_AI]', e);
        }
      })();
    }
  }, [inspectionId, inspection, isLoading, conversationId, listData]);

  if (isLoading || (inspectionId && !inspection)) {
    return <Center h={200}><Loader size="sm" color="haist" /></Center>;
  }

  if (!inspectionId || !inspection) {
    return <Center h={200}><Loader size="sm" color="haist" /></Center>;
  }

  const { completed, total } = inspection.progress;

  return (
    <>
      {/* Vehicle detection toast */}
      {vehicleToast && (
        <Box
          style={{
            position: 'absolute', top: 12, left: '50%', transform: 'translateX(-50%)', zIndex: 100,
            background: '#1a3a2a', border: '1px solid #22C55E44', borderRadius: 8,
            padding: '8px 16px', maxWidth: 420, animation: 'fadeIn 0.2s ease',
          }}
        >
          <Group gap={8} wrap="nowrap">
            <IconCar size={16} color="#22C55E" style={{ flexShrink: 0 }} />
            <Text size="xs" c="#22C55E" fw={500}>{vehicleToast}</Text>
            <UnstyledButton onClick={() => setVehicleToast(null)} style={{ flexShrink: 0, color: TEXT_MUTED }}>
              <IconX size={14} />
            </UnstyledButton>
          </Group>
        </Box>
      )}
      <Box style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* TOC + scrollable content — centered as a unit */}
        <Box style={{ flex: 1, display: 'flex', justifyContent: 'center', minHeight: 0, overflow: 'hidden' }}>
        <Box style={{ display: 'flex', width: '100%', maxWidth: '50vw', minHeight: 0 }}>
          {/* Table of contents — hidden */}
          <Box style={{ width: 200, flexShrink: 0, overflowY: 'auto', borderRight: `1px solid ${BORDER}`, paddingTop: 0, paddingBottom: 40, background: '#0C0C0E', display: 'none' }}>
            {/* Severity summary */}
            <Box style={{ padding: '10px 12px 8px', borderBottom: `1px solid ${BORDER}` }}>
              <Group gap={10} align="center">
                {[
                  { state: 'good', label: 'Good', color: '#22C55E' },
                  { state: 'soon', label: 'Soon', color: '#F59E0B' },
                  { state: 'replace', label: 'Replace', color: '#EF4444' },
                  { state: 'pending', label: 'Pending', color: TEXT_MUTED },
                ].map(({ state, label, color }) => {
                  const count = inspection.items.filter(i => (state === 'pending' ? !isRatedInspState(i.state) : i.state === state) && !i.is_hidden && !i.is_extra).length;
                  const active = inspSevFilter === state;
                  return (
                    <UnstyledButton key={state} onClick={() => setInspSevFilter(active ? null : state)}
                      style={{ display: 'flex', alignItems: 'center', gap: 3, padding: '2px 8px', borderRadius: 10,
                        background: active ? `${color}18` : 'transparent', border: `1px solid ${active ? `${color}40` : 'transparent'}` }}>
                      <Box style={{ width: 7, height: 7, borderRadius: '50%', background: color }} />
                      <Text size="xs" c={active ? color : (count > 0 ? TEXT_SECONDARY : TEXT_MUTED)} fw={active ? 700 : 400} style={{ fontVariantNumeric: 'tabular-nums', fontSize: 11 }}>{count}</Text>
                    </UnstyledButton>
                  );
                })}
                <Text size="xs" c={TEXT_MUTED} style={{ fontVariantNumeric: 'tabular-nums', fontSize: 11, marginLeft: 'auto' }}>{completed}/{total}</Text>
              </Group>
            </Box>
            {categories.map((cat, i) => {
              // Hide nav entries for categories with no visible items, matching
              // the list below (after filter same).
              const visibleItems = cat.items.filter(itemPassesFilter);
              if (!visibleItems.length) return null;
              const isActive = activeCategory === cat.name;
              const catCompleted = visibleItems.filter((item) => item.state === 'good' || item.state === 'replace' || item.state === 'soon').length;
              return (
                <UnstyledButton
                  key={cat.name}
                  onClick={() => scrollToCategory(i)}
                  style={{
                    width: '100%',
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '10px 12px',
                    borderLeft: isActive ? `3px solid ${BLUE}` : '3px solid transparent',
                    background: isActive ? '#1a2a4a' : 'transparent',
                    transition: 'background 0.12s',
                  }}
                >
                  <Box style={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0, background: catDotColor({ ...cat, items: visibleItems }) }} />
                  <Box style={{ flex: 1, minWidth: 0 }}>
                    <Text size="xs" fw={isActive ? 700 : 500} c={isActive ? BLUE : TEXT_SECONDARY} truncate style={{ textTransform: 'capitalize' }}>
                      {cat.display_name}
                    </Text>
                    <Text size="11px" c={isActive ? BLUE : TEXT_MUTED} style={{ fontVariantNumeric: 'tabular-nums' }}>
                      {catCompleted}/{visibleItems.length}
                    </Text>
                  </Box>
                </UnstyledButton>
              );
            })}
          </Box>

          {/* Category list */}
          <Box ref={scrollRef} style={{ flex: 1, overflowY: 'auto', padding: '0 16px 0' }}>
            <Box style={{ paddingTop: 2 }}>
            {inspectionId && <InspectionVideoSegments inspectionId={inspectionId} />}
            {isSwitchingTemplate && (
              <Box style={{ background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.25)', borderRadius: 10, padding: '10px 14px', marginBottom: 12 }}>
                <Group gap={10}>
                  <Loader size={14} color={BLUE} />
                  <Text size="xs" fw={600} c={BLUE}>Switching template…</Text>
                </Group>
              </Box>
            )}
            {isAnalyzingAny && (
              <Box style={{ background: 'rgba(59,130,246,0.08)', border: `1px solid rgba(59,130,246,0.25)`, borderRadius: 10, padding: '10px 14px', marginBottom: 12 }}>
                <Group gap={10}>
                  <Loader size={14} color={BLUE} />
                  <Text size="xs" fw={600} c={BLUE}>AI is analyzing…</Text>
                </Group>
              </Box>
            )}
            {(() => {
              const allItems = inspection?.items || [];
              const hiddenCount = allItems.filter(i => i.is_hidden).length;
              return (
                <>
                <Box style={{ position: 'sticky', top: 0, zIndex: 3, background: '#0C0C0E', display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: 8, margin: '0 -16px', padding: '6px 16px 8px', borderBottom: `1px solid ${BORDER}` }}>
                  <Group gap={4}>
                    {[
                      { state: 'good', label: 'Good', color: '#22c55e' },
                      { state: 'soon', label: 'Soon', color: '#f59e0b' },
                      { state: 'replace', label: 'Replace', color: '#ef4444' },
                      { state: 'pending', label: 'Pending', color: '#71717a' },
                    ].map(({ state, label, color }) => {
                      const active = inspSevFilter === state;
                      return (
                        <UnstyledButton key={state} onClick={() => setInspSevFilter(active ? null : state)}
                          style={{ fontSize: 13, fontWeight: 600, padding: '4px 12px', borderRadius: 12,
                            background: active ? `${color}20` : `${color}08`,
                            color: active ? color : `${color}99`,
                            border: `1px solid ${active ? `${color}50` : `${color}25`}`,
                          }}>{label}</UnstyledButton>
                      );
                    })}
                  </Group>
                  {hiddenCount > 0 && (
                    <UnstyledButton onClick={() => setShowHidden(p => !p)} style={{ fontSize: 13, color: BLUE }}>
                      {showHidden ? `Hide ${hiddenCount}` : `+${hiddenCount} hidden`}
                    </UnstyledButton>
                  )}
                </Box>
                </>
              );
            })()}
            {categories.map((cat, i) => {
              const visibleItems = cat.items.filter(itemPassesFilter);
              if (!visibleItems.length) return null;
              return (
              <Fragment key={cat.name}>
                {cat.is_extra && (i === 0 || !categories[i - 1]?.is_extra) && (
                  <Box style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '16px 0 8px', opacity: 0.5 }}>
                    <Box style={{ flex: 1, height: 1, background: BORDER }} />
                    <Text size="10px" c={TEXT_MUTED} fw={600} style={{ textTransform: 'uppercase', letterSpacing: '0.08em', whiteSpace: 'nowrap' }}>From previous template</Text>
                    <Box style={{ flex: 1, height: 1, background: BORDER }} />
                  </Box>
                )}
                <CategorySection
                  ref={(el) => { catRefs.current[i] = el; }}
                  category={{ ...cat, items: visibleItems }}
                  onPatch={patchItem}
                  onUploadPhoto={(id, files) => addPhotos(id, files)}
                  onSaveAnnotation={(id, photoIndex, file) => editPhoto(id, photoIndex, file)}
                  onRevertAnnotation={(id, photoIndex) => revertPhoto(id, photoIndex)}
                  readOnly={readOnly}
                  isActive={activeCategory === cat.name}
                  uploadingItemIds={uploadingItemIds}
                  onAddToQuote={onAddToQuote}
                  inspectionId={inspectionId}
                  stickyTop={41}
                  tekMediaByUrl={tekMedia.byUrl}
                />
              </Fragment>
              );
            })}
            {/* Draft items — local only, NOT saved until "Save". A new item
                starts in edit mode: editable title + description, and only the
                long Save / Cancel finish it (no save-on-blur). */}
            {draftItems.map((draft) => {
              const setDraft = (patch: Partial<{ name: string; desc: string; saving: boolean }>) =>
                setDraftItems(prev => prev.map(d => d.id === draft.id ? { ...d, ...patch } : d));
              const removeDraft = () => setDraftItems(prev => prev.filter(d => d.id !== draft.id));
              const saveDraft = async () => {
                const v = draft.name.trim();
                if (!v || draft.saving) return;
                const snake = v.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
                setDraft({ saving: true });
                try {
                  const { inspectionApi: api } = await import('../api/client');
                  // New items always land in 'Other Inspections' (backend
                  // display_order=9999) — capture the id to scroll it into view.
                  const resp = await api.addItem(inspectionId!, { part_name: snake || v, description: draft.desc.trim() || undefined });
                  await Promise.all([mutateList(), swrMutate(`vehicle/inspection/${inspectionId}`)]);
                  removeDraft();
                  const newItemId = (resp as { item_id?: number } | null | undefined)?.item_id;
                  if (newItemId) {
                    requestAnimationFrame(() => {
                      const el = document.querySelector(`[data-item-id="${newItemId}"]`);
                      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      else if (spacerRef.current) spacerRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
                    });
                  }
                } catch { setDraft({ saving: false }); }
              };
              return (
              <Box key={draft.id} style={{ margin: '4px 16px', padding: '12px 14px', borderRadius: 10, border: `1px solid ${BORDER}`, background: '#111114' }}>
                <Box style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <IconEye size={13} color={TEXT_MUTED} style={{ flexShrink: 0 }} />
                  <input
                    autoFocus
                    placeholder="Item name"
                    value={draft.name}
                    onChange={(e) => setDraft({ name: e.currentTarget.value })}
                    onKeyDown={(e) => { if (e.key === 'Escape') removeDraft(); }}
                    style={{ flex: 1, minWidth: 0, background: '#0C0C0E', border: `1px solid #3F3F46`, borderRadius: 6, color: TEXT, fontSize: 14, fontWeight: 600, outline: 'none', padding: '4px 8px' }}
                  />
                </Box>
                <Box mt={10} ml={16} style={{ background: '#0C0C0E', borderRadius: 6, border: `1px solid #3F3F46` }}>
                  <Textarea
                    placeholder="Type the finding…"
                    value={draft.desc}
                    onChange={(e) => setDraft({ desc: e.currentTarget.value })}
                    minRows={2}
                    autosize
                    maxRows={8}
                    size="xs"
                    styles={{ input: { background: '#0C0C0E', border: 'none', borderRadius: 6, color: TEXT_SECONDARY, fontSize: 13, padding: '8px 12px' } }}
                  />
                </Box>
                <Group gap={8} mt={8} ml={16} wrap="nowrap" align="stretch">
                  <UnstyledButton
                    onClick={removeDraft}
                    style={{ flexShrink: 0, fontSize: 12, fontWeight: 600, color: TEXT_MUTED, textAlign: 'center', padding: '8px 14px', borderRadius: 6, border: `1px solid ${BORDER}`, background: 'transparent' }}
                  >Cancel</UnstyledButton>
                  <UnstyledButton
                    onClick={saveDraft}
                    style={{ flex: 1, fontSize: 12, fontWeight: 600, color: '#fff', textAlign: 'center', padding: '8px 14px', borderRadius: 6, border: `1px solid ${BLUE}`, background: BLUE, opacity: draft.name.trim() && !draft.saving ? 1 : 0.5 }}
                  >{draft.saving ? 'Saving…' : 'Save'}</UnstyledButton>
                </Group>
              </Box>
              );
            })}
            {!readOnly && inspectionId && (
              <Box px={16} py={8}>
                <UnstyledButton
                  onClick={() => setDraftItems(prev => [...prev, { id: `draft_${Date.now()}`, name: '', desc: '' }])}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', width: '100%', background: '#111113', borderRadius: 10, border: `1px solid ${BORDER}`, transition: 'border-color 0.15s' }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.borderColor = BLUE; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.borderColor = BORDER; }}
                >
                  <IconPlus size={14} color={TEXT_MUTED} />
                  <Text size="sm" c={TEXT_MUTED}>Add Item</Text>
                </UnstyledButton>
              </Box>
            )}

            {/* Dynamic spacer: allows last category to scroll to top of viewport */}
            <Box ref={spacerRef} />
            </Box>
          </Box>
        </Box>
        </Box>

        {/* Bottom bar: input + toggle — hidden inside the Inspect card (the page's
            unified composer covers messaging there). */}
        {!readOnly && !hideComposer && (
        <Box style={{ flexShrink: 0, padding: '8px 24px 16px', background: `linear-gradient(to bottom, transparent, ${BG} 40%)`, zIndex: 5 }}>
          {voice.isRecording || voiceSending ? (
            <Box style={{ maxWidth: '50vw', margin: '0 auto' }}>
              <AudioRecordingOverlay
                durationMs={voice.durationMs}
                audioLevel={voice.audioLevel}
                transcript={voice.transcript}
                isTranscriptAvailable={voice.isTranscriptAvailable}
                onCancel={voice.cancelVoice}
                onSend={handleVoiceSend}
                sending={voiceSending}
              />
            </Box>
          ) : (
          <Box style={{ maxWidth: '50vw', margin: '0 auto', background: '#1E1E21', border: `1px solid ${inspDragging ? BLUE : BORDER}`, borderRadius: 16, boxShadow: '0 4px 24px rgba(0,0,0,0.4)', overflow: 'hidden', transition: 'border-color 0.15s' }}
            onDragEnter={(e) => { e.stopPropagation(); inspHandleDragEnter(e); }}
            onDragLeave={(e) => { e.stopPropagation(); inspHandleDragLeave(e); }}
            onDragOver={(e) => { e.stopPropagation(); inspHandleDragOver(e); }}
            onDrop={(e) => { e.stopPropagation(); inspHandleDrop(e); }}
          >
            {inspDragging && (
              <Box style={{ padding: '8px 16px', textAlign: 'center', background: 'rgba(37,99,235,0.08)' }}>
                <Text size="xs" c={BLUE} fw={500}>Drop photos or video here</Text>
              </Box>
            )}
            {inspPendingPreviews.length > 0 && (
              <Box style={{ display: 'flex', gap: 10, padding: '12px 16px 0', overflowX: 'auto' }}>
                {inspPendingPreviews.map((url, i) => (
                  <Box key={i} style={{ position: 'relative', flexShrink: 0 }}>
                    {inspPendingFiles[i] && isVideoFile(inspPendingFiles[i]) ? (
                      <video src={url} muted style={{ width: 110, height: 90, objectFit: 'cover', borderRadius: 10, border: `1px solid ${BORDER}`, display: 'block' }} />
                    ) : (
                      <img src={url} alt="" style={{ width: 110, height: 90, objectFit: 'cover', borderRadius: 10, border: `1px solid ${BORDER}`, display: 'block' }} />
                    )}
                    <ActionIcon size={26} radius="xl" onClick={() => inspRemoveFile(i)}
                      style={{ position: 'absolute', top: -8, right: -8, background: '#fff', border: '1px solid #ccc', color: '#333' }}>
                      <IconX size={12} />
                    </ActionIcon>
                  </Box>
                ))}
              </Box>
            )}
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                const input = e.currentTarget.querySelector('input[type="text"]') as HTMLInputElement;
                const prompt = input?.value?.trim();
                const hasFiles = inspPendingFiles.length > 0;
                if ((!prompt && !hasFiles) || isAnalyzingAny) return;
                if (!inspectionId) {
                  // No inspection yet — send as main conversation message
                  if (prompt && onSendMessage) {
                    input.value = '';
                    await onSendMessage(prompt);
                  }
                  return;
                }
                setAiChatLoading(true);
                // Queue an in-flight entry so the history badge tracks
                // this AI-inspect run alongside main-chat messages. The
                // optimisticMessageId is a placeholder negative timestamp
                // (no inspect-chat message row to scroll to today).
                const queueId = conversationId ? `iq_${Date.now()}_${Math.random().toString(36).slice(2, 8)}` : null;
                const queueContent = prompt || (hasFiles ? `[${inspPendingFiles.length} file${inspPendingFiles.length > 1 ? 's' : ''}]` : 'Analyze');
                if (conversationId && queueId) {
                  messageQueue.enqueue({
                    id: queueId,
                    conversationId,
                    content: queueContent,
                    status: 'in-flight',
                    optimisticMessageId: -Date.now(),
                    createdAt: Date.now(),
                  });
                }
                try {
                  const { inspectionApi: api, storageApi } = await import('../api/client');
                  let photoBlobNames: string[] = [];
                  let videoBlobName: string | undefined;
                  if (hasFiles) {
                    const photoFiles = inspPendingFiles.filter((f) => !isVideoFile(f));
                    const videoFile = inspPendingFiles.find(isVideoFile);
                    if (photoFiles.length) {
                      const uploads = await Promise.all(photoFiles.map((f) => storageApi.uploadToPresignedUrl(f)));
                      photoBlobNames = uploads.map((u) => u.blob_name);
                    }
                    if (videoFile) {
                      const upload = await storageApi.uploadToPresignedUrl(videoFile);
                      videoBlobName = upload.blob_name;
                    }
                  }
                  if ((photoBlobNames.length > 0 || videoBlobName) && !prompt) {
                    const res = await api.aiInspect(inspectionId, { photo_blob_names: photoBlobNames, video_blob_name: videoBlobName });
                    // aiInspect is async (worker creates items later), so there's
                    // no item_id yet — stamp the batch id on the queue entry so a
                    // click resolves to the items it creates via [data-batch-id]
                    // (instead of falling through to the Chat tab).
                    if (res?.batch_id && conversationId && queueId) {
                      messageQueue.setBatch(conversationId, queueId, res.batch_id);
                    }
                  } else {
                    // When the mechanic typed nothing, the prompt is an internal
                    // 'Analyze these' placeholder (the photos/video are the real input,
                    // shown via the queue entry) — keep that out of the visible chat.
                    const chatResult = await api.aiInspectChat(inspectionId, { prompt: prompt || 'Analyze these', photo_blob_names: photoBlobNames.length ? photoBlobNames : undefined, video_blob_name: videoBlobName, persist_to_chat: !!prompt });
                    if (chatResult.vehicle_detection) {
                      const vd = chatResult.vehicle_detection;
                      const parts: string[] = [];
                      if (vd.make || vd.model || vd.year) parts.push(`${vd.year || ''} ${vd.make || ''} ${vd.model || ''}`.trim());
                      if (vd.plate_number) parts.push(`Plate: ${vd.plate_number}`);
                      if (parts.length) { setVehicleToast(`Vehicle detected — ${parts.join(' · ')}`); setTimeout(() => setVehicleToast(null), 5000); }
                      swrMutate(`conversations/${conversationId}`);
                    }
                    // Attach the first affected item so the history badge
                    // can scroll the user there on click — without this
                    // the entry is just status with no destination.
                    const firstItemId = (chatResult.actions || []).find((a: any) => typeof a?.item_id === 'number')?.item_id as number | undefined;
                    if (firstItemId && conversationId && queueId) {
                      messageQueue.setTargetItem(conversationId, queueId, firstItemId);
                    }
                  }
                  input.value = '';
                  setInspPendingFiles([]);
                  setInspPendingPreviews((prev) => { prev.forEach(URL.revokeObjectURL); return []; });
                  await mutateList();
                  await swrMutate(`vehicle/inspection/${inspectionId}`);
                  if (conversationId && queueId) {
                    messageQueue.markOldestDone(conversationId, Date.now());
                  }
                } catch (err) {
                  console.error('[AI_INSPECT_CHAT]', err);
                  if (conversationId && queueId) {
                    messageQueue.fail(conversationId, queueId);
                  }
                } finally {
                  setAiChatLoading(false);
                }
              }}
              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px' }}
            >
              <UnstyledButton
                onClick={() => inspectionFileInputRef.current?.click()}
                style={{
                  width: 34, height: 34, borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  border: `1px solid ${BORDER}`, background: '#111113',
                  flexShrink: 0, color: TEXT_MUTED,
                }}
              >
                <IconPlus size={16} />
              </UnstyledButton>
              <input ref={inspectionFileInputRef} type="file" accept="image/*,video/*" multiple style={{ display: 'none' }}
                onChange={(e) => { inspAddFiles(e.target.files); e.target.value = ''; }}
              />
              <input
                type="text"
                placeholder={isAnalyzingAny ? 'Waiting for analysis…' : inspPendingFiles.length ? (() => { const pc = inspPendingFiles.filter(f => !isVideoFile(f)).length; const vc = inspPendingFiles.filter(isVideoFile).length; const parts: string[] = []; if (pc) parts.push(`${pc} photo${pc > 1 ? 's' : ''}`); if (vc) parts.push('1 video'); return `${parts.join(' + ')} — add a note or send`; })() : 'Ask anything'}
                disabled={isAnalyzingAny}
                style={{
                  flex: 1, background: 'transparent', border: 'none',
                  padding: '7px 4px', color: TEXT, fontSize: 15,
                  outline: 'none', opacity: isAnalyzingAny ? 0.5 : 1,
                }}
              />
              {/* Voice input removed */}
              <MessageQueueBadge conversationId={conversationId} onScrollToMessage={onScrollToQueueEntry} />
              <button
                type="submit"
                disabled={isAnalyzingAny || aiChatLoading}
                style={{
                  width: 34, height: 34, borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: (isAnalyzingAny || aiChatLoading) ? '#2A2A2E' : (inspPendingFiles.length > 0 ? BLUE : '#2A2A2E'), border: 'none', flexShrink: 0,
                  cursor: (isAnalyzingAny || aiChatLoading) ? 'not-allowed' : 'pointer',
                  opacity: (isAnalyzingAny || aiChatLoading) ? 0.4 : 1,
                  pointerEvents: (isAnalyzingAny || aiChatLoading) ? 'none' : undefined,
                }}
              >
                {(isAnalyzingAny || aiChatLoading) ? <Loader size={12} color="white" /> : <IconSend size={15} color={inspPendingFiles.length > 0 ? '#fff' : TEXT_MUTED} />}
              </button>
            </form>
          </Box>
          )}
          {/* tagline removed */}
        </Box>
        )}
      </Box>

    </>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

type QuoteItemPhoto = { url: string; label?: string; uploaded_at?: string; original_url?: string; edited_at?: string };
type QuoteItem = {
  label: string; parts: string; qty: string; labor: string; sublet: string; fee: string; discount: string; laborHours: string; description: string;
  inspectionItemId?: string;
  photos?: QuoteItemPhoto[];
  audio_blob_name?: string | null;
  video_blob_name?: string | null;
  is_hidden?: boolean;
  state?: string;
};
const EMPTY_QUOTE: QuoteItem = { label: '', parts: '', qty: '1', labor: '', sublet: '', fee: '', discount: '', laborHours: '', description: '' };

// Mint a client-side source_id so freshly-added items can be referenced
// in batch operations (Send for approval / Digital signature / Document
// upload) BEFORE the debounced backend save assigns one. Same shape as
// the mobile app's generator so backend dedup logic works uniformly.
// Backend's quote upsert preserves loc-* source_ids on round-trip.
function makeLocalSourceId(): string {
  return `loc-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}
function newEmptyQuote(): QuoteItem {
  return { ...EMPTY_QUOTE, source_id: makeLocalSourceId() } as any;
}

// ─── Main JobPage ─────────────────────────────────────────────────────────────

type ActiveTab = 'chat' | 'inspect' | 'quote' | 'repair' | 'sms';
type JobMode = 'inspection' | 'repair';

// One-time migration: push all quote_v2:* localStorage entries to the DB
async function migrateLocalStorageQuotes() {
  const DONE_KEY = 'quote_migration_done';
  if (localStorage.getItem(DONE_KEY)) return;
  const keys = Object.keys(localStorage).filter((k) => k.startsWith('quote_v2:'));
  await Promise.allSettled(
    keys.map(async (key) => {
      try {
        const { cid, items, description, note } = JSON.parse(localStorage.getItem(key) ?? '{}');
        if (!cid || !items?.length) return;
        await quoteApi.upsert(cid, { items, description: description || undefined, note: note || undefined });
        localStorage.removeItem(key);
      } catch { /* ignore individual failures */ }
    })
  );
  localStorage.setItem(DONE_KEY, '1');
}

// A single labor line wrapped with @dnd-kit/sortable so the user can drag-
// reorder lines within a quote item. The grip on the left activates dragging;
// everything else (textarea / inputs / delete) keeps its native interaction.
// `id` must be unique within the SortableContext — we prefer tek_labor_id and
// fall back to a per-row synthetic when the line is HAIST-local.
function SortableLaborRow({
  id, l, li, i, shopLaborRate, BORDER, TEXT, TEXT_SECONDARY, TEXT_MUTED, setQuoteItems, authLocked,
}: {
  id: string;
  l: any;
  li: number;
  i: number;
  shopLaborRate: number | null;
  BORDER: string;
  TEXT: string;
  TEXT_SECONDARY: string;
  TEXT_MUTED: string;
  setQuoteItems: (updater: (prev: any[]) => any[]) => void;
  // When true, all inputs are read-only because this item is bound to a
  // customer authorization. The user must cancel the auth doc before editing.
  authLocked: boolean;
}) {
  const sortable = useSortable({ id, disabled: authLocked });
  const style: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '8px 0',
    borderBottom: `1px solid ${BORDER}`,
    transform: CSS.Transform.toString(sortable.transform),
    transition: sortable.transition,
    opacity: sortable.isDragging ? 0.6 : 1,
    background: sortable.isDragging ? 'rgba(96,165,250,0.06)' : undefined,
  };
  const effectiveRate = parseFloat(l.rate || '0') || (shopLaborRate ?? 0);
  const computed = (parseFloat(l.hours || '0') || 0) * effectiveRate;
  const totalNum = parseFloat(l.total) || computed || 0;
  const totalDisplay = '$' + totalNum.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const ratePlaceholder = shopLaborRate ? String(shopLaborRate) : '0.00';
  return (
    <Box ref={sortable.setNodeRef} style={style}>
      <Box
        ref={sortable.setActivatorNodeRef}
        {...sortable.attributes}
        {...sortable.listeners}
        title="Drag to reorder"
        style={{ cursor: 'grab', display: 'flex', alignItems: 'center', color: TEXT_MUTED, touchAction: 'none', padding: '0 4px' }}
      >
        <IconGripVertical size={14} />
      </Box>
      <textarea value={l.description || ''} placeholder="Description" rows={1} readOnly={authLocked}
        onChange={(e) => { if (authLocked) return; const v = e.target.value; setQuoteItems((prev) => prev.map((q: any, idx: number) => idx === i ? { ...q, labors: q.labors?.map((ll: any, lli: number) => lli === li ? { ...ll, description: v } : ll) } : q)); }}
        onInput={(e) => { const t = e.target as HTMLTextAreaElement; t.style.height = 'auto'; t.style.height = t.scrollHeight + 'px'; }}
        style={{ flex: 1, background: 'transparent', border: 'none', color: TEXT_SECONDARY, fontSize: 14, outline: 'none', minWidth: 80, resize: 'none', overflow: 'hidden', fontFamily: 'inherit', lineHeight: 1.4, padding: 0, cursor: authLocked ? 'not-allowed' : 'text' }} />
      <input value={l.hours || ''} placeholder="0" readOnly={authLocked}
        onChange={(e) => { if (authLocked) return; const v = e.target.value; setQuoteItems((prev) => prev.map((q: any, idx: number) => idx === i ? { ...q, labors: q.labors?.map((ll: any, lli: number) => lli === li ? { ...ll, hours: v } : ll) } : q)); }}
        style={{ width: 64, background: 'transparent', border: 'none', borderBottom: `1px solid ${BORDER}`, color: TEXT, fontSize: 14, borderRadius: 0, padding: '4px 4px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', cursor: authLocked ? 'not-allowed' : 'text' }} />
      <Box style={{ position: 'relative', width: 90 }}>
        <Text size="13px" c={TEXT_MUTED} style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>$</Text>
        <input value={l.rate || ''} placeholder={ratePlaceholder} readOnly={authLocked}
          onChange={(e) => { if (authLocked) return; const v = e.target.value; setQuoteItems((prev) => prev.map((q: any, idx: number) => idx === i ? { ...q, labors: q.labors?.map((ll: any, lli: number) => lli === li ? { ...ll, rate: v } : ll) } : q)); }}
          style={{ width: '100%', background: 'transparent', border: 'none', borderBottom: `1px solid ${BORDER}`, color: TEXT, fontSize: 14, borderRadius: 0, padding: '4px 4px 4px 18px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', cursor: authLocked ? 'not-allowed' : 'text' }} />
      </Box>
      <Text size="14px" fw={700} c={TEXT} style={{ width: 100, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{totalDisplay}</Text>
      {!authLocked && (
        <UnstyledButton onClick={() => setQuoteItems((prev) => prev.map((q: any, idx: number) => idx === i ? { ...q, labors: (q.labors || []).filter((_: any, j: number) => j !== li) } : q))} style={{ color: '#ef4444', fontSize: 16, fontWeight: 700, lineHeight: 1, padding: '0 4px' }}>×</UnstyledButton>
      )}
    </Box>
  );
}

// Outer guard: short-circuits a dead conv URL BEFORE the heavy page body
// mounts. Done at component-boundary level — embedding an early-return
// inside JobPageInner skips hundreds of hooks below it and trips React's
// "rendered fewer hooks than expected" guard. The wrapper's hook order
// stays constant whether we redirect or render the body.
export function JobPage() {
  const { conversationId: paramId } = useParams<{ conversationId: string }>();
  const navigate = useNavigate();
  const conversationId = paramId ? parseInt(paramId, 10) : undefined;
  // Note: SWR cache key is shared with JobPageInner's own useConversation,
  // so this doesn't double-fetch — same request, two subscribers.
  const { error: convError } = useConversation(conversationId);

  const httpStatus = (convError as { response?: { status?: number } } | undefined)?.response?.status;
  const badParam = paramId !== undefined && (conversationId === undefined || Number.isNaN(conversationId));
  // 404 = deleted/missing, 403 = lost access, 410 = gone.
  // Transient 5xx intentionally excluded — api-client retries those 3×.
  const isDead = badParam || httpStatus === 404 || httpStatus === 403 || httpStatus === 410;

  useEffect(() => {
    if (isDead) navigate('/shop/dashboard', { replace: true });
  }, [isDead, navigate]);

  if (isDead) return null;
  return <JobPageInner />;
}

function JobPageInner() {
  const { conversationId: paramId } = useParams<{ conversationId: string }>();
  const navigate = useNavigate();
  const conversationId = paramId ? parseInt(paramId, 10) : undefined;
  const { user: currentUser } = useAuth();

  const { conversation, isLoading: convLoading, error: convError, mutate: mutateConversation } = useConversation(conversationId);
  const { messages, isLoading: msgsLoading, sendMessage, mutate: mutateMessages } = useMessages(conversationId);
  const { updateConversationStatus, deleteConversation, createConversation, mutate: mutateConversations } = useConversations();

  // HAIST→Tek auto-follow: when this conversation opens, drive the Tek tab to its
  // linked RO (tekmetric_last_ro_id; null = no Tek link → Tek stays). Runs within
  // the click's user activation so the cross-origin top-nav is allowed; no-op when
  // not embedded, and skipped when Tek is already on that RO (see maybeFollowTekTab).
  const tekFollowRoId = (() => {
    const raw = (conversation as any)?.vehicle?.vehicle_metadata?.tekmetric_last_ro_id;
    const n = raw != null ? Number(raw) : NaN;
    return Number.isFinite(n) ? n : null;
  })();
  useEffect(() => {
    if (conversationId == null || Number.isNaN(conversationId)) return;
    maybeFollowTekTab(tekFollowRoId);
  }, [conversationId, tekFollowRoId]);

  // One-time localStorage → DB migration (runs once per browser, no-op if already done)
  useEffect(() => { migrateLocalStorageQuotes(); }, []);

  // Shop labor rate — fetched once, used to pre-fill labor-line rates
  const { data: shopData } = useSWR('shop', () => shopApi.get().catch(() => null));
  const canSms = shopData?.sms_enabled;
  const deviceOverrides = useDeviceOverrides(shopData?.id);
  const ff = effectiveFlags('web', shopData?.feature_flags?.web, deviceOverrides);
  const canQuote = ff.quote;
  const canSmsFf = ff.sms;
  const signatureRequired = !!(shopData as any)?.report_config?.signature_required;
  const shopLaborRate = shopData?.labor_rate_default ?? null;
  // Labor tax = 0%, parts tax defaults to 9.75%
  const shopTaxRate = shopData?.tax_rate ?? 0.0975;

  // On-creation RO job recommendations (opt-in 'rec' device flag — default off).
  // Once per conversation, when no 'ro_suggestion' chat message exists yet, ask the
  // backend to generate from conversation data (concerns/vehicle/mileage/history/
  // inspection) and persist it as a chat card. The extension covers the Tek path on
  // RO create; this covers the web/manual (no-Tek) path. Lazy-on-open avoids racing
  // the create→quote-upsert sequence. The backend upsert is the real idempotency
  // guard; the ref + presence-check just avoid redundant calls.
  // NOTE: gated on the `rec` flag AND a non-prod backend. We key off the runtime
  // backend mode (getBackendMode), NOT import.meta.env.DEV — the prod-built embedded
  // shop panel can still point at the DEV backend, and that case SHOULD generate.
  // Recs stay OFF against the prod backend regardless of the flag.
  const recFiredRef = useRef<number | null>(null);
  // Loading bubble while an RO recommendation is being worked up. The web ff.rec
  // trigger sets this directly; the Tek/extension trigger signals it over the embed
  // bridge (haist-embed:rec-generating). Cleared on card arrival / done / timeout.
  const [roGenerating, setRoGenerating] = useState(false);
  useEffect(() => {
    if (!ff.rec || getBackendMode() === 'prod') return;
    if (!conversationId || msgsLoading || !messages) return;
    if (recFiredRef.current === conversationId) return;
    const hasRec = messages.some((m) => (m.message_metadata as any)?.type === 'ro_suggestion');
    recFiredRef.current = conversationId; // handled for this conversation
    if (hasRec) return; // already have a recommendation card
    const v = conversation?.vehicle;
    const bundle: Record<string, unknown> = {
      vehicle: v ? { year: v.year, make: v.make, model: v.model, vin: v.vin } : undefined,
      mileage: v?.mileage ?? null,
    };
    setRoGenerating(true);
    conversationsApi
      .generateRoSuggestions(conversationId, bundle, true)
      .then(() => mutateMessages())
      .catch((e) => console.error('[REC_ON_OPEN] generate failed', e))
      .finally(() => setRoGenerating(false));
  }, [ff.rec, conversationId, msgsLoading, messages, conversation, mutateMessages]);

  // "Pulling recommended work…" bubble lifecycle.
  const hasRecCard = useMemo(
    // ro_suggestion_pending counts too: the persisted server-side marker already
    // renders its own "Pulling…" row — don't double it with the event bubble.
    () => (messages || []).some((m) => ['ro_suggestion', 'ro_suggestion_pending'].includes((m.message_metadata as any)?.type)),
    [messages],
  );
  // Tek/extension trigger: the panel signals generation start/stop over the embed bridge.
  useEffect(() => {
    const onRecGen = (e: Event) => {
      const d = (e as CustomEvent).detail as { conversationId?: number; on?: boolean } | undefined;
      if (d?.conversationId != null && d.conversationId !== conversationId) return;
      setRoGenerating(!!d?.on);
    };
    window.addEventListener('haist-embed:rec-generating', onRecGen);
    return () => window.removeEventListener('haist-embed:rec-generating', onRecGen);
  }, [conversationId]);
  // Never let it stick: clear once the card arrives, and on a safety timeout.
  useEffect(() => {
    if (!roGenerating) return;
    if (hasRecCard) { setRoGenerating(false); return; }
    const t = setTimeout(() => setRoGenerating(false), 25000);
    return () => clearTimeout(t);
  }, [roGenerating, hasRecCard]);

  // Shop members — for account assignment
  const { data: shopMembers } = useSWR('shop-members', () => shopApi.listMembers().catch(() => []));

  // Eager-fetch vehicle history (shared key w/ the card body — one request) so
  // the collapsed Vehicle History card can show a last-service preview without
  // waiting for the user to expand it.
  const { data: vehicleHistoryData } = useSWR(
    conversationId ? vehicleHistoryKey(conversationId) : null,
    () => fetchVehicleHistory(conversationId!),
  );
  const historyPreview = useMemo(() => {
    const sources = (vehicleHistoryData as any)?.sources as any[] | undefined;
    if (!sources?.length) return undefined;
    const recs = sources.flatMap((s) => s.records || []).filter((r: any) => r?.source !== 'recall');
    if (!recs.length) return undefined;
    recs.sort((a: any, b: any) => (b.date || '').localeCompare(a.date || ''));
    const last = recs[0];
    let d = '';
    if (last?.date) { try { d = new Date(last.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }); } catch { /* skip */ } }
    const desc = String(last?.description || last?.lines?.[0]?.description || '').trim();
    const head = desc ? (desc.length > 36 ? `${desc.slice(0, 36)}…` : desc) : (last?.ro_number ? `RO #${last.ro_number}` : 'Service');
    const money = last?.total != null && last.total > 0 ? `$${Math.round(last.total).toLocaleString()}` : null;
    const miles = last?.mileage ? `${Number(last.mileage).toLocaleString()} mi` : null;
    return [`Last: ${head}`, d || null, money, miles, recs.length > 1 ? `${recs.length} records` : null].filter(Boolean).join(' · ');
  }, [vehicleHistoryData]);

  // Customer used to seed the stage-transition SMS modal: prefer the primary
  // contact, else fall back to the first sub-contact (so a customer stored only
  // as a sub-contact still pre-fills — matching what the Customer card shows).
  const txnContact = useMemo(() => {
    const v = conversation?.vehicle as any;
    if (v?.first_name || v?.last_name || v?.phone || v?.email) {
      return { first_name: v?.first_name || '', last_name: v?.last_name || '', phone: v?.phone || '', email: v?.email || '' };
    }
    const subs = ((v?.sub_phones ?? []) as any[]).map((sp) => (typeof sp === 'string' ? { phone: sp } : sp));
    const first = subs.find((s: any) => s?.name || s?.phone || s?.email);
    if (first) {
      const parts = String(first.name || '').trim().split(/\s+/).filter(Boolean);
      return { first_name: parts[0] || '', last_name: parts.slice(1).join(' '), phone: first.phone || '', email: first.email || '' };
    }
    return { first_name: '', last_name: '', phone: '', email: '' };
  }, [conversation?.vehicle]);

  // Job mode derived from conversation status or whether messages exist
  const hasMessages = (messages?.length ?? 0) > 0;
  const jobMode: JobMode = (conversation?.status === 'repair' || hasMessages) ? 'repair' : 'inspection';
  const isInspectionMode = jobMode === 'inspection';
  const isRepairMode = jobMode === 'repair';

  const [searchParams, setSearchParams] = useSearchParams();
  const VALID_TABS: ActiveTab[] = ['repair', 'inspect', 'quote', 'chat', 'sms'];
  const tabParam = searchParams.get('tab') as ActiveTab | null;
  const stageTab = conversation?.workflow_stage ? (STAGE_TAB_MAP[conversation.workflow_stage] || 'repair') : 'repair';
  const activeTab: ActiveTab = tabParam && VALID_TABS.includes(tabParam) ? tabParam : stageTab as ActiveTab;

  const setActiveTab = useCallback((tab: ActiveTab) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', tab);
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const reportActionsRef = useRef<ReportActions | null>(null);

  // ── Workflow transition state ──
  const [showTransitionModal, setShowTransitionModal] = useState(false);
  const [transitioning, setTransitioning] = useState(false);
  const [transitionPhoneInput, setTransitionPhoneInput] = useState('');
  const [transitionFirstName, setTransitionFirstName] = useState('');
  const [transitionLastName, setTransitionLastName] = useState('');
  const [transitionEmail, setTransitionEmail] = useState('');
  const currentStage = (conversation?.workflow_stage as typeof WORKFLOW_STAGES[number]) || 'registration';
  // The workspace (6 panels + chat + stage toolbar) is ALWAYS shown — even before
  // a vehicle is identified. Vehicle/customer entry lives in the Customer panel +
  // the lookup modal (opened from the chat empty-state), not a full-page gate.
  const currentStageIdx = WORKFLOW_STAGES.indexOf(currentStage);
  // Compute enabled stages from shop config + feature flags
  const enabledStages = useMemo(() => {
    const cfg = (shopData as any)?.report_config?.enabled_stages as string[] | undefined;
    const base = new Set(cfg || [...WORKFLOW_STAGES]);
    if (ff?.inspect === false) base.delete('inspection');
    if (ff?.quote === false) base.delete('quote');
    base.add('registration');
    base.add('completed');
    return WORKFLOW_STAGES.filter((s) => base.has(s));
  }, [shopData, ff]);
  const nextStage = enabledStages.find((s) => WORKFLOW_STAGES.indexOf(s) > currentStageIdx) || null;
  const prevStage = [...enabledStages].reverse().find((s) => WORKFLOW_STAGES.indexOf(s) < currentStageIdx) || null;

  // ── Card-stack state (replaces the tab system) ──
  // workflow_stage is the source of truth; the active stage's card is expanded.
  const activeStageCard = STAGE_CARD_OF_STAGE[currentStage] ?? 'intake';
  const cardParam = searchParams.get('card') as StageCardId | null;
  const [expandedStageCard, setExpandedStageCard] = useState<StageCardId | null>(
    cardParam && (STAGE_CARD_ORDER as readonly string[]).includes(cardParam)
      ? cardParam
      // Intake (registration) opens in the collapsed "unselected" card grid — no
      // stage card expanded. Other stages still auto-expand their active card.
      : (currentStage === 'registration' ? null : activeStageCard),
  );
  const [openInfoCards, setOpenInfoCards] = useState<Set<InfoCardId>>(() => new Set());
  // Visible inspection item count, reported up by InspectionTab → shown on the Inspect card title.
  const [inspectCounts, setInspectCounts] = useState<{ total: number; good: number; soon: number; replace: number; pending: number }>({ total: 0, good: 0, soon: 0, replace: 0, pending: 0 });
  // Composer mode: AI prompt vs SMS-to-customer (toggle in the composer header).
  const [composerMode, setComposerMode] = useState<'prompt' | 'sms' | 'report'>('prompt');

  const setExpandedStageCardUrl = useCallback((card: StageCardId | null) => {
    setExpandedStageCard(card);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (card) next.set('card', card); else next.delete('card');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  // Single global accordion: opening any card collapses every other card.
  const toggleStageCard = useCallback((card: StageCardId) => {
    const opening = expandedStageCard !== card;
    setExpandedStageCardUrl(opening ? card : null);
    if (opening) setOpenInfoCards(new Set()); // collapse info cards
  }, [expandedStageCard, setExpandedStageCardUrl]);

  const toggleInfoCard = useCallback((card: InfoCardId) => {
    const opening = !openInfoCards.has(card);
    setOpenInfoCards(opening ? new Set([card]) : new Set()); // at most one info card
    if (opening) setExpandedStageCardUrl(null); // collapse the stage card
  }, [openInfoCards, setExpandedStageCardUrl]);

  // Auto-expand the active stage card whenever the stage changes (e.g. after a
  // transition) — and collapse the info cards so only one card is ever open.
  // Intake (registration) is the exception: open in the collapsed card grid
  // (nothing expanded) so it's the "unselected" overview state.
  useEffect(() => {
    setExpandedStageCard(currentStage === 'registration' ? null : activeStageCard);
    setOpenInfoCards(new Set());
  }, [currentStage]); // eslint-disable-line react-hooks/exhaustive-deps

  // A stage card is readonly once its stage is behind the current one, or on Complete/archived.
  const isStageCardReadonly = useCallback((card: StageCardId) => {
    if (conversation?.status === 'archived') return true;
    if (currentStage === 'completed') return true;
    // Intake + Job stay editable through registration/inspection/quote; only the
    // Inspect card locks once the stage has advanced past inspection.
    if (card === 'intake' || card === 'job') return false;
    return WORKFLOW_STAGES.indexOf(STAGE_CARD_STAGE[card]) < currentStageIdx;
  }, [conversation?.status, currentStage, currentStageIdx]);

  // Every enabled stage card is always visible (incl. Inspect during Intake) —
  // the card grid is a set of tabs, not a progressive reveal. Editability is
  // still gated per-stage by isStageCardReadonly.
  const visibleStageCards = STAGE_CARD_ORDER.filter((card) =>
    enabledStages.includes(STAGE_CARD_STAGE[card]),
  );

  // The Job card hosts the quote builder + the approve/sign toolbar. Per user
  // request the toolbar shows ANY time the Job card is available — i.e. whenever
  // the quote stage is enabled — not only after the backend advances to the quote
  // stage, so a quote built during intake/inspection can still be sent/signed.
  // Still gated on the quote stage being enabled, so a shop with quote disabled
  // (ff.quote===false) never gets the Job card or the toolbar.
  const showQuoteApproval = enabledStages.includes('quote');

  const handleTransitionAdvance = useCallback(async (skipSms: boolean) => {
    if (!conversationId || transitioning) return;
    setTransitioning(true);
    try {
      // Save customer info entered in the modal
      const customerPatch: Record<string, string> = {};
      if (transitionFirstName.trim()) customerPatch.first_name = transitionFirstName.trim();
      if (transitionLastName.trim()) customerPatch.last_name = transitionLastName.trim();
      if (transitionPhoneInput.trim()) customerPatch.phone = transitionPhoneInput.trim();
      if (transitionEmail.trim()) customerPatch.email = transitionEmail.trim();
      if (Object.keys(customerPatch).length && conversationId) {
        await vehicleApi.patch(conversationId, customerPatch as any);
      }
      const result = await conversationsApi.transition(conversationId, 'advance', skipSms);
      await mutateConversation();
      setLastItemChangeAt(Date.now());
      setShowTransitionModal(false);
      // Auto-switch tab to match the new stage
      const tabForStage = STAGE_TAB_MAP[result.workflow_stage];
      if (tabForStage) setActiveTab(tabForStage as any);
    } catch (err: any) {
      alert(err?.response?.data?.detail || 'Transition failed');
    } finally {
      setTransitioning(false);
    }
  }, [conversationId, transitioning, transitionPhoneInput, mutateConversation, setActiveTab]);

  const handleTransitionRevert = useCallback(async () => {
    if (!conversationId || transitioning) return;
    setTransitioning(true);
    try {
      const result = await conversationsApi.transition(conversationId, 'revert');
      await mutateConversation();
      setLastItemChangeAt(Date.now());
      const tabForStage = STAGE_TAB_MAP[result.workflow_stage];
      if (tabForStage) setActiveTab(tabForStage as any);
    } catch (err: any) {
      alert(err?.response?.data?.detail || 'Transition failed');
    } finally {
      setTransitioning(false);
    }
  }, [conversationId, transitioning, mutateConversation, setActiveTab]);

  // Reset transient view state on conversation change.
  useEffect(() => {
    if (!tabParam || !VALID_TABS.includes(tabParam)) {
      setActiveTab('repair');
    }
    vehicleAutoStartRef.current = false;
    setOpenInfoCards(new Set());
    setComposerMode('prompt');
  }, [conversationId]);


  const [lastItemChangeAt, setLastItemChangeAt] = useState(0);
  // Fetch inspection at JobPage level so report panel works regardless of active tab
  // Use same SWR key as InspectionTab so cache is shared (no duplicate fetch)
  const jobInspListKey = conversationId ? `vehicle/inspection/list?conversation=${conversationId}` : null;
  const { data: jobInspectionList } = useSWR(
    jobInspListKey,
    async () => {
      const { inspectionApi: api } = await import('../api/client');
      return api.list({ conversation_id: conversationId! });
    },
  );
  const jobInspectionId = (() => {
    const list = jobInspectionList?.inspections;
    if (!list?.length) return undefined;
    const withItems = list.find((i) => i.item_count > 0);
    if (withItems) return withItems.id;
    const inProgress = list.find((i) => i.status === 'in_progress');
    if (inProgress) return inProgress.id;
    return list[0]?.id;
  })();
  const [currentInspectionId, setCurrentInspectionId] = useState<{ convId: number; inspId: number } | undefined>();
  const setCurrentInspectionIdWrapped = useCallback((id: number | undefined) => {
    if (id != null && conversationId) setCurrentInspectionId({ convId: conversationId, inspId: id });
  }, [conversationId]);
  // Only use currentInspectionId if it belongs to the current conversation
  const validCurrentInspId = currentInspectionId && currentInspectionId.convId === conversationId ? currentInspectionId.inspId : undefined;
  // Prefer InspectionTab's reported ID (most up-to-date), fall back to job-level fetch
  const effectiveInspectionId = validCurrentInspId ?? jobInspectionId;
  // Templates for empty state template picker
  const { data: jobTemplates } = useSWR(
    'vehicle/inspection/templates',
    async () => {
      const { inspectionApi: api } = await import('../api/client');
      return api.getTemplates();
    },
  );
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);
  const [selectedAiInspect, setSelectedAiInspect] = useState(true);
  const [startingInspection, setStartingInspection] = useState(false);
  // Single in-flight lock shared by every auto-start path. The
  // effectiveInspectionId / jobInspectionList guards only update AFTER start()
  // resolves and SWR refetches, so without this multiple callers (a re-render, a
  // send + the vehicle-identified effect) all pass their guard in the same tick
  // and each fire /start. The lock collapses them to one.
  const startInFlightRef = useRef(false);
  // Start inspection with selected template (called before first message or vehicle lookup)
  const ensureInspectionStarted = useCallback(async () => {
    if (!conversationId || effectiveInspectionId) return;
    // Don't fire until the list fetch has settled — undefined means "still
    // loading", not "no inspection". Prevents a refresh-time spurious start.
    if (jobInspectionList === undefined) return;
    if (!selectedTemplateId && !selectedAiInspect) return;
    if (startInFlightRef.current) return;
    startInFlightRef.current = true;
    try {
      const { inspectionApi: api } = await import('../api/client');
      if (selectedAiInspect) {
        await api.start({ conversation_id: conversationId, mode: 'ai' });
      } else {
        await api.start({ conversation_id: conversationId, template_id: selectedTemplateId! });
      }
      const { mutate: swrMutate } = await import('swr');
      await swrMutate(`vehicle/inspection/list?conversation=${conversationId}`);
    } catch {
      // ignore — inspection may already exist
    } finally {
      startInFlightRef.current = false;
    }
  }, [conversationId, selectedTemplateId, selectedAiInspect, effectiveInspectionId, jobInspectionList]);
  // Auto-start AI inspection when vehicle is already identified but no inspection exists
  const vehicleAutoStartRef = useRef(false);
  useEffect(() => {
    if (!conversationId) return;
    if (!conversation?.vehicle || vehicleLabel(conversation) === 'Unknown vehicle') return;
    // Wait for the list to load before consuming the once-ref — on refresh it's
    // briefly undefined and effectiveInspectionId would read as none → spurious
    // start. The tab-switch below still runs once the list is known.
    if (jobInspectionList === undefined) return;
    if (vehicleAutoStartRef.current) return;
    vehicleAutoStartRef.current = true;
    // Honor an explicit ?tab=... in the URL (safe-shield → ?tab=quote, etc.).
    // On a fresh conv load with no explicit tab, open on the INTAKE stage (the
    // 'repair' tab — the Intake/Job/Inspect stage-card view, Intake at top)
    // rather than auto-jumping into Inspect. The AI inspection still auto-starts
    // below; we just don't yank the user onto it.
    const urlTab = new URLSearchParams(window.location.search).get('tab');
    if (!urlTab) setActiveTab('repair');
    if (effectiveInspectionId) return;
    if (startInFlightRef.current) return;
    startInFlightRef.current = true;
    (async () => {
      try {
        const { inspectionApi: api } = await import('../api/client');
        await api.start({ conversation_id: conversationId, mode: 'ai' });
        const { mutate: swrMutate } = await import('swr');
        await swrMutate(`vehicle/inspection/list?conversation=${conversationId}`);
      } catch {} finally {
        startInFlightRef.current = false;
      }
    })();
  }, [conversationId, effectiveInspectionId, conversation?.vehicle, jobInspectionList]);
  const [showVehicleInfo, setShowVehicleInfo] = useState(false);
  const [showVehicleLookup, setShowVehicleLookup] = useState(false);
  const [inputText, setInputText] = useState('');
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [pendingPreviews, setPendingPreviews] = useState<string[]>([]);
  const [sending, setSending] = useState(false);
  const [smsEnabled, setSmsEnabled] = useState(false);
  const [smsMessages, setSmsMessages] = useState<import('../types').SmsMessageResponse[]>([]);
  const [smsRecipient, setSmsRecipient] = useState(conversation?.vehicle?.phone || '');
  const [chatFilter, setChatFilter] = useState<'all' | 'customer' | 'mine' | 'others' | 'haist'>('all');
  const chatVoice = useInspectionVoice();
  const [chatVoiceSending, setChatVoiceSending] = useState(false);

  const handleChatVoiceSend = useCallback(async () => {
    if (!conversationId) return;
    setChatVoiceSending(true);
    try {
      const { blob, transcript } = await chatVoice.stopVoice();
      if (!blob) { setChatVoiceSending(false); return; }
      const text = (transcript || '').trim();
      if (!text) { setChatVoiceSending(false); return; }

      // Quote tab: send voice transcript to AI endpoint
      if (activeTab === 'quote' && conversationId) {
        try {
          const result = await quoteApi.aiChat(conversationId, text);
          if (result.actions?.length) {
            setQuoteItems((prev) => {
              let items = [...prev];
              // Signed-item protection: chat may not change price/title of items
              // covered by a legal doc. Skip update/remove on those indices; adds
              // (new rows) are always allowed. Indices from `prev` (what the AI
              // indexed against), computed before the isEmpty reset.
              const lockedIdx = new Set<number>();
              prev.forEach((it, idx) => { if (isItemAuthLocked(it)) lockedIdx.add(idx); });
              const isEmpty = items.length === 1 && !items[0].label && !items[0].parts && !items[0].labor;
              if (isEmpty) items = [];
              for (const a of result.actions) {
                if (a.action === 'add') {
                  items.push({ label: a.label || '', parts: a.parts || '', qty: a.qty || '1', labor: a.labor || '', sublet: a.sublet || '', fee: a.fee || '', discount: a.discount || '', laborHours: a.laborHours || '', description: a.description || '' });
                } else if (a.action === 'update' && a.index != null && a.index < items.length) {
                  if (lockedIdx.has(a.index)) continue;
                  const cur = items[a.index];
                  items[a.index] = { ...cur, ...(a.label != null ? { label: a.label } : {}), ...(a.parts != null ? { parts: a.parts } : {}), ...(a.qty != null ? { qty: a.qty } : {}), ...(a.labor != null ? { labor: a.labor } : {}), ...(a.sublet != null ? { sublet: a.sublet } : {}), ...(a.fee != null ? { fee: a.fee } : {}), ...(a.discount != null ? { discount: a.discount } : {}), ...(a.laborHours != null ? { laborHours: a.laborHours } : {}), ...(a.description != null ? { description: a.description } : {}) };
                } else if (a.action === 'remove' && a.index != null && a.index < items.length) {
                  if (lockedIdx.has(a.index)) continue;
                  items = items.filter((_, idx) => idx !== a.index);
                }
              }
              return items.length ? items : [{ label: '', parts: '', qty: '1', labor: '', sublet: '', fee: '', discount: '', laborHours: '', description: '' }];
            });
          }
          if (result.description_update) setQuoteDescription(result.description_update);
          if (result.note_update) setTechnicianNote(result.note_update);
        } catch {
          // Fallback: add as plain item
          setQuoteItems((prev) => {
            const isEmpty = prev.length === 1 && !prev[0].label && !prev[0].parts && !prev[0].labor;
            return isEmpty ? [{ label: text, parts: '', qty: '1', labor: '', sublet: '', fee: '', discount: '', laborHours: '', description: '' }] : [...prev, { label: text, parts: '', qty: '1', labor: '', sublet: '', fee: '', discount: '', laborHours: '', description: '' }];
          });
        }
        return;
      }

      await sendMessage(text);
    } catch (err) {
      console.error('[CHAT_VOICE_SEND]', err);
    } finally {
      setChatVoiceSending(false);
    }
  }, [conversationId, chatVoice, sendMessage, activeTab]);

  const [quoteItems, _setQuoteItemsRaw] = useState<QuoteItem[]>(() => [newEmptyQuote()]);
  // Per-session dedup for auto-suggest. The actual fire happens in the
  // quote upsert's .then() (line ~5275) right after a successful save —
  // same pattern as inspection (fire RIGHT after the data is persisted).
  // Reset on conversation switch so a re-opened conv re-fires correctly.
  const firedSuggestRef = useRef<Set<string>>(new Set());
  useEffect(() => { firedSuggestRef.current = new Set(); }, [conversationId]);
  // expandedItems removed — quote cards are always expanded
  // Fee/discount are now per-item fields — no quote-level arrays
  const [priceModalIdx, setPriceModalIdx] = useState<number | null>(null);
  const [jobLightboxUrl, setJobLightboxUrl] = useState<string | null>(null);
  // Quote-tab photo annotator: edit a quote item's photo, save an annotated copy
  // appended to that item's photos (debounced quote upsert persists it).
  const [annotateQuote, setAnnotateQuote] = useState<{ url: string; index: number; photoIndex: number } | null>(null);
  const [showHiddenJobs, setShowHiddenJobs] = useState(false);
  const [paperSignatureOpen, setPaperSignatureOpen] = useState(false);
  const [legalDocsOpen, setLegalDocsOpen] = useState(false);
  const [digitalSignatureOpen, setDigitalSignatureOpen] = useState(false);
  const [sendApprovalOpen, setSendApprovalOpen] = useState(false);
  const [bulkSending, setBulkSending] = useState(false);
  const [quoteFilter, setQuoteFilter] = useState<'all' | 'approved' | 'declined' | 'pending'>('all');
  const quotedLabels = useMemo(() => new Set(quoteItems.map((q) => (q.label ?? '').trim().toLowerCase())), [quoteItems]);
  const [quoteDescription, _setQuoteDescriptionRaw] = useState('');
  const [technicianNote, _setTechnicianNoteRaw] = useState('');

  // Quote read-only state — user role + per-device localStorage override.
  // Setters below are wrapped via a ref so their identity stays stable
  // (downstream useCallback/useEffect deps don't churn) while still
  // honoring the live lock state. `quoteLocked` (incl. isArchived) is
  // assigned later — the ref is updated when it becomes available.
  // Override-toggle UI lives in Settings > My Account > Quote Editing. Here
  // we only read the effective state to gate edits + show the banner.
  const { readOnly: isQuoteReadOnly } = useQuoteReadOnly();
  const quoteLockedRef = useRef(isQuoteReadOnly);
  const setQuoteItems = useCallback(
    (arg: QuoteItem[] | ((prev: QuoteItem[]) => QuoteItem[])) => {
      if (quoteLockedRef.current) return;
      _setQuoteItemsRaw(arg);
    },
    [],
  );
  const setQuoteDescription = useCallback(
    (arg: string | ((prev: string) => string)) => {
      if (quoteLockedRef.current) return;
      _setQuoteDescriptionRaw(arg);
    },
    [],
  );
  const setTechnicianNote = useCallback(
    (arg: string | ((prev: string) => string)) => {
      if (quoteLockedRef.current) return;
      _setTechnicianNoteRaw(arg);
    },
    [],
  );
  const [customerApprovals, setCustomerApprovals] = useState<{ approved_items?: { source_id?: string | number; label: string; amount: number }[]; declined_items?: { source_id?: string | number; label: string; amount: number }[]; submitted_at?: string; signature_method?: string | null } | null>(null);
  // INVOICE-track legal doc (post-service signature on the final bill). Parallel
  // to customerApprovals (the ESTIMATE track). Same shape.
  const [invoiceApprovals, setInvoiceApprovals] = useState<{ approved_items?: { source_id?: string | number; label: string; amount: number }[]; declined_items?: { source_id?: string | number; label: string; amount: number }[]; submitted_at?: string; signature_method?: string | null } | null>(null);
  // Which legal-doc track the next signature/photo/send capture writes to.
  // Defaults by workflow stage (completed → invoice, else estimate) on load;
  // the writer can override via the toolbar toggle before opening a capture.
  const [captureDocType, setCaptureDocType] = useState<'estimate' | 'invoice' | 'both'>('estimate');
  // STRICT "real signature" check — only the two methods HAIST's own signing
  // flows produce count. The Tek mirror populates customer_approvals with
  // approved_items + a non-empty signature_method that ISN'T one of these
  // (e.g. 'tek', 'in_person', etc.), and we must NOT treat that as a legal
  // doc anywhere. Previous truthy `!!signature_method` check was too loose.
  const isRealCustomerSignature = (ca: any): boolean => {
    const m = ca?.signature_method;
    return m === 'live' || m === 'paper_photo';
  };

  // Locally-computed "safe": every in_batch quote item is covered by a
  // real legal doc. Updates live as the user edits / signs / unlinks —
  // no need to wait for the next conv-list refresh. Mirrors the backend
  // batch_quote_legal_safe logic exactly.
  const quoteLegalSafe: boolean | null = useMemo(() => {
    if (!quoteItems || quoteItems.length === 0) return null;
    const ca: any = customerApprovals;
    const caSigned = isRealCustomerSignature(ca);
    const caSids = new Set<string>();
    for (const bucket of ['approved_items', 'declined_items'] as const) {
      for (const entry of (ca?.[bucket] || [])) {
        const sid = entry?.source_id;
        if (sid != null) caSids.add(String(sid));
      }
    }
    let inBatchTotal = 0;
    let covered = 0;
    for (const it of quoteItems) {
      const item: any = it;
      if (item.is_hidden) continue;
      const status = (item.item_status || '').toLowerCase();
      // Selected = in the signing batch OR already decided. Approval flips
      // in_batch→false, so decided items must still count or a fully-signed
      // quote collapses to null instead of green.
      if (item.in_batch !== true && status !== 'approved' && status !== 'declined') continue;
      inBatchTotal++;
      const sid = item.source_id;
      const sidStr = sid != null ? String(sid) : null;
      const authId = item.authorization_id;
      const shadowedByUnsignedShell = !!(sidStr && !caSigned && caSids.has(sidStr));
      const hasItemAuth = !!(authId && (status === 'approved' || status === 'declined') && !shadowedByUnsignedShell);
      const inSignedCa = !!(caSigned && sidStr && caSids.has(sidStr));
      if (hasItemAuth || inSignedCa) covered++;
    }
    if (inBatchTotal === 0) return null;
    return covered === inBatchTotal;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quoteItems, customerApprovals]);

  // INVOICE-track legal-safe — same logic as quoteLegalSafe but reads
  // invoiceApprovals + item.invoice_authorization_id. Mirrors the backend
  // _compute_quote_safe(item_auth_field='invoice_authorization_id').
  const invoiceLegalSafe: boolean | null = useMemo(() => {
    if (!quoteItems || quoteItems.length === 0) return null;
    const ca: any = invoiceApprovals;
    const caSigned = isRealCustomerSignature(ca);
    const caSids = new Set<string>();
    for (const bucket of ['approved_items', 'declined_items'] as const) {
      for (const entry of (ca?.[bucket] || [])) {
        const sid = entry?.source_id;
        if (sid != null) caSids.add(String(sid));
      }
    }
    let inBatchTotal = 0;
    let covered = 0;
    for (const it of quoteItems) {
      const item: any = it;
      if (item.is_hidden) continue;
      const status = (item.item_status || '').toLowerCase();
      // INVOICE denominator = work DONE (approved) only — declined work is never
      // invoiced, so it must not keep the invoice half amber. Mirrors the backend
      // _compute_quote_safe invoice decided_statuses.
      if (item.in_batch !== true && status !== 'approved') continue;
      inBatchTotal++;
      const sid = item.source_id;
      const sidStr = sid != null ? String(sid) : null;
      const authId = item.invoice_authorization_id;
      const shadowedByUnsignedShell = !!(sidStr && !caSigned && caSids.has(sidStr));
      const hasItemAuth = !!(authId && (status === 'approved' || status === 'declined') && !shadowedByUnsignedShell);
      const inSignedCa = !!(caSigned && sidStr && caSids.has(sidStr));
      if (hasItemAuth || inSignedCa) covered++;
    }
    if (inBatchTotal === 0) return null;
    return covered === inBatchTotal;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quoteItems, invoiceApprovals]);

  // Conversation-level "any signed legal doc" — true iff ANY item is covered by
  // a REAL signed estimate doc OR invoice doc. Mirrors the per-item hasLegalDoc /
  // hasInvoiceLegalDoc rules in renderQuoteCard EXACTLY, including the Tek-mirror
  // -shell filter (an UNSIGNED Tek customer_approvals shell must NOT count). When
  // true the whole quote goes read-only for FIELD edits (tech-note, the per-item
  // Select box, Unlink, the capture toolbar, and chat stay editable).
  const hasAnyLegalDoc: boolean = useMemo(() => {
    if (!quoteItems || quoteItems.length === 0) return false;
    const caHasSignature = isRealCustomerSignature(customerApprovals);
    const invCaHasSignature = isRealCustomerSignature(invoiceApprovals);
    for (const it of quoteItems) {
      const item: any = it;
      const itemStatus = (item.item_status || '').toLowerCase();
      const itemSourceId = item.source_id != null ? String(item.source_id) : '';
      // estimate branch
      const rawItemAuthDoc = !!item.authorization_id && (itemStatus === 'approved' || itemStatus === 'declined');
      const caContainsThisItem = !!(
        itemSourceId && customerApprovals && (
          (customerApprovals.approved_items || []).some((a: any) => String(a.source_id || '') === itemSourceId) ||
          (customerApprovals.declined_items || []).some((a: any) => String(a.source_id || '') === itemSourceId)
        )
      );
      const itemAuthDocIsTekMirrorShell = caContainsThisItem && !caHasSignature;
      const hasItemAuthDoc = rawItemAuthDoc && !itemAuthDocIsTekMirrorShell;
      if (hasItemAuthDoc || (caHasSignature && caContainsThisItem)) return true;
      // invoice branch
      const invCaContainsThisItem = !!(
        itemSourceId && invoiceApprovals && (
          (invoiceApprovals.approved_items || []).some((a: any) => String(a.source_id || '') === itemSourceId) ||
          (invoiceApprovals.declined_items || []).some((a: any) => String(a.source_id || '') === itemSourceId)
        )
      );
      if (item.invoice_authorization_id || (invCaHasSignature && invCaContainsThisItem)) return true;
    }
    return false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quoteItems, customerApprovals, invoiceApprovals]);

  // Default the capture doc-type by workflow stage, once per conversation —
  // a completed job defaults to INVOICE, everything else to ESTIMATE. The
  // writer can still override via the toolbar toggle.
  const docTypeDefaultedRef = useRef<string | number | null>(null);
  useEffect(() => {
    if (!conversationId) return;
    const stage = conversation?.workflow_stage;
    if (stage == null) return; // wait until the conv is loaded
    if (docTypeDefaultedRef.current === conversationId) return;
    docTypeDefaultedRef.current = conversationId;
    setCaptureDocType(stage === 'completed' ? 'invoice' : 'estimate');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, conversation?.workflow_stage]);

  const [tekAuthorizations, setTekAuthorizations] = useState<import('../api/client').TekAuthorization[]>([]);
  // Per-item receipt-photo file inputs. Keyed by item index — used by the
  // "📷 Receipt" button to upload signed receipt photos per line item.
  const receiptInputRefs = useRef<Map<number, HTMLInputElement>>(new Map());

  // ID-only sets (per feedback_id_over_name_match). Items without a
  // source_id won't be discoverable — but those are HAIST-native rows
  // that shouldn't appear in customer_approvals anyway.
  const approvedSet = useMemo(() => new Set(
    (customerApprovals?.approved_items || [])
      .filter((a) => a?.source_id != null)
      .map((a) => String(a.source_id))
  ), [customerApprovals]);
  const declinedSet = useMemo(() => new Set(
    (customerApprovals?.declined_items || [])
      .filter((a) => a?.source_id != null)
      .map((a) => String(a.source_id))
  ), [customerApprovals]);
  const handleToggleApproval = useCallback((sourceId: string, label: string, approve: boolean) => {
    const sid = String(sourceId);
    const isApproved = approvedSet.has(sid);
    const isDeclined = declinedSet.has(sid);
    setCustomerApprovals((prev: any) => {
      const ap = (prev?.approved_items || []).filter((a: any) => String(a.source_id ?? '') !== sid);
      const dc = (prev?.declined_items || []).filter((a: any) => String(a.source_id ?? '') !== sid);
      if (approve && !isApproved) ap.push({ source_id: sid, label, amount: 0 });
      else if (!approve && !isDeclined) dc.push({ source_id: sid, label, amount: 0 });
      const next = { ...prev, approved_items: ap, declined_items: dc, submitted_at: prev?.submitted_at || new Date().toISOString() };
      if (conversationId) quoteApi.upsert(conversationId, { items: quoteItems, description: quoteDescription, note: technicianNote, customer_approvals: next } as any).catch(() => {});
      return next;
    });
  }, [approvedSet, declinedSet, conversationId, quoteItems, quoteDescription, technicianNote]);

  const quoteSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [focusedDescIdx, setFocusedDescIdx] = useState<number | 'top' | 'note' | null>(null);
  // Explicit edit mode for a quote line-item description (no autosave). While a
  // row is being edited, the draft lives here and is NOT written into quoteItems
  // until Save — so the quote autosave effect doesn't fire on every keystroke.
  const [editingDescIdx, setEditingDescIdx] = useState<number | null>(null);
  const [descDraft, setDescDraft] = useState('');
  // The add-job pop-up (RO recommendation / inspection "Add to Job") edits a
  // STANDALONE draft — held as a 1-element array so the shared card editor's
  // index-based closures work — and is only appended to the quote on Save. So
  // the rec is NOT "added" (visible / persisted / flipped to ✓ Added) until the
  // writer confirms in the pop-up.
  const [jobDraft, setJobDraft] = useState<QuoteItem[] | null>(null);
  const cellRefs = useRef<Map<string, HTMLInputElement>>(new Map());
  const buildDescRef = useRef<(label: string) => { description: string; parts: string; labor: string }>(() => ({ description: '', parts: '', labor: '' }));
  const [importingQuote, setImportingQuote] = useState(false);

  const handleImportFromShop = async () => {
    const cid = conversationId;
    if (!cid || importingQuote) return;
    setImportingQuote(true);
    try {
      const result = await quoteApi.importFromShop(cid);
      if (result.items?.length) {
        setQuoteItems((prev) => {
          const existing = prev.filter((q) => q.label || q.parts || q.labor);
          return existing.length ? [...existing, ...result.items] : result.items;
        });
        if (result.description && !quoteDescription) {
          setQuoteDescription(result.description);
        }
      } else {
        console.warn('[IMPORT] No items returned');
      }
    } catch (err: any) {
      console.error('[IMPORT]', err);
      const msg = err?.response?.data?.detail || 'No shop history found for this vehicle';
      alert(msg);
    } finally {
      setImportingQuote(false);
    }
  };

  const handleImportFromTekmetric = async () => {
    if (importingQuote) return;
    setImportingQuote(true);
    try {
      // Send message to Chrome extension content script
      const result = await new Promise<{ ro?: any; jobs?: any[]; error?: string }>((resolve) => {
        const handler = (event: MessageEvent) => {
          if (event.data?.type === 'HAIST_IMPORT_QUOTE_RESULT') {
            window.removeEventListener('message', handler);
            resolve(event.data);
          }
        };
        window.addEventListener('message', handler);
        window.postMessage({ type: 'HAIST_IMPORT_QUOTE' }, '*');
        // Timeout after 10s
        setTimeout(() => { window.removeEventListener('message', handler); resolve({ error: 'Timeout — is the HAIST extension installed?' }); }, 10000);
      });

      if (result.error) {
        alert(result.error);
        return;
      }

      if (result.jobs?.length) {
        const items = result.jobs.map((job: any) => {
          const totalLabor = (job.laborItems || []).reduce((s: number, l: any) => s + (l.hours || 0) * (l.rate || 0), 0);
          const totalParts = (job.partItems || []).reduce((s: number, p: any) => s + (p.quantity || 0) * (p.price || 0), 0);
          const totalHours = (job.laborItems || []).reduce((s: number, l: any) => s + (l.hours || 0), 0);
          return {
            label: job.name || '',
            parts: String(Math.round(totalParts)),
            qty: '1',
            labor: String(Math.round(totalLabor)),
            sublet: '',
            fee: '',
            discount: '',
            laborHours: totalHours ? String(totalHours.toFixed(1)) : '',
            description: job.note || '',
          };
        });
        setQuoteItems((prev) => {
          const existing = prev.filter((q) => q.label || q.parts || q.labor);
          return existing.length ? [...existing, ...items] : items;
        });
        // Don't seed the concern from result.ro.customerConcern — on the raw Tek
        // RO that's `notes` (the RO-level service note, often a stale maintenance
        // line), not the customer's complaint. The real customer concerns sync
        // separately (RO-scoped) and surface via the quote-description fallback.
      }
    } catch {
      alert('Failed to import from Tekmetric');
    } finally {
      setImportingQuote(false);
    }
  };

  const [reportSkill, setReportSkill] = useState<'estimate' | 'final' | null>(null);
  const [reportPreviewOpen, setReportPreviewOpen] = useState(false);
  const [previewWorkItems, setPreviewWorkItems] = useState<string[]>(['']);
  const [previewCosts, setPreviewCosts] = useState<{ label: string; amount: string; type: string }[]>([
    { label: '', amount: '', type: 'parts' },
  ]);
  const [previewNotes, setPreviewNotes] = useState('');

  // Inspection data for report pre-population (shares SWR cache with InspectionTab)
  const vehicleId = conversation?.vehicle?.id;
  const inspListKey = vehicleId
    ? `vehicle/inspection/list?vehicle=${vehicleId}`
    : conversationId
      ? `vehicle/inspection/list?conversation=${conversationId}`
      : null;
  const { data: inspListData } = useSWR(
    inspListKey,
    async () => {
      const { inspectionApi: api } = await import('../api/client');
      return vehicleId
        ? api.list({ vehicle_id: vehicleId })
        : api.list({ conversation_id: conversationId! });
    },
  );
  const reportInspId = inspListData?.inspections?.[0]?.id;
  const { categories: reportCategories } = useInspection(reportInspId);
  const { inspection: jobInspection, categories: jobInspectionCategories } = useInspection(effectiveInspectionId);

  // Show setup form when no vehicle is linked

  // Build description + prices from inspection data or keyword template — always up-to-date via ref
  const buildDescription = useCallback((label: string): { description: string; parts: string; labor: string } => {
    const normalize = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');
    const key = normalize(label);
    if (!key) return { description: '', parts: '', labor: '' };
    const insp = reportCategories
      .flatMap((c) => c.items)
      .find((item) => {
        const k = normalize(item.display_name);
        return k === key || k.includes(key) || key.includes(k);
      });
    if (insp) {
      return {
        description: insp.technician_notes || '',
        parts: insp.estimated_parts_cost != null ? String(Math.round(insp.estimated_parts_cost)) : '',
        labor: insp.estimated_labor_cost != null ? String(Math.round(insp.estimated_labor_cost)) : '',
      };
    }
    // Keyword template fallback (description only, no price estimate)
    const l = label.toLowerCase();
    const d = (s: string) => ({ description: s, parts: '', labor: '' });
    if (/brake pad/.test(l)) return d('Brake pads worn — replacement recommended.');
    if (/brake rotor|brake disc/.test(l)) return d('Rotors showing wear grooves — replacement required.');
    if (/caliper/.test(l)) return d('Brake caliper seized — replacement required.');
    if (/oil change|oil service/.test(l)) return d('Engine oil change with filter replacement.');
    if (/air filter/.test(l)) return d('Air filter dirty — replacement recommended.');
    if (/cabin filter|cabin air/.test(l)) return d('Cabin air filter dirty — replacement recommended.');
    if (/spark plug/.test(l)) return d('Spark plugs worn — replacement recommended.');
    if (/battery/.test(l)) return d('Battery tested weak — replacement recommended.');
    if (/tire rotation/.test(l)) return d('Tire rotation and torque to spec.');
    if (/alignment/.test(l)) return d('Wheel alignment out of spec — adjustment required.');
    if (/coolant|antifreeze/.test(l)) return d('Coolant flush and fill with fresh coolant.');
    if (/transmission fluid|trans flush/.test(l)) return d('Transmission fluid change.');
    if (/power steering/.test(l)) return d('Power steering fluid flush and fill.');
    if (/fuel filter/.test(l)) return d('Fuel filter clogged — replacement recommended.');
    if (/wiper blade/.test(l)) return d('Wiper blades streaking — replacement recommended.');
    if (/serpentine belt|drive belt/.test(l)) return d('Belt cracking/glazing — replacement recommended.');
    if (/timing belt|timing chain/.test(l)) return d('Timing belt/chain service due.');
    if (/shock|strut/.test(l)) return d('Shock/strut worn — replacement recommended.');
    if (/cv axle|cv joint/.test(l)) return d('CV axle/joint worn — replacement required.');
    if (/wheel bearing/.test(l)) return d('Wheel bearing worn — noise under load, replacement required.');
    if (/water pump/.test(l)) return d('Water pump seeping — replacement required.');
    if (/thermostat/.test(l)) return d('Thermostat faulty — replacement required.');
    if (/alternator/.test(l)) return d('Alternator output low — replacement recommended.');
    if (/starter/.test(l)) return d('Starter motor sluggish — replacement recommended.');
    if (/oxygen sensor|o2 sensor/.test(l)) return d('Oxygen sensor faulty — replacement required.');
    if (/catalytic converter/.test(l)) return d('Catalytic converter degraded — replacement required.');
    if (/windshield|glass/.test(l)) return d('Glass damaged — replacement required.');
    return { description: '', parts: '', labor: '' };
  }, [reportCategories]);

  // Keep ref in sync so auto-merge effect can call it without adding to deps
  useEffect(() => { buildDescRef.current = buildDescription; }, [buildDescription]);

  const [dragging, setDragging] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [vpPlate, setVpPlate] = useState('');
  const [vpState, setVpState] = useState<string | null>(null);
  const [vpVin, setVpVin] = useState('');
  const [vpError, setVpError] = useState('');
  const [vpFirstName, setVpFirstName] = useState('');
  const [vpLastName, setVpLastName] = useState('');
  const [vpPhone, setVpPhone] = useState('');
  const [vpEmail, setVpEmail] = useState('');
  const [vpCustSaving, setVpCustSaving] = useState(false);

  // Pin: local only, stored in localStorage
  const PINNED_KEY = 'haist_pinned_conversations';
  const getPinned = (): number[] => {
    try { return JSON.parse(localStorage.getItem(PINNED_KEY) ?? '[]'); } catch { return []; }
  };
  const [isPinned, setIsPinned] = useState(() => conversationId ? getPinned().includes(conversationId) : false);

  const togglePin = () => {
    if (!conversationId) return;
    const pinned = getPinned();
    const next = isPinned ? pinned.filter((id) => id !== conversationId) : [...pinned, conversationId];
    localStorage.setItem(PINNED_KEY, JSON.stringify(next));
    setIsPinned(!isPinned);
    // Notify same-tab listeners (ShopLayout sidebar)
    window.dispatchEvent(new StorageEvent('storage', { key: PINNED_KEY }));
  };

  const handleDelete = async () => {
    if (!conversationId) return;
    setDeleting(true);
    try {
      await deleteConversation(conversationId);
      navigate('/shop/dashboard', { replace: true });
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const chatViewportRef = useRef<HTMLDivElement>(null);
  const chatSpacerRef = useRef<HTMLDivElement>(null);
  const lastUserMsgRef = useRef<HTMLDivElement>(null);
  const justSentRef = useRef(false);
  const atBottomRef = useRef(true);
  const followMessagesRef = useRef(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const dragCounter = useRef(0);
  const deleteBtnRef = useRef<HTMLButtonElement>(null);
  const archiveBtnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (showDeleteConfirm) setTimeout(() => deleteBtnRef.current?.focus(), 50);
  }, [showDeleteConfirm]);

  useEffect(() => {
    if (showArchiveConfirm) setTimeout(() => archiveBtnRef.current?.focus(), 50);
  }, [showArchiveConfirm]);

  // Keep a ref to the current conversationId so the persist effect never
  // accidentally saves quote data under a NEW conversation ID during the
  // same render cycle when switching conversations.
  const conversationIdRef = useRef(conversationId);
  useEffect(() => { conversationIdRef.current = conversationId; }, [conversationId]);

  // Refresh conversation (vehicle info) whenever a new completed assistant message arrives.
  // useConversation has no refreshInterval — without this the vehicle stays stale after lookup.
  const lastReplyKeyRef = useRef<string | null>(null);
  useEffect(() => {
    const key = latestReplyKey(messages);
    if (key && key !== lastReplyKeyRef.current) {
      lastReplyKeyRef.current = key;
      mutateConversation();
    }
  }, [messages, mutateConversation]);

  // Persist quote to DB (debounced 1.5 s) whenever it changes
  useEffect(() => {
    const cid = conversationIdRef.current;
    if (!cid) return;
    // Read-only mode: skip autosave entirely. The guarded setters above already
    // block user mutations; this is the belt-and-suspenders against multi-tab
    // races where a read-only device might otherwise echo polled state back.
    if (quoteLockedRef.current) return;
    const hasData = quoteItems.some((q) => q.label || q.parts || q.labor) || quoteDescription || technicianNote;
    if (!hasData) return;
    if (quoteSaveTimer.current) clearTimeout(quoteSaveTimer.current);
    quoteSaveTimer.current = setTimeout(() => {
      quoteApi.upsert(cid, {
        items: quoteItems,
        description: quoteDescription || undefined,
        note: technicianNote || undefined,
      })
        .then((saved) => {
          // Backfill server-assigned source_ids so newly-created rows can be
          // SELECT'd into the bulk-approval batch on the next click. Match
          // by case-insensitive trimmed label so the user can keep editing
          // other fields without us clobbering them.
          //
          // CRITICAL: only call setQuoteItems + setLastItemChangeAt when we
          // actually backfilled something. Otherwise every successful upsert
          // produces a new quoteItems array reference → our deps re-fire →
          // upsert re-fires forever, and the report iframe reloads on every
          // tick because lastChangedAt also keeps advancing.
          const sourceIdsByLabel: Record<string, string> = {};
          for (const it of (saved as any)?.items ?? []) {
            const lbl = (((it as any).label ?? '') as string).trim().toLowerCase();
            if (lbl && (it as any).source_id) {
              sourceIdsByLabel[lbl] = String((it as any).source_id);
            }
          }
          let backfilled = false;
          setQuoteItems((prev) => {
            const next = prev.map((q) => {
              if ((q as any).source_id) return q;
              const lbl = ((q.label ?? '') as string).trim().toLowerCase();
              const sid = lbl ? sourceIdsByLabel[lbl] : undefined;
              if (sid) {
                backfilled = true;
                return { ...q, source_id: sid } as any;
              }
              return q;
            });
            return backfilled ? next : prev;
          });
          if (backfilled) setLastItemChangeAt(Date.now());
          // Auto-fire AI suggest for any item that landed in DB without a
          // description_autofill yet. Matches inspection's pattern: trigger
          // runs RIGHT after the data is persisted (upload → in GCS → fire
          // for inspection; add → in DB → fire for quote). Dedup via the
          // session Set so this only fires once per (idx, label) pair.
          const savedItems = ((saved as any)?.items ?? []) as any[];
          for (let i = 0; i < savedItems.length; i++) {
            const it = savedItems[i];
            const lbl = String(it?.label || '').trim();
            if (!lbl) continue;
            if (it?.description_autofill) continue;
            if (String(it?.description || '').trim()) continue;
            const key = `${i}|${lbl.toLowerCase()}`;
            if (firedSuggestRef.current.has(key)) continue;
            firedSuggestRef.current.add(key);
            quoteApi.suggestDescription(cid, i).catch(() => {});
          }
        })
        .catch(() => {});
    }, 1500);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quoteItems, quoteDescription, technicianNote]);

  // Fetch SMS messages for this conversation (poll every 5s)
  useEffect(() => {
    const customerPhone = conversation?.vehicle?.phone;
    if (customerPhone && !smsRecipient) setSmsRecipient(customerPhone);
    if (!canSms) { setSmsMessages([]); return; }
    // Thread tracks the selected To: contact (smsRecipient), defaulting to the
    // primary phone — switching to a sub-phone reloads that contact's texts.
    const threadPhone = smsRecipient || customerPhone;
    if (!threadPhone) { setSmsMessages([]); return; }
    const fetch = () => smsApi.list({
      phone: threadPhone,
      limit: 100
    }).then(setSmsMessages).catch(() => {});
    fetch();
    const interval = setInterval(fetch, 5000);
    return () => clearInterval(interval);
  }, [conversationId, canSms, conversation?.vehicle?.phone, smsRecipient]);

  // Reset UI state on conversation switch, restore saved quote if available
  useEffect(() => {
    setShowVehicleInfo(false);
    setShowVehicleLookup(false);
    setVpPlate('');
    setVpState(null);
    setVpVin('');
    setVpError('');
    setInputText('');
    setReportSkill(null);
    setPendingFiles([]);
    setPendingPreviews([]);
    setSmsEnabled(false);
    setChatFilter('all');
    // Reset scroll state for the new conversation
    if (chatViewportRef.current) chatViewportRef.current.scrollTop = 0;
    if (chatSpacerRef.current) chatSpacerRef.current.style.height = '0px';
    followMessagesRef.current = false;
    justSentRef.current = false;

    // Restore saved quote from DB for this conversation — bypass the
    // read-only guard so switching conversations resets local state even
    // for users in read-only mode.
    _setQuoteItemsRaw([newEmptyQuote()]);
    _setQuoteDescriptionRaw('');
    _setTechnicianNoteRaw('');
    if (conversationId) {
      quoteApi.get(conversationId).then(({ items, description, note, customer_approvals, invoice_approvals }) => {
        if (items?.length) {
          // Defensive: API can return items with label=null/undefined (e.g.
          // partially-imported rows). Normalize so downstream .trim() calls
          // don't blow up the whole page.
          // Use raw setters — server hydration must bypass the read-only
          // guard so read-only users still see the saved quote.
          _setQuoteItemsRaw(items.map((it: any) => ({ ...it, label: it.label ?? '' })));
        }
        if (description) _setQuoteDescriptionRaw(description);
        if (note) _setTechnicianNoteRaw(note);
        if (customer_approvals) setCustomerApprovals(customer_approvals);
        setInvoiceApprovals(invoice_approvals ?? null);
      }).catch(() => { /* no saved quote — start fresh */ });
      quoteApi.listTekAuthorizations(conversationId)
        .then(setTekAuthorizations)
        .catch(() => setTekAuthorizations([]));
    }
  }, [conversationId]);

  // Poll for analyzing items — refresh quote when any item is in "analyzing" state
  useEffect(() => {
    const hasAnalyzing = quoteItems.some((q) => q.state === 'analyzing');
    if (!hasAnalyzing || !conversationId) return;
    const timer = setInterval(() => {
      quoteApi.get(conversationId).then(({ items, customer_approvals }) => {
        // Server-poll hydration — bypass the read-only guard.
        if (items?.length) _setQuoteItemsRaw(items);
        // Keep customer_approvals fresh too. Without this, an item that gets
        // signed/loaded while ANOTHER item is in 'analyzing' loses its
        // legal-doc state (this poll overwrites quoteItems every 3s but left
        // customerApprovals stale) → hasLegalDoc goes false → the "Unlink doc"
        // button disappears until a full reload. Guarded like the initial load
        // so a no-approvals response can't clobber existing state.
        if (customer_approvals) setCustomerApprovals(customer_approvals);
      }).catch(() => {});
    }, 3000);
    return () => clearInterval(timer);
  }, [conversationId, quoteItems.some((q) => q.state === 'analyzing')]);




  // Track whether viewport is at the bottom; collapse spacer on manual scroll
  const programmaticScrollRef = useRef(false);
  useEffect(() => {
    const viewport = chatViewportRef.current;
    if (!viewport) return;
    const onScroll = () => {
      atBottomRef.current = viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight < 80;
      if (!programmaticScrollRef.current) {
        followMessagesRef.current = false;
        if (chatSpacerRef.current) chatSpacerRef.current.style.height = '0px';
      }
    };
    viewport.addEventListener('scroll', onScroll, { passive: true });
    return () => viewport.removeEventListener('scroll', onScroll);
  }, []);

  // Scroll logic when messages update
  useEffect(() => {
    const viewport = chatViewportRef.current;
    if (!viewport) return;

    if (justSentRef.current) {
      justSentRef.current = false;
      const spacer = chatSpacerRef.current;
      if (spacer) spacer.style.height = `${Math.round(viewport.clientHeight * 0.15)}px`;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const msg = lastUserMsgRef.current;
          if (!viewport || !msg) return;
          const msgRect = msg.getBoundingClientRect();
          const vpRect = viewport.getBoundingClientRect();
          const targetScrollTop = viewport.scrollTop + (msgRect.top - vpRect.top) - vpRect.height * 0.55;
          programmaticScrollRef.current = true;
          viewport.scrollTo({ top: Math.max(0, targetScrollTop), behavior: 'smooth' });
          setTimeout(() => {
            programmaticScrollRef.current = false;
            followMessagesRef.current = true; // start following new content after animation
          }, 700);
        });
      });
      return;
    }

    if (followMessagesRef.current || atBottomRef.current) {
      viewport.scrollTop = viewport.scrollHeight;
    }
  }, [messages]);

  // Scroll to bottom when messages first load for a new conversation
  const scrolledConvIdRef = useRef<number | undefined>();
  useEffect(() => {
    if (!conversationId || msgsLoading || messages.length === 0) return;
    if (scrolledConvIdRef.current === conversationId) return;
    scrolledConvIdRef.current = conversationId;
    atBottomRef.current = true;
    requestAnimationFrame(() => {
      const viewport = chatViewportRef.current;
      if (viewport) viewport.scrollTop = viewport.scrollHeight;
    });
  }, [conversationId, msgsLoading, messages.length]);

  // Revalidate conversation list when AI responds — title may have been updated
  const lastAiMsgIdRef = useRef<number | undefined>();
  useEffect(() => {
    const first = messages[0];
    if (first?.role === 'assistant' && first.id !== lastAiMsgIdRef.current) {
      lastAiMsgIdRef.current = first.id;
      swrMutate('conversations');
    }
  }, [messages]);

  useEffect(() => {
    return () => pendingPreviews.forEach(URL.revokeObjectURL);
  }, [pendingPreviews]);

  useEffect(() => {
    const onPaste = (e: ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      const imageFiles: File[] = [];
      for (const item of Array.from(items)) {
        if (item.type.startsWith('image/')) {
          const raw = item.getAsFile();
          if (!raw) continue;
          const file = raw.type ? raw : new File([raw], raw.name || 'pasted-image.png', { type: item.type });
          imageFiles.push(file);
        }
      }
      if (imageFiles.length > 0) {
        e.preventDefault();
        handleAddFiles(imageFiles);
      }
    };
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, []);

  const handleAddFiles = (files: FileList | File[] | null) => {
    if (!files) return;
    const arr = Array.from(files).filter(isMediaFile);
    if (!arr.length) return;
    setPendingFiles((prev) => [...prev, ...arr]);
    setPendingPreviews((prev) => [...prev, ...arr.map((f) => URL.createObjectURL(f))]);
  };

  const handleRemoveFile = (idx: number) => {
    URL.revokeObjectURL(pendingPreviews[idx]);
    setPendingFiles((prev) => prev.filter((_, i) => i !== idx));
    setPendingPreviews((prev) => prev.filter((_, i) => i !== idx));
  };

  const confirmQuote = useCallback(() => {
    setActiveTab('chat');
    setTimeout(() => textareaRef.current?.focus(), 80);
  }, []);


  const handleSend = useCallback(async () => {
    // No `sending` guard here — the main send path is re-entrant by design so
    // users can fire multiple messages while previous HTTP posts are still in
    // flight. The report/quote-AI sub-paths below still use `sending` to block
    // their own double-submission since they mutate complex local state.
    const text = inputText.trim();
    if (!text && pendingFiles.length === 0 && !reportSkill) return;

    // Prompt/SMS toggle — SMS mode texts the customer directly (highest priority,
    // independent of which stage card is open).
    if (composerMode === 'sms') {
      const smsTo = smsRecipient || conversation?.vehicle?.phone;
      if (!canSms || !smsTo || !text) return;
      justSentRef.current = true;
      setInputText('');
      setSending(true);
      try {
        await smsApi.send({ to: smsTo, body: text, conversation_id: conversationId });
        smsApi.list({ phone: smsTo, limit: 100 }).then(setSmsMessages).catch(() => {});
      } catch (err) {
        console.error('[SMS_SEND] failed', err);
        setInputText(text);
      } finally {
        setSending(false);
      }
      return;
    }

    // Start inspection with selected template on first message
    await ensureInspectionStarted();

    if (reportSkill === 'final' || reportSkill === 'estimate') {
      if (sending) return;
      setSending(true);
      try {
        const filled = quoteItems.filter((q) => (q.label ?? '').trim());
        const isFinal = reportSkill === 'final';
        let content = isFinal
          ? (text ? `Generate a final report. ${text}` : 'Generate a final report for this vehicle.')
          : (text ? `Generate an estimate report. ${text}` : 'Generate an estimate report for this vehicle.');

        if (quoteDescription.trim()) {
          content += '\n\nDescription: ' + quoteDescription.trim();
        }
        if (filled.length > 0) {
          content += `\n\n${isFinal ? 'Work performed' : 'Items to estimate'}:`;
          for (const q of filled) {
            content += `\n- ${q.label}`;
            if (q.description.trim()) content += `: ${q.description.trim()}`;
          }
          const costLines: string[] = [];
          for (const q of filled) {
            const qty = parseFloat(q.qty) || 1;
            const partsUnit = parseFloat(q.parts) || 0;
            const partsSum = partsUnit * qty;
            const labor = parseFloat(q.labor) || 0;
            if (partsSum > 0) costLines.push(`- ${q.label} parts: $${Math.round(partsSum)}${qty > 1 ? ` (${qty} × $${Math.round(partsUnit)})` : ''}`);
            if (labor > 0) costLines.push(`- ${q.label} labor: $${Math.round(labor)}`);
          }
          if (costLines.length > 0) {
            content += '\n\nCost breakdown:\n' + costLines.join('\n');
            const _tp = filled.reduce((s, q) => s + (parseFloat(q.parts) || 0) * (parseFloat(q.qty) || 1), 0);
            const _tl = filled.reduce((s, q) => s + (parseFloat(q.labor) || 0), 0);
            const _ptax = Math.round(_tp * shopTaxRate);
            content += `\n\nTotal parts: $${Math.round(_tp)}, Parts tax (${(shopTaxRate * 100).toFixed(3).replace(/\.?0+$/, '')}%): $${_ptax}, Total labor: $${Math.round(_tl)}`;
            if (totalSublets > 0) content += `, Sublet: $${Math.round(totalSublets)}`;
            if (totalFees > 0) content += `, Fees: +$${Math.round(totalFees)}`;
            if (totalDiscounts > 0) content += `, Discount: -$${Math.round(totalDiscounts)}`;
            content += `, Grand total: $${Math.round(_tp + _ptax + _tl + totalSublets + totalFees - totalDiscounts)}`;
          }
        }
        if (technicianNote.trim()) {
          content += '\n\nNotes: ' + technicianNote.trim();
        }

        justSentRef.current = true;
        await sendMessage(content, {
          files: pendingFiles.length ? pendingFiles : undefined,
          usedTools: [isFinal ? 'final_report' : 'estimate_report'],
        });
        setInputText('');
        setPendingFiles([]);
        setPendingPreviews([]);
        setReportSkill(null);
      } finally {
        setSending(false);
      }
      return;
    }

    // Quote tab: send to AI endpoint for smart parsing (text and/or photos)
    if (activeTab === 'quote' && (text || pendingFiles.length > 0) && conversationId) {
      if (sending) return;
      const quoteFiles = [...pendingFiles];
      setInputText('');
      setPendingFiles([]);
      setPendingPreviews([]);
      setSending(true);
      try {
        // Upload images if any (quote/receipt photo import)
        let blobNames: string[] = [];
        if (quoteFiles.length > 0) {
          const uploads = await Promise.all(quoteFiles.map((f) => storageApi.uploadToPresignedUrl(f)));
          blobNames = uploads.map((u) => u.blob_name);
        }
        const prompt = text || 'Extract all line items from this quote/receipt photo and add them to the quote.';
        const result = await quoteApi.aiChat(conversationId, prompt, blobNames.length ? blobNames : undefined);
        if (result.actions?.length) {
          setQuoteItems((prev) => {
            let items = [...prev];
            // Signed-item protection: chat may not change price/title of items
            // covered by a legal doc. Skip update/remove on those indices; adds
            // (new rows) are always allowed. Indices from `prev` (what the AI
            // indexed against), computed before the isEmpty reset.
            const lockedIdx = new Set<number>();
            prev.forEach((it, idx) => { if (isItemAuthLocked(it)) lockedIdx.add(idx); });
            const isEmpty = items.length === 1 && !items[0].label && !items[0].parts && !items[0].labor;
            if (isEmpty) items = [];
            for (const a of result.actions) {
              if (a.action === 'add') {
                items.push({ label: a.label || '', parts: a.parts || '', qty: a.qty || '1', labor: a.labor || '', sublet: a.sublet || '', fee: a.fee || '', discount: a.discount || '', laborHours: a.laborHours || '', description: a.description || '' });
              } else if (a.action === 'update' && a.index != null && a.index < items.length) {
                if (lockedIdx.has(a.index)) continue;
                const cur = items[a.index];
                items[a.index] = { ...cur, ...(a.label != null ? { label: a.label } : {}), ...(a.parts != null ? { parts: a.parts } : {}), ...(a.qty != null ? { qty: a.qty } : {}), ...(a.labor != null ? { labor: a.labor } : {}), ...(a.sublet != null ? { sublet: a.sublet } : {}), ...(a.fee != null ? { fee: a.fee } : {}), ...(a.discount != null ? { discount: a.discount } : {}), ...(a.laborHours != null ? { laborHours: a.laborHours } : {}), ...(a.description != null ? { description: a.description } : {}) };
              } else if (a.action === 'remove' && a.index != null && a.index < items.length) {
                if (lockedIdx.has(a.index)) continue;
                items = items.filter((_, idx) => idx !== a.index);
              }
            }
            return items.length ? items : [{ label: '', parts: '', qty: '1', labor: '', sublet: '', fee: '', discount: '', laborHours: '', description: '' }];
          });
        }
        if (result.description_update) setQuoteDescription((prev) => result.description_update!);
        if (result.note_update) setTechnicianNote((prev) => result.note_update!);
        // The backend persists this exchange as user/assistant Message rows; pull
        // them into the unified thread immediately instead of waiting for the poll.
        mutateMessages();
      } catch (err) {
        console.error('[QUOTE_AI]', err);
        // Fallback: add as plain item
        if (text) {
          setQuoteItems((prev) => {
            const isEmpty = prev.length === 1 && !prev[0].label && !prev[0].parts && !prev[0].labor;
            const newItem: QuoteItem = { label: text, parts: '', qty: '1', labor: '', sublet: '', fee: '', discount: '', laborHours: '', description: '' };
            return isEmpty ? [newItem] : [...prev, newItem];
          });
        }
      } finally {
        setSending(false);
      }
      return;
    }

    let savedText = inputText.trim();
    const savedFiles = pendingFiles;
    const savedPreviews = pendingPreviews;
    justSentRef.current = true;
    setInputText('');
    setPendingFiles([]);
    setPendingPreviews([]);
    setSending(true);
    try {
      // SMS tab has its own send handler — this is for repair/quote chat only
      const smsTo = smsRecipient || conversation?.vehicle?.phone;
      if (activeTab === 'sms' && canSms && smsTo) {
        await smsApi.send({ to: smsTo, body: savedText, conversation_id: conversationId });
        smsApi.list({ phone: smsTo, limit: 100 }).then(setSmsMessages).catch(() => {});
      } else {
        const result = await sendMessage(savedText, { files: savedFiles.length ? savedFiles : undefined });
        // Chat-side paper-signature mapping: if the message carried an image
        // AND there are quote items currently in 'selected' state, treat the
        // image as the signed-paper evidence and bulk-approve those items.
        // Replaces the old "Upload signed photo" button — selection is now
        // required, never a free-floating doc upload.
        if (conversationId && result?.attachments?.length) {
          const firstImageBlob = result.attachments.find((a) => a.is_image)?.blob_name;
          if (firstImageBlob) {
            const selected = quoteItems
              .filter((q) => { const ib = (q as any).in_batch; const s = (q as any).item_status; const inBatch = ib === true || (ib === undefined && (s == null || s === 'selected' || s === 'unauthorized')); return inBatch && (q as any).source_id; })
              .map((q) => String((q as any).source_id));
            if (selected.length > 0) {
              try {
                const decisions = Object.fromEntries(selected.map((sid) => [sid, 'approved' as const]));
                await quoteApi.paperAuthorization(conversationId, firstImageBlob, decisions);
                // SWR polling will pick up the new item statuses on next tick.
              } catch (err) {
                console.error('[CHAT_PAPER_AUTH] failed', err);
              }
            }
          }
        }
      }
    } catch (err) {
      console.error('[SEND] failed:', err);
      setInputText(savedText);
      setPendingFiles(savedFiles);
      setPendingPreviews(savedPreviews);
    } finally {
      setSending(false);
    }
  }, [inputText, pendingFiles, pendingPreviews, reportSkill, quoteItems, quoteDescription, technicianNote, sendMessage, mutateMessages, messages, quotedLabels, ensureInspectionStarted, smsEnabled, conversation, conversationId, sending, activeTab, composerMode, smsRecipient, canSms]);

  const handleGenerateReport = useCallback(async () => {
    setSending(true);
    try {
      await sendMessage('Generate a report for this vehicle.', {
        usedTools: ['report'],
      });
    } finally {
      setSending(false);
    }
  }, [sendMessage]);

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (canSend) handleSend();
    }
  };

  // Drag & drop
  const handleDragEnter = (e: DragEvent) => {
    e.preventDefault();
    dragCounter.current++;
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) setDragging(true);
  };
  const handleDragLeave = (e: DragEvent) => {
    e.preventDefault();
    dragCounter.current--;
    if (dragCounter.current === 0) setDragging(false);
  };
  const handleDragOver = (e: DragEvent) => { e.preventDefault(); };
  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    dragCounter.current = 0;
    setDragging(false);
    const files = Array.from(e.dataTransfer.files).filter((f) => f.type.startsWith('image/'));
    if (files.length) handleAddFiles(files);
  };

  // DO NOT early-return here. There are ~10 hooks declared further down
  // (useMemo, useCallback, useMessageQueue, useEffect, useSensors,
  // useState). Returning when convError shows up on a later render skips
  // them and React crashes with "Rendered fewer hooks than expected".
  // The deleted-conv branch is rendered conditionally inside the JSX
  // return below — see the `isDeletedConv` check just before the main
  // return statement.
  const isDeletedConv = !convLoading && !!convError;

  const isArchived = conversation?.status === 'archived';
  const quoteLocked = isArchived || isQuoteReadOnly;
  // Keep the ref in sync so the early-declared setter wrappers see the
  // current lock state (archived | read-only by role | per-device override).
  quoteLockedRef.current = quoteLocked;
  // Field-level lock: the WHOLE quote is read-only once ANY legal doc is signed,
  // on top of role/archived. Does NOT cover the tech-note, the per-item Select
  // box, Unlink, the capture toolbar, or chat — those are exempt by design (the
  // Select/Unlink are zIndex-4 floats above the row overlay; chat is guarded
  // per-signed-item in the action appliers). NOT pushed into quoteLockedRef —
  // setQuoteItems must stay alive so the Select box keeps working.
  const docLocked = quoteLocked || hasAnyLegalDoc;

  // Per-item authorization gate. Per user rule 2026-05-27:
  //   "in HAIST, we must not change any price or name without cancel auth doc"
  // Lock the row only when a REAL legal doc covers the item — must match
  // the hasLegalDoc rule used by the row UI / Unlink-doc button. The Tek
  // mirror writes an UNSIGNED customer_approvals shell (signature_method
  // empty or non-HAIST); without the shell-aware gate, items lock here
  // but the Unlink button doesn't render (because hasLegalDoc correctly
  // says no doc) → row is unrecoverable. Match the rules:
  //   coverage = isRealCustomerSignature(ca) AND sid in ca, OR
  //              item.authorization_id + terminal status (not shown
  //              here — that path is handled by the row's hasLegalDoc
  //              directly and has its own Unlink-doc button).
  const authorizedSourceIds = useMemo(() => {
    const ca: any = customerApprovals;
    if (!ca || !isRealCustomerSignature(ca)) return new Set<string>();
    if (!ca.approved_items && !ca.declined_items) return new Set<string>();
    const s = new Set<string>();
    for (const i of (ca.approved_items || [])) if (i?.source_id != null) s.add(String(i.source_id));
    for (const i of (ca.declined_items || [])) if (i?.source_id != null) s.add(String(i.source_id));
    return s;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerApprovals]);
  // INVOICE-track equivalent of authorizedSourceIds — items covered by a signed
  // invoice doc lock the row too (a signed invoice can't be silently edited).
  const invoiceAuthorizedSourceIds = useMemo(() => {
    const ca: any = invoiceApprovals;
    if (!ca || !isRealCustomerSignature(ca)) return new Set<string>();
    if (!ca.approved_items && !ca.declined_items) return new Set<string>();
    const s = new Set<string>();
    for (const i of (ca.approved_items || [])) if (i?.source_id != null) s.add(String(i.source_id));
    for (const i of (ca.declined_items || [])) if (i?.source_id != null) s.add(String(i.source_id));
    return s;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceApprovals]);
  const isItemAuthLocked = useCallback(
    (item: any) => {
      if (item?.source_id == null) return false;
      if (authorizedSourceIds.has(String(item.source_id))) return true;
      if (invoiceAuthorizedSourceIds.has(String(item.source_id))) return true;
      // Invoice per-item lock — stamped without changing item_status.
      if (item.invoice_authorization_id) return true;
      // Real per-item legal doc (paper/digital signature flow stamps
      // authorization_id + sets status). Mirrors hasLegalDoc's
      // hasItemAuthDoc branch — without this, paper-signed items
      // (which don't populate customer_approvals at all) wouldn't lock.
      const status = (item.item_status || '').toLowerCase();
      return !!(item.authorization_id && (status === 'approved' || status === 'declined'));
    },
    [authorizedSourceIds, invoiceAuthorizedSourceIds],
  );
  const queueEntries = useMessageQueue(conversationId);
  const queueCounts = useMemo(() => selectCounts(queueEntries), [queueEntries]);
  const atInFlightCap = queueCounts.allInFlight >= MAX_IN_FLIGHT;
  // canSend deliberately omits !sending so the user can fire another message
  // while the previous POST is still in flight. The inflight cap (queue
  // selector) is the real rate limit.
  const canSend = (inputText.trim().length > 0 || pendingFiles.length > 0 || !!reportSkill) && !isArchived && !atInFlightCap;

  const scrollToQueueEntry = useCallback((entry: QueueEntry): boolean => {
    // Inspect-chat entries: target an inspection item card on the
    // Inspect tab. Set during the chat send by messageQueue.setTargetItem,
    // sourced from chatResult.actions[0].item_id.
    if (entry.targetItemId) {
      const targetItemId = entry.targetItemId;
      const tryScrollItem = (): boolean => {
        const el = document.querySelector(`[data-item-id="${targetItemId}"]`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return true;
        }
        return false;
      };
      if (activeTab === 'inspect' && tryScrollItem()) return true;
      setActiveTab('inspect');
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (!tryScrollItem()) setTimeout(tryScrollItem, 100);
        });
      });
      return true;
    }

    // Photo/video inspect uploads (api.aiInspect): no item_id at send time, but
    // the items it created carry data-batch-id. Scroll to the first of them on
    // the Inspect tab — NOT the Chat tab.
    if (entry.batchId) {
      const batchId = entry.batchId;
      const tryScrollBatch = (): boolean => {
        const el = document.querySelector(`[data-batch-id="${batchId}"]`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return true;
        }
        return false;
      };
      if (activeTab === 'inspect' && tryScrollBatch()) return true;
      setActiveTab('inspect');
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (!tryScrollBatch()) setTimeout(tryScrollBatch, 100);
        });
      });
      return true;
    }

    // Chat-message entries: existing behavior — switch to Chat and
    // scroll to the matching message row.
    const matched = messages.find(
      (m) => m.role === 'user' && m.content === entry.content && m.id > 0,
    );
    const targetId = matched?.id ?? entry.optimisticMessageId;

    const tryScroll = (): boolean => {
      const el = document.querySelector(`[data-msg-id="${targetId}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return true;
      }
      return false;
    };

    if (activeTab === 'chat' && tryScroll()) return true;
    setActiveTab('chat');
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!tryScroll()) {
          setTimeout(tryScroll, 100);
        }
      });
    });
    return true;
  }, [messages, activeTab, setActiveTab]);

  // Sync sending flag for sidebar navigation guard. Phase 2 will widen this
  // to include the per-conversation message queue.
  useEffect(() => {
    processingState.isProcessing = sending;
    return () => { processingState.isProcessing = false; };
  }, [sending]);

  // Warn on tab close/refresh while a send is in flight.
  useEffect(() => {
    if (!sending) return;
    const handler = (e: BeforeUnloadEvent) => { e.preventDefault(); };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [sending]);

  // Bottom navigation bar — tab pills + status + back/next
  const bottomBar = conversationId ? (
    <Box style={{
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '16px 28px',
      background: '#111113',
      borderTop: `1px solid ${BORDER}`,
    }}>
     {/* Centered footer: breadcrumb + workflow CTA grouped together in the middle. */}
     <Box style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 48 }}>
      {/* Breadcrumb */}
      <Box style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 1, minWidth: 0, overflow: 'hidden' }}>
          {WORKFLOW_STAGES.filter((s) => enabledStages.includes(s)).map((s, i) => (
            <Fragment key={s}>
              {i > 0 && <Text size="md" c={TEXT_MUTED} style={{ flexShrink: 0 }}>·</Text>}
              <Text
                size="md"
                fw={s === currentStage ? 700 : 500}
                c={s === currentStage ? '#fff' : TEXT_MUTED}
                style={{ textTransform: 'uppercase', letterSpacing: '0.06em', whiteSpace: 'nowrap' }}
              >
                {STAGE_LABELS[s]}
              </Text>
            </Fragment>
          ))}
        </Box>

        {!isArchived ? (
          <Box style={{ display: 'flex', gap: 14, alignItems: 'center', flexShrink: 0 }}>
            {prevStage && (
              <UnstyledButton onClick={handleTransitionRevert} style={{ fontSize: 16, fontWeight: 600, color: '#A1A1AA', padding: '12px 18px' }}>
                ← Undo
              </UnstyledButton>
            )}
            {currentStage !== 'completed' ? (
              <UnstyledButton
                onClick={() => setShowTransitionModal(true)}
                style={{ fontSize: 16, fontWeight: 700, color: '#fff', background: BLUE, padding: '14px 36px', borderRadius: 12, whiteSpace: 'nowrap' }}
              >
                {NEXT_LABELS[currentStage] || 'Next →'}
              </UnstyledButton>
            ) : (
              <UnstyledButton
                onClick={() => setShowArchiveConfirm(true)}
                style={{ fontSize: 16, fontWeight: 700, color: '#fff', background: '#3F3F46', padding: '14px 30px', borderRadius: 12, whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 8 }}
              >
                <IconArchive size={18} /> Archive
              </UnstyledButton>
            )}
          </Box>
        ) : (
          <Box style={{ display: 'flex', alignItems: 'center', gap: 16, flexShrink: 0 }}>
            <Text size="md" fw={600} c={TEXT_MUTED}>Archived</Text>
            <UnstyledButton
              onClick={() => conversationId && updateConversationStatus(conversationId, 'active')}
              style={{ fontSize: 16, fontWeight: 700, color: BLUE, padding: '12px 24px', borderRadius: 10, border: `1px solid ${BLUE}` }}
            >
              Unarchive
            </UnstyledButton>
          </Box>
        )}
     </Box>
    </Box>
  ) : null;

  // Simplified footer for split view left panel — just the next button, matches report action bar height

  // Reusable chat messages content — used in both split-view and normal view
  const chatMessagesContent = (
    <>
      {msgsLoading ? (
        <MessageSkeleton />
      ) : !messages.some(m => m.role === 'user') && (!conversation?.vehicle || vehicleLabel(conversation) === 'Unknown vehicle') ? (
        <Center style={{ minHeight: 200 }}>
          <Stack align="center" gap={10} style={{ maxWidth: 380, width: '100%', padding: '0 16px' }}>
            <IconCar size={32} color={BLUE} />
            <Text size="sm" fw={600} c={TEXT}>No vehicle yet</Text>
            <Text size="xs" c={TEXT_MUTED} ta="center">
              Look up the vehicle by plate or VIN, or add customer info — the panels on the right stay available either way.
            </Text>
            {conversationId && (
              <Button
                size="sm"
                radius="md"
                style={{ background: BLUE, color: '#fff' }}
                onClick={() => setShowVehicleLookup(true)}
              >
                Look up vehicle / add customer
              </Button>
            )}
          </Stack>
        </Center>
      ) : (
        (() => {
          // Unified thread: chat messages + customer SMS + activity events, merged by time.
          type TimelineItem =
            | { type: 'msg'; msg: MessageResponse }
            | { type: 'event'; msg: MessageResponse }
            | { type: 'sms'; sms: import('../types').SmsMessageResponse };
          const timeline: TimelineItem[] = [];
          {
            const seenEvt = new Set<string>();
            for (const msg of messages) {
              const mtype = (msg as any).message_metadata?.type;
              if (mtype === 'report_event' || mtype === 'customer_event') {
                // Activity events render inline as chips; dedup same type per day.
                const evtType = (msg as any).message_metadata?.event_type || 'event';
                const key = `${evtType}:${msg.created_at.slice(0, 10)}`;
                if (seenEvt.has(key)) continue;
                seenEvt.add(key);
                timeline.push({ type: 'event', msg });
                continue;
              }
              timeline.push({ type: 'msg', msg });
            }
            for (const sms of smsMessages) {
              timeline.push({ type: 'sms', sms });
            }
          }
          timeline.sort((a, b) => {
            const ta = a.type === 'sms' ? a.sms.created_at : a.msg.created_at;
            const tb = b.type === 'sms' ? b.sms.created_at : b.msg.created_at;
            return ta < tb ? -1 : ta > tb ? 1 : 0;
          });

          const filtered = composerMode === 'sms'
            // SMS mode = a texting thread with the selected contact: SMS + activity
            // chips only, no AI-prompt messages ("SMS chatting, not prompt chat").
            ? timeline.filter((item) => item.type === 'sms' || item.type === 'event')
            // Prompt mode: activity events live in the Summary card's Activity feed,
            // not inline in the chat — so exclude them here.
            : (chatFilter === 'all' ? timeline : timeline.filter((item) => {
            if (item.type === 'sms') return chatFilter === 'customer' || (chatFilter === 'mine' && item.sms.direction === 'outbound');
            if (item.type === 'event') return chatFilter === 'customer';
            const msg = item.msg;
            if (chatFilter === 'customer') return false;
            if (chatFilter === 'haist') return msg.role === 'assistant';
            if (chatFilter === 'mine') return msg.role === 'user' && msg.user_name === currentUser?.name;
            if (chatFilter === 'others') return msg.role === 'user' && msg.user_name !== currentUser?.name;
            return true;
          })).filter((item) => item.type !== 'event');

          return filtered.map((item, i, arr) => {
            if (item.type === 'event') {
              const time = formatTime(item.msg.created_at);
              const txt = item.msg.content.replace(/\*\*/g, '').split('\n')[0];
              return (
                <Box key={`evt-${item.msg.id}`} px={24} py={6}>
                  <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, maxWidth: 520, margin: '0 auto', background: 'rgba(245,158,11,0.08)', borderRadius: 8, padding: '6px 12px', border: '1px solid rgba(245,158,11,0.15)' }}>
                    <Box style={{ width: 5, height: 5, borderRadius: '50%', background: '#F59E0B', flexShrink: 0 }} />
                    <Text size="xs" c="#F59E0B" fw={600} style={{ textAlign: 'center' }}>{txt}</Text>
                    <Text size="10px" c={TEXT_MUTED} style={{ flexShrink: 0 }}>{time}</Text>
                  </Box>
                </Box>
              );
            }
            if (item.type === 'sms') {
              const sms = item.sms;
              const isOutbound = sms.direction === 'outbound';
              return (
                <Box key={`sms-${sms.id}`} px={24} py={6} style={{ display: 'flex', justifyContent: isOutbound ? 'flex-end' : 'flex-start' }}>
                  <Box style={{ maxWidth: '68%' }}>
                    <Box style={{
                      background: isOutbound ? 'rgba(34,197,94,0.18)' : 'rgba(34,197,94,0.08)',
                      border: `1px solid ${isOutbound ? 'rgba(34,197,94,0.35)' : 'rgba(34,197,94,0.2)'}`,
                      borderRadius: isOutbound ? '12px 12px 4px 12px' : '12px 12px 12px 4px',
                      padding: '10px 14px',
                    }}>
                      <Group gap={6} mb={4}>
                        <IconMessage size={12} color="#22C55E" />
                        <Text size="xs" c="#22C55E" fw={600}>
                          {isOutbound ? 'SMS sent' : `SMS from customer`}
                        </Text>
                      </Group>
                      <Text size="sm" c={TEXT} style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.6 }}>{sms.body}</Text>
                    </Box>
                    <Text size="xs" c={TEXT_MUTED} mt={4} ta={isOutbound ? 'right' : 'left'}>{formatTime(sms.created_at)}</Text>
                  </Box>
                </Box>
              );
            }

            const msg = item.msg;
            const prevItem = arr[i - 1];
            const prevRole = prevItem?.type === 'msg' ? prevItem.msg.role : 'sms';
            // Show inline thinking indicator after user messages waiting for AI reply, and under
            // a short answer while the same row is still becoming the long one.
            const nextItem = arr[i + 1];
            const nextIsAssistantMsg = nextItem?.type === 'msg' && nextItem.msg.role === 'assistant';
            const showThinking = (msg.role === 'user' && !nextIsAssistantMsg) || Boolean(msg.loading_more);
            return (
              <div key={msg.id} data-msg-id={msg.id} ref={msg.role === 'user' && arr.slice(i + 1).every((it) => it.type !== 'msg' || it.msg.role !== 'user') ? lastUserMsgRef : undefined}>
                <Box style={{ display: 'block', width: '100%' }}>
                  <ChatMessage
                    msg={msg}
                    showHeader={i === 0 || prevRole !== msg.role}
                    onSuggestReply={(text) => {
                      setInputText(text);
                      textareaRef.current?.focus();
                    }}
                    onAddToQuote={canQuote ? (item) => openJobDraft(item) : undefined}
                    quotedLabels={canQuote ? quotedLabels : undefined}
                    shopLaborRate={shopLaborRate}
                    shopName={shopData?.name}
                    customerName={[conversation?.vehicle?.first_name, conversation?.vehicle?.last_name].filter(Boolean).join(' ') || null}
                    customerEmail={conversation?.vehicle?.email}
                    customerPhone={canSms ? conversation?.vehicle?.phone : null}
                    conversationId={conversationId}
                  />
                </Box>
                {showThinking && (
                  <Box px={24} py={6}>
                    <Group gap={10} align="flex-start">
                      <Box style={{ width: 28, height: 28, borderRadius: '50%', overflow: 'hidden', flexShrink: 0, marginTop: 2 }}>
                        <img src="/images/haist/haist-icon.jpg" alt="HAIST" style={{ width: 28, height: 28, objectFit: 'cover', display: 'block' }} />
                      </Box>
                      <Box style={{ display: 'flex', alignItems: 'center', gap: 5, paddingTop: 6 }}>
                        {[0, 1, 2].map((n) => (
                          <Box
                            key={n}
                            style={{
                              width: 6, height: 6, borderRadius: '50%',
                              background: TEXT_MUTED,
                              animation: `thinking-dot 1.2s ease-in-out ${n * 0.2}s infinite`,
                            }}
                          />
                        ))}
                      </Box>
                    </Group>
                  </Box>
                )}
              </div>
            );
          });
        })()
      )}
      {/* Per-message thinking indicators are now inline after each pending user message */}

      {roGenerating && !hasRecCard && (
        <Box px={24} py={6}>
          <Group gap={10} align="flex-start">
            <Box style={{ width: 28, height: 28, borderRadius: '50%', overflow: 'hidden', flexShrink: 0, marginTop: 2 }}>
              <img src="/images/haist/haist-icon.jpg" alt="HAIST" style={{ width: 28, height: 28, objectFit: 'cover', display: 'block' }} />
            </Box>
            <Group gap={8} align="center" style={{ paddingTop: 4 }}>
              <Text size="sm" c={TEXT_MUTED}>Pulling recommended work…</Text>
              <Box style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                {[0, 1, 2].map((n) => (
                  <Box key={n} style={{ width: 6, height: 6, borderRadius: '50%', background: TEXT_MUTED, animation: `thinking-dot 1.2s ease-in-out ${n * 0.2}s infinite` }} />
                ))}
              </Box>
            </Group>
          </Group>
        </Box>
      )}

      <div ref={chatSpacerRef} style={{ height: 0 }} />
    </>
  );

  // Quote content — extracted so it can be rendered inline in inspect/quote toggle
  const QUOTE_INPUT_STYLES = { input: { background: '#111113', borderColor: BORDER, color: TEXT, padding: '0 8px', textAlign: 'right' as const } };
  const QUOTE_COL = { job: 'minmax(160px, 1fr)', parts: '80px', qty: '52px', partsSum: '80px', labor: '80px', sum: '80px', del: '48px' };
  const QUOTE_GRID = `${QUOTE_COL.job} ${QUOTE_COL.parts} ${QUOTE_COL.qty} ${QUOTE_COL.partsSum} ${QUOTE_COL.labor} ${QUOTE_COL.sum} ${QUOTE_COL.del}`;

  const focusCell = (row: number, col: number) => {
    const COLS = 4;
    let r = row, c = col + 1;
    if (c >= COLS) { c = 0; r++; }
    if (r >= quoteItems.length) {
      setQuoteItems((prev) => [...prev, newEmptyQuote()]);
    }
    setTimeout(() => cellRefs.current.get(`${r}-${c}`)?.focus(), 10);
  };

  // Open a pre-filled job draft (from an RO recommendation or an inspection
  // item) in the add-job pop-up — the SAME card editor used inline, but on a
  // STANDALONE draft. Nothing touches quoteItems until the writer hits Save.
  const openJobDraft = (draft: QuoteItem) => {
    const lbl = (draft.label ?? '').trim().toLowerCase();
    if (lbl && quoteItems.some((q) => (q.label ?? '').trim().toLowerCase() === lbl)) {
      // Already on the quote — just jump to the Job tab, don't duplicate.
      setActiveTab('quote');
      return;
    }
    // Backfill an empty labor rate from the shop default so the Rate/hr column
    // reads naturally; total still drives the row sum.
    const withRates: QuoteItem = {
      ...draft,
      labors: ((draft as any).labors || []).map((l: any) =>
        (!l.rate && shopLaborRate) ? { ...l, rate: String(shopLaborRate) } : l,
      ),
    } as any;
    setDescDraft(withRates.description || '');
    setJobDraft([withRates]);
  };

  const closeJobDraft = (save: boolean) => {
    if (jobDraft && save) {
      // Commit only now: merge the notes draft and append to the quote.
      const finalItem: QuoteItem = { ...jobDraft[0], description: descDraft } as any;
      setQuoteItems((prev) => {
        const isEmptySeed = prev.length === 1
          && !prev[0].label && !prev[0].parts && !prev[0].labor
          && !((prev[0] as any).labors || []).length && !((prev[0] as any).partLines || []).length;
        return isEmptySeed ? [finalItem] : [...prev, finalItem];
      });
      setActiveTab('quote');
    }
    setJobDraft(null);
    setDescDraft('');
  };

  const totalParts = quoteItems.reduce((s, i) => s + (parseFloat(i.parts) || 0) * (parseFloat(i.qty) || 1), 0);
  const totalLabor = quoteItems.reduce((s, i) => s + (parseFloat(i.labor) || 0), 0);
  const totalSublets = quoteItems.reduce((s, i) => s + (parseFloat(i.sublet) || 0), 0);
  const partsTax = Math.round(totalParts * shopTaxRate);

  // Per-item fees and discounts
  const totalFees = quoteItems.reduce((s, i) => s + (parseFloat(i.fee) || 0), 0);
  const totalDiscounts = quoteItems.reduce((s, i) => s + (parseFloat(i.discount) || 0), 0);

  const grandTotal = totalParts + partsTax + totalLabor + totalSublets + totalFees - totalDiscounts;
  const fmtQuote = (n: number) => '$' + n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });

  // Drag sensors for labor-line reordering. 6px activation distance keeps
  // clicks on the row inputs (textarea, number inputs) from being hijacked
  // as drag-starts. Only the grip handle (configured via setActivatorNodeRef
  // in SortableLaborRow) initiates a drag.
  const laborDragSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  // Compose a stable SortableContext id for a labor line. Prefer tek_labor_id
  // (back-end stamps it from Tek's labor.id); for HAIST-local labors that
  // don't yet have one, fall back to `local-${index}` — drag completes before
  // re-render so the synthetic id is stable for the duration of the gesture.
  const laborSortId = (l: any, li: number) => `lab-${l?.tek_labor_id != null ? l.tek_labor_id : `local-${li}`}`;

  const quoteHeaderCell = (label: string, align: 'left' | 'right' = 'right') => (
    <Text size="10px" fw={600} c={TEXT_MUTED} tt="uppercase" ta={align} style={{ letterSpacing: '0.05em', userSelect: 'none' }}>{label}</Text>
  );


            // Capture the live quote arrays so the editor can be re-pointed at a
            // standalone draft (the add-job pop-up) without rewriting its ~45
            // index-based closures — opts.items/setItems shadow the names below.
            const _allQuoteItems = quoteItems;
            const _setAllQuoteItems = setQuoteItems;
            const _editingDescIdx = editingDescIdx;
            // Single source of truth for a job card — used both inline (the
            // map below) and inside the add-job pop-up (JobAddModal) so the
            // recommendation editor is literally the same editor as a job card.
            const renderQuoteCard = (item: QuoteItem, i: number, opts: { inModal?: boolean; items?: QuoteItem[]; setItems?: (u: any) => void; editing?: boolean } = {}) => {
            // In pop-up mode the editor targets the standalone draft array and
            // is forced into edit mode; inline it uses the real quote + state.
            const quoteItems = opts.items ?? _allQuoteItems;
            const setQuoteItems = opts.setItems ?? _setAllQuoteItems;
            const editingDescIdx = opts.editing ? i : _editingDescIdx;
            // Hide the seeded/blank placeholder row (no fields, no lines, no
            // photo) unless the user is actively editing it. New "Add Job"
            // rows open in edit mode (editingDescIdx === i) so they stay
            // visible while being filled in.
            const isBlankPlaceholder = !(
              (item.label ?? '').trim() ||
              (item.description ?? '').trim() ||
              (item as any).parts || (item as any).labor ||
              ((item as any).labors || []).length ||
              ((item as any).partLines || []).length ||
              ((item as any).subletLines || []).length ||
              ((item as any).feeLines || []).length ||
              ((item as any).discountLines || []).length ||
              ((item as any).photos || []).length
            );
            if (isBlankPlaceholder && editingDescIdx !== i) return null;
            if (item.is_hidden && !showHiddenJobs) return null;
            // Apply approval filter — match by source_id (stable ID) only.
            if (quoteFilter !== 'all') {
              const sid = String((item as any).source_id ?? '');
              const isAppr = !!sid && approvedSet.has(sid);
              const isDecl = !!sid && declinedSet.has(sid);
              if (quoteFilter === 'approved' && !isAppr) return null;
              if (quoteFilter === 'declined' && !isDecl) return null;
              if (quoteFilter === 'pending' && (isAppr || isDecl)) return null;
            }
            // Totals are computed from the per-line arrays ONLY. Legacy
            // aggregate scalars (item.parts/.labor/.fee/.qty/.discount/
            // .sublet) are no longer read or written by the backend mirror
            // or extension UPDATE-path; preserved here only as a no-op
            // alias on item.total when the arrays are empty (legacy data).
            const laborsTotal = ((item as any).labors || []).reduce((s: number, l: any) => s + (parseFloat(l.total) || (parseFloat(l.hours || '0') * parseFloat(l.rate || '0')) || 0), 0);
            const partsLinesTotal = ((item as any).partLines || []).reduce((s: number, p: any) => s + ((parseFloat(p.retail || p.cost || '0') || 0) * (p.qty || 1)), 0);
            const subletLinesTotal = ((item as any).subletLines || []).reduce((s: number, sl: any) => s + (parseFloat(sl.price || '0') || 0), 0);
            const feeLinesTotal = ((item as any).feeLines || []).reduce((s: number, f: any) => s + (parseFloat(f.amount || '0') || 0), 0);
            const discLinesTotal = ((item as any).discountLines || []).reduce((s: number, d: any) => s + (parseFloat(d.amount || '0') || 0), 0);
            const rowSum = laborsTotal + partsLinesTotal + subletLinesTotal + feeLinesTotal - discLinesTotal;
            const isHidden = item.is_hidden === true;
            const itemPhotos = item.photos || [];
            const itemState = item.state || 'pending';

            // Per-row approval state — match by stable source_id only.
            const sid = String((item as any).source_id ?? '');
            const isApproved = !!sid && !!customerApprovals?.approved_items?.some((a: any) => String(a.source_id ?? '') === sid);
            const isDeclined = !!sid && !!customerApprovals?.declined_items?.some((a: any) => String(a.source_id ?? '') === sid);
            const toggleApproval = (approve: boolean) => {
              if (signatureRequired) {
                // Signature-required shops must go through the selection-first
                // flow: tap Select on each item, then either Send for Approval
                // or Paper Signature in the bulk toolbar. No more single-doc
                // upload back-door from the per-row Approve/Decline button.
                alert('Select the items first (Select button), then use the bulk toolbar to send the report or attach a paper signature.');
                return;
              }
              if (!sid) return;  // can't push without a stable id
              const amt = Math.round(rowSum);
              setCustomerApprovals((prev: any) => {
                const ap = (prev?.approved_items || []).filter((a: any) => String(a.source_id ?? '') !== sid);
                const dc = (prev?.declined_items || []).filter((a: any) => String(a.source_id ?? '') !== sid);
                if (approve && !isApproved) ap.push({ source_id: sid, label: item.label, amount: amt });
                else if (!approve && !isDeclined) dc.push({ source_id: sid, label: item.label, amount: amt });
                const next = { ...prev, approved_items: ap, declined_items: dc, submitted_at: prev?.submitted_at || new Date().toISOString() };
                quoteApi.upsert(conversationId!, { items: quoteItems, description: quoteDescription, note: technicianNote, customer_approvals: next } as any).catch(() => {});
                return next;
              });
            };

            const handlePhotoUpload = async (files: FileList | File[]) => {
              const fileArr = Array.from(files).filter((f) => f.type.startsWith('image/') || f.type.startsWith('video/'));
              if (!fileArr.length || !conversationId) return;
              // Attach-only: no AI analysis kicked off here. The 1.5s debounced
              // quote upsert (useEffect on quoteItems) persists the new photos
              // automatically. Re-introduce an inspect call only when a UI
              // surface explicitly asks for an analysis pass.
              for (const file of fileArr) {
                try {
                  const { blob_name } = await storageApi.uploadToPresignedUrl(file);
                  setQuoteItems((prev) => prev.map((q, idx) => idx === i ? { ...q, photos: [...(q.photos || []), { url: blob_name, label: 'primary', uploaded_at: new Date().toISOString() }] } : q));
                } catch (e) { console.error('[JOB_PHOTO] upload failed', e); }
              }
            };

            const handleGenerateDesc = async () => {
              if (!conversationId) return;
              // Suggestion-card flavor: writes to description_autofill, the
              // QuoteSuggestionCard above the textarea picks it up via SWR
              // refetch. Mirror of the inspection AI-suggestion pattern.
              try {
                await quoteApi.suggestDescription(conversationId, i);
                setQuoteItems((prev) => prev.map((q, idx) => idx === i ? { ...q, state: 'analyzing' } : q));
              } catch (e) {
                console.error('[JOB_SUGGEST] failed', e);
              }
            };

            const toggleHidden = () => {
              setQuoteItems((prev) => prev.map((q, idx) => idx === i ? { ...q, is_hidden: !q.is_hidden } : q));
              if (conversationId) quoteApi.patchItem(conversationId, i, { is_hidden: !isHidden }).catch(() => {});
            };

            // Phase 1 — per-item authorization state surfacing
            // Default to 'selected' so the per-row checkbox is checked from
            // the start — mechanic unchecks anything they don't want. Works
            // across signature-required and non-signature shops alike.
            const itemStatus: 'unauthorized' | 'selected' | 'approved' | 'declined' =
              (item as any).item_status ?? 'selected';
            const itemSourceId: string | undefined = (item as any).source_id ? String((item as any).source_id) : undefined;
            // Lock the row only when a signed legal doc actually covers this
            // item. Three signals — ANY of them counts:
            //   1. item.authorization_id stamped — paper / digital signature
            //      modal wrote a QuoteAuthorization and tied the item to it.
            //      Doesn't touch customer_approvals at all, so we MUST check
            //      this directly (paper-signed items used to render as
            //      unsigned because the check below missed them).
            //   2. customer_approvals.approved_items contains this source_id
            //      — customer portal whole-quote signature.
            //   3. customer_approvals.declined_items contains this source_id
            //      — same flow, declined direction.
            // authorization_id alone isn't enough — historical rows can be
            // left with auth_id set even after the revoke flow popped it
            // server-side (frontend used to keep the stale id around, then
            // the debounced upsert re-wrote it). Treat 'doc on file' as
            // {auth_id set AND state is a terminal decision}. If the state
            // is 'unauthorized' / 'selected' / null, the doc is logically
            // detached even if the field hasn't been cleaned up yet.
            const rawItemAuthDoc = !!(item as any).authorization_id
              && (itemStatus === 'approved' || itemStatus === 'declined');
            // customer_approvals.approved_items alone is NOT enough — the
            // banner can be "Customer authorization on file · unsigned"
            // (approved_items listed but no signature_method captured yet).
            // Require a real signature (signature_method set) before treating
            // the portal path as a legal doc.
            const caHasSignature = isRealCustomerSignature(customerApprovals);
            const caContainsThisItem = !!(
              itemSourceId && customerApprovals && (
                (customerApprovals.approved_items || []).some((a: any) => String(a.source_id || '') === itemSourceId) ||
                (customerApprovals.declined_items || []).some((a: any) => String(a.source_id || '') === itemSourceId)
              )
            );
            // Tek mirror stamps item.authorization_id even when there's no
            // real signed doc (Tek tracks approval state outside HAIST).
            // If customer_approvals exists for this conv AND is unsigned AND
            // contains this item, the per-item authorization_id is a
            // mirror placeholder — drop it. Otherwise (no customer_approvals,
            // or signed) we trust authorization_id as paper/digital flows do.
            const itemAuthDocIsTekMirrorShell = caContainsThisItem && !caHasSignature;
            const hasItemAuthDoc = rawItemAuthDoc && !itemAuthDocIsTekMirrorShell;
            const hasLegalDoc = !!(
              hasItemAuthDoc ||
              (caHasSignature && caContainsThisItem)
            );
            // INVOICE-track parallel of hasLegalDoc. Invoice signatures stamp
            // invoice_authorization_id WITHOUT touching item_status (the
            // estimate decision), so the item-auth branch isn't gated on a
            // terminal status. The Tek mirror only ever writes the estimate
            // block, so there's no unsigned-shell shadow to filter here.
            const invCaHasSignature = isRealCustomerSignature(invoiceApprovals);
            const invCaContainsThisItem = !!(
              itemSourceId && invoiceApprovals && (
                (invoiceApprovals.approved_items || []).some((a: any) => String(a.source_id || '') === itemSourceId) ||
                (invoiceApprovals.declined_items || []).some((a: any) => String(a.source_id || '') === itemSourceId)
              )
            );
            const hasInvoiceLegalDoc = !!(
              (item as any).invoice_authorization_id ||
              (invCaHasSignature && invCaContainsThisItem)
            );
            const isLockedStatus = hasLegalDoc;
            // Selection (in_batch) is now independent from approve/decline
            // intent (item_status). Both can be true at once: the writer can
            // tag an item "in this signature batch" AND mark their plan as
            // approve/decline. Back-compat: if in_batch is undefined on
            // existing rows, fall back to the old item_status='selected'
            // signal so existing data still reads correctly.
            const inBatch: boolean = (item as any).in_batch === true
              || ((item as any).in_batch === undefined && (itemStatus === 'selected' || itemStatus === 'unauthorized'));
            const isChecked = inBatch;
            // Intent (item_status) and selection (in_batch) update independently.
            // Materialize in_batch on intent change so the implicit selection
            // (rows with item_status='selected' read as in_batch=true via the
            // back-compat fallback) becomes explicit before item_status flips
            // off 'selected' — otherwise the row's in_batch derivation would
            // suddenly read false and the select box would visually disappear.
            const setIntent = (target: 'approved' | 'declined' | 'unauthorized') => {
              if (hasLegalDoc) return;
              // Picking approve/decline IS a selection — pull the row into the
              // batch even if the writer had previously toggled Select off.
              // Otherwise the 3-method toolbar excludes it (in_batch=false)
              // and the writer sees a marked-intent row with the signature
              // buttons mysteriously disabled. Clearing intent (target=
              // 'unauthorized') leaves in_batch alone — the writer is just
              // un-marking their decision, not changing the batch membership.
              const nextInBatch = target === 'unauthorized' ? inBatch : true;
              setQuoteItems((prev) => prev.map((q, idx) =>
                idx === i ? ({ ...q, in_batch: nextInBatch, item_status: target } as any) : q
              ));
            };
            const handleSelectToggle = () => {
              // No hasLegalDoc guard: selection (in_batch) stays editable even
              // after the estimate is signed, so the writer can pick items for
              // the invoice batch. Signing a doc never freezes selection — the
              // covered state shows via the doc pills / Unlink handles instead.
              setQuoteItems((prev) => prev.map((q, idx) =>
                idx === i ? ({ ...q, in_batch: !inBatch } as any) : q
              ));
              // Persistence: the debounced quoteApi.upsert (replace=true)
              // round-trips items[] including this new in_batch flag. We
              // no longer call the legacy selectItems endpoint here — that
              // path mutated item_status, which would un-set any approve/
              // decline intent the writer had marked.
            };
            const statusPill = (() => {
              // No pill while the row is still editable (no signed doc yet).
              // The mechanic's intent (Select / Approve / Decline) shows
              // via the inline three-button group rendered after the price.
              if (!hasLegalDoc) {
                return null;
              }
              // Has legal doc → render the locked colored pill + ↺ revoke.
              // item_status here is 'approved' or 'declined' — whichever was
              // signed for.
              // EST badge — green when approved, red when declined. Short label
              // keeps it parallel with the INV badge; direction stays legible via
              // color + the title tooltip on the box below.
              const map: Record<'approved' | 'declined', { label: string; bg: string; fg: string }> = {
                approved: { label: 'EST', bg: 'rgba(34,197,94,0.18)',  fg: '#22C55E' },
                declined: { label: 'EST', bg: 'rgba(239,68,68,0.18)',  fg: '#EF4444' },
              };
              // Direction: paper/digital signature stamps the decision
              // directly on item.item_status — trust that first. Fall back to
              // customer_approvals lists (whole-quote customer-portal flow).
              const isApprovedByItem = itemStatus === 'approved';
              const isDeclinedByItem = itemStatus === 'declined';
              const isApprovedByCA =
                (customerApprovals?.approved_items || []).some((a: any) => String(a.source_id || '') === itemSourceId);
              const docDirection: 'approved' | 'declined' =
                isApprovedByItem || isApprovedByCA
                  ? 'approved'
                  : (isDeclinedByItem ? 'declined' : 'declined');
              const s = map[docDirection];
              const handleCancelItem = async () => {
                if (!conversationId || !itemSourceId) return;
                const verb = itemStatus === 'approved' ? 'approval' : 'decline';
                if (!window.confirm(`Cancel ${verb} for this item? It'll go back to Selected and need a new signature.`)) return;
                try {
                  const updated = await quoteApi.revokeApprovals(conversationId, { source_ids: [itemSourceId] });
                  if (updated.items?.length) {
                    setQuoteItems(updated.items.map((it: any) => ({ ...it, label: it.label ?? '' })));
                  }
                  setCustomerApprovals(updated.customer_approvals ?? null);
                } catch (err: any) {
                  alert(`Could not cancel: ${err?.response?.data?.detail || err?.message || 'unknown error'}`);
                }
              };
              // Legal-doc indicator per item — same signature method covers
              // every approved/declined item in the current customer_approvals
              // set. Show "📄 signed paper" (clickable to the photo) or
              // "✍️ signed online" so the mechanic can see at a glance which
              // legal doc backs this specific item.
              const ca = customerApprovals as any;
              const sigMethod = ca?.signature_method as string | undefined;
              const sigBlob = ca?.signature_evidence_blob as string | undefined;
              const sigHref = sigBlob ? `https://storage.gethaist.com/${sigBlob}` : null;
              const sigLabel = sigMethod === 'paper_photo'
                ? '📄 signed paper'
                : sigMethod === 'live'
                  ? '✍️ signed online'
                  : null;
              return (
                <Group gap={4} wrap="nowrap" style={{ flexShrink: 0 }}>
                  <Box title={docDirection === 'approved' ? 'Estimate approved' : 'Estimate declined'} style={{
                    padding: '1px 6px', borderRadius: 4, background: s.bg, color: s.fg,
                    fontSize: 10, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase',
                  }}>{s.label}</Box>
                  {sigLabel && (
                    sigHref ? (
                      <a
                        href={sigHref}
                        target="_blank"
                        rel="noreferrer"
                        title="View the signed document"
                        style={{ fontSize: 10, color: '#71717A', textDecoration: 'underline' }}
                      >{sigLabel}</a>
                    ) : (
                      <Text size="10px" c={TEXT_MUTED}>{sigLabel}</Text>
                    )
                  )}
                </Group>
              );
            })();

            // INVOICE-doc indicator — parallel to statusPill but for the invoice
            // track. Blue, to distinguish from the green/red estimate (EST) badge.
            // Direction from invoiceApprovals lists (the invoice_authorization_id
            // path carries no per-item decision, so default to approved framing —
            // an invoice attests billable work). Compact, matches the EST sizing.
            const invoiceBadge = (() => {
              if (!hasInvoiceLegalDoc) return null;
              const invA = invoiceApprovals as any;
              const invApprovedByCA = (invA?.approved_items || []).some((a: any) => String(a.source_id || '') === itemSourceId);
              const invDeclinedByCA = (invA?.declined_items || []).some((a: any) => String(a.source_id || '') === itemSourceId);
              const invDir: 'approved' | 'declined' = invDeclinedByCA && !invApprovedByCA ? 'declined' : 'approved';
              const bg = invDir === 'declined' ? 'rgba(239,68,68,0.18)' : 'rgba(59,130,246,0.18)';
              const fg = invDir === 'declined' ? '#EF4444' : '#60A5FA';
              const sigMethod = invA?.signature_method as string | undefined;
              const sigBlob = invA?.signature_evidence_blob as string | undefined;
              const sigHref = sigBlob ? `https://storage.gethaist.com/${sigBlob}` : null;
              const sigLabel = sigMethod === 'paper_photo' ? '📄 signed paper'
                : sigMethod === 'live' ? '✍️ signed online' : null;
              return (
                <Group gap={4} wrap="nowrap" style={{ flexShrink: 0 }}>
                  <Box title={invDir === 'approved' ? 'Invoice approved' : 'Invoice declined'} style={{
                    padding: '1px 6px', borderRadius: 4, background: bg, color: fg,
                    fontSize: 10, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase',
                  }}>INV</Box>
                  {sigLabel && (
                    sigHref ? (
                      <a href={sigHref} target="_blank" rel="noreferrer"
                        title="View the signed invoice document"
                        style={{ fontSize: 10, color: '#71717A', textDecoration: 'underline' }}
                      >{sigLabel}</a>
                    ) : (
                      <Text size="10px" c={TEXT_MUTED}>{sigLabel}</Text>
                    )
                  )}
                </Group>
              );
            })();

            // Border policy:
            //   With legal doc on file  → green (approved) / red (declined) —
            //                              the customer's signed decision.
            //   Without legal doc       → blue if in the signature batch,
            //                              else neutral. Intent (approve/decline)
            //                              alone is NOT enough to color the row;
            //                              it's just the writer's plan until a
            //                              doc lands.
            const rowBorder = hasLegalDoc
              ? (itemStatus === 'approved'
                  ? 'rgba(34,197,94,0.45)'
                  : itemStatus === 'declined'
                    ? 'rgba(239,68,68,0.45)'
                    : BORDER)
              : (inBatch ? 'rgba(59,130,246,0.6)' : BORDER);
            // Lock this row's FIELDS when it's individually signed OR when the
            // conversation has ANY signed doc (whole-quote read-only). The Select
            // box + Unlink are zIndex-4 floats above this overlay, so they stay
            // clickable on every row. `!opts.inModal` keeps the price-lookup modal
            // draft editor unlocked.
            const itemAuthLocked = isItemAuthLocked(item) || (docLocked && !opts.inModal);
            return (
              <Box key={i} style={{ position: 'relative', margin: '6px 12px', padding: '12px 14px', borderRadius: 10, border: `1px solid ${rowBorder}`, background: '#111114', opacity: isHidden ? 0.4 : 1, transition: 'opacity 0.15s, border-color 0.15s' }}>
                {itemAuthLocked && (
                  // Read-only overlay — NO dim/blur so the writer can fully read the
                  // signed line item. This transparent layer only intercepts clicks
                  // so fields aren't editable; the Unlink-doc button in Row 1 sits
                  // ABOVE it (zIndex 4) as the escape hatch. A small corner chip
                  // marks the locked state without covering the content.
                  <Box
                    role="button"
                    title="Locked by customer authorization — Unlink doc to edit"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
                    style={{
                      position: 'absolute', inset: 0, borderRadius: 10,
                      background: 'transparent',
                      cursor: 'not-allowed', zIndex: 3,
                    }}
                  >
                    <Box style={{
                      position: 'absolute', bottom: 8, right: 10,
                      padding: '2px 8px', borderRadius: 999,
                      background: 'rgba(245,158,11,0.16)', border: '1px solid rgba(245,158,11,0.4)',
                      pointerEvents: 'none', display: 'inline-flex', alignItems: 'center', gap: 4,
                    }}>
                      <Text size="11px" fw={700} c="#F59E0B" style={{ letterSpacing: '0.02em' }}>
                        🔒 Locked
                      </Text>
                    </Box>
                  </Box>
                )}
                {/* Row 1: eye/delete + name + total + approve/decline */}
                <Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {((item.label ?? '').trim() || isHidden) ? (
                    <UnstyledButton onClick={toggleHidden} title={isHidden ? 'Show' : 'Hide'} style={{
                      flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, borderRadius: 4,
                      background: isHidden ? 'rgba(239,68,68,0.12)' : 'transparent', border: isHidden ? '1px solid rgba(239,68,68,0.3)' : '1px solid transparent',
                    }}>
                      {isHidden ? <IconEyeOff size={13} color="#EF4444" /> : <IconEye size={13} color={TEXT_MUTED} />}
                    </UnstyledButton>
                  ) : (
                    // Empty (never-saved) row: offer an × to remove it entirely
                    // from the quote. Hiding makes no sense — there's nothing
                    // to hide. Deletion is local-only; the next autosave
                    // persists the trimmed items list.
                    <UnstyledButton
                      onClick={() => setQuoteItems((prev) => prev.filter((_, idx) => idx !== i))}
                      title="Remove this empty row"
                      style={{
                        flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, borderRadius: 4,
                        color: '#71717A', fontSize: 16, fontWeight: 700, lineHeight: 1,
                      }}
                    >
                      ×
                    </UnstyledButton>
                  )}
                  {(editingDescIdx === i || !(item.label ?? '').trim()) ? (
                    <TextInput
                      ref={(el) => { if (el) cellRefs.current.set(`${i}-0`, el); }}
                      placeholder={(item as any).tek_job_id ? `Job ${(item as any).tek_job_id}` : 'Job name'}
                      value={item.label ?? ''}
                      size="xs"
                      style={{ flex: 1 }}
                      styles={{ input: { background: '#111113', borderColor: item.label ? '#3F3F46' : BORDER, color: TEXT, fontWeight: 600 } }}
                      onChange={(e) => { const v = e.currentTarget.value; setQuoteItems((prev) => prev.map((q, idx) => idx === i ? { ...q, label: v } : q)); }}
                      onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
                      onBlur={(e) => {
                        const lbl = e.currentTarget.value.trim();
                        if (!lbl) return;
                        const { description, parts, labor } = buildDescRef.current(lbl);
                        if (description || parts || labor) {
                          setQuoteItems((prev) => prev.map((q, idx) => idx === i ? { ...q, description: q.description || description, parts: q.parts || parts, labor: q.labor || labor } : q));
                        }
                      }}
                    />
                  ) : (
                    <Text size="sm" fw={600} c={TEXT} style={{ flex: 1, minWidth: 0 }} truncate>
                      {item.label}
                    </Text>
                  )}
                  {statusPill}
                  {invoiceBadge}
                  {item.label && (
                    <Group gap={6} wrap="nowrap" style={{ flexShrink: 0 }}>
                      {/* State-machine gated rendering (sig-required shops):
                          unauthorized / selected → NO buttons. Item needs a
                          signed auth doc before approve/decline are possible.
                          approved / declined → state pill + Unmark.

                          Non-sig-required shops keep the legacy direct
                          approve/decline buttons (toggleApproval). */}
                      {(() => {
                        // Per-item, per-track "Unlink" affordance. It does NOT
                        // delete the legal document; the doc stays on file in
                        // Legal docs for audit. Only the item's tie to that
                        // track's doc is cleared so it can be re-authorized.
                        // Two independent tracks → up to two buttons (estimate
                        // back to Selected; invoice just drops its lock).
                        const mkUnlink = (track: 'estimate' | 'invoice', label: string) => (
                          // position:relative + zIndex:4 floats the button ABOVE
                          // the row's locked overlay (zIndex 3) so the user keeps
                          // a working escape hatch even with everything else
                          // click-blocked.
                          <Box key={track} style={{ position: 'relative', zIndex: 4 }}>
                            <UnstyledButton
                              onClick={async () => {
                                if (!conversationId || !itemSourceId) return;
                                if (!window.confirm(`Unlink this item from its ${track} legal document? The document stays on file in Legal docs.`)) return;
                                try {
                                  const updated = await quoteApi.revokeApprovals(conversationId, { source_ids: [itemSourceId], doc_type: track });
                                  if (updated.items?.length) {
                                    setQuoteItems(updated.items.map((it: any) => ({ ...it, label: it.label ?? '' })));
                                  }
                                  setCustomerApprovals(updated.customer_approvals ?? null);
                                  setInvoiceApprovals(updated.invoice_approvals ?? null);
                                } catch (e: any) {
                                  console.error('[ITEM_STATUS] unlink failed', e);
                                  alert(`Could not unlink: ${e?.response?.data?.detail || e?.message || 'unknown error'}`);
                                }
                              }}
                              title={`Unlink this item from its ${track} legal document — the doc stays on file`}
                              style={{
                                height: 30, padding: '0 10px', borderRadius: 6,
                                border: '1px solid #3F3F46', color: TEXT_MUTED,
                                fontSize: 11, fontWeight: 600,
                                display: 'inline-flex', alignItems: 'center',
                                background: '#18181B',
                              }}
                            >{label}</UnstyledButton>
                          </Box>
                        );
                        const btns: React.ReactNode[] = [];
                        const estimateUnlinkable = hasLegalDoc && (itemStatus === 'approved' || itemStatus === 'declined');
                        if (estimateUnlinkable) btns.push(mkUnlink('estimate', hasInvoiceLegalDoc ? 'Unlink estimate' : 'Unlink doc'));
                        if (hasInvoiceLegalDoc) btns.push(mkUnlink('invoice', 'Unlink invoice'));
                        return btns.length ? <>{btns}</> : null;
                      })()}
                      {/* Per-item receipt-photo button removed — legal docs
                          are captured per-batch via the three-method toolbar
                          at the top (Send for approval / Digital signature /
                          Document upload). */}
                    </Group>
                  )}
                  {rowSum > 0 && <Text size="14px" fw={700} c={TEXT} style={{ flexShrink: 0 }}>{fmtQuote(rowSum)}</Text>}
                  {/* Per-row picker: Select-only when no legal doc on file.
                      Approve/Decline intent buttons are intentionally hidden
                      here — the decision is captured at signature time via
                      the 3-method toolbar, so a pre-signature 'intent' state
                      just confuses what's actually authorized. With a legal
                      doc the colored pill + Remove doc handle it elsewhere. */}
                  {/* Select box stays available even after the estimate is signed
                      — the writer still needs to (de)select items for the INVOICE
                      track. position:relative + zIndex:4 floats it ABOVE the row's
                      locked overlay (zIndex 3) so it stays clickable on a
                      signed/locked row, like the Unlink-doc button. */}
                  {((item as any).label ?? '').trim() && !opts.inModal && (
                    <Group gap={4} wrap="nowrap" style={{ flexShrink: 0, marginLeft: 8, position: 'relative', zIndex: 4 }}>
                      <UnstyledButton
                        onClick={handleSelectToggle}
                        title={isChecked ? 'Unselect' : 'Select for bulk signature'}
                        style={{
                          flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                          width: 22, height: 22, borderRadius: 4,
                          border: `1px solid ${isChecked ? '#6366F1' : '#3F3F46'}`,
                          background: isChecked ? '#4F46E5' : 'transparent',
                          color: 'white', fontSize: 12, fontWeight: 700, lineHeight: 1,
                          cursor: 'pointer',
                        }}
                      >{isChecked ? '✓' : ''}</UnstyledButton>
                    </Group>
                  )}
                </Box>

                {/* Row 2: Line items table */}
                <Box mt={8}>
                  {/* ── Labor lines ── */}
                  {((item as any).labors?.length > 0) && (
                    <Box mb={8}>
                      <Text size="13px" fw={700} c={TEXT} tt="uppercase" mb={4}>Labor</Text>
                      {/* Column headers — only render when there are labor lines to align to */}
                      {(item as any).labors?.length > 0 && (
                        <Box style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '4px 24px 4px 0', borderBottom: `1px solid ${BORDER}` }}>
                          <Text size="11px" c={TEXT_MUTED} tt="uppercase" fw={600} style={{ flex: 1, letterSpacing: '0.04em' }}>Description</Text>
                          <Text size="11px" c={TEXT_MUTED} tt="uppercase" fw={600} style={{ width: 64, textAlign: 'right', letterSpacing: '0.04em' }}>Hours</Text>
                          <Text size="11px" c={TEXT_MUTED} tt="uppercase" fw={600} style={{ width: 90, textAlign: 'right', letterSpacing: '0.04em' }}>Rate / hr</Text>
                          <Text size="11px" c={TEXT_MUTED} tt="uppercase" fw={600} style={{ width: 100, textAlign: 'right', letterSpacing: '0.04em' }}>Total</Text>
                        </Box>
                      )}
                      <DndContext
                        sensors={laborDragSensors}
                        collisionDetection={closestCenter}
                        onDragEnd={(ev: DragEndEvent) => {
                          const { active, over } = ev;
                          if (!over || active.id === over.id) return;
                          setQuoteItems((prev) => prev.map((q, idx) => {
                            if (idx !== i) return q;
                            const labors = (q as any).labors || [];
                            const oldIdx = labors.findIndex((ll: any, lli: number) => laborSortId(ll, lli) === active.id);
                            const newIdx = labors.findIndex((ll: any, lli: number) => laborSortId(ll, lli) === over.id);
                            if (oldIdx < 0 || newIdx < 0) return q;
                            return { ...q, labors: arrayMove(labors, oldIdx, newIdx) };
                          }));
                        }}
                      >
                        <SortableContext
                          items={((item as any).labors || []).map((l: any, li: number) => laborSortId(l, li))}
                          strategy={verticalListSortingStrategy}
                        >
                          {((item as any).labors || []).map((l: any, li: number) => (
                            <SortableLaborRow
                              key={laborSortId(l, li)}
                              id={laborSortId(l, li)}
                              l={l}
                              li={li}
                              i={i}
                              shopLaborRate={shopLaborRate}
                              BORDER={BORDER}
                              TEXT={TEXT}
                              TEXT_SECONDARY={TEXT_SECONDARY}
                              TEXT_MUTED={TEXT_MUTED}
                              setQuoteItems={setQuoteItems}
                              authLocked={isItemAuthLocked(item) || editingDescIdx !== i}
                            />
                          ))}
                        </SortableContext>
                      </DndContext>
                    </Box>
                  )}
                  {/* ── Get vendor pricing — always visible on labeled lines ── */}
                  {item.label && !opts.inModal && (
                    <Box mb={6} style={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <UnstyledButton
                        onClick={() => setPriceModalIdx(i)}
                        title="Look up vendor pricing"
                        style={{ fontSize: 11, color: '#60A5FA', padding: '2px 8px', border: '1px solid #3F3F46', borderRadius: 4 }}
                      >
                        💲 Get vendor pricing
                      </UnstyledButton>
                    </Box>
                  )}
                  {/* ── Parts lines ── */}
                  {((item as any).partLines?.length > 0) && (
                    <Box mb={8}>
                      <Group justify="space-between" mb={4} wrap="nowrap">
                        <Text size="13px" fw={700} c={TEXT} tt="uppercase">Parts</Text>
                        {false && item.label && (
                          <UnstyledButton
                            onClick={() => setPriceModalIdx(i)}
                            title="Look up vendor pricing"
                            style={{ fontSize: 11, color: '#60A5FA', padding: '2px 8px', border: '1px solid #3F3F46', borderRadius: 4 }}
                          >
                            💲 Get vendor pricing
                          </UnstyledButton>
                        )}
                      </Group>
                      {((item as any).partLines || []).map((p: any, pi: number) => {
                        const upd = (field: string, val: any) => setQuoteItems((prev) => prev.map((q, idx) => idx === i ? { ...q, partLines: (q as any).partLines?.map((pp: any, ppi: number) => ppi === pi ? { ...pp, [field]: val } : pp) } : q));
                        const lineTotal = ((parseFloat(p.retail || '0') || 0) * (p.qty || 1));
                        return (
                        <Box key={pi} style={{ padding: '8px 0', borderBottom: `1px solid ${BORDER}` }}>
                          {/* Row 1: Brand, Name, Qty, Retail, Total */}
                          <Box style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <input value={p.brand || ''} placeholder="Brand" onChange={(e) => upd('brand', e.target.value)}
                              style={{ width: 80, background: 'transparent', border: `1px solid ${BORDER}`, color: TEXT_SECONDARY, fontSize: 13, borderRadius: 4, padding: '5px 8px' }} />
                            <input value={p.name || ''} placeholder="Part name" onChange={(e) => upd('name', e.target.value)}
                              style={{ flex: 1, background: 'transparent', border: `1px solid ${BORDER}`, color: TEXT, fontSize: 13, borderRadius: 4, padding: '5px 8px', minWidth: 80 }} />
                            <input value={String(p.qty ?? 1)} placeholder="1" onChange={(e) => upd('qty', parseInt(e.target.value) || 1)}
                              style={{ width: 40, background: 'transparent', border: `1px solid ${BORDER}`, color: TEXT, fontSize: 13, borderRadius: 4, padding: '5px 4px', textAlign: 'center' }} />
                            <input value={p.retail || ''} placeholder="Price" onChange={(e) => upd('retail', e.target.value)}
                              style={{ width: 65, background: 'transparent', border: `1px solid ${BORDER}`, color: TEXT, fontSize: 13, borderRadius: 4, padding: '5px 6px', textAlign: 'right' }} />
                            <Text size="14px" fw={700} c={TEXT} style={{ width: 65, textAlign: 'right' }}>${lineTotal.toFixed(0)}</Text>
                            {editingDescIdx === i && <UnstyledButton onClick={() => setQuoteItems((prev) => prev.map((q, idx) => idx === i ? { ...q, partLines: ((q as any).partLines || []).filter((_: any, j: number) => j !== pi) } : q))} style={{ color: '#ef4444', fontSize: 15, fontWeight: 700, lineHeight: 1, padding: '0 2px' }}>×</UnstyledButton>}
                          </Box>
                          {/* Row 2: Part #, Details */}
                          <Box style={{ display: 'flex', gap: 6, marginTop: 4, paddingLeft: 0 }}>
                            <input value={p.partNumber || ''} placeholder="Part #" onChange={(e) => upd('partNumber', e.target.value)}
                              style={{ width: 80, background: 'transparent', border: `1px solid ${BORDER}`, color: TEXT_MUTED, fontSize: 11, borderRadius: 4, padding: '3px 8px' }} />
                            <input value={p.details || ''} placeholder="Details" onChange={(e) => upd('details', e.target.value)}
                              style={{ flex: 1, background: 'transparent', border: `1px solid ${BORDER}`, color: TEXT_MUTED, fontSize: 11, borderRadius: 4, padding: '3px 8px' }} />
                          </Box>
                        </Box>
                        );
                      })}
                    </Box>
                  )}
                  {/* ── Sublet lines ── */}
                  {((item as any).subletLines?.length > 0) && (
                    <Box mb={8}>
                      <Text size="13px" fw={700} c="#818cf8" tt="uppercase" mb={4}>Sublet</Text>
                      {((item as any).subletLines || []).map((s: any, si: number) => (
                        <Box key={si} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 0', borderBottom: `1px solid ${BORDER}` }}>
                          <input value={s.name || ''} placeholder="Description" onChange={(e) => { const v = e.target.value; setQuoteItems((prev) => prev.map((q, idx) => idx === i ? { ...q, subletLines: (q as any).subletLines?.map((ss: any, ssi: number) => ssi === si ? { ...ss, name: v } : ss) } : q)); }}
                            style={{ flex: 1, background: 'transparent', border: 'none', color: TEXT_SECONDARY, fontSize: 14, outline: 'none', minWidth: 80 }} />
                          <Text size="13px" c={TEXT_MUTED}>$</Text>
                          <input value={s.price || ''} placeholder="0" onChange={(e) => { const v = e.target.value; setQuoteItems((prev) => prev.map((q, idx) => idx === i ? { ...q, subletLines: (q as any).subletLines?.map((ss: any, ssi: number) => ssi === si ? { ...ss, price: v } : ss) } : q)); }}
                            style={{ width: 55, background: 'transparent', border: 'none', borderBottom: '1px solid #3F3F46', color: '#818cf8', fontSize: 14, borderRadius: 0, padding: '4px 4px', textAlign: 'right' }} />
                          {editingDescIdx === i && <UnstyledButton onClick={() => setQuoteItems((prev) => prev.map((q, idx) => idx === i ? { ...q, subletLines: ((q as any).subletLines || []).filter((_: any, j: number) => j !== si) } : q))} style={{ color: '#ef4444', fontSize: 16, fontWeight: 700, lineHeight: 1, padding: '0 4px' }}>×</UnstyledButton>}
                        </Box>
                      ))}
                    </Box>
                  )}
                  {/* ── Fee lines ── */}
                  {((item as any).feeLines?.length > 0) && (
                    <Box mb={8}>
                      <Text size="13px" fw={700} c={TEXT_SECONDARY} tt="uppercase" mb={4}>Fees</Text>
                      {((item as any).feeLines || []).map((f: any, fi: number) => (
                        <Box key={fi} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 0', borderBottom: `1px solid ${BORDER}` }}>
                          <input value={f.name || ''} placeholder="Fee name" onChange={(e) => { const v = e.target.value; setQuoteItems((prev) => prev.map((q, idx) => idx === i ? { ...q, feeLines: (q as any).feeLines?.map((ff: any, ffi: number) => ffi === fi ? { ...ff, name: v } : ff) } : q)); }}
                            style={{ flex: 1, background: 'transparent', border: 'none', color: TEXT_SECONDARY, fontSize: 14, outline: 'none', minWidth: 80 }} />
                          <Text size="13px" c={TEXT_MUTED}>$</Text>
                          <input value={f.amount || ''} placeholder="0" onChange={(e) => { const v = e.target.value; setQuoteItems((prev) => prev.map((q, idx) => idx === i ? { ...q, feeLines: (q as any).feeLines?.map((ff: any, ffi: number) => ffi === fi ? { ...ff, amount: v } : ff) } : q)); }}
                            style={{ width: 55, background: 'transparent', border: 'none', borderBottom: '1px solid #3F3F46', color: TEXT, fontSize: 14, borderRadius: 0, padding: '4px 4px', textAlign: 'right' }} />
                          {editingDescIdx === i && <UnstyledButton onClick={() => setQuoteItems((prev) => prev.map((q, idx) => idx === i ? { ...q, feeLines: ((q as any).feeLines || []).filter((_: any, j: number) => j !== fi) } : q))} style={{ color: '#ef4444', fontSize: 16, fontWeight: 700, lineHeight: 1, padding: '0 4px' }}>×</UnstyledButton>}
                        </Box>
                      ))}
                    </Box>
                  )}
                  {/* ── Discount lines ── */}
                  {((item as any).discountLines?.length > 0) && (
                    <Box mb={8}>
                      <Text size="13px" fw={700} c="#f87171" tt="uppercase" mb={4}>Discount</Text>
                      {((item as any).discountLines || []).map((d: any, di: number) => (
                        <Box key={di} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 0', borderBottom: `1px solid ${BORDER}` }}>
                          <input value={d.name || ''} placeholder="Discount name" onChange={(e) => { const v = e.target.value; setQuoteItems((prev) => prev.map((q, idx) => idx === i ? { ...q, discountLines: (q as any).discountLines?.map((dd: any, ddi: number) => ddi === di ? { ...dd, name: v } : dd) } : q)); }}
                            style={{ flex: 1, background: 'transparent', border: 'none', color: TEXT_SECONDARY, fontSize: 14, outline: 'none', minWidth: 80 }} />
                          <Text size="13px" c={TEXT_MUTED}>-$</Text>
                          <input value={d.amount || ''} placeholder="0" onChange={(e) => { const v = e.target.value; setQuoteItems((prev) => prev.map((q, idx) => idx === i ? { ...q, discountLines: (q as any).discountLines?.map((dd: any, ddi: number) => ddi === di ? { ...dd, amount: v } : dd) } : q)); }}
                            style={{ width: 55, background: 'transparent', border: 'none', borderBottom: '1px solid #3F3F46', color: '#f87171', fontSize: 14, borderRadius: 0, padding: '4px 4px', textAlign: 'right' }} />
                          {editingDescIdx === i && <UnstyledButton onClick={() => setQuoteItems((prev) => prev.map((q, idx) => idx === i ? { ...q, discountLines: ((q as any).discountLines || []).filter((_: any, j: number) => j !== di) } : q))} style={{ color: '#ef4444', fontSize: 16, fontWeight: 700, lineHeight: 1, padding: '0 4px' }}>×</UnstyledButton>}
                        </Box>
                      ))}
                    </Box>
                  )}
                  {/* + Add buttons — only while this row is in edit mode. */}
                  {editingDescIdx === i && (
                  <Box mt={6} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
                    <Group gap={6}>
                      <UnstyledButton onClick={() => setQuoteItems((prev) => prev.map((q, idx) => idx === i ? { ...q, labors: [...((q as any).labors || []), { description: '', hours: '', rate: shopLaborRate ? String(shopLaborRate) : '', total: '' }] } : q))}
                        style={{ fontSize: 12, color: '#60a5fa', padding: '4px 12px', borderRadius: 6, border: '1px solid #1e3a5f', background: 'rgba(96,165,250,0.08)' }}>+ labor</UnstyledButton>
                      <UnstyledButton onClick={() => setQuoteItems((prev) => prev.map((q, idx) => idx === i ? { ...q, partLines: [...((q as any).partLines || []), { name: '', partNumber: '', qty: 1, cost: '', retail: '' }] } : q))}
                        style={{ fontSize: 12, color: '#f59e0b', padding: '4px 12px', borderRadius: 6, border: '1px solid #92400e', background: 'rgba(245,158,11,0.08)' }}>+ part</UnstyledButton>
                    </Group>
                    <Group gap={6} wrap="nowrap">
                      <UnstyledButton onClick={() => setQuoteItems((prev) => prev.map((q, idx) => idx === i ? { ...q, subletLines: [...((q as any).subletLines || []), { name: '', vendor: '', cost: '', price: '' }] } : q))}
                        style={{ fontSize: 12, color: '#818cf8', padding: '4px 10px', borderRadius: 6, border: '1px solid #3730a3', background: 'rgba(129,140,248,0.08)' }}>+ sublet</UnstyledButton>
                      <UnstyledButton onClick={() => setQuoteItems((prev) => prev.map((q, idx) => idx === i ? { ...q, feeLines: [...((q as any).feeLines || []), { name: '', amount: '' }] } : q))}
                        style={{ fontSize: 12, color: '#4ade80', padding: '4px 10px', borderRadius: 6, border: '1px solid #166534', background: 'rgba(74,222,128,0.08)' }}>+ fee</UnstyledButton>
                      <UnstyledButton onClick={() => setQuoteItems((prev) => prev.map((q, idx) => idx === i ? { ...q, discountLines: [...((q as any).discountLines || []), { name: '', amount: '' }] } : q))}
                        style={{ fontSize: 12, color: '#f87171', padding: '4px 10px', borderRadius: 6, border: '1px solid #991b1b', background: 'rgba(248,113,113,0.08)' }}>+ disc</UnstyledButton>
                    </Group>
                  </Box>
                  )}
                  {/* Job total */}
                  {rowSum > 0 && (
                    <Box style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 8, marginTop: 8, borderTop: `1px solid ${BORDER}` }}>
                      <Group gap={8}>
                        <Text size="14px" fw={700} c={TEXT}>Total</Text>
                        {(item as any).status && (
                          <Text size="11px" fw={700} style={{
                            padding: '2px 8px', borderRadius: 4,
                            background: (item as any).status === 'Approved' ? 'rgba(34,197,94,0.15)' : (item as any).status === 'Declined' ? 'rgba(239,68,68,0.15)' : 'rgba(161,161,170,0.15)',
                            color: (item as any).status === 'Approved' ? '#22C55E' : (item as any).status === 'Declined' ? '#EF4444' : TEXT_MUTED,
                          }}>{(item as any).status}</Text>
                        )}
                        {(item as any).technician && <Text size="11px" c={TEXT_MUTED}>{(item as any).technician}</Text>}
                      </Group>
                      <Text size="14px" fw={700} c={TEXT}>{fmtQuote(rowSum)}</Text>
                    </Box>
                  )}
                </Box>

                {/* Row 3: photos + generate (only when expanded) */}
                <Group gap={6} mt={8} pl={0} wrap="wrap" align="center" style={{ display: 'flex' }}>
                  {itemPhotos.map((photo, pi) => {
                    // Canonical (edited) version — opens the annotator. The
                    // pristine original is preserved in `original_url` but not shown.
                    const editUrl = photoDisplayUrl(photo as PhotoLike);
                    return (
                      <Box key={pi} style={{ width: 56, height: 42, borderRadius: 6, overflow: 'hidden', border: `1px solid ${BORDER}`, cursor: 'pointer' }} onClick={() => setAnnotateQuote({ url: editUrl, index: i, photoIndex: pi })}>
                        <img src={thumb(editUrl)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      </Box>
                    );
                  })}
                  <UnstyledButton
                    onClick={() => { const input = document.createElement('input'); input.type = 'file'; input.accept = 'image/*,video/*'; input.multiple = true; input.onchange = () => { if (input.files) handlePhotoUpload(input.files); }; input.click(); }}
                    style={{ width: 56, height: 42, borderRadius: 6, border: `1px dashed #52525B`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: TEXT_MUTED }}
                    onDragOver={(e) => { e.preventDefault(); (e.currentTarget as HTMLElement).style.borderColor = BLUE; }}
                    onDragLeave={(e) => { (e.currentTarget as HTMLElement).style.borderColor = '#3F3F46'; }}
                    onDrop={(e) => { e.preventDefault(); (e.currentTarget as HTMLElement).style.borderColor = '#3F3F46'; if (e.dataTransfer.files.length) handlePhotoUpload(e.dataTransfer.files); }}
                  >
                    <IconCamera size={15} />
                  </UnstyledButton>
                  {/* Violet ✨ button removed — QuoteSuggestionCard below
                      the textarea is the only AI-suggestion surface. */}
                  {itemState === 'analyzing' && <Group gap={4}><Loader size={10} color="#A855F7" /><Text size="9px" c="#A855F7" fw={600}>Suggesting…</Text></Group>}
                </Group>

                {/* Row 4: description — explicit Edit / Save / Cancel (no autosave) */}
                <Box mt={6} pl={0}>
                  {(() => {
                    const isEditingDesc = editingDescIdx === i;
                    const descLocked = isItemAuthLocked(item) || (docLocked && !opts.inModal);
                    const descValue = isEditingDesc ? descDraft : (item.description || '');
                    return (
                      <>
                        <Textarea
                          placeholder={isEditingDesc ? 'Notes for this line item...' : (item.description ? undefined : 'No notes yet…')}
                          value={descValue}
                          onChange={(e) => { if (isEditingDesc) setDescDraft(e.currentTarget.value); }}
                          readOnly={!isEditingDesc}
                          autosize minRows={isEditingDesc ? 3 : 1} maxRows={6}
                          onFocus={() => setFocusedDescIdx(i)}
                          onBlur={() => setFocusedDescIdx((prev) => prev === i ? null : prev)}
                          styles={{ input: { background: isEditingDesc ? '#111113' : 'transparent', borderColor: isEditingDesc ? '#3F3F46' : 'transparent', color: descValue ? '#D4D4D8' : '#9CA3AF', fontSize: 13, lineHeight: 1.5, transition: 'background 0.15s, border-color 0.15s', padding: isEditingDesc ? '6px 8px' : '2px 0', cursor: isEditingDesc ? 'text' : 'default' } }}
                        />
                        {/* AI autofill suggestion renders directly under the
                            description, ABOVE the Edit/Save/Cancel row. */}
                        <QuoteSuggestionCard
                          currentText={item.description || ''}
                          suggestion={(item as any).description_autofill || ''}
                          onPaste={() => {
                            // Mirror inspection joinSuggestion: sentence-boundary
                            // hygiene so the resulting prose reads cleanly even when
                            // one side skipped terminal punctuation or started
                            // lowercase. Empty existing description → paste = replace.
                            const l = (item.description || '').trim().replace(/\s+/g, ' ');
                            let r = ((item as any).description_autofill || '').trim().replace(/\s+/g, ' ');
                            let joined: string;
                            if (!l) joined = r;
                            else if (!r) joined = l;
                            else {
                              const lEnd = /[.!?]$/.test(l) ? l : `${l}.`;
                              if (/^[a-z]/.test(r)) r = r.charAt(0).toUpperCase() + r.slice(1);
                              joined = `${lEnd} ${r}`;
                            }
                            setQuoteItems((prev) => prev.map((q, idx) => idx === i ? ({ ...q, description: joined, description_autofill: '' } as any) : q));
                            if (conversationId) {
                              quoteApi.patchItem(conversationId, i, ({ description: joined, description_autofill: '' } as any)).catch(() => {});
                            }
                          }}
                        />
                        {!descLocked && !opts.inModal && (
                          isEditingDesc ? (
                            <Group gap={8} mt={6} wrap="nowrap" align="stretch">
                              <UnstyledButton
                                onClick={() => {
                                  // Cancel on a never-saved EMPTY new job removes the
                                  // row entirely; otherwise just revert the edit.
                                  const isEmptyNew = !((item.label ?? '').trim() || (item.description ?? '').trim()
                                    || (item as any).parts || (item as any).labor
                                    || ((item as any).labors || []).length || ((item as any).partLines || []).length
                                    || ((item as any).subletLines || []).length || ((item as any).feeLines || []).length
                                    || ((item as any).discountLines || []).length);
                                  if (isEmptyNew) setQuoteItems((prev) => prev.filter((_, idx) => idx !== i));
                                  setEditingDescIdx(null);
                                  setDescDraft('');
                                }}
                                style={{ flexShrink: 0, fontSize: 12, fontWeight: 600, color: TEXT_MUTED, textAlign: 'center', padding: '7px 12px', borderRadius: 6, border: `1px solid ${BORDER}`, background: 'transparent' }}
                              >Cancel</UnstyledButton>
                              <UnstyledButton
                                onClick={() => {
                                  const v = descDraft;
                                  setQuoteItems((prev) => prev.map((q, idx) => idx === i ? { ...q, description: v } : q));
                                  setEditingDescIdx(null);
                                  setDescDraft('');
                                }}
                                style={{ flex: 1, fontSize: 12, fontWeight: 600, color: '#fff', textAlign: 'center', padding: '7px 12px', borderRadius: 6, border: `1px solid ${BLUE}`, background: BLUE }}
                              >Save</UnstyledButton>
                            </Group>
                          ) : (
                            <UnstyledButton
                              onClick={() => { setDescDraft(item.description || ''); setEditingDescIdx(i); }}
                              style={{ display: 'block', width: '100%', marginTop: 6, fontSize: 12, fontWeight: 600, color: TEXT_SECONDARY, textAlign: 'center', padding: '7px 12px', borderRadius: 6, border: `1px solid ${BORDER}`, background: '#27272A' }}
                            >Edit</UnstyledButton>
                          )
                        )}
                      </>
                    );
                  })()}
                </Box>
              </Box>
            );
            };

  const quoteContent = (
    <>
      {/* Read-only banner — shown only when the effective state is locked,
          either by user role or device override. The override toggle lives in
          Settings > My Account > Quote Editing. */}
      {(isQuoteReadOnly || hasAnyLegalDoc) && (
        <Box style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 16px', borderBottom: `1px solid ${BORDER}`, background: 'rgba(245,158,11,0.06)' }}>
          <Text size="13px" c="#F59E0B" fw={600}>
            {isQuoteReadOnly
              ? 'Read-only — edits disabled on this device.'
              : 'Signed — quote locked. Tech note still editable.'}
          </Text>
        </Box>
      )}
      {/* ── Sticky Header: Filters + Totals ── */}
      <Box style={{ flexShrink: 0 }}>
        {/* Jobs + filter pills (first) */}
        {(() => {
          const hiddenCount = quoteItems.filter((q) => q.is_hidden).length;
          const filterBtn = (label: string, value: typeof quoteFilter, color: string) => (
            <UnstyledButton key={label} onClick={() => setQuoteFilter(quoteFilter === value ? 'all' : value)}
              style={{ fontSize: 13, fontWeight: 600, padding: '4px 12px', borderRadius: 12,
                background: quoteFilter === value ? `${color}20` : `${color}08`,
                color: quoteFilter === value ? color : `${color}99`,
                border: `1px solid ${quoteFilter === value ? `${color}50` : `${color}25`}`,
              }}>{label}</UnstyledButton>
          );
          return (
            <Box style={{ padding: '8px 16px', borderBottom: `1px solid ${BORDER}`, display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: 8 }}>
              <Group gap={4}>
                {filterBtn('Approved', 'approved', '#22c55e')}
                {filterBtn('Declined', 'declined', '#ef4444')}
                {filterBtn('Pending', 'pending', '#f59e0b')}
              </Group>
              {hiddenCount > 0 && (
                <UnstyledButton onClick={() => setShowHiddenJobs((p) => !p)} style={{ fontSize: 13, color: BLUE }}>
                  {showHiddenJobs ? `Hide ${hiddenCount}` : `+${hiddenCount} hidden`}
                </UnstyledButton>
              )}
            </Box>
          );
        })()}
        {/* Totals row (second) */}
        <Box style={{ display: 'flex', flexDirection: 'row', flexWrap: 'nowrap', width: '100%', borderBottom: `1px solid ${BORDER}`, minWidth: 0, overflowX: 'auto' }}>
          <Box style={{ flex: 1, padding: '10px 14px', minWidth: 0 }}>
            <Text size="9px" fw={600} c={TEXT_MUTED} tt="uppercase" style={{ letterSpacing: '0.07em' }}>Parts</Text>
            <Text size="13px" fw={600} c={totalParts > 0 ? TEXT : TEXT_MUTED}>{totalParts > 0 ? fmtQuote(totalParts) : '—'}</Text>
          </Box>
          <Box style={{ flex: 1, padding: '10px 14px', borderLeft: `1px solid ${BORDER}`, minWidth: 0 }}>
            <Text size="9px" fw={600} c={TEXT_MUTED} tt="uppercase" style={{ letterSpacing: '0.07em' }}>Tax</Text>
            <Text size="13px" fw={600} c={partsTax > 0 ? TEXT : TEXT_MUTED}>{partsTax > 0 ? fmtQuote(partsTax) : '—'}</Text>
          </Box>
          <Box style={{ flex: 1, padding: '10px 14px', borderLeft: `1px solid ${BORDER}`, minWidth: 0 }}>
            <Text size="9px" fw={600} c={TEXT_MUTED} tt="uppercase" style={{ letterSpacing: '0.07em' }}>Labor</Text>
            <Text size="13px" fw={600} c={totalLabor > 0 ? TEXT : TEXT_MUTED}>{totalLabor > 0 ? fmtQuote(totalLabor) : '—'}</Text>
          </Box>
          {totalSublets > 0 && (
            <Box style={{ flex: 1, padding: '10px 14px', borderLeft: `1px solid ${BORDER}`, minWidth: 0 }}>
              <Text size="9px" fw={600} c="#818cf8" tt="uppercase" style={{ letterSpacing: '0.07em' }}>Sub</Text>
              <Text size="13px" fw={600} c="#818cf8">{fmtQuote(totalSublets)}</Text>
            </Box>
          )}
          {totalFees > 0 && (
            <Box style={{ flex: 1, padding: '10px 14px', borderLeft: `1px solid ${BORDER}`, minWidth: 0 }}>
              <Text size="9px" fw={600} c={TEXT_SECONDARY} tt="uppercase" style={{ letterSpacing: '0.07em' }}>Fees</Text>
              <Text size="13px" fw={600} c={TEXT_SECONDARY}>+{fmtQuote(totalFees)}</Text>
            </Box>
          )}
          {totalDiscounts > 0 && (
            <Box style={{ flex: 1, padding: '10px 14px', borderLeft: `1px solid ${BORDER}`, minWidth: 0 }}>
              <Text size="9px" fw={600} c="#f87171" tt="uppercase" style={{ letterSpacing: '0.07em' }}>Disc</Text>
              <Text size="13px" fw={600} c="#f87171">-{fmtQuote(totalDiscounts)}</Text>
            </Box>
          )}
          <Box style={{ flex: 1, padding: '10px 14px', borderLeft: `1px solid ${BORDER}`, minWidth: 0 }}>
            <Text size="9px" fw={600} c={TEXT_MUTED} tt="uppercase" style={{ letterSpacing: '0.07em' }}>Total</Text>
            <Text size="15px" fw={700} c={grandTotal > 0 ? TEXT : TEXT_MUTED}>{grandTotal > 0 ? fmtQuote(grandTotal) : '—'}</Text>
          </Box>
        </Box>
        {reportSkill && (
          <Box px={16} py={8} style={{ borderBottom: `1px solid ${BORDER}` }}>
            <Button
              fullWidth
              size="xs"
              leftSection={<IconCircleCheck size={14} />}
              onClick={confirmQuote}
              style={{ background: BLUE, fontWeight: 600, letterSpacing: '0.02em', fontSize: 12 }}
            >
              {reportSkill === 'final' ? 'Confirm & Write Report' : 'Confirm & Write Estimate'}
            </Button>
          </Box>
        )}
      </Box>

      {/* ── Flagged from Inspection ── */}
      {(() => {
        const flagged = reportCategories.flatMap((c) => c.items.filter((i) => (i.state === 'replace' || i.state === 'soon') && !i.is_hidden));
        if (!flagged.length) return null;
        const badCount = flagged.filter((i) => i.state === 'replace').length;
        const soonCount = flagged.filter((i) => i.state === 'soon').length;
        return (
          <Box style={{ flexShrink: 0, borderBottom: `1px solid ${BORDER}` }}>
            <Box style={{ padding: '10px 20px 6px', background: '#1C1C1F', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Text size="10px" fw={700} c={TEXT_SECONDARY} tt="uppercase" style={{ letterSpacing: '0.08em' }}>From Inspection</Text>
              <Group gap={8}>
                {badCount > 0 && <Text size="10px" c="#EF4444" fw={600}>{badCount} bad</Text>}
                {soonCount > 0 && <Text size="10px" c="#F59E0B" fw={600}>{soonCount} soon</Text>}
              </Group>
            </Box>
            <Box style={{ padding: '4px 20px 10px', background: '#1C1C1F' }}>
              <Stack gap={5}>
                {flagged.map((item) => {
                  const lbl = item.display_name || item.part_name;
                  const alreadyAdded = quotedLabels.has(lbl.trim().toLowerCase());
                  const isBad = item.state === 'replace';
                  return (
                    <Box key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Box style={{ width: 6, height: 6, borderRadius: '50%', background: isBad ? '#EF4444' : '#F59E0B', flexShrink: 0 }} />
                      <Text size="xs" c={TEXT_SECONDARY} style={{ flex: 1, minWidth: 0 }} truncate>{lbl}</Text>
                      {alreadyAdded ? (
                        <Text size="10px" c="#22C55E" fw={600}>✓ Added</Text>
                      ) : (
                        <UnstyledButton
                          onClick={() => {
                            const partsDollars = item.estimated_parts_cost != null ? Math.round(item.estimated_parts_cost) : 0;
                            const laborDollars = item.estimated_labor_cost != null ? Math.round(item.estimated_labor_cost) : 0;
                            openJobDraft({
                              label: lbl,
                              description: item.technician_notes || '',
                              parts: '', qty: '1', labor: '', sublet: '', fee: '', discount: '', laborHours: '',
                              source_id: makeLocalSourceId(),
                              labors: laborDollars ? [{ description: lbl, hours: '', rate: '', total: String(laborDollars) }] : [],
                              partLines: partsDollars ? [{ name: lbl, partNumber: '', qty: 1, cost: '', retail: String(partsDollars) }] : [],
                            } as any);
                          }}
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: 3,
                            padding: '2px 8px', borderRadius: 4, flexShrink: 0,
                            border: `1px solid ${BORDER}`, color: TEXT_MUTED,
                            fontSize: 10, fontWeight: 600,
                          }}
                        >
                          <IconPlus size={9} /> Add
                        </UnstyledButton>
                      )}
                    </Box>
                  );
                })}
              </Stack>
            </Box>
          </Box>
        );
      })()}

      {/* ── Legal Documents Banner — shown only when per-item legal docs
            exist (paper / digital per-item flow). The "no documents yet"
            empty state is intentionally NOT rendered. Whole-quote portal
            signatures are handled by the customer-approval banner below. */}
      {(() => {
        const perItemSigned = quoteItems.filter((q: any) =>
          q.authorization_id && (q.item_status === 'approved' || q.item_status === 'declined')
        );
        const hasPortalSig = !!(customerApprovals && ((customerApprovals.approved_items?.length || 0) + (customerApprovals.declined_items?.length || 0) > 0));
        if (hasPortalSig) return null; // Existing banner below handles this state.
        const count = perItemSigned.length;
        if (count === 0) return null; // No legal docs → show nothing.
        return (
          <Box style={{ padding: '8px 20px', borderBottom: `1px solid ${BORDER}`, background: 'rgba(34,197,94,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexShrink: 0 }}>
            <Text size="xs" fw={700} c="#22C55E">
              📄 {count} item{count === 1 ? '' : 's'} signed under a legal doc
            </Text>
            <UnstyledButton
              onClick={() => setLegalDocsOpen(true)}
              style={{
                padding: '4px 10px', borderRadius: 6,
                border: `1px solid ${BORDER}`, color: TEXT_MUTED,
                fontSize: 11, fontWeight: 600,
              }}
            >
              View documents
            </UnstyledButton>
          </Box>
        );
      })()}
      {/* ── Customer Approval Banner (whole-quote portal signature flow) ──
            Requires an actual signature_method on the customer_approvals
            object — the Tek mirror can populate approved_items without
            signing anything, which we explicitly do NOT treat as a legal
            doc anywhere else in the UI. Showing "Customer authorization
            on file" for an unsigned shell was the symptom of that gap. */}
      {/* Top-of-quote LOCK BANNER removed per user request — selection toolbar
          below (with count + Legal docs button) is the single source of
          truth for authorization state. */}

      {/* Tek Authorization Banner removed per user request — the Legal
          docs button in the bulk toolbar is the single entry point for
          viewing imported Tek authorizations (PDF + metadata). */}

      {/* ── Bulk-action toolbar: appears when any item is in 'selected' state.
            Per CLAUDE selection set = items currently marked 'selected'
            (i.e. checked but not yet customer-decided). Send-for-Approval
            flips them through the existing select_quote_items path then
            pings the service writer; Paper Signature opens the modal. ── */}
      {showQuoteApproval && (() => {
        // Treat null/undefined item_status the same as 'selected' — the
        // per-row checkbox defaults to ✓ for those, so the toolbar counter
        // must agree. Anything explicitly 'unauthorized' / 'approved' /
        // 'declined' is excluded.
        // Toolbar visibility tracks the in_batch flag (with the back-compat
        // fallback) — independent from approve/decline intent. So once a
        // writer picks intent on a row, the toolbar stays so they can send
        // for signature.
        // Shared prefix gate (hidden / blank-label / junk placeholder) —
        // identical for both tracks; false ⇒ never a candidate anywhere.
        const passesCommonGate = (q: any): boolean => {
          if (q.is_hidden) return false;
          const lbl = ((q.label ?? '') as string).trim();
          if (!lbl) return false;
          const lower = lbl.toLowerCase();
          if (lower === 'new job' || lower === 'new jobs') return false;
          if (/^job\s+\d+$/i.test(lbl)) return false;
          return true;
        };
        // INVOICE-track candidate: billable/selected work NOT yet covered by an
        // INVOICE doc. Estimate-signed items DO qualify (they're the invoice
        // candidates). Verbatim from the old captureDocType==='invoice' branch.
        const needsInvoiceDoc = (q: any): boolean => {
          if (!passesCommonGate(q)) return false;
          const qSidInv = q.source_id ? String(q.source_id) : null;
          if (q.invoice_authorization_id) return false;
          const invSigned = isRealCustomerSignature(invoiceApprovals);
          if (qSidInv && invoiceApprovals && invSigned) {
            const inA = (invoiceApprovals.approved_items || []).some((a: any) => String(a.source_id || '') === qSidInv);
            const inD = (invoiceApprovals.declined_items || []).some((a: any) => String(a.source_id || '') === qSidInv);
            if (inA || inD) return false;
          }
          const sInv = q.item_status;
          const ibInv = q.in_batch;
          return sInv === 'approved' || ibInv === true || (ibInv === undefined && (sInv == null || sInv === 'selected' || sInv === 'unauthorized'));
        };
        // ESTIMATE-track candidate: selected work NOT yet covered by an ESTIMATE
        // doc. Verbatim from the old `else` (estimate) branch incl. the
        // unsigned-Tek-shell handling.
        const needsEstimateDoc = (q: any): boolean => {
          if (!passesCommonGate(q)) return false;
          const qSidGate = q.source_id ? String(q.source_id) : null;
          const caSigned = isRealCustomerSignature(customerApprovals);
          const qInCaUnsigned = !!(
            qSidGate && customerApprovals && !caSigned && (
              (customerApprovals.approved_items || []).some((a: any) => String(a.source_id || '') === qSidGate) ||
              (customerApprovals.declined_items || []).some((a: any) => String(a.source_id || '') === qSidGate)
            )
          );
          if (q.authorization_id && !qInCaUnsigned) return false;
          const qSid = q.source_id ? String(q.source_id) : null;
          if (qSid && customerApprovals && isRealCustomerSignature(customerApprovals)) {
            const inApproved = (customerApprovals.approved_items || []).some((a: any) => String(a.source_id || '') === qSid);
            const inDeclined = (customerApprovals.declined_items || []).some((a: any) => String(a.source_id || '') === qSid);
            if (inApproved || inDeclined) return false;
          }
          const ib = q.in_batch;
          const s = q.item_status;
          return ib === true || (ib === undefined && (s == null || s === 'selected' || s === 'unauthorized'));
        };
        // EFFECTIVE toolbar track. If the chosen track has no candidates but the
        // OTHER does, the toolbar (count / empty-state / source_ids) reflects the
        // track that actually has work — so ticking Select on an estimate-signed
        // item no longer dead-ends at "No items selected" (it's an invoice
        // candidate). When the chosen track HAS candidates it is unchanged —
        // preserving today's fresh-quote behavior exactly. 'both' → estimate
        // candidates (matches the old fall-through), capture still iterates both.
        const estimateCandidates = quoteItems.filter(needsEstimateDoc);
        const invoiceCandidates = quoteItems.filter(needsInvoiceDoc);
        const effectiveDocType: 'estimate' | 'invoice' | 'both' =
          captureDocType === 'both'
            ? 'both'
            : captureDocType === 'estimate'
              ? (estimateCandidates.length > 0 || invoiceCandidates.length === 0 ? 'estimate' : 'invoice')
              : (invoiceCandidates.length > 0 || estimateCandidates.length === 0 ? 'invoice' : 'estimate');
        const selectedItems = effectiveDocType === 'invoice' ? invoiceCandidates : estimateCandidates;
        // One-click open: any selected items missing a source_id get one
        // minted right now (mutating the array in place AND flushing to
        // state for persistence), then the modal opens in the same click.
        // React batches both updates so the modal renders with the fresh
        // source_ids on first render — no double-click, no alert.
        const openWithSourceIds = (open: () => void) => {
          // Seed the capture modal's doc-type step to the track the toolbar is
          // actually showing. When the chosen track had no candidates we fell
          // back to the other (effectiveDocType) — without this the modal would
          // open on the empty original track and capture nothing. The modal's
          // first step still lets the writer override.
          if (effectiveDocType !== 'both' && effectiveDocType !== captureDocType) {
            setCaptureDocType(effectiveDocType);
          }
          let mutated = false;
          for (const it of selectedItems) {
            if (!(it as any).source_id) {
              (it as any).source_id = makeLocalSourceId();
              mutated = true;
            }
          }
          if (mutated) {
            // Sync minted IDs back to the canonical quoteItems list so
            // the debounced upsert persists them. Items are matched by
            // identity since selectedItems references the same dicts.
            setQuoteItems((prev) => [...prev]);
          }
          open();
        };
        const selectedSourceIds = selectedItems
          .map((q) => (q as any).source_id ? String((q as any).source_id) : null)
          .filter((s): s is string => !!s);
        if (!conversationId) return null;
        // Legal Document + Send-for-approval are shown in BOTH banner states
        // (selection empty or not) so the writer always has them. Send is
        // disabled with no selection (the flow needs source_ids); Legal
        // Document always opens the viewer.
        const legalDocBtn = (
          <Button
            size="xs"
            variant="outline"
            color="gray"
            styles={{ root: { color: '#F4F4F5', borderColor: '#3F3F46' } }}
            onClick={() => setLegalDocsOpen(true)}
            title="View all signed paper / digital / Tek RO authorizations on this conversation"
          >
            📋 Legal Document
          </Button>
        );
        const sendApprovalBtn = (
          <Button
            size="xs"
            variant="outline"
            color="gray"
            styles={{ root: { color: '#F4F4F5', borderColor: '#3F3F46' } }}
            onClick={() => openWithSourceIds(() => setSendApprovalOpen(true))}
            loading={bulkSending}
            disabled={selectedItems.length === 0}
            title={selectedItems.length === 0 ? 'Select items to request approval' : 'Customer decides on their own from a link'}
          >
            Send for customer approval
          </Button>
        );
        // 0 items selected: still expose Send (disabled) + Legal Document so the
        // banner is consistent. Skip the extra capture CTAs (digital/paper —
        // nothing to authorize without items).
        if (selectedItems.length === 0) {
          return (
            <Box style={{
              margin: '6px 12px', padding: '8px 12px', borderRadius: 8,
              background: 'rgba(99,102,241,0.04)', border: '1px dashed rgba(99,102,241,0.30)',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap',
            }}>
              <Group gap={8} align="center">
                <Text size="xs" c="#A1A1AA" fs="italic">
                  {effectiveDocType === 'invoice'
                    ? 'No billable items to invoice — approve work first, or switch to Estimate'
                    : 'No items selected'}
                </Text>
                <AuthorizedDot safeOverride={quoteLegalSafe} invoiceSafeOverride={invoiceLegalSafe} size={15} />
              </Group>
              <Group gap={6} align="center">
                {/* Doc-type ("Capture as") is now chosen as the first step inside
                    each capture pop-up + the Legal Document modal — not here. */}
                {sendApprovalBtn}
                {legalDocBtn}
              </Group>
            </Box>
          );
        }
        // Whether the customer already signed a prior batch on this conv —
        // surfaces in copy so the writer knows this will produce a SECOND
        // legal doc (the existing one stays locked; new items get their
        // own signed authorization).
        const hasPriorAuthDoc =
          ((customerApprovals?.approved_items?.length || 0)
            + (customerApprovals?.declined_items?.length || 0)) > 0;
        return (
          // Single-row toolbar: count + 3 capture CTAs + Legal docs.
          // Collapsed from the prior two-card layout — one thin line keeps
          // the focus on the line items underneath.
          <Box style={{
            margin: '6px 12px', padding: '8px 12px', borderRadius: 8,
            background: 'rgba(99,102,241,0.10)', border: '1px solid rgba(99,102,241,0.35)',
            display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
          }}>
            <Group gap={8} align="center" style={{ flexShrink: 0 }}>
              <Text size="sm" fw={600} c="#A5B4FC">
                {selectedItems.length} {hasPriorAuthDoc ? 'new ' : ''}item{selectedItems.length === 1 ? '' : 's'} selected
              </Text>
              {/* Split shield: left half = estimate legal-safe, right = invoice. */}
              <AuthorizedDot safeOverride={quoteLegalSafe} invoiceSafeOverride={invoiceLegalSafe} size={15} />
            </Group>
            <Box style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginLeft: 'auto', alignItems: 'center' }}>
              {/* Doc-type ("Capture as") is chosen as the first step inside each
                  capture pop-up (Digital / Document upload / Send) + the Legal
                  Document modal — no longer an inline toolbar control. */}
              {sendApprovalBtn}
              <Button
                size="xs"
                variant="outline"
                color="gray"
                styles={{ root: { color: '#F4F4F5', borderColor: '#3F3F46' } }}
                onClick={() => openWithSourceIds(() => setDigitalSignatureOpen(true))}
                title="Shop picks approve/decline, customer signs in the app"
              >
                Digital signature
              </Button>
              <Button
                size="xs"
                variant="outline"
                color="gray"
                styles={{ root: { color: '#F4F4F5', borderColor: '#3F3F46' } }}
                onClick={() => openWithSourceIds(() => setPaperSignatureOpen(true))}
                title="Shop picks approve/decline, upload a photo of the signed paper"
              >
                Document upload
              </Button>
              {legalDocBtn}
            </Box>
          </Box>
        );
      })()}

      {/* ── Section 2: Line Items (card view) ── */}
      <ScrollArea style={{ flex: 1 }}>
        <Stack gap={0} pb={4}>
          {quoteItems.map((it, idx) => renderQuoteCard(it, idx))}

          {/* Add job */}
          <Box px={16} py={8}>
            <UnstyledButton
              onClick={() => setQuoteItems((prev) => {
                const next = [...prev, newEmptyQuote()];
                // New job opens directly in edit mode (no Edit button shown
                // until first Save); finished via the long Save / Cancel.
                setEditingDescIdx(next.length - 1);
                setDescDraft('');
                return next;
              })}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                width: '100%', padding: '10px 14px',
                borderRadius: 8, border: `1px solid ${BORDER}`, background: '#111113',
                color: TEXT_MUTED, fontSize: 13, transition: 'border-color 0.15s',
              }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.borderColor = BLUE; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.borderColor = BORDER; }}
            >
              <IconPlus size={14} /> Add Job
            </UnstyledButton>
          </Box>

          {/* Fee/Discount are now per-item fields in each card above */}
        </Stack>
      </ScrollArea>

    </>
  );

  const quoteFooter = (
    <Box style={{ background: '#1C1C1F', flexShrink: 0, minWidth: 0 }}>
      {/* Totals row */}
      <Box style={{ display: 'flex', borderTop: `1px solid ${BORDER}`, minWidth: 0, overflowX: 'auto' }}>
        <Box style={{ flex: 1, padding: '14px 16px', minWidth: 0 }}>
          <Text size="10px" fw={600} c={TEXT_MUTED} tt="uppercase" mb={5} style={{ letterSpacing: '0.07em' }}>Parts</Text>
          <Text size="14px" fw={600} c={totalParts > 0 ? TEXT : TEXT_MUTED}>{totalParts > 0 ? fmtQuote(totalParts) : '—'}</Text>
        </Box>
        <Box style={{ flex: 1, padding: '14px 16px', borderLeft: `1px solid ${BORDER}`, minWidth: 0 }}>
          <Text size="10px" fw={600} c={TEXT_MUTED} tt="uppercase" mb={5} style={{ letterSpacing: '0.07em' }}>
            Tax ({(shopTaxRate * 100).toFixed(3).replace(/\.?0+$/, '')}%)
          </Text>
          <Text size="14px" fw={600} c={partsTax > 0 ? TEXT : TEXT_MUTED}>{partsTax > 0 ? fmtQuote(partsTax) : '—'}</Text>
        </Box>
        <Box style={{ flex: 1, padding: '14px 16px', borderLeft: `1px solid ${BORDER}`, minWidth: 0 }}>
          <Text size="10px" fw={600} c={TEXT_MUTED} tt="uppercase" mb={5} style={{ letterSpacing: '0.07em' }}>Labor</Text>
          <Text size="14px" fw={600} c={totalLabor > 0 ? TEXT : TEXT_MUTED}>{totalLabor > 0 ? fmtQuote(totalLabor) : '—'}</Text>
        </Box>
        {totalSublets > 0 && (
          <Box style={{ flex: 1, padding: '14px 16px', borderLeft: `1px solid ${BORDER}`, minWidth: 0 }}>
            <Text size="10px" fw={600} c="#818cf8" tt="uppercase" mb={5} style={{ letterSpacing: '0.07em' }}>Sublet</Text>
            <Text size="14px" fw={600} c="#818cf8">{fmtQuote(totalSublets)}</Text>
          </Box>
        )}
        {totalFees > 0 && (
          <Box style={{ flex: 1, padding: '14px 16px', borderLeft: `1px solid ${BORDER}`, minWidth: 0 }}>
            <Text size="10px" fw={600} c={TEXT_SECONDARY} tt="uppercase" mb={5} style={{ letterSpacing: '0.07em' }}>Fees</Text>
            <Text size="14px" fw={600} c={TEXT_SECONDARY}>+{fmtQuote(totalFees)}</Text>
          </Box>
        )}
        {totalDiscounts > 0 && (
          <Box style={{ flex: 1, padding: '14px 16px', borderLeft: `1px solid ${BORDER}`, minWidth: 0 }}>
            <Text size="10px" fw={600} c="#f87171" tt="uppercase" mb={5} style={{ letterSpacing: '0.07em' }}>Discount</Text>
            <Text size="14px" fw={600} c="#f87171">-{fmtQuote(totalDiscounts)}</Text>
          </Box>
        )}
        <Box style={{ flex: 1, padding: '14px 16px', borderLeft: `1px solid ${BORDER}`, minWidth: 0 }}>
          <Text size="10px" fw={600} c={TEXT_MUTED} tt="uppercase" mb={5} style={{ letterSpacing: '0.07em' }}>Grand Total</Text>
          <Text size="18px" fw={700} c={grandTotal > 0 ? TEXT : TEXT_MUTED}>{grandTotal > 0 ? fmtQuote(grandTotal) : '—'}</Text>
        </Box>
      </Box>

      {reportSkill && (
        <Box px={20} py={14} style={{ borderTop: `1px solid ${BORDER}` }}>
          <Button
            fullWidth
            size="md"
            leftSection={<IconCircleCheck size={16} />}
            onClick={confirmQuote}
            style={{ background: BLUE, fontWeight: 600, letterSpacing: '0.02em' }}
          >
            {reportSkill === 'final' ? 'Confirm & Write Report' : 'Confirm & Write Estimate'}
          </Button>
        </Box>
      )}

    </Box>
  );

  const quoteSidebar = (
    <Box style={{ width: 200, flexShrink: 0, overflowY: 'auto', borderRight: `1px solid ${BORDER}`, background: '#0C0C0E', display: 'flex', flexDirection: 'column' }}>
      {[
        { label: 'Parts', value: totalParts, fmt: fmtQuote(totalParts), color: undefined },
        { label: `Tax (${(shopTaxRate * 100).toFixed(3).replace(/\.?0+$/, '')}%)`, value: partsTax, fmt: fmtQuote(partsTax), color: undefined },
        { label: 'Labor', value: totalLabor, fmt: fmtQuote(totalLabor), color: undefined },
        ...(totalSublets > 0 ? [{ label: 'Sublet', value: totalSublets, fmt: fmtQuote(totalSublets), color: '#818cf8' }] : []),
        ...(totalFees > 0 ? [{ label: 'Fees', value: totalFees, fmt: `+${fmtQuote(totalFees)}`, color: undefined }] : []),
        ...(totalDiscounts > 0 ? [{ label: 'Discount', value: totalDiscounts, fmt: `-${fmtQuote(totalDiscounts)}`, color: '#f87171' }] : []),
      ].map(({ label, value, fmt, color }) => (
        <Box key={label} style={{ padding: '14px 16px', borderBottom: `1px solid ${BORDER}` }}>
          <Text size="10px" fw={600} c={color || TEXT_MUTED} tt="uppercase" mb={4} style={{ letterSpacing: '0.07em' }}>{label}</Text>
          <Text size="14px" fw={600} c={value > 0 ? (color || TEXT) : TEXT_MUTED}>{value > 0 ? fmt : '—'}</Text>
        </Box>
      ))}
      <Box style={{ padding: '14px 16px', borderBottom: `1px solid ${BORDER}` }}>
        <Text size="10px" fw={600} c={TEXT_MUTED} tt="uppercase" mb={4} style={{ letterSpacing: '0.07em' }}>Grand Total</Text>
        <Text size="20px" fw={700} c={grandTotal > 0 ? TEXT : TEXT_MUTED}>{grandTotal > 0 ? fmtQuote(grandTotal) : '—'}</Text>
      </Box>
      {reportSkill && (
        <Box style={{ padding: '14px 12px', marginTop: 'auto' }}>
          <Button
            fullWidth
            size="sm"
            leftSection={<IconCircleCheck size={14} />}
            onClick={confirmQuote}
            style={{ background: BLUE, fontWeight: 600, letterSpacing: '0.02em', fontSize: 12 }}
          >
            {reportSkill === 'final' ? 'Write Report' : 'Write Estimate'}
          </Button>
        </Box>
      )}
    </Box>
  );

  const quoteTagline = null;

  // ── Card-stack layout (replaces the tab system) ─────────────────────────────
  const vehicleInfoBody = (() => {
    const vi = conversation?.vehicle;
    if (!vi) return <Text size="sm" c={TEXT_MUTED}>No vehicle on file.</Text>;
    const md = (vi.vehicle_metadata || {}) as any;
    const mget = (...keys: string[]) => { for (const k of keys) { const v = md[k]; if (v != null && v !== '') return v; } return null; };
    const cityMpg = mget('city_mpg', 'cityMpg');
    const hwyMpg = mget('highway_mpg', 'highwayMpg', 'hwy_mpg');
    const mpg = (cityMpg || hwyMpg) ? `${cityMpg ?? '–'} city / ${hwyMpg ?? '–'} hwy` : null;
    const fmtArr = (v: any) => (Array.isArray(v) ? v.join(' / ') : v ? String(v) : null);
    const tireFront = fmtArr(mget('front_sizes', 'tire_size', 'tireSize'));
    const tireRear = fmtArr(mget('rear_sizes'));
    const tire = tireFront && tireRear && tireFront !== tireRear ? `${tireFront} / ${tireRear}` : (tireFront || tireRear);
    const rows: [string, string | null][] = [
      ['Vehicle', [vi.year, vi.make, vi.model].filter(Boolean).join(' ') || null],
      ['VIN', vi.vin || null],
      ['Plate', vi.plate || null],
      ['Engine', mget('engine_description', 'engine', 'engineDescription')],
      ['Transmission', mget('trans_type', 'transmission', 'transType')],
      ['Drive', mget('drive', 'drivetrain', 'driveType')],
      ['Fuel', mget('fuel_type', 'fuelType', 'fuel')],
      ['Body', mget('body_class', 'bodyClass', 'body')],
      ['MPG', mpg],
      ['Tire Size', tire],
      ['Bolt Pattern', mget('bolt_pattern', 'boltPattern')],
    ].map(([k, v]) => [k, v != null && v !== '' ? String(v) : null]) as [string, string | null][];
    return (
      <Stack gap={6}>
        {rows.filter(([, val]) => val).map(([label, val]) => (
          <Group key={label} justify="space-between" gap={8} wrap="nowrap">
            <Text size="xs" c={TEXT_MUTED}>{label}</Text>
            <Text size="sm" c={TEXT} fw={600} style={{ textAlign: 'right', wordBreak: 'break-all' }}>{val}</Text>
          </Group>
        ))}
      </Stack>
    );
  })();

  const customerInfoBody = (
    <CustomerContacts conversationId={conversationId} vehicle={conversation?.vehicle} onSaved={mutateConversation} />
  );

  const inspectBody = conversationId ? (
    <InspectionTab
      conversationId={conversationId}
      readOnly={isStageCardReadonly('inspect')}
      onAddToQuote={canQuote ? (item) => openJobDraft(item) : undefined}
      onInspectionIdChange={setCurrentInspectionIdWrapped}
      onItemChange={() => setLastItemChangeAt(Date.now())}
      toggleOverlayNode={undefined}
      isSwitchingTemplate={startingInspection}
      onSendMessage={async (text) => { await ensureInspectionStarted(); await sendMessage(text); }}
      onScrollToQueueEntry={scrollToQueueEntry}
      hideComposer
      onCountsChange={setInspectCounts}
    />
  ) : (
    <Center h={120}><Text c="dimmed" size="sm">No inspection available</Text></Center>
  );

  // Datetime helpers for the intake/complete editors (datetime-local wants
  // 'YYYY-MM-DDTHH:mm' in LOCAL time).
  const toLocalInput = (raw: any): string => {
    if (!raw) return '';
    try { const d = new Date(raw); return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16); } catch { return ''; }
  };
  // Customer activity feed — report/customer events that already live in the
  // message stream (report opened, recs requested, authorized $X, review, …).
  const activityEvents = (messages || []).filter((m: any) => {
    const t = m?.message_metadata?.type;
    return t === 'report_event' || t === 'customer_event';
  });

  // Activity card body: intake + complete date/time, mileage, concerns, then the
  // customer-activity feed. (CRUD memo list — Tek->HAIST synced — is pending a
  // ConversationMemo backend store; not wired here yet.)
  const activityBody = (
    <Stack gap={14}>
      <Group gap={12} grow align="flex-start">
        <Box>
          <Text size="xs" fw={700} c={TEXT_MUTED} tt="uppercase" mb={4} style={{ letterSpacing: '0.06em' }}>Intake</Text>
          <input
            type="datetime-local"
            value={toLocalInput((conversation as any)?.intake_date || (conversation as any)?.created_at)}
            onChange={async (e) => {
              const v = e.currentTarget.value || '';
              // datetime-local is browser-local (CA) → send a real ISO instant so
              // the backend stores the correct moment (mig 112: intake_date is now
              // a timestamp). Empty clears.
              try { if (conversationId) { await conversationsApi.patch(conversationId, { intake_date: v ? new Date(v).toISOString() : '' } as any); await mutateConversation(); } } catch (err) { console.error('[INTAKE_DATE] patch failed', err); }
            }}
            style={{ background: '#111113', border: `1px solid ${BORDER}`, borderRadius: 6, color: TEXT, fontSize: 13, padding: '7px 10px', colorScheme: 'dark', width: '100%' }}
          />
        </Box>
        <Box>
          <Text size="xs" fw={700} c={TEXT_MUTED} tt="uppercase" mb={4} style={{ letterSpacing: '0.06em' }}>Completed</Text>
          <input
            type="datetime-local"
            value={toLocalInput((conversation as any)?.complete_date)}
            onChange={async (e) => {
              const v = e.currentTarget.value || '';
              try { if (conversationId) { await conversationsApi.patch(conversationId, { complete_date: v ? new Date(v).toISOString() : '' } as any); await mutateConversation(); } } catch (err) { console.error('[COMPLETE_DATE] patch failed', err); }
            }}
            style={{ background: '#111113', border: `1px solid ${BORDER}`, borderRadius: 6, color: TEXT, fontSize: 13, padding: '7px 10px', colorScheme: 'dark', width: '100%' }}
          />
        </Box>
      </Group>
      {shopMembers && shopMembers.length > 0 && (
        <Box>
          <Text size="xs" fw={700} c={TEXT_MUTED} tt="uppercase" mb={6} style={{ letterSpacing: '0.06em' }}>Chargers</Text>
          <MultiSelect
            value={((conversation as any)?.charger_user_ids ?? []).map((id: number) => String(id))}
            onChange={async (vals) => {
              try { if (conversationId) { await conversationsApi.patch(conversationId, { user_ids: vals.map(Number) }); await mutateConversation(); } } catch (err) { console.error('[CHARGERS] patch failed', err); }
            }}
            data={(shopMembers as any[]).map((m) => ({ value: String(m.id), label: m.name }))}
            placeholder="Assign chargers…"
            size="sm"
            searchable
            clearable
            styles={{ input: { background: '#111113', border: `1px solid ${BORDER}`, color: TEXT, fontSize: 13 } }}
            comboboxProps={{ withinPortal: true, styles: { dropdown: { background: '#1C1C1F', border: `1px solid ${BORDER}` }, option: { color: TEXT, fontSize: 13 } } }}
          />
        </Box>
      )}
      <Box>
        <Text size="xs" fw={700} c={TEXT_MUTED} tt="uppercase" mb={6} style={{ letterSpacing: '0.06em' }}>Mileage</Text>
        <MileageQuickEdit conversationId={conversationId} mileage={conversation?.vehicle?.mileage ?? null} onSaved={() => mutateConversation()} />
      </Box>
      {/* Customer concerns — editable free text (was Findings; no AI generate). */}
      <Box>
        <Text size="xs" fw={700} c={TEXT_MUTED} tt="uppercase" mb={6} style={{ letterSpacing: '0.06em' }}>Concerns</Text>
        <Textarea
          placeholder="Customer concerns…"
          value={quoteDescription}
          onChange={(e) => setQuoteDescription(e.currentTarget.value)}
          readOnly={docLocked}
          autosize
          minRows={2}
          maxRows={8}
          styles={{ input: { background: '#111113', borderColor: BORDER, color: TEXT, fontSize: 13, lineHeight: 1.5, ...(docLocked ? { opacity: 0.6, cursor: 'not-allowed' } : {}) } }}
        />
      </Box>
      {/* Timeline — memos (editable; Tek RO notes read-only) + customer/report
          activity events merged into one newest-first feed. */}
      <Box>
        <Text size="xs" fw={700} c={TEXT_MUTED} tt="uppercase" mb={8} style={{ letterSpacing: '0.06em' }}>Timeline</Text>
        <ConversationTimeline conversationId={conversationId} events={activityEvents} />
      </Box>
    </Stack>
  );

  // Context banners — rendered as a DIRECT child of the scrolling chat column
  // (see chatThread) so their containing block spans the whole conversation and
  // they stay pinned (position: sticky) while the stage card + messages scroll.
  // Unified, explicitly-ordered card stack: Activity → Customer Info → Car Info
  // → Vehicle History → Job → Inspect. Stage cards (Activity/Job/Inspect) use the
  // expandedStageCard accordion + stage gating; info cards (Customer/Car/History)
  // free-toggle via openInfoCards.
  // Card headers act like TABS: they stay in their 2-col grid positions; opening
  // one highlights its header (active bg) and drops its body full-width below its
  // row. Single global accordion — one open at a time.
  // Compact preview helpers — a clean key/value row + an empty-state line.
  const pvRow = (label: string, value: any) => (
    <Group key={label} justify="space-between" gap={8} wrap="nowrap">
      <Text size="sm" c={TEXT_MUTED} style={{ flexShrink: 0 }}>{label}</Text>
      <Text size="sm" c={TEXT} fw={500} style={{ textAlign: 'right', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{value}</Text>
    </Group>
  );
  const pvEmpty = (t: string) => <Text size="sm" c={TEXT_MUTED}>{t}</Text>;
  // Inspection items/counts derived from the EAGERLY-loaded job inspection
  // (useInspection at JobPage level, line ~5303) — so the Inspect card title +
  // preview show real items WITHOUT first opening the card (the preview was only
  // showing the count summary; now it lists the items).
  // Order must match the open card: flatten the SAME grouped+ordered categories
  // (template-first, by category_order, then id) instead of the raw items array.
  const inspectItems = (jobInspectionCategories || []).flatMap((c: any) => c.items as any[]).filter((i) => !i.is_hidden && !i.is_extra);
  const inspectC = {
    total: inspectItems.length,
    good: inspectItems.filter((i) => i.state === 'good').length,
    soon: inspectItems.filter((i) => i.state === 'soon').length,
    replace: inspectItems.filter((i) => i.state === 'replace').length,
    pending: inspectItems.filter((i) => !isRatedInspState(i.state)).length,
  };
  const cards = ([
    visibleStageCards.includes('intake') && {
      id: 'intake', icon: '📋', label: 'Summary',
      title: (() => {
        const parts: string[] = [];
        const raw = (conversation as any)?.intake_date || (conversation as any)?.created_at;
        if (raw) { try { parts.push(new Date(raw).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })); } catch { /* skip */ } }
        const mi = conversation?.vehicle?.mileage;
        if (mi != null) parts.push(`In: ${mi.toLocaleString()}`);
        if (activityEvents.length) parts.push(`${activityEvents.length} event${activityEvents.length > 1 ? 's' : ''}`);
        return parts.length ? parts.join(' · ') : 'Summary';
      })(),
      open: expandedStageCard === 'intake', toggle: () => toggleStageCard('intake'),
      body: activityBody,
      preview: (() => {
        const rows: any[] = [];
        const ir = (conversation as any)?.intake_date || (conversation as any)?.created_at;
        if (ir) { try { rows.push(pvRow('Intake', new Date(ir).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }))); } catch { /* skip */ } }
        const cr = (conversation as any)?.complete_date;
        if (cr) { try { rows.push(pvRow('Done', new Date(cr).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }))); } catch { /* skip */ } }
        const chargers = (((conversation as any)?.charger_user_ids ?? []) as number[]).map((id) => (shopMembers as any[])?.find((m) => m.id === id)?.name).filter(Boolean);
        if (chargers.length) rows.push(pvRow('Chargers', chargers.join(', ')));
        const mi = conversation?.vehicle?.mileage;
        if (mi != null) rows.push(pvRow('Mileage', mi.toLocaleString()));
        if (quoteDescription?.trim()) rows.push(pvRow('Concerns', quoteDescription.trim()));
        if (!rows.length && !activityEvents.length) return pvEmpty('No intake details yet');
        // Show the real Timeline feed inside the preview (not just an event count),
        // mirroring the expanded Summary body.
        return (
          <Stack gap={10}>
            {rows.length > 0 && <Stack gap={4}>{rows}</Stack>}
            {activityEvents.length > 0 && (
              <Box>
                <Text size="xs" fw={700} c={TEXT_MUTED} tt="uppercase" mb={6} style={{ letterSpacing: '0.06em' }}>Timeline</Text>
                <ConversationTimeline conversationId={conversationId} events={activityEvents} hideComposer />
              </Box>
            )}
          </Stack>
        );
      })(),
    },
    {
      id: 'customerInfo', icon: '👤', label: 'Customer',
      title: (() => {
        const v = conversation?.vehicle;
        const nm = [v?.first_name, v?.last_name].filter(Boolean).join(' ').trim();
        const extra = ((v?.sub_phones ?? []) as any[]).length;
        const phone = v?.phone || ((v?.sub_phones ?? []) as any[]).map((sp) => (typeof sp === 'string' ? sp : sp?.phone)).find(Boolean);
        return [nm || 'Customer', phone || null, extra ? `+${extra} contact${extra > 1 ? 's' : ''}` : null].filter(Boolean).join(' · ');
      })(),
      open: openInfoCards.has('customerInfo'), toggle: () => toggleInfoCard('customerInfo'),
      body: customerInfoBody,
      preview: (() => {
        const v = conversation?.vehicle as any;
        const name = [v?.first_name, v?.last_name].filter(Boolean).join(' ').trim();
        const subs = ((v?.sub_phones ?? []) as any[]);
        const rows: any[] = [];
        if (name) rows.push(pvRow('Name', name));
        if (v?.phone) rows.push(pvRow('Phone', v.phone));
        if (v?.email) rows.push(pvRow('Email', v.email));
        subs.forEach((sp, i) => {
          const ph = typeof sp === 'string' ? sp : sp?.phone;
          const nm = typeof sp === 'string' ? null : sp?.name;
          if (ph || nm) rows.push(pvRow(nm || `Contact ${i + 1}`, ph || '—'));
        });
        return rows.length ? <Stack gap={4}>{rows}</Stack> : pvEmpty('No customer info');
      })(),
    },
    conversation?.vehicle && {
      id: 'vehicleInfo', icon: '🚗', label: 'Vehicle Spec',
      title: (() => {
        const v = conversation?.vehicle as any;
        const parts: string[] = [vehicleShort(conversation)];
        if (v?.plate) parts.push(v.plate);
        else if (v?.vin) parts.push(`VIN ${v.vin}`);
        return parts.filter(Boolean).join(' · ') || 'Vehicle Spec';
      })(),
      open: openInfoCards.has('vehicleInfo'), toggle: () => toggleInfoCard('vehicleInfo'),
      body: vehicleInfoBody,
      preview: (() => {
        const v = conversation?.vehicle as any;
        const md = (v?.vehicle_metadata || {}) as any;
        const rows: any[] = [];
        const veh = [v?.year, v?.make, v?.model].filter(Boolean).join(' ');
        if (veh) rows.push(pvRow('Vehicle', veh));
        if (v?.plate) rows.push(pvRow('Plate', v.plate));
        if (v?.vin) rows.push(pvRow('VIN', v.vin));
        const eng = md.engine_description || md.engine || md.engineDescription; if (eng) rows.push(pvRow('Engine', String(eng)));
        const tr = md.trans_type || md.transmission; if (tr) rows.push(pvRow('Trans', String(tr)));
        const dr = md.drive || md.drivetrain; if (dr) rows.push(pvRow('Drive', String(dr)));
        const fu = md.fuel_type || md.fuel; if (fu) rows.push(pvRow('Fuel', String(fu)));
        const col = v?.color || md.color; if (col) rows.push(pvRow('Color', String(col)));
        return rows.length ? <Stack gap={4}>{rows}</Stack> : pvEmpty('No vehicle details');
      })(),
    },
    conversationId && {
      id: 'vehicleHistory', icon: '🕘', label: 'Vehicle History',
      title: historyPreview || 'Vehicle History',
      open: openInfoCards.has('vehicleHistory'), toggle: () => toggleInfoCard('vehicleHistory'),
      body: <VehicleHistory conversationId={conversationId} />,
      preview: (() => {
        const sources = (vehicleHistoryData as any)?.sources as any[] | undefined;
        const all = (sources || []).flatMap((s) => s.records || []);
        const recalls = all.filter((r: any) => r?.source === 'recall').length;
        const recs = all.filter((r: any) => r?.source !== 'recall');
        if (!recs.length && !recalls) return pvEmpty('No service history');
        recs.sort((a: any, b: any) => String(b.date || '').localeCompare(String(a.date || '')));
        const fmtDate = (s?: string) => {
          if (!s) return '';
          try { return new Date(s).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }); } catch { return String(s).slice(0, 10); }
        };
        return (
          <Stack gap={7}>
            {recalls > 0 && pvRow('Open recalls', recalls)}
            {recs.map((r: any, i: number) => {
              const head = String(r.description || r.lines?.[0]?.description || '').trim() || 'Service';
              const money = r.total != null && r.total > 0 ? `$${Math.round(r.total).toLocaleString()}` : null;
              const meta = [
                fmtDate(r.date),
                r.mileage ? `${Number(r.mileage).toLocaleString()} mi` : null,
                r.lines?.length ? `${r.lines.length} item${r.lines.length > 1 ? 's' : ''}` : null,
              ].filter(Boolean).join(' · ');
              return (
                <Box key={i}>
                  <Group justify="space-between" gap={8} wrap="nowrap" align="flex-start">
                    <Text size="sm" c={TEXT} style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {r.ro_number ? <Text span c={TEXT_MUTED}>RO #{r.ro_number} · </Text> : null}{head}
                    </Text>
                    {money && <Text size="sm" fw={600} c={TEXT} ff="monospace" style={{ flexShrink: 0 }}>{money}</Text>}
                  </Group>
                  {meta && <Text size="xs" c={TEXT_MUTED} mt={1}>{meta}</Text>}
                </Box>
              );
            })}
          </Stack>
        );
      })(),
    },
    visibleStageCards.includes('job') && {
      id: 'job', icon: '🔧', label: 'Job',
      title: (() => {
        const items = (quoteItems || []).filter((i) => (i.label || '').trim() || (i.description || '').trim() || parseFloat(i.parts) || parseFloat(i.labor) || parseFloat(i.sublet));
        if (!items.length) return 'Job';
        const name = String(items[0].label || items[0].description || 'Job').trim();
        const head = name.length > 28 ? `${name.slice(0, 28)}…` : name;
        const more = items.length > 1 ? ` +${items.length - 1}` : '';
        return `${head}${more} · ${fmtQuote(grandTotal)}`;
      })(),
      open: expandedStageCard === 'job', toggle: () => toggleStageCard('job'),
      badge: (() => {
        const n = (quoteItems || []).filter((i) => (i.label || '').trim() || (i.description || '').trim() || parseFloat(i.parts) || parseFloat(i.labor) || parseFloat(i.sublet)).length;
        return n > 0 ? <Box style={{ fontSize: 12, fontWeight: 700, color: TEXT_MUTED, background: '#27272A', borderRadius: 999, padding: '1px 8px' }}>{n}</Box> : undefined;
      })(),
      body: <Box data-no-collapse>{quoteContent}</Box>,
      preview: (() => {
        const items = (quoteItems || []).filter((i) => (i.label || '').trim() || (i.description || '').trim() || parseFloat(i.parts) || parseFloat(i.labor) || parseFloat(i.sublet));
        if (!items.length) return pvEmpty('No jobs yet');
        const amtOf = (it: QuoteItem) => (parseFloat(it.parts) || 0) * (parseFloat(it.qty) || 1) + (parseFloat(it.labor) || 0) + (parseFloat(it.sublet) || 0) + (parseFloat(it.fee) || 0) - (parseFloat(it.discount) || 0);
        // Per-item LEGAL status — the customer's recorded decision, not the
        // writer's plan. approved/declined come from customer_approvals; pending
        // = in the signature batch but undecided; unselected = not in the batch.
        const STATUS = {
          approved:   { label: 'Approved',   color: '#22c55e', bg: 'rgba(34,197,94,0.12)' },
          declined:   { label: 'Declined',   color: '#ef4444', bg: 'rgba(239,68,68,0.12)' },
          pending:    { label: 'Pending',    color: '#f59e0b', bg: 'rgba(245,158,11,0.12)' },
          unselected: { label: 'Unselected', color: '#a1a1aa', bg: 'rgba(161,161,170,0.12)' },
        } as const;
        const statusOf = (it: QuoteItem): keyof typeof STATUS => {
          const sid = String((it as any).source_id ?? '');
          if (sid && approvedSet.has(sid)) return 'approved';
          if (sid && declinedSet.has(sid)) return 'declined';
          const ib = (it as any).in_batch;
          const s = (it as any).item_status;
          const inBatch = ib === true || (ib === undefined && (s == null || s === 'selected' || s === 'unauthorized'));
          return inBatch ? 'pending' : 'unselected';
        };
        // Two totals by customer decision (declined excluded from both):
        //   approvedTotal = approved items only (the committed work)
        //   withPendingTotal = approved + still-pending items
        const approvedTotal = items.reduce((s, it) => s + (statusOf(it) === 'approved' ? amtOf(it) : 0), 0);
        const pendingTotal = items.reduce((s, it) => s + (statusOf(it) === 'pending' ? amtOf(it) : 0), 0);
        const withPendingTotal = approvedTotal + pendingTotal;
        return (
          <Stack gap={4}>
            {items.map((it, i) => {
              const st = STATUS[statusOf(it)];
              return (
                <Group key={i} justify="space-between" gap={8} wrap="nowrap">
                  <Group gap={6} wrap="nowrap" style={{ minWidth: 0 }}>
                    <Box style={{ flexShrink: 0, fontSize: 9, fontWeight: 700, color: st.color, background: st.bg, borderRadius: 4, padding: '1px 6px', textTransform: 'uppercase', letterSpacing: 0.4 }}>{st.label}</Box>
                    <Text size="sm" c={TEXT} style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.label || it.description || 'Job'}</Text>
                  </Group>
                  <Text size="sm" c={TEXT_MUTED} style={{ flexShrink: 0 }}>{fmtQuote(amtOf(it))}</Text>
                </Group>
              );
            })}
            <Group justify="space-between" gap={8} style={{ borderTop: `1px solid ${BORDER}`, paddingTop: 4 }}>
              <Text size="sm" fw={700} c={TEXT_MUTED}>Total (approved)</Text>
              <Text size="sm" fw={700} c={TEXT}>{fmtQuote(approvedTotal)}</Text>
            </Group>
            {pendingTotal > 0 && (
              <Group justify="space-between" gap={8}>
                <Text size="sm" c={TEXT_MUTED}>With pending</Text>
                <Text size="sm" c={STATUS.pending.color}>{fmtQuote(withPendingTotal)}</Text>
              </Group>
            )}
          </Stack>
        );
      })(),
    },
    visibleStageCards.includes('inspect') && {
      id: 'inspect', icon: '🔍', label: 'Inspect',
      title: (() => {
        const c = inspectC.total ? inspectC : inspectCounts;
        if (!c.total) return 'Inspect';
        const parts: string[] = [];
        if (c.replace) parts.push(`${c.replace} Replace`);
        if (c.soon) parts.push(`${c.soon} Soon`);
        if (c.good) parts.push(`${c.good} Good`);
        if (c.pending) parts.push(`${c.pending} Pending`);
        return parts.join(' · ') || 'Inspect';
      })(),
      open: expandedStageCard === 'inspect', toggle: () => toggleStageCard('inspect'),
      badge: (inspectC.total || inspectCounts.total) > 0 ? <Box style={{ fontSize: 12, fontWeight: 700, color: TEXT_MUTED, background: '#27272A', borderRadius: 999, padding: '1px 8px' }}>{inspectC.total || inspectCounts.total}</Box> : undefined,
      // height:100% so InspectionTab's height:100% root resolves → its internal
      // scrollRef becomes the real scroll container → sticky chips/headers engage.
      body: <Box data-no-collapse style={{ height: '100%' }}>{inspectBody}</Box>,
      // Preview now LISTS the inspection items (state dot + name), scrollable in
      // the tile — so they're visible without opening the card.
      preview: (() => {
        if (!inspectItems.length) return pvEmpty('No inspection items');
        const stateColor = (s: string) => s === 'replace' ? '#ef4444' : s === 'soon' ? '#f59e0b' : s === 'good' ? '#22c55e' : '#71717a';
        return (
          <Stack gap={4}>
            {inspectItems.map((it: any, i: number) => (
              <Group key={it.id ?? i} gap={8} wrap="nowrap">
                <Box style={{ width: 7, height: 7, borderRadius: '50%', background: stateColor(it.state), flexShrink: 0, marginTop: 1 }} />
                <Text size="sm" c={TEXT} style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {it.display_name || String(it.part_name || 'Item').replace(/_/g, ' ')}
                </Text>
              </Group>
            ))}
          </Stack>
        );
      })(),
    },
  ].filter(Boolean) as Array<{ id: string; icon: string; label: string; title: any; badge?: any; open: boolean; toggle: () => void; body: any; preview?: any }>);

  const openCard = cards.find((c) => c.open);
  // Compact 2-col tab bar — icon + rich title + badge only, NO preview. Pinned at
  // the bottom of the right pane WHEN a card is open, to switch between cards.
  const cardTabBar = (
    <Box style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8 }}>
      {cards.map((c) => (
        <UnstyledButton
          key={c.id}
          onClick={c.toggle}
          style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', minWidth: 0, borderRadius: 10, border: `1px solid ${c.open ? BLUE : BORDER}`, background: c.open ? 'rgba(37,99,235,0.18)' : 'transparent', transition: 'background 0.12s, border-color 0.12s' }}
        >
          <Text span style={{ fontSize: 15, lineHeight: 1, flexShrink: 0 }}>{c.icon}</Text>
          <Text size="sm" fw={700} c={c.open ? '#fff' : TEXT} style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>{c.title}</Text>
          {c.badge}
        </UnstyledButton>
      ))}
    </Box>
  );
  // Full-height 2-col preview grid — each tile = header + rich preview, stretches
  // to fill the right pane. Shown ONLY when NO card is selected.
  const cardPreviewGrid = (
    <Box style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gridAutoRows: 'minmax(0, 1fr)', gap: 8, height: '100%' }}>
      {cards.map((c) => (
        <Box
          key={c.id}
          onClick={c.toggle}
          style={{ minWidth: 0, minHeight: 0, borderRadius: 10, overflow: 'hidden', border: `1px solid ${BORDER}`, display: 'flex', flexDirection: 'column', cursor: 'pointer' }}
        >
          <Box style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', minWidth: 0, flexShrink: 0, borderBottom: `1px solid ${BORDER}` }}>
            <Text span style={{ fontSize: 15, lineHeight: 1, flexShrink: 0 }}>{c.icon}</Text>
            <Text size="sm" fw={700} c={TEXT} style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>{c.label}</Text>
            {c.badge}
          </Box>
          <Box style={{ flex: 1, minHeight: 0, padding: '10px 12px', overflowY: 'auto' }}>{c.preview}</Box>
        </Box>
      ))}
    </Box>
  );

  // One unified scroll: the stage cards render as inline banners at the top of
  // the chat stream, then the messages — no horizontal split.
  const chatThread = (
    <Box style={{ flex: 1, minHeight: 0, position: 'relative', display: 'flex', flexDirection: 'column', background: '#0C0C0E' }}>
      {dragging && (
        <Box style={{ position: 'absolute', inset: 0, zIndex: 10, background: 'rgba(37,99,235,0.12)', border: `2px dashed ${BLUE}`, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(2px)' }}>
          <Stack align="center" gap="xs"><IconPhoto size={36} color={BLUE} /><Text size="sm" fw={600} c={BLUE}>Drop photos here</Text></Stack>
        </Box>
      )}
      <div ref={chatViewportRef} style={{ flex: 1, overflowY: 'auto', position: 'relative' }}>
        <Box style={{ maxWidth: '100%', margin: '0 auto', width: '100%', padding: '10px 12px 16px' }}>
          {chatMessagesContent}
        </Box>
      </div>
    </Box>
  );

  // SMS is a manual outbound text to the customer — gate it to exactly what the
  // SMS send path needs (recipient + text), NOT the AI-conversation gates
  // (isArchived / in-flight cap) baked into `canSend`. AI/prompt mode keeps them.
  const composerCanSend = composerMode === 'sms'
    ? !!(canSms && (smsRecipient || conversation?.vehicle?.phone) && inputText.trim().length > 0)
    : canSend;
  const customerNameForSms = [conversation?.vehicle?.first_name, conversation?.vehicle?.last_name].filter(Boolean).join(' ').trim();
  const composerOnQuoteCard = expandedStageCard === 'job';
  // Prompt/SMS toggle — lives in the chat pane's title bar (see chat column).
  const promptSmsToggle = (
    <Box style={{ display: 'inline-flex', flexShrink: 0, background: '#0C0C0E', border: `1px solid ${BORDER}`, borderRadius: 10, padding: 3, gap: 2 }}>
      {([
        { val: 'prompt' as const, label: 'Prompt', Icon: IconSparkles, accent: BLUE },
        ...((canSms && canSmsFf) ? [{ val: 'sms' as const, label: 'SMS', Icon: IconMessage, accent: '#22C55E' }] : []),
        { val: 'report' as const, label: 'Report', Icon: IconFileText, accent: BLUE },
      ]).map(({ val, label, Icon, accent }) => {
        const active = composerMode === val;
        return (
          <UnstyledButton
            key={val}
            onClick={() => setComposerMode(val)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              padding: '4px 10px', borderRadius: 7, fontSize: 12, fontWeight: 600, lineHeight: 1,
              color: active ? '#fff' : TEXT_MUTED,
              background: active ? accent : 'transparent',
              transition: 'background .15s, color .15s',
            }}
          >
            <Icon size={13} />
            {label}
          </UnstyledButton>
        );
      })}
    </Box>
  );
  const unifiedComposer = isArchived ? (
    <Box style={{ flexShrink: 0, padding: '0 16px 10px', background: BG }}>
      <Box style={{ maxWidth: '50vw', margin: '0 auto', background: '#1E1E21', border: `1px solid ${BORDER}`, borderRadius: 12, padding: '0 20px', minHeight: 54, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <Text size="sm" c={TEXT_MUTED}>This conversation is archived.</Text>
        <UnstyledButton onClick={() => conversationId && updateConversationStatus(conversationId, 'active')} style={{ fontSize: 12, color: BLUE, fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0 }}>Unarchive</UnstyledButton>
      </Box>
    </Box>
  ) : (
    <Box style={{ flexShrink: 0, padding: '0 16px 10px', background: BG }}>
      {chatVoice.isRecording || chatVoiceSending ? (
        <Box style={{ maxWidth: '50vw', margin: '0 auto' }}>
          <AudioRecordingOverlay durationMs={chatVoice.durationMs} audioLevel={chatVoice.audioLevel} transcript={chatVoice.transcript} isTranscriptAvailable={chatVoice.isTranscriptAvailable} onCancel={chatVoice.cancelVoice} onSend={handleChatVoiceSend} sending={chatVoiceSending} />
        </Box>
      ) : (
        <Box style={{ maxWidth: '50vw', margin: '0 auto', background: '#1E1E21', border: `1px solid ${BORDER}`, borderRadius: 16, boxShadow: '0 4px 24px rgba(0,0,0,0.4)', overflow: 'hidden' }}>
          {composerMode === 'sms' && (() => {
            const v = conversation?.vehicle;
            const smsContacts: { label: string; phone: string }[] = [];
            if (v?.phone) smsContacts.push({ label: [v.first_name, v.last_name].filter(Boolean).join(' ') || '', phone: v.phone });
            for (const sp of ((v?.sub_phones || []) as any[])) {
              const phone = typeof sp === 'string' ? sp : sp?.phone;
              const label = typeof sp === 'object' ? (sp?.name || '') : '';
              if (phone && !smsContacts.some((c) => c.phone === phone)) smsContacts.push({ label, phone });
            }
            return (
              <Box style={{ padding: '8px 12px 0', display: 'flex', alignItems: 'center', gap: 6 }}>
                <Text size="xs" fw={600} c={TEXT_MUTED} style={{ flexShrink: 0 }}>To:</Text>
                {smsContacts.length > 0 ? (
                  <select
                    value={smsRecipient || smsContacts[0]?.phone || ''}
                    onChange={(e) => { if (e.target.value === '__add__') { setShowVehicleInfo(true); return; } setSmsRecipient(e.target.value); }}
                    style={{ padding: '4px 8px', fontSize: 12, fontWeight: 600, background: 'rgba(34,197,94,0.1)', color: '#22C55E', border: '1px solid rgba(34,197,94,0.3)', borderRadius: 6, outline: 'none', cursor: 'pointer' }}
                  >
                    {smsContacts.map((c) => <option key={c.phone} value={c.phone}>{c.label ? `${c.label} · ${c.phone}` : c.phone}</option>)}
                    <option value="__add__">+ Add contact</option>
                  </select>
                ) : (
                  <UnstyledButton onClick={() => (conversation?.vehicle ? setShowVehicleInfo(true) : setShowVehicleLookup(true))} style={{ padding: '2px 8px', borderRadius: 6, background: 'rgba(255,255,255,0.05)', border: `1px solid ${BORDER}` }}><Text size="xs" fw={600} c={TEXT_MUTED}>Add phone number</Text></UnstyledButton>
                )}
              </Box>
            );
          })()}
          {reportSkill && composerMode !== 'sms' && (
            <Box style={{ padding: '10px 16px 0', display: 'flex', gap: 8 }}>
              <Box style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px', borderRadius: 999, background: 'rgba(124,58,237,0.15)', border: '1px solid rgba(124,58,237,0.4)', color: '#A78BFA', fontSize: 12, fontWeight: 600 }}>
                {reportSkill === 'estimate' ? <IconReportMedical size={13} /> : <IconCircleCheck size={13} />}
                {reportSkill === 'estimate' ? 'Estimate Report' : 'Final Report'}
                <UnstyledButton onClick={() => setReportSkill(null)} style={{ color: '#A78BFA', display: 'flex', alignItems: 'center', marginLeft: 2 }}><IconX size={12} /></UnstyledButton>
              </Box>
            </Box>
          )}
          {pendingPreviews.length > 0 && (
            <Box style={{ display: 'flex', gap: 10, padding: '12px 16px 0', overflowX: 'auto' }}>
              {pendingPreviews.map((url, i) => (
                <Box key={i} style={{ position: 'relative', flexShrink: 0 }}>
                  {pendingFiles[i] && isVideoFile(pendingFiles[i]) ? (
                    <video src={url} muted style={{ width: 110, height: 90, objectFit: 'cover', borderRadius: 10, border: `1px solid ${BORDER}`, display: 'block' }} />
                  ) : (
                    <img src={url} alt="" style={{ width: 110, height: 90, objectFit: 'cover', borderRadius: 10, border: `1px solid ${BORDER}`, display: 'block' }} />
                  )}
                  <ActionIcon size={26} radius="xl" onClick={() => handleRemoveFile(i)} style={{ position: 'absolute', top: -8, right: -8, background: '#fff', border: '1px solid #ccc', color: '#333' }}><IconX size={12} /></ActionIcon>
                </Box>
              ))}
            </Box>
          )}
          <Group gap={6} px={10} py={1} align="center">
            <ActionIcon variant="subtle" size={30} radius="xl" onClick={() => fileInputRef.current?.click()} style={{ flexShrink: 0, border: `1px solid ${BORDER}`, background: '#111113', color: TEXT_MUTED }}><IconPlus size={17} /></ActionIcon>
            <input ref={fileInputRef} type="file" accept="image/*,video/*" multiple style={{ display: 'none' }} onChange={(e) => { handleAddFiles(e.target.files); e.target.value = ''; }} />
            <Textarea
              ref={textareaRef}
              placeholder={composerMode === 'sms' ? `Message ${customerNameForSms || 'customer'}…` : composerOnQuoteCard ? 'Add item (e.g. "oil change $45") or ask anything' : reportSkill === 'estimate' ? 'ex) Please generate an estimate report.' : reportSkill === 'final' ? 'ex) Please generate a final report.' : 'Ask anything'}
              value={inputText}
              onChange={(e) => setInputText(e.currentTarget.value)}
              onKeyDown={handleKeyDown}
              autosize minRows={1} maxRows={6} style={{ flex: 1 }}
              styles={{ input: { background: 'transparent', border: 'none', color: TEXT, padding: '6px 4px', resize: 'none', fontSize: 14, lineHeight: 1.25, boxShadow: 'none', outline: 'none' }, root: { flex: 1 } }}
            />
            {shopData?.pos_provider && composerOnQuoteCard ? (
              <Tooltip label={importingQuote ? 'Syncing...' : `Import from ${shopData.pos_provider}`} position="top" withArrow>
                <ActionIcon variant="subtle" size={30} radius="xl" onClick={handleImportFromShop} disabled={importingQuote} loading={importingQuote} style={{ flexShrink: 0, border: `1px solid ${BORDER}`, background: 'rgba(124,58,237,0.15)', color: '#A78BFA' }}><IconFileImport size={17} /></ActionIcon>
              </Tooltip>
            ) : null}
            <MessageQueueBadge conversationId={conversationId} onScrollToMessage={scrollToQueueEntry} />
            <ActionIcon size={30} radius="xl" disabled={!composerCanSend} loading={sending} onClick={handleSend} style={{ background: composerCanSend ? (composerMode === 'sms' ? '#22C55E' : BLUE) : '#2A2A2E', flexShrink: 0, transition: 'all 0.15s' }}><IconSend size={16} color={composerCanSend ? '#fff' : TEXT_MUTED} /></ActionIcon>
          </Group>
        </Box>
      )}
    </Box>
  );

  // Deleted-conv branch — see the `isDeletedConv` comment near the top of
  // this component. Render INSIDE the return path so every hook above
  // always runs; an early return up there causes "Rendered fewer hooks
  // than expected" the moment the GET 404s after first paint.
  if (isDeletedConv) {
    return (
      <Center style={{ height: '100%', background: BG }}>
        <Stack align="center" gap="xs">
          <IconTrash size={32} color={TEXT_MUTED} />
          <Text size="sm" c={TEXT_MUTED}>This conversation has been deleted.</Text>
          <UnstyledButton onClick={() => navigate(-1)} style={{ color: BLUE, fontSize: 13, marginTop: 4 }}>← Go back</UnstyledButton>
        </Stack>
      </Center>
    );
  }

  return (
    <Box style={{ height: '100%', display: 'flex', background: BG }}>
      {/* Job item photo lightbox */}
      {jobLightboxUrl && <PhotoLightbox url={jobLightboxUrl} onClose={() => setJobLightboxUrl(null)} />}
      {annotateQuote && (
        <PhotoAnnotator
          opened
          photoUrl={annotateQuote.url}
          onClose={() => setAnnotateQuote(null)}
          onSave={async (file) => {
            const { blob_name } = await storageApi.uploadToPresignedUrl(file);
            const { index: idx, photoIndex } = annotateQuote;
            // Edit IN PLACE: the edited blob becomes the canonical `url`; the
            // pristine original is preserved under `original_url` (set once). No
            // new photo is appended — the UI toggles edited↔original. The
            // debounced quote upsert (useEffect on quoteItems) persists it.
            setQuoteItems((prev) => prev.map((q, j) => {
              if (j !== idx) return q;
              const photos = [...((q.photos as any[]) || [])];
              const entry = { ...(photos[photoIndex] || {}) };
              if (!entry.original_url) entry.original_url = entry.url;
              entry.url = blob_name;
              entry.edited_at = new Date().toISOString();
              photos[photoIndex] = entry;
              return { ...q, photos };
            }));
          }}
          canRevert={!!((quoteItems[annotateQuote.index]?.photos as any[])?.[annotateQuote.photoIndex]?.original_url)}
          onRevert={() => {
            // Mirror the in-place edit model: restore the pristine `url` from
            // `original_url` and drop the edit markers. Same quoteItems target
            // (index → quote item, photoIndex → photo) the onSave path edits.
            const { index: idx, photoIndex } = annotateQuote;
            setQuoteItems((prev) => prev.map((q, j) => {
              if (j !== idx) return q;
              const photos = [...((q.photos as any[]) || [])];
              const entry = { ...(photos[photoIndex] || {}) };
              if (!entry.original_url) return q;
              entry.url = entry.original_url;
              delete entry.original_url;
              delete entry.edited_at;
              photos[photoIndex] = entry;
              return { ...q, photos };
            }));
          }}
        />
      )}
      {priceModalIdx !== null && quoteItems[priceModalIdx] && conversationId && (
        <PriceLookupModal
          opened={priceModalIdx !== null}
          onClose={() => setPriceModalIdx(null)}
          conversationId={Number(conversationId)}
          quoteItemIdx={priceModalIdx}
          partName={quoteItems[priceModalIdx].label}
          partHint={quoteItems[priceModalIdx].description}
          vendors={['autozone']}
          onApplied={(_jobId, result) => {
            if (result?.price != null) {
              setQuoteItems((prev) => prev.map((q, idx) => idx === priceModalIdx ? { ...q, parts: String(result.price) } : q));
            }
          }}
        />
      )}
      {conversationId && (() => {
        // Include items where the mechanic has expressed intent: selected
        // (default), approved, or declined. Each row carries its intent so
        // the signature modal can pre-set the decision instead of falling
        // back to 'skip'.
        // Skip junk rows: empty labels, placeholder 'New Job' / 'New job'
        // from extension auto-create. These have no real customer-facing
        // content; surfacing them as "(no label)" in the signature picker
        // just clutters the UI.
        const isJunkLabel = (lbl: string) => {
          const trimmed = lbl.trim();
          if (!trimmed) return true;
          const lower = trimmed.toLowerCase();
          return lower === 'new job' || lower === 'new jobs';
        };
        const selectedRows = quoteItems
          .filter((q) => {
            // Hidden items (is_hidden=true / Tek-side deselected) must not
            // appear in the signature modal — the customer should only see
            // what's in the visible cart.
            if ((q as any).is_hidden) return false;
            // INVOICE track: the modal batch is the billable (approved) /
            // selected work not yet invoice-covered. Estimate-approved items
            // belong here (they're what the final invoice attests).
            if (captureDocType === 'invoice') {
              if ((q as any).invoice_authorization_id) return false;
              const _qSidInv = (q as any).source_id ? String((q as any).source_id) : null;
              const _invSigned = isRealCustomerSignature(invoiceApprovals);
              if (_qSidInv && invoiceApprovals && _invSigned) {
                const inA = (invoiceApprovals.approved_items || []).some((a: any) => String(a.source_id || '') === _qSidInv);
                const inD = (invoiceApprovals.declined_items || []).some((a: any) => String(a.source_id || '') === _qSidInv);
                if (inA || inD) return false;
              }
              const lblInv = ((q.label ?? '') as string);
              if (/^job\s+\d+$/i.test(lblInv.trim())) return false;
              if (/^pending[-_]/i.test(lblInv.trim())) return false;
              if (/^pending[-_]/i.test(String((q as any).source_id || ''))) return false;
              const sInv = (q as any).item_status;
              const ibInv = (q as any).in_batch;
              const inBatchInv = sInv === 'approved' || ibInv === true || (ibInv === undefined && (sInv == null || sInv === 'selected' || sInv === 'unauthorized'));
              return inBatchInv && !!(q as any).source_id && !isJunkLabel(lblInv);
            }
            // Already-signed items are out of the bulk-signature batch.
            // Mirror the toolbar gate: authorization_id ALONE isn't enough
            // — Tek mirror stamps it without a real signature. Drop only
            // when authorization_id is real (item is in a SIGNED
            // customer_approvals, OR not shadowed by an unsigned shell).
            const _qSid = (q as any).source_id ? String((q as any).source_id) : null;
            const _caSigned = isRealCustomerSignature(customerApprovals);
            const _qInCaUnsigned = !!(
              _qSid && customerApprovals && !_caSigned && (
                (customerApprovals.approved_items || []).some((a: any) => String(a.source_id || '') === _qSid) ||
                (customerApprovals.declined_items || []).some((a: any) => String(a.source_id || '') === _qSid)
              )
            );
            if ((q as any).authorization_id && !_qInCaUnsigned) return false;
            const lbl = ((q.label ?? '') as string);
            // Auto-generated "Job <tek_id>" and "pending-…" placeholders
            // (rows that never got a real name) — drop them too.
            if (/^job\s+\d+$/i.test(lbl.trim())) return false;
            if (/^pending[-_]/i.test(lbl.trim())) return false;
            const sid = String((q as any).source_id || '');
            if (/^pending[-_]/i.test(sid)) return false;
            const ib = (q as any).in_batch;
            const s = (q as any).item_status;
            const inBatch = ib === true || (ib === undefined && (s == null || s === 'selected' || s === 'unauthorized' || s === 'approved' || s === 'declined'));
            return inBatch && (q as any).source_id && !isJunkLabel(lbl);
          })
          .map((q) => {
            const s = (q as any).item_status;
            const intent: 'approve' | 'decline' | 'skip' =
              s === 'approved' ? 'approve' : s === 'declined' ? 'decline' : 'skip';
            return {
              source_id: String((q as any).source_id),
              label: q.label || '',
              amount: Number((q as any).amount || (q as any).total || ((parseFloat(q.parts || '0') + parseFloat(q.labor || '0')) || 0)),
              default_decision: intent,
            };
          });
        const customerHasContact = !!(conversation?.vehicle?.phone || conversation?.vehicle?.email);
        // After an auth lands, send a read-only receipt to the customer.
        // Honors the channel/phone/email payload the signature modal collected
        // (the writer may have edited the customer's saved contact info, or
        // picked Skip explicitly). SMS goes via the existing /sms/send path
        // (backend picks OpenPhone when configured, Twilio as fallback). Email
        // currently piggybacks on the inspection email-report endpoint since
        // that's the only generic email path we have — if there's no
        // inspection on this conversation, surface a clear error to the user
        // instead of silently dropping.
        const sendReceiptIfPossible = async (opts: {
          channel?: 'sms' | 'email' | 'skip';
          phone?: string;
          email?: string;
        } = {}) => {
          const channel = opts.channel ?? 'sms';
          if (channel === 'skip') return;
          // Find the most recent shareable artifact (the report/quote link)
          // so we have something to send.
          let receiptUrl: string | null = null;
          for (let i = messages.length - 1; i >= 0 && !receiptUrl; i--) {
            const arts = (messages[i] as any).artifacts || [];
            for (const a of arts) {
              if (a.public_url) { receiptUrl = a.public_url; break; }
            }
          }
          if (!receiptUrl) {
            alert("Couldn't send receipt — no shareable report on this conversation yet.");
            return;
          }
          const fullName = [conversation?.vehicle?.first_name, conversation?.vehicle?.last_name]
            .filter(Boolean)
            .join(' ')
            .trim() || 'Valued Customer';
          const body = `Hi ${fullName}, here's your authorized quote: ${receiptUrl}`;

          if (channel === 'sms') {
            const to = (opts.phone || conversation?.vehicle?.phone || '').trim();
            if (!to) {
              alert("Couldn't send SMS — no phone number on file.");
              return;
            }
            try {
              await smsApi.send({ to, body, conversation_id: Number(conversationId) });
            } catch (e) {
              console.error('[QUOTE_RECEIPT_SMS] failed', e);
              alert(`Failed to send SMS: ${(e as Error)?.message || 'unknown error'}`);
            }
            return;
          }

          if (channel === 'email') {
            const to = (opts.email || conversation?.vehicle?.email || '').trim();
            if (!to) {
              alert("Couldn't send email — no email address on file.");
              return;
            }
            try {
              // New conversation-level email endpoint — works on quote-only
              // conversations (no inspection required). Backend picks the
              // most-recent shareable artifact as the report link.
              await quoteApi.emailShare(Number(conversationId), {
                to,
                subject: 'Your authorized quote',
                body: `Hi {{customer_name}},\n\nHere's your authorized quote: {{report_url}}\n\nThank you!`,
              });
            } catch (e: any) {
              console.error('[QUOTE_RECEIPT_EMAIL] failed', e);
              const detail = e?.response?.data?.detail || e?.message || 'unknown error';
              alert(`Failed to send email: ${detail}`);
            }
          }
        };
        return (
          <>
            <PaperSignatureModal
              opened={paperSignatureOpen}
              onClose={() => setPaperSignatureOpen(false)}
              conversationId={Number(conversationId)}
              items={selectedRows}
              customerHasContact={customerHasContact}
              customerName={[conversation?.vehicle?.first_name, conversation?.vehicle?.last_name].filter(Boolean).join(' ').trim()}
              customerPhone={conversation?.vehicle?.phone || null}
              customerEmail={conversation?.vehicle?.email || null}
              docType={captureDocType}
              onDocTypeChange={setCaptureDocType}
              onApplied={({ notify, channel, phone, email }) => {
                mutateConversation?.();
                // Refetch quote so the JobPage rows reflect the just-
                // applied approve/decline without a page reload.
                quoteApi.get(conversationId).then(({ items, customer_approvals, invoice_approvals }) => {
                  if (items) _setQuoteItemsRaw(items as any);
                  if (customer_approvals) setCustomerApprovals(customer_approvals);
                  setInvoiceApprovals(invoice_approvals ?? null);
                }).catch(() => {});
                if (notify) sendReceiptIfPossible({ channel, phone, email });
              }}
            />
            <DigitalSignatureModal
              opened={digitalSignatureOpen}
              onClose={() => setDigitalSignatureOpen(false)}
              conversationId={Number(conversationId)}
              items={selectedRows}
              customerHasContact={customerHasContact}
              customerName={[conversation?.vehicle?.first_name, conversation?.vehicle?.last_name].filter(Boolean).join(' ').trim()}
              customerPhone={conversation?.vehicle?.phone || null}
              customerEmail={conversation?.vehicle?.email || null}
              teardownReassemblyDays={(shopData as any)?.report_config?.teardown_reassembly_days ?? null}
              docType={captureDocType}
              onDocTypeChange={setCaptureDocType}
              onApplied={({ notify, channel, phone, email }) => {
                mutateConversation?.();
                // Refetch quote so the JobPage rows reflect the just-
                // applied approve/decline without a page reload.
                quoteApi.get(conversationId).then(({ items, customer_approvals, invoice_approvals }) => {
                  if (items) _setQuoteItemsRaw(items as any);
                  if (customer_approvals) setCustomerApprovals(customer_approvals);
                  setInvoiceApprovals(invoice_approvals ?? null);
                }).catch(() => {});
                if (notify) sendReceiptIfPossible({ channel, phone, email });
              }}
            />
            <LegalDocsModal
              opened={legalDocsOpen}
              onClose={() => setLegalDocsOpen(false)}
              conversationId={Number(conversationId)}
              docType={captureDocType}
              onDocTypeChange={setCaptureDocType}
            />
          </>
        );
      })()}
      {conversationId && (() => {
        const selectedItems = quoteItems.filter((q) => {
          // Hidden items (is_hidden=true OR Tek-side selected=false) must
          // not be sent to the customer — they're not in the visible cart.
          // Auto-generated "Job <id>" placeholders (tek_job_id with no name
          // set) are also dropped so the SMS isn't full of $0 lines.
          if ((q as any).is_hidden) return false;
          const lbl = ((q.label ?? '') as string).trim();
          if (!lbl) return false;
          const lower = lbl.toLowerCase();
          if (lower === 'new job' || lower === 'new jobs') return false;
          if (/^job\s+\d+$/i.test(lbl)) return false;  // "Job 1160637194" style placeholders
          // INVOICE track: send the billable (approved) / selected work not yet
          // invoice-covered (estimate-approved items ARE the invoice candidates).
          if (captureDocType === 'invoice') {
            const qSidInv = (q as any).source_id ? String((q as any).source_id) : null;
            if ((q as any).invoice_authorization_id) return false;
            // Also drop items already covered by a SIGNED invoice doc (mirrors
            // the toolbar invoice predicate — keeps the two filters aligned).
            const invSigned = isRealCustomerSignature(invoiceApprovals);
            if (qSidInv && invoiceApprovals && invSigned) {
              const inA = (invoiceApprovals.approved_items || []).some((a: any) => String(a.source_id || '') === qSidInv);
              const inD = (invoiceApprovals.declined_items || []).some((a: any) => String(a.source_id || '') === qSidInv);
              if (inA || inD) return false;
            }
            const sInv = (q as any).item_status;
            const ibInv = (q as any).in_batch;
            const okInv = sInv === 'approved' || ibInv === true || (ibInv === undefined && (sInv == null || sInv === 'selected' || sInv === 'unauthorized'));
            return okInv && !!(q as any).source_id;
          }
          // Already-signed items don't belong in the SMS/email approval batch.
          if ((q as any).authorization_id) return false;
          const ib = (q as any).in_batch;
          const s = (q as any).item_status;
          // Skip already-decided items too — re-sending an approved/declined
          // line would bubble the customer count and risk double-sign.
          if (s === 'approved' || s === 'declined') return false;
          const inBatch = ib === true || (ib === undefined && (s == null || s === 'selected' || s === 'unauthorized'));
          return inBatch && (q as any).source_id;
        });
        const selectedSourceIds = selectedItems.map((q) => String((q as any).source_id));
        const customerName = [conversation?.vehicle?.first_name, conversation?.vehicle?.last_name].filter(Boolean).join(' ').trim();
        const customerPhone = conversation?.vehicle?.phone || null;
        const customerEmail = conversation?.vehicle?.email || null;
        return (
          <SendApprovalModal
            opened={sendApprovalOpen}
            onClose={() => !bulkSending && setSendApprovalOpen(false)}
            customerName={customerName}
            customerPhone={customerPhone}
            customerEmail={customerEmail}
            selectedItems={selectedItems as any[]}
            docType={captureDocType}
            onDocTypeChange={setCaptureDocType}
            sending={bulkSending}
            onSend={async ({ authorizerName, channel, phone, email }) => {
              setBulkSending(true);
              try {
                // Mark items as 'selected' + ping the writer. The actual
                // SMS/email delivery is orchestrated separately (existing
                // report-url + send flow); the channel choice + recipient
                // get forwarded to that flow once it's wired up.
                // "Both" sends the same items down both tracks (estimate +
                // invoice); each is an independent send-for-approval.
                for (const t of (captureDocType === 'both' ? ['estimate', 'invoice'] as const : [captureDocType])) {
                  await quoteApi.sendForApproval(Number(conversationId), selectedSourceIds, t);
                }
                void authorizerName;
                void channel;
                void phone;
                void email;
                setSendApprovalOpen(false);
              } catch (e) {
                console.error('[QUOTE_SEND_APPROVAL] failed', e);
                alert('Failed to send for approval. Please try again.');
              } finally {
                setBulkSending(false);
              }
            }}
          />
        );
      })()}
      {/* ── Chat panel ── */}
      <Box style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        {/* Detail title-header bar removed per design — the conversation title
            now lives on the sidebar row, and its actions (Pin / Archive / New
            visit / Delete) moved to that row's hover 3-dots menu (ShopLayout). */}

        {/* Transition confirmation modal */}
        <Modal
          opened={showTransitionModal}
          onClose={() => setShowTransitionModal(false)}
          title={<Text size="sm" fw={600} c={TEXT}>{nextStage === 'inspection' ? 'Send intake summary to customer?' : nextStage === 'quote' ? 'Send inspection results to customer?' : nextStage === 'completed' ? 'Send final report to customer?' : 'Advance stage?'}</Text>}
          centered
          styles={{
            content: { background: CARD_BG, border: `1px solid ${BORDER}` },
            header: { background: CARD_BG },
            overlay: { backdropFilter: 'blur(4px)' },
          }}
          radius="md"
        >
          <Stack gap={12}>
            <Text size="sm" c={TEXT_SECONDARY}>Add customer info to send status update.</Text>
            <Group gap={8} grow>
              <TextInput
                label="First name"
                placeholder="John"
                value={transitionFirstName || txnContact.first_name || ''}
                onChange={(e) => setTransitionFirstName(e.currentTarget.value)}
                size="xs"
                styles={{ input: { background: '#1E1E21', border: `1px solid ${BORDER}`, color: TEXT }, label: { color: TEXT_SECONDARY, fontSize: 11 } }}
              />
              <TextInput
                label="Last name"
                placeholder="Smith"
                value={transitionLastName || txnContact.last_name || ''}
                onChange={(e) => setTransitionLastName(e.currentTarget.value)}
                size="xs"
                styles={{ input: { background: '#1E1E21', border: `1px solid ${BORDER}`, color: TEXT }, label: { color: TEXT_SECONDARY, fontSize: 11 } }}
              />
            </Group>
            <TextInput
              label="Phone (required for SMS)"
              placeholder="+1 555-123-4567"
              value={transitionPhoneInput || txnContact.phone || ''}
              onChange={(e) => setTransitionPhoneInput(e.currentTarget.value)}
              size="xs"
              styles={{ input: { background: '#1E1E21', border: `1px solid ${BORDER}`, color: TEXT }, label: { color: TEXT_SECONDARY, fontSize: 11 } }}
            />
            <TextInput
              label="Email"
              placeholder="john@example.com"
              value={transitionEmail || txnContact.email || ''}
              onChange={(e) => setTransitionEmail(e.currentTarget.value)}
              size="xs"
              styles={{ input: { background: '#1E1E21', border: `1px solid ${BORDER}`, color: TEXT }, label: { color: TEXT_SECONDARY, fontSize: 11 } }}
            />
            <Box style={{ background: '#1E1E21', border: `1px solid ${BORDER}`, borderRadius: 8, padding: '10px 14px', fontSize: 13, color: TEXT_SECONDARY, lineHeight: 1.5 }}>
              {nextStage === 'inspection' && `Registration completed for your ${[conversation?.vehicle?.year, conversation?.vehicle?.make, conversation?.vehicle?.model].filter(Boolean).join(' ') || 'vehicle'}.\nView your vehicle history, recalls, and recommendations.`}
              {nextStage === 'quote' && `Inspection has started on your ${[conversation?.vehicle?.year, conversation?.vehicle?.make, conversation?.vehicle?.model].filter(Boolean).join(' ') || 'vehicle'}.\nShare your preferences.`}
              {nextStage === 'completed' && `Service is complete for your ${[conversation?.vehicle?.year, conversation?.vehicle?.make, conversation?.vehicle?.model].filter(Boolean).join(' ') || 'vehicle'}.\nView your full report and leave a review.`}
            </Box>
            <Group justify="flex-end" gap={8}>
              <Button variant="subtle" color="gray" size="sm" onClick={() => setShowTransitionModal(false)} disabled={transitioning}>Cancel</Button>
              <Button variant="outline" color="gray" size="sm" onClick={() => handleTransitionAdvance(true)} loading={transitioning}>Skip &amp; Advance</Button>
              <Button color="blue" size="sm" onClick={() => handleTransitionAdvance(false)} loading={transitioning}>Send &amp; Advance</Button>
            </Group>
          </Stack>
        </Modal>

        {/* Archive confirm modal */}
        <Modal
          opened={showArchiveConfirm}
          onClose={() => setShowArchiveConfirm(false)}
          title={<Text size="sm" fw={600} c={TEXT}>Archive conversation?</Text>}
          centered
          styles={{
            content: { background: CARD_BG, border: `1px solid ${BORDER}` },
            header: { background: CARD_BG },
            overlay: { backdropFilter: 'blur(4px)' },
          }}
          radius="md"
        >
          <Stack gap={16}>
            <Text size="sm" c={TEXT_SECONDARY}>This conversation will be moved to the archived section. You can unarchive it at any time.</Text>
            <Group justify="flex-end" gap={8}>
              <Button variant="subtle" color="gray" size="sm" onClick={() => setShowArchiveConfirm(false)}>Cancel</Button>
              <Button ref={archiveBtnRef} color="yellow" size="sm" onClick={() => { conversationId && updateConversationStatus(conversationId, 'archived'); setShowArchiveConfirm(false); }}>Archive</Button>
            </Group>
          </Stack>
        </Modal>

        {/* Delete confirm modal */}
        <Modal
          opened={showDeleteConfirm}
          onClose={() => setShowDeleteConfirm(false)}
          title={<Text size="sm" fw={600} c={TEXT}>Delete conversation?</Text>}
          centered
          styles={{
            content: { background: CARD_BG, border: `1px solid ${BORDER}` },
            header: { background: CARD_BG },
            overlay: { backdropFilter: 'blur(4px)' },
          }}
          radius="md"
        >
          <Stack gap={16}>
            <Text size="sm" c={TEXT_SECONDARY}>This will permanently delete this conversation and all its messages. This cannot be undone.</Text>
            <Group justify="flex-end" gap={8}>
              <Button variant="subtle" color="gray" size="sm" onClick={() => setShowDeleteConfirm(false)} disabled={deleting}>Cancel</Button>
              <Button ref={deleteBtnRef} color="red" size="sm" onClick={handleDelete} loading={deleting}>Delete</Button>
            </Group>
          </Stack>
        </Modal>

        {/* Content */}
        <Box
          style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', position: 'relative' }}
          onDragEnter={!isArchived ? handleDragEnter : undefined}
          onDragLeave={!isArchived ? handleDragLeave : undefined}
          onDragOver={!isArchived ? handleDragOver : undefined}
          onDrop={!isArchived ? handleDrop : undefined}
        >
          <Box style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'row', overflow: 'hidden' }}>
            {/* Chat column (left) — messages + composer + stage toolbar. Fills the
                remaining width (≈60vw alone, ≈30vw when a report is open). */}
            <Box style={{ flexGrow: 1, flexBasis: 0, minWidth: 0, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
              {/* LEFT pane: chat (title + Prompt/SMS/Report, messages/report, input). RIGHT pane: open card + buttons. */}
              <Box style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'row', overflow: 'hidden' }}>
                {/* LEFT — chat */}
                <Box style={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                  <Box style={{ flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '8px 12px', borderBottom: `1px solid ${BORDER}` }}>
                    <Box style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                      <Text span style={{ fontSize: 15, lineHeight: 1, flexShrink: 0 }}>💬</Text>
                      <Text size="sm" fw={700} c={TEXT}>Chat</Text>
                    </Box>
                    {promptSmsToggle}
                  </Box>
                  {composerMode === 'report' ? (
                    <Box style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                      <ReportPreviewPanel
                        inspectionId={effectiveInspectionId}
                        lastChangedAt={lastItemChangeAt}
                        onClose={() => setComposerMode('prompt')}
                        onGenerateReport={handleGenerateReport}
                        generating={sending}
                        vehicleEmail={conversation?.vehicle?.email}
                        vehiclePhone={conversation?.vehicle?.phone}
                        vehicleDescription={conversation?.vehicle ? [conversation.vehicle.year, conversation.vehicle.make, conversation.vehicle.model].filter(Boolean).join(' ') : null}
                        shopName={shopData?.name}
                        messages={messages}
                        conversationId={conversationId}
                        actionsRef={reportActionsRef}
                      />
                    </Box>
                  ) : (
                    <>
                      <Box style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                        {chatThread}
                      </Box>
                      {unifiedComposer}
                    </>
                  )}
                </Box>
                {/* RIGHT — the 6 panels are ALWAYS available (even before a vehicle
                    is identified) so the workspace never collapses to a lookup-only
                    page. Vehicle/customer entry lives in the Customer panel + the
                    lookup modal. Nothing selected → preview tiles; a card open →
                    its body fills + a compact tab bar pinned below. */}
                {(
                  <Box style={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', borderLeft: `3px solid ${BORDER}`, background: '#0C0C0E' }}>
                    {openCard ? (
                      <>
                        {/* Label is a fixed header; the body owns the scroll with a
                            definite height so children that fill height (e.g. the
                            Inspect tab) can scroll internally — required for the
                            sticky filter chips / category headers to actually stick. */}
                        <Group justify="space-between" wrap="nowrap" gap={8} style={{ flexShrink: 0, padding: '12px 14px 8px' }}>
                          <Text size="sm" fw={700} c={TEXT} style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{openCard.label}</Text>
                          {/* Inspect card only: quick template switcher in the top-right
                              corner (mirrors the sidebar's template list — switchMode /
                              switchTemplate — so you don't have to leave the card). */}
                          {openCard.id === 'inspect' && effectiveInspectionId && (jobTemplates?.length ?? 0) > 0 && (
                            <Menu position="bottom-end" withinPortal shadow="md" width={220}>
                              <Menu.Target>
                                <UnstyledButton
                                  style={{
                                    display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0,
                                    padding: '5px 10px', borderRadius: 7, border: `1px solid ${BORDER}`,
                                    background: '#18181B',
                                  }}
                                >
                                  <Text size="xs" fw={600} c={TEXT} style={{ maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {jobInspection?.mode === 'ai' ? 'AI Inspection' : (jobTemplates?.find((t) => t.id === jobInspection?.template_id)?.name ?? 'Template')}
                                  </Text>
                                  <IconChevronDown size={13} color={TEXT_MUTED} />
                                </UnstyledButton>
                              </Menu.Target>
                              <Menu.Dropdown style={{ background: '#1C1C1F', border: `1px solid ${BORDER}` }}>
                                <Menu.Label c={TEXT_MUTED}>Switch template</Menu.Label>
                                <Menu.Item
                                  onClick={async () => {
                                    if (jobInspection?.mode === 'ai') return;
                                    try {
                                      const { inspectionApi: api } = await import('../api/client');
                                      await api.switchMode(effectiveInspectionId, 'ai');
                                      const { mutate: swrMutate } = await import('swr');
                                      await swrMutate(`vehicle/inspection/${effectiveInspectionId}`);
                                    } catch (e) { console.error('Switch to AI failed:', e); }
                                  }}
                                  rightSection={jobInspection?.mode === 'ai' ? <IconCheck size={14} color={BLUE} /> : undefined}
                                  styles={{ item: { color: jobInspection?.mode === 'ai' ? '#fff' : TEXT, fontSize: 13 } }}
                                >
                                  AI Inspection
                                </Menu.Item>
                                {(jobTemplates ?? [])
                                  .filter((t) => t.is_active !== false && t.name?.trim().toLowerCase() !== 'ai inspection')
                                  .map((t) => {
                                    const active = jobInspection?.template_id === t.id && jobInspection?.mode !== 'ai';
                                    return (
                                      <Menu.Item
                                        key={t.id}
                                        onClick={async () => {
                                          if (active) return;
                                          try {
                                            const { inspectionApi: api } = await import('../api/client');
                                            await api.switchTemplate(effectiveInspectionId, t.id);
                                            const { mutate: swrMutate } = await import('swr');
                                            await swrMutate(`vehicle/inspection/${effectiveInspectionId}`);
                                          } catch (e) { console.error('Switch template failed:', e); }
                                        }}
                                        rightSection={active ? <IconCheck size={14} color={BLUE} /> : (t.items_count != null ? <Text size="xs" c={TEXT_MUTED}>{t.items_count}</Text> : undefined)}
                                        styles={{ item: { color: active ? '#fff' : TEXT, fontSize: 13 } }}
                                      >
                                        {t.name}
                                      </Menu.Item>
                                    );
                                  })}
                              </Menu.Dropdown>
                            </Menu>
                          )}
                        </Group>
                        <Box style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '0 14px 16px' }}>
                          {openCard.body}
                        </Box>
                        {/* Back — close the open card → preview grid (pick another).
                            No top divider — reads as one continuous panel with the body. */}
                        <Box style={{ flexShrink: 0, padding: '4px 12px 10px' }}>
                          <UnstyledButton
                            onClick={() => openCard.toggle()}
                            style={{
                              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                              width: '100%', padding: '15px 12px', borderRadius: 8,
                              border: '2px solid rgba(37,99,235,0.45)', background: 'transparent',
                              color: '#60A5FA', fontSize: 15, fontWeight: 600,
                              transition: 'background 0.12s',
                            }}
                            onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = 'rgba(37,99,235,0.10)'; }}
                            onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                          >
                            <IconLayoutGrid size={17} /> Overview
                          </UnstyledButton>
                        </Box>
                      </>
                    ) : (
                      <Box style={{ flex: 1, minHeight: 0, overflow: 'hidden', padding: 12 }}>
                        {cardPreviewGrid}
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
              {bottomBar}
            </Box>
          </Box>
        </Box>{/* end Content Container */}
      </Box>{/* end Left Panel */}

      {/* Add-job pop-up — always mounted so the rec "+ Add to Job" opens it from any view. */}
                {/* Add-job pop-up — the SAME card editor, on a standalone draft
                    that is only appended to the quote on Save. */}
                <Modal
                  opened={!!jobDraft}
                  onClose={() => closeJobDraft(false)}
                  withCloseButton={false}
                  centered
                  size="lg"
                  padding="lg"
                  styles={{ content: { background: '#0C0C0E', border: `1px solid ${BORDER}` }, body: { padding: 16 } }}
                >
                  {jobDraft && renderQuoteCard(jobDraft[0], 0, { inModal: true, editing: true, items: jobDraft, setItems: setJobDraft })}
                  <Group gap={8} mt="md" wrap="nowrap" align="stretch">
                    <UnstyledButton
                      onClick={() => closeJobDraft(false)}
                      style={{ flexShrink: 0, fontSize: 13, fontWeight: 600, color: TEXT_MUTED, textAlign: 'center', padding: '10px 16px', borderRadius: 8, border: `1px solid ${BORDER}`, background: 'transparent' }}
                    >Cancel</UnstyledButton>
                    <UnstyledButton
                      onClick={() => closeJobDraft(true)}
                      style={{ flex: 1, fontSize: 14, fontWeight: 700, color: '#fff', textAlign: 'center', padding: '10px 16px', borderRadius: 8, border: `1px solid ${BLUE}`, background: BLUE }}
                    >Save</UnstyledButton>
                  </Group>
                </Modal>

      {/* Pulse animation for skeleton */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
        .md-content ul:has(li > .md-img) {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
          padding: 0;
          list-style: none;
          margin: 6px 0;
        }
        .md-content ul:has(li > .md-img) li {
          list-style: none;
          margin: 0;
          padding: 0;
        }
      `}</style>


      <VehicleInfoModal
        opened={showVehicleInfo}
        onClose={() => setShowVehicleInfo(false)}
        conv={conversation}
        onSaved={async () => { await Promise.all([mutateConversation(), mutateConversations()]); setLastItemChangeAt(Date.now()); }}
        categories={reportCategories}
        inspectionId={effectiveInspectionId}
        inspectionMode={jobInspection?.mode}
        inspectionTemplateId={jobInspection?.template_id}
        templates={jobTemplates}
        members={shopMembers?.map((m) => ({ id: m.id, name: m.name }))}
        onAssign={async (userIds) => {
          if (!conversationId) return;
          await conversationsApi.patch(conversationId, { user_ids: userIds });
          await Promise.all([mutateConversation(), mutateConversations()]);
        }}
      />

      {/* Vehicle lookup drawer — opened when "No vehicle" is clicked */}
      <Drawer
        opened={showVehicleLookup}
        onClose={() => { setShowVehicleLookup(false); setVpPlate(''); setVpState(null); setVpVin(''); setVpError(''); setVpFirstName(''); setVpLastName(''); setVpPhone(''); setVpEmail(''); }}
        title={
          <Group gap={8}>
            <Box style={{ width: 28, height: 28, borderRadius: 6, background: 'rgba(37,99,235,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <IconCar size={15} color={BLUE} />
            </Box>
            <Text fw={700} c={TEXT} size="sm">Vehicle & Customer</Text>
          </Group>
        }
        position="right"
        size="md"
        styles={{
          content: { background: CARD_BG, borderLeft: `1px solid ${BORDER}` },
          header: { background: CARD_BG, borderBottom: `1px solid ${BORDER}`, paddingBottom: 12 },
          close: { color: TEXT_MUTED },
          body: { background: CARD_BG, paddingTop: 0 },
        }}
      >
        <Stack gap={10}>
          {/* Customer Info */}
          <Text size="xs" fw={600} c={TEXT_SECONDARY} style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>Customer Info</Text>
          <Group gap={8} wrap="nowrap">
            <TextInput
              placeholder="First"
              value={vpFirstName}
              onChange={(e) => setVpFirstName(e.currentTarget.value)}
              style={{ flex: 1 }}
              styles={{ input: { background: '#111113', borderColor: BORDER, color: TEXT }, label: { color: TEXT_SECONDARY, fontSize: 12, marginBottom: 4 } }}
            />
            <TextInput
              placeholder="Last"
              value={vpLastName}
              onChange={(e) => setVpLastName(e.currentTarget.value)}
              style={{ flex: 1 }}
              styles={{ input: { background: '#111113', borderColor: BORDER, color: TEXT }, label: { color: TEXT_SECONDARY, fontSize: 12, marginBottom: 4 } }}
            />
          </Group>
          <TextInput
            placeholder="(555) 123-4567"
            value={vpPhone}
            onChange={(e) => setVpPhone(e.currentTarget.value)}
            styles={{ input: { background: '#111113', borderColor: BORDER, color: TEXT }, label: { color: TEXT_SECONDARY, fontSize: 12, marginBottom: 4 } }}
          />
          <TextInput
            placeholder="email@example.com"
            value={vpEmail}
            onChange={(e) => setVpEmail(e.currentTarget.value)}
            styles={{ input: { background: '#111113', borderColor: BORDER, color: TEXT }, label: { color: TEXT_SECONDARY, fontSize: 12, marginBottom: 4 } }}
          />

          <Divider label={<Text size="xs" c={TEXT_MUTED}>Vehicle Lookup</Text>} labelPosition="center" color={BORDER} my={4} />

          {vpError && (
            <Alert color="red" icon={<IconAlertCircle size={14} />} radius="sm" styles={{ message: { fontSize: 12 } }}>{vpError}</Alert>
          )}
          <Group gap={8} wrap="nowrap">
            <TextInput
              placeholder="ABC 1234"
              label="License plate"
              value={vpPlate}
              onChange={(e) => setVpPlate(e.currentTarget.value.toUpperCase())}
              style={{ flex: 1 }}
              styles={{ input: { background: '#111113', borderColor: BORDER, color: TEXT, fontFamily: 'monospace', letterSpacing: 1 }, label: { color: TEXT_SECONDARY, fontSize: 12, marginBottom: 4 } }}
            />
            <Select
              label="State"
              placeholder="CA"
              data={US_STATES}
              value={vpState}
              onChange={setVpState}
              w={80}
              autoComplete="off"
              styles={{ input: { background: '#111113', borderColor: BORDER, color: TEXT }, label: { color: TEXT_SECONDARY, fontSize: 12, marginBottom: 4 } }}
              comboboxProps={{ withinPortal: true, styles: { dropdown: { background: '#1C1C1F', border: `1px solid ${BORDER}` }, option: { color: TEXT, fontSize: 12 } } }}
            />
          </Group>
          <Divider label={<Text size="xs" c={TEXT_MUTED}>or</Text>} labelPosition="center" color={BORDER} />
          <TextInput
            label="VIN"
            placeholder="1HGBH41JXMN109186"
            value={vpVin}
            onChange={(e) => setVpVin(e.currentTarget.value.toUpperCase())}
            styles={{ input: { background: '#111113', borderColor: BORDER, color: TEXT, fontFamily: 'monospace', letterSpacing: 1 }, label: { color: TEXT_SECONDARY, fontSize: 12, marginBottom: 4 } }}
            maxLength={17}
          />
          {(() => {
            const canLookup = (vpPlate.trim().length >= 2 && !!vpState) || /^[A-HJ-NPR-Z0-9]{17}$/i.test(vpVin.trim());
            return (
              <Button
                fullWidth
                radius="md"
                size="md"
                mt={8}
                loading={sending}
                disabled={!canLookup}
                style={canLookup ? { background: BLUE, color: '#fff' } : undefined}
                onClick={async () => {
                  const plate = vpPlate.trim();
                  const vin = vpVin.trim();
                  if (!plate && !vin) return setVpError('Enter a license plate or VIN.');
                  if (vin && vin.length !== 17) return setVpError('VIN must be exactly 17 characters.');
                  if (plate && !vpState) return setVpError('Select a state for the license plate.');
                  setVpError('');
                  setSending(true);
                  try {
                    await vehicleApi.textLookup({
                      ...(vin ? { vin } : { plate, state: vpState! }),
                      conversation_id: conversationId!,
                    });
                    setShowVehicleLookup(false);
                    setActiveTab('repair');
                    await mutateMessages();
                    await Promise.all([mutateConversation(), mutateConversations()]);
                  } catch (e: any) {
                    setVpError(e?.response?.data?.message || 'Lookup failed. Try again.');
                  } finally {
                    setSending(false);
                  }
                }}
              >
                Look up vehicle
              </Button>
            );
          })()}
        </Stack>
      </Drawer>

      {/* Final Report Preview Drawer */}
      <Drawer
        opened={reportPreviewOpen}
        onClose={() => setReportPreviewOpen(false)}
        position="right"
        size="lg"
        title={<Text fw={600} size="sm">Final Report Preview</Text>}
        styles={{
          content: { background: '#18181B', borderLeft: '1px solid #27272A' },
          header: { background: '#18181B', borderBottom: '1px solid #27272A' },
          body: { padding: 0, display: 'flex', flexDirection: 'column', height: 'calc(100% - 60px)' },
        }}
      >
        <ScrollArea style={{ flex: 1 }} p="md">
          <Stack gap="xl">
            {/* Work Performed */}
            <Box>
              <Text size="xs" fw={600} c="#71717A" mb={10} style={{ textTransform: 'uppercase', letterSpacing: 0.7 }}>
                Work Performed
              </Text>
              <Stack gap={8}>
                {previewWorkItems.map((item, i) => (
                  <Group key={i} gap={8}>
                    <TextInput
                      placeholder={`e.g. Replaced front brake pads`}
                      value={item}
                      onChange={(e) => setPreviewWorkItems((prev) => prev.map((v, idx) => idx === i ? e.currentTarget.value : v))}
                      style={{ flex: 1 }}
                      styles={{ input: { background: '#111113', borderColor: '#27272A', color: '#F4F4F5' } }}
                    />
                    {previewWorkItems.length > 1 && (
                      <ActionIcon variant="subtle" color="gray" onClick={() => setPreviewWorkItems((prev) => prev.filter((_, idx) => idx !== i))}>
                        <IconTrashX size={14} />
                      </ActionIcon>
                    )}
                  </Group>
                ))}
                <UnstyledButton
                  onClick={() => setPreviewWorkItems((prev) => [...prev, ''])}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#2563EB', padding: '4px 0' }}
                >
                  <IconPlus size={13} /> Add item
                </UnstyledButton>
              </Stack>
            </Box>

            <Divider color="#27272A" />

            {/* Cost Breakdown */}
            <Box>
              <Text size="xs" fw={600} c="#71717A" mb={10} style={{ textTransform: 'uppercase', letterSpacing: 0.7 }}>
                Cost Breakdown
              </Text>
              <Stack gap={8}>
                {previewCosts.map((cost, i) => (
                  <Group key={i} gap={8} align="flex-start">
                    <TextInput
                      placeholder="Description"
                      value={cost.label}
                      onChange={(e) => setPreviewCosts((prev) => prev.map((c, idx) => idx === i ? { ...c, label: e.currentTarget.value } : c))}
                      style={{ flex: 2 }}
                      styles={{ input: { background: '#111113', borderColor: '#27272A', color: '#F4F4F5' } }}
                    />
                    <TextInput
                      placeholder="0.00"
                      value={cost.amount}
                      onChange={(e) => setPreviewCosts((prev) => prev.map((c, idx) => idx === i ? { ...c, amount: e.currentTarget.value } : c))}
                      leftSection={<Text size="xs" c="#71717A">$</Text>}
                      style={{ width: 100 }}
                      styles={{ input: { background: '#111113', borderColor: '#27272A', color: '#F4F4F5' } }}
                    />
                    <Select
                      value={cost.type}
                      onChange={(v) => setPreviewCosts((prev) => prev.map((c, idx) => idx === i ? { ...c, type: v ?? 'parts' } : c))}
                      data={[
                        { value: 'parts', label: 'Parts' },
                        { value: 'labor', label: 'Labor' },
                        { value: 'fee', label: 'Fee' },
                        { value: 'tax', label: 'Tax' },
                        { value: 'discount', label: 'Discount' },
                      ]}
                      style={{ width: 110 }}
                      styles={{ input: { background: '#111113', borderColor: '#27272A', color: '#F4F4F5' } }}
                      comboboxProps={{ withinPortal: true }}
                    />
                    {previewCosts.length > 1 && (
                      <ActionIcon variant="subtle" color="gray" mt={2} onClick={() => setPreviewCosts((prev) => prev.filter((_, idx) => idx !== i))}>
                        <IconTrashX size={14} />
                      </ActionIcon>
                    )}
                  </Group>
                ))}
                <Group justify="space-between" mt={4}>
                  <UnstyledButton
                    onClick={() => setPreviewCosts((prev) => [...prev, { label: '', amount: '', type: 'parts' }])}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#2563EB', padding: '4px 0' }}
                  >
                    <IconPlus size={13} /> Add cost
                  </UnstyledButton>
                  {previewCosts.some((c) => c.amount) && (
                    <Text size="sm" fw={700} c="#F4F4F5">
                      Total: ${previewCosts.reduce((sum, c) => {
                        const v = parseFloat(c.amount) || 0;
                        return sum + (c.type === 'discount' ? -v : v);
                      }, 0).toFixed(2)}
                    </Text>
                  )}
                </Group>
              </Stack>
            </Box>

            <Divider color="#27272A" />

            {/* Notes */}
            <Box>
              <Text size="xs" fw={600} c="#71717A" mb={10} style={{ textTransform: 'uppercase', letterSpacing: 0.7 }}>
                Additional Notes (optional)
              </Text>
              <Textarea
                placeholder="Any notes for the customer..."
                value={previewNotes}
                onChange={(e) => setPreviewNotes(e.currentTarget.value)}
                minRows={3}
                styles={{ input: { background: '#111113', borderColor: '#27272A', color: '#F4F4F5' } }}
              />
            </Box>
          </Stack>
        </ScrollArea>

        <Box style={{ padding: '16px 20px', borderTop: '1px solid #27272A', background: '#18181B' }}>
          <Group justify="space-between">
            <Button variant="subtle" color="gray" onClick={() => setReportPreviewOpen(false)}>Cancel</Button>
            <Button
              color="haist"
              leftSection={<IconCircleCheck size={15} />}
              onClick={handleGenerateReport}
              loading={sending}
            >
              Generate Report
            </Button>
          </Group>
        </Box>
      </Drawer>
    </Box>
  );
}

// Legal-documents browser — list every signature source attached to the
// conversation (HAIST paper/digital signatures, customer-portal whole-quote
// signature, Tek RO authorizations) with file link, authorizer, date,
// items + totals, RO# (Tek only). Read-only: writer can View the signature
// image or copy the URL. Remove-doc still lives per-row.
function LegalDocsModal({
  opened, onClose, conversationId, docType, onDocTypeChange,
}: {
  opened: boolean;
  onClose: () => void;
  conversationId: number;
  docType: 'estimate' | 'invoice' | 'both';
  onDocTypeChange: (v: 'estimate' | 'invoice' | 'both') => void;
}) {
  const [docs, setDocs] = useState<import('../api/client').LegalDocument[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    if (!opened) return;
    let cancelled = false;
    setLoading(true);
    setError('');
    quoteApi.listLegalDocuments(conversationId)
      .then((d) => { if (!cancelled) setDocs(d); })
      .catch((e: any) => { if (!cancelled) setError(e?.response?.data?.detail || e?.message || 'failed to load'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [opened, conversationId]);

  const methodLabel = (m: string | null) => {
    if (!m) return 'Unknown';
    if (m === 'paper_photo') return 'Paper signature';
    if (m === 'live') return 'Customer portal';
    if (m === 'digital_pending' || m === 'digital') return 'Digital signature';
    if (m === 'tek_ro') return 'Tek RO';
    return m;
  };
  const methodColor = (m: string | null) => {
    if (m === 'paper_photo') return '#F59E0B';
    if (m === 'live') return '#22C55E';
    if (m === 'digital_pending' || m === 'digital') return '#3B82F6';
    if (m === 'tek_ro') return '#A78BFA';
    return '#71717A';
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size="xl"
      title={<Text size="sm" fw={600} c="#F4F4F5">Legal documents on this conversation</Text>}
      styles={{
        content: { background: '#18181B', border: '1px solid #27272A' },
        header: { background: '#18181B' },
        overlay: { backdropFilter: 'blur(4px)' },
      }}
      radius="md"
    >
      <Stack gap={12}>
        <CaptureDocTypePicker value={docType} onChange={onDocTypeChange} hint="Track for new captures" />
        <Text size="xs" c="#71717A">
          {docs.length === 0 ? 'No documents yet.' : `${docs.length} document${docs.length === 1 ? '' : 's'}`}
        </Text>
        {loading && <Text size="sm" c="#A1A1AA">Loading…</Text>}
        {error && (
          <Alert color="red" radius="sm">{error}</Alert>
        )}
        {!loading && !error && docs.length === 0 && (
          <Text size="sm" c="#71717A">No legal documents on file yet.</Text>
        )}
        {[...docs].sort((a, b) => Number(!!a.superseded) - Number(!!b.superseded)).map((d) => {
          const dateStr = d.signed_at ? new Date(d.signed_at).toLocaleString() : '—';
          const approvedCount = d.items.filter((it) => it.decision === 'approved').length;
          const declinedCount = d.items.filter((it) => it.decision === 'declined').length;
          return (
            <Box
              key={d.id}
              style={{
                padding: '12px 14px', borderRadius: 8,
                background: '#0C0C0E', border: '1px solid #27272A',
                opacity: d.superseded ? 0.55 : 1,
              }}
            >
              <Group justify="space-between" mb={8} align="flex-start" wrap="nowrap">
                <Box style={{ minWidth: 0, flex: 1 }}>
                  <Group gap={8} wrap="nowrap">
                    <Box style={{
                      padding: '2px 8px', borderRadius: 4,
                      background: `${methodColor(d.method)}22`,
                      color: methodColor(d.method),
                      fontSize: 11, fontWeight: 700, letterSpacing: '0.04em',
                      textTransform: 'uppercase',
                    }}>{methodLabel(d.method)}</Box>
                    {d.doc_type && (
                      <Box style={{
                        padding: '2px 8px', borderRadius: 4,
                        background: d.doc_type === 'invoice' ? 'rgba(99,102,241,0.18)' : 'rgba(34,197,94,0.15)',
                        color: d.doc_type === 'invoice' ? '#A5B4FC' : '#22C55E',
                        fontSize: 11, fontWeight: 700, letterSpacing: '0.04em',
                        textTransform: 'uppercase',
                      }}>{d.doc_type}</Box>
                    )}
                    {d.source === 'tek' && d.ro_number && (
                      <Box style={{
                        padding: '2px 8px', borderRadius: 4,
                        background: 'rgba(167,139,250,0.15)', color: '#A78BFA',
                        fontSize: 11, fontWeight: 600,
                      }}>RO #{d.ro_number}</Box>
                    )}
                    {d.status && d.status !== 'applied' && (
                      <Box style={{
                        padding: '2px 8px', borderRadius: 4,
                        background: 'rgba(113,113,122,0.18)', color: '#A1A1AA',
                        fontSize: 11, fontWeight: 600, textTransform: 'capitalize',
                      }}>{d.status}</Box>
                    )}
                    {d.superseded && (
                      <Box style={{
                        padding: '2px 8px', borderRadius: 4,
                        background: 'rgba(245,158,11,0.15)', color: '#F59E0B',
                        fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase',
                      }} title="A newer authorization covers these items — kept for audit history">Superseded</Box>
                    )}
                  </Group>
                  <Text size="sm" c="#F4F4F5" fw={600} mt={6}>
                    {d.authorizer_name || '(no authorizer name)'}
                  </Text>
                  <Text size="xs" c="#71717A" mt={2}>
                    {dateStr} · {d.item_count} item{d.item_count === 1 ? '' : 's'} · ${d.total.toFixed(2)} total
                    {(approvedCount + declinedCount) > 0 && (
                      <>
                        {' '}·{' '}
                        <Text span c="#22C55E" fw={600}>{approvedCount} approved</Text>
                        {' · '}
                        <Text span c="#EF4444" fw={600}>{declinedCount} declined</Text>
                      </>
                    )}
                  </Text>
                </Box>
                {d.file_url && (
                  <Button
                    size="compact-xs"
                    variant="outline"
                    color="gray"
                    component="a"
                    href={d.file_url}
                    target="_blank"
                    rel="noreferrer"
                  >
                    View signature
                  </Button>
                )}
              </Group>
              {d.file_url && (
                /\.pdf($|\?)/i.test(d.file_url) ? (
                  <iframe
                    src={d.file_url}
                    title="signed document"
                    style={{ width: '100%', height: 380, border: `1px solid ${BORDER}`, borderRadius: 6, marginTop: 8, background: '#fff' }}
                  />
                ) : (
                  <Box mt={8} style={{ borderRadius: 6, overflow: 'hidden', border: `1px solid ${BORDER}`, background: '#fff' }}>
                    <a href={d.file_url} target="_blank" rel="noreferrer">
                      <img src={d.file_url} alt="signed document" style={{ width: '100%', maxHeight: 460, objectFit: 'contain', display: 'block' }} />
                    </a>
                  </Box>
                )
              )}
              {d.items.length > 0 && (
                <Stack gap={3} mt={6}>
                  {d.items.map((it) => (
                    <Group key={it.source_id} justify="space-between" wrap="nowrap" style={{ padding: '4px 8px', background: '#18181B', borderRadius: 4, opacity: it.superseded ? 0.5 : 1 }}>
                      <Text size="xs" c="#F4F4F5" truncate style={{ flex: 1, minWidth: 0, textDecoration: it.superseded ? 'line-through' : 'none' }}>
                        {it.label || '(no label)'}
                      </Text>
                      <Group gap={6} style={{ flexShrink: 0 }}>
                        {it.superseded && (
                          <Text size="xs" c="#71717A" fw={600}>superseded</Text>
                        )}
                        {it.decision === 'approved' && (
                          <Text size="xs" c="#22C55E" fw={600}>✓ Approved</Text>
                        )}
                        {it.decision === 'declined' && (
                          <Text size="xs" c="#EF4444" fw={600}>✗ Declined</Text>
                        )}
                        <Text size="xs" c="#A1A1AA" style={{ fontVariantNumeric: 'tabular-nums', minWidth: 60, textAlign: 'right' }}>
                          ${it.amount.toFixed(2)}
                        </Text>
                      </Group>
                    </Group>
                  ))}
                </Stack>
              )}
            </Box>
          );
        })}
        <Group justify="flex-end" mt={4}>
          <Button size="sm" variant="subtle" color="gray" onClick={onClose}>Close</Button>
        </Group>
      </Stack>
    </Modal>
  );
}

// Send-for-approval modal — SMS/email link path only. The other two
// methods (Screen signature, Offline print) are separate top-level
// toolbar buttons and don't re-appear inside this modal.
function SendApprovalModal({
  opened, onClose, customerName, customerPhone, customerEmail,
  selectedItems, docType, onDocTypeChange, sending, onSend,
}: {
  opened: boolean;
  onClose: () => void;
  customerName: string;
  customerPhone: string | null;
  customerEmail: string | null;
  selectedItems: any[];
  docType: 'estimate' | 'invoice' | 'both';
  onDocTypeChange: (v: 'estimate' | 'invoice' | 'both') => void;
  sending: boolean;
  onSend: (args: { authorizerName: string; channel: 'sms' | 'email'; phone: string; email: string }) => Promise<void>;
}) {
  const [authorizerName, setAuthorizerName] = useState(customerName || '');
  const [phone, setPhone] = useState(customerPhone || '');
  const [email, setEmail] = useState(customerEmail || '');
  // Channel pick — SMS or Email, no Both. Default to SMS when phone is
  // on file, else Email when only email exists, else SMS as fallback.
  const initialChannel: 'sms' | 'email' = (customerPhone || '').trim() ? 'sms' : ((customerEmail || '').trim() ? 'email' : 'sms');
  const [channel, setChannel] = useState<'sms' | 'email'>(initialChannel);
  useEffect(() => {
    if (opened) {
      setAuthorizerName(customerName || '');
      setPhone(customerPhone || '');
      setEmail(customerEmail || '');
      setChannel((customerPhone || '').trim() ? 'sms' : ((customerEmail || '').trim() ? 'email' : 'sms'));
    }
  }, [opened, customerName, customerPhone, customerEmail]);

  const target = channel === 'sms' ? phone.trim() : email.trim();
  const canSend = authorizerName.trim().length > 0 && target.length > 0;

  return (
    <Modal
      opened={opened}
      onClose={() => !sending && onClose()}
      withCloseButton={!sending}
      title={<Text size="sm" fw={600} c="#F4F4F5">Send for approval</Text>}
      size="md"
      radius="md"
      styles={{
        content: { background: '#18181B', border: '1px solid #27272A' },
        header: { background: '#18181B' },
        overlay: { backdropFilter: 'blur(4px)' },
      }}
    >
      <Stack gap={14}>
        <CaptureDocTypePicker value={docType} onChange={onDocTypeChange} hint="What this send authorizes" />
        <Box>
          <Text size="xs" c="#71717A" mb={6}>Customer — authorizing the work</Text>
          <TextInput
            label="Authorizer name"
            required
            value={authorizerName}
            onChange={(e) => setAuthorizerName(e.currentTarget.value)}
            placeholder="Full name"
            styles={{
              input: { background: '#0C0C0E', borderColor: '#27272A', color: '#F4F4F5' },
              label: { color: '#A1A1AA', fontSize: 12 },
            }}
          />
        </Box>

        <Box>
          <Text size="xs" c="#71717A" mb={6}>Send via</Text>
          <SegmentedControl
            fullWidth
            value={channel}
            onChange={(v) => setChannel(v as 'sms' | 'email')}
            data={[
              { label: '📲 SMS', value: 'sms' },
              { label: '✉️ Email', value: 'email' },
            ]}
            styles={{
              root: { background: '#0C0C0E', border: '1px solid #27272A' },
              indicator: { background: '#27272A' },
              label: { color: '#F4F4F5' },
            }}
          />
          {channel === 'sms' ? (
            <TextInput
              mt={8}
              label="Phone"
              required
              value={phone}
              onChange={(e) => setPhone(e.currentTarget.value)}
              placeholder="+1 555 123 4567"
              styles={{
                input: { background: '#0C0C0E', borderColor: '#27272A', color: '#F4F4F5' },
                label: { color: '#A1A1AA', fontSize: 12 },
              }}
            />
          ) : (
            <TextInput
              mt={8}
              label="Email"
              required
              value={email}
              onChange={(e) => setEmail(e.currentTarget.value)}
              placeholder="customer@example.com"
              styles={{
                input: { background: '#0C0C0E', borderColor: '#27272A', color: '#F4F4F5' },
                label: { color: '#A1A1AA', fontSize: 12 },
              }}
            />
          )}
        </Box>

        <Box>
          <Text size="xs" c="#71717A" mb={6}>
            {selectedItems.length} line item{selectedItems.length === 1 ? '' : 's'} will be sent for the customer to approve or decline:
          </Text>
          <ScrollArea.Autosize mah={220}>
            <Stack gap={4}>
              {selectedItems.map((it: any) => (
                <Box
                  key={it.source_id}
                  style={{
                    display: 'flex', justifyContent: 'space-between', gap: 10,
                    padding: '8px 10px', borderRadius: 6,
                    background: '#0C0C0E', border: '1px solid #27272A',
                  }}
                >
                  <Text size="sm" c="#F4F4F5" truncate style={{ flex: 1, minWidth: 0 }}>
                    {it.label || '(no label)'}
                  </Text>
                  <Text size="sm" c="#A1A1AA" style={{ fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
                    ${Number(it.amount || it.total || ((parseFloat(it.parts || '0') + parseFloat(it.labor || '0')) || 0)).toFixed(0)}
                  </Text>
                </Box>
              ))}
            </Stack>
          </ScrollArea.Autosize>
        </Box>

        {!target && (
          <Alert color="yellow" radius="sm" icon={null}>
            <Text size="xs">Enter a {channel === 'sms' ? 'phone number' : 'email address'} to send the link.</Text>
          </Alert>
        )}

        <Group justify="flex-end" gap={8}>
          <Button size="sm" variant="subtle" color="gray" onClick={onClose} disabled={sending}>
            Cancel
          </Button>
          <Button
            size="sm"
            color="haist"
            loading={sending}
            disabled={!canSend}
            leftSection={<span aria-hidden>{channel === 'sms' ? '📲' : '✉️'}</span>}
            onClick={() => onSend({
              authorizerName: authorizerName.trim(),
              channel,
              phone: phone.trim(),
              email: email.trim(),
            })}
          >
            Send via {channel === 'sms' ? 'SMS' : 'email'}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
